import * as THREE from 'three'

// 启动探针：排查"页面卡在加载画面"这类问题（只在控制台打一行，不影响使用）
console.log('[boot] 模块开始执行 origin=' + location.origin + ' secure=' + String(window.isSecureContext))
window.addEventListener('error', (e) => {
  console.log('[boot-error] ' + (e && e.message ? e.message : '(无消息)') +
    ' @ ' + ((e && e.filename) || '?') + ':' + ((e && e.lineno) || '?'))
})
window.addEventListener('unhandledrejection', (e) => {
  const r = e && e.reason
  console.log('[boot-reject] ' + (r && r.message ? r.message : String(r)))
})
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { TransformControls } from 'three/addons/controls/TransformControls.js'
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { SSAOPass } from 'three/addons/postprocessing/SSAOPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { FXAAPass } from 'three/addons/postprocessing/FXAAPass.js'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js'
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { createVegetation } from './vegetation.js'
import { gsap } from 'gsap'
import { t, getLang, setLang, ZONE_NAMES_EN, PRODUCT_NAMES_EN, enVideoName, TRANSLATIONS } from './i18n.js'
import { createBoothUI } from './vendor/booth-ui.js'

// ---------------------------------------------------------------------------
// 资源版本号：换过模型/贴图/视频后把它加一，URL 就变，浏览器和 CDN 都会重新拉。
// 值写在 index.html 的 <meta name="model-version"> 里 —— 那里还挂着 hall.glb 的 preload，
// 两边同源，避免 preload 与实际请求 URL 不一致导致重复下载。
// 带 ?v= 的大资源在服务器侧会被标成 long-cache(immutable)，方便以后在前面挂 CDN。
// ---------------------------------------------------------------------------
const MODEL_VERSION = document.querySelector('meta[name="model-version"]')?.content || '20260920g'
const withVersion = (url) => url + (url.includes('?') ? '&' : '?') + 'v=' + MODEL_VERSION

// 爆炸图模型（全精度、给分解演示用）单独一个版本号：模型重新压缩后改这个，
// 不会连带把展厅里的 LOD 模型缓存作废。
const EXPLODE_VERSION = '20261020a'

// 视频存放位置：按访问域名自动分流 —— 国内站(.com)走大陆加速，海外站(.net)走海外加速。
// meta 里的地址作为兜底（本地 / IP 访问时用）；空 = 与站点同源（videos/products/...）。
// 例：<meta name="video-base" content="https://video.oubokt.net/videos/" />
const VIDEO_BASE = (() => {
  const meta = (document.querySelector('meta[name="video-base"]')?.content || '').trim()
  const host = (location.hostname || '').toLowerCase()
  if (host === 'oubokt.com' || host.endsWith('.oubokt.com')) return 'https://video.oubokt.com/videos/'
  if (host === 'oubokt.net' || host.endsWith('.oubokt.net')) return 'https://video.oubokt.net/videos/'
  return meta
})()
const videoUrl = (rel) => (VIDEO_BASE ? VIDEO_BASE + rel : 'videos/' + rel)

// ---------------------------------------------------------------------------
// 设备类别：手机 / 平板 / 电脑
// 判断顺序很关键：**先看主指针是鼠标还是手指**。鼠标/触控板为主的一律算电脑，
// 所以"带触摸屏的笔记本"（哪怕窗口很小）不会再被误判成手机去加载移动端资源。
// 另外 iPadOS 会把自己伪装成 Mac，得靠"Macintosh + 有触摸点"认出来。
// 调试时可以用 ?device=phone / tablet / desktop 强制指定，方便在电脑上验证移动端布局。
// ---------------------------------------------------------------------------
const DEVICE_OVERRIDE = (new URLSearchParams(location.search).get('device') || '').toLowerCase()
const UA_STR = navigator.userAgent || ''
const HAS_TOUCH = (navigator.maxTouchPoints || 0) > 0
const POINTER_COARSE = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
const POINTER_FINE = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches
// 用"屏幕短边"而不是窗口短边：窗口调小不该改变设备类别
const SCREEN_SHORT = Math.min(
  (window.screen && window.screen.width) || window.innerWidth,
  (window.screen && window.screen.height) || window.innerHeight
)
const UA_PHONE = /iPhone|iPod|Windows Phone|BlackBerry/i.test(UA_STR) ||
  (/Android/i.test(UA_STR) && /Mobile/i.test(UA_STR))
const UA_TABLET = /iPad/i.test(UA_STR) ||
  (/Android/i.test(UA_STR) && !/Mobile/i.test(UA_STR)) ||
  (/Macintosh/i.test(UA_STR) && HAS_TOUCH)   // iPadOS 伪装成 Mac
const IS_PHONE = DEVICE_OVERRIDE
  ? DEVICE_OVERRIDE === 'phone'
  : (UA_PHONE || (POINTER_COARSE && !POINTER_FINE && SCREEN_SHORT < 600))
const IS_TABLET = DEVICE_OVERRIDE
  ? DEVICE_OVERRIDE === 'tablet'
  : (!IS_PHONE && (UA_TABLET || (POINTER_COARSE && !POINTER_FINE && SCREEN_SHORT >= 600 && SCREEN_SHORT <= 1366)))
const IS_TOUCH = IS_PHONE || IS_TABLET
// 手机/平板：关闭阴影/泛光/抗锯齿并降像素比
// （第 3 步会改成"按显卡能力分档"，平板未必是低端机）
const IS_MOBILE = IS_TOUCH
document.documentElement.classList.add(IS_PHONE ? 'dev-phone' : IS_TABLET ? 'dev-tablet' : 'dev-desktop')
function updateOrientationClass() {
  const portrait = window.innerHeight > window.innerWidth
  document.documentElement.classList.toggle('orient-portrait', portrait)
  document.documentElement.classList.toggle('orient-landscape', !portrait)
}
// 弹窗里的"文字和按钮"按屏幕尺寸等比缩放：以 1600×900 为电脑基准，
// 这样手机上"文字 : 图片/模型"的比例和电脑上接近，按钮不会挤压内容。
function updatePopupScale() {
  const k = Math.max(0.5, Math.min(1, Math.min(window.innerWidth / 1600, window.innerHeight / 900)))
  document.documentElement.style.setProperty('--pop-scale', k.toFixed(3))
}
updatePopupScale()
window.addEventListener('resize', updatePopupScale)
updateOrientationClass()
window.addEventListener('resize', updateOrientationClass)
window.addEventListener('orientationchange', updateOrientationClass)

// 手机/平板换成更小的环境贴图：小屏幕上 1K/2K 几乎看不出差别，但能省几十 MB。
// （定义必须放在这里，因为它要在 HDRI 初始化之前就能用。）
const MOBILE_HDRI_ALT = {
  'env/qwantani_moonrise_puresky_update_4k.exr': 'env/qwantani_moonrise_puresky_update_2k.exr',
  'env/017.hdr': 'env/017-1k.hdr',
  'env/017-2k.hdr': 'env/017-1k.hdr',
}
const hdriUrlForDevice = (url) => (IS_TOUCH && MOBILE_HDRI_ALT[url]) || url

// 交付模式开关：把「调试」按钮和右下角 FPS 读数藏起来，避免误操作。
// 需要自己调面板时改回 true 即可。
const DEBUG_UI_ENABLED = false

// 泛光层分辨率倍率 / 是否开抗锯齿：真实取值在画质分档之后统一决定（见 QUALITY）
let BLOOM_RES_SCALE = 1

// 超采样下限。屏幕像素比只有 1 的时候（比如 1920×1080、缩放 100%），1:1 渲染等于
// 完全没有抗锯齿，细线一定是锯齿。这里强制按更高的倍率渲染，再缩回屏幕尺寸，
// 相当于自带抗锯齿。数值越大越干净、越吃性能（面板里的"像素比"仍然是上限）。
const SS_FLOOR = (() => {
  const q = new URLSearchParams(location.search).get('ss')
  return q === null ? 1.25 : Math.max(1, Math.min(2, Number(q) || 1))
})()
// 自动画质：机器扛得住就用超采样把边缘磨干净，扛不住就自动降回 1.0（不用手动调）。
// 想固定住不动，地址后加 ?autoq=0
const AUTO_QUALITY = new URLSearchParams(location.search).get('autoq') !== '0'
let ssCurrent = SS_FLOOR // 稍后按画质档位夹到 [ssMin, ssMax]
let qGood = 0
let qBad = 0
let qRecover = 0    // 连续"够快"秒数（高于 Q_RECOVER_FPS）
let qWarmup = 20 // 刚进页面还在加载模型，帧率不具参考性
const fpsHist = [] // 最近几秒的帧率（取中位数判断，抗单次抖动）
const qPageStart = performance.now()
// 面板里的"像素比"上限。用一个独立变量保存，避免初始化早期去读还没声明的 rdDpr
// （那样会抛 "Cannot access 'rdDpr' before initialization"）
let panelDprCap = 1.5
// 泛光那一遍是"把整个场景再画一次"，手机上很贵。自动画质在最差的机器上会把它关掉。
let bloomAutoOff = false
let qBloomOffCount = 0
let qBloomBack = 0        // 泛光被自动关掉后，连续够快的秒数（够久就把泛光还回来）
// 画质调整记录（右下角读数里会显示最后一条，方便定位"什么时候被降的、当时多少帧"）
const qualityEvents = []
function logQuality(msg) {
  let stamp = ''
  try { stamp = new Date().toLocaleTimeString('zh-CN', { hour12: false }) } catch {}
  qualityEvents.push(stamp + ' ' + msg)
  if (qualityEvents.length > 6) qualityEvents.shift()
  console.log('[autoq] ' + msg)
}
function setBloomAutoOff(on) {
  if (bloomAutoOff === on) return
  bloomAutoOff = on
  if (!on) qBloomBack = 0
  if (on) {
    if (bloomPassOnly) bloomPassOnly.enabled = false
    if (bloomMixPass) bloomMixPass.enabled = false
  } else {
    // 恢复时按面板设置重新算一遍，避免把用户手动关掉的泛光又打开
    try { fdPostApply() } catch {}
  }
  console.log('[autoq] ' + (on ? '已自动关闭泛光（保住流畅度）' : '已恢复泛光'))
}
// 高 DPI 屏（手机/平板）的像素比下限，见下方 levelDprCap 的说明。
// 声明必须在 effectivePixelRatio 第一次被调用之前（它在初始化早期就会被调用）。
const TOUCH_DPR_FLOOR = 1.5
// 用 ?device=phone/tablet 强制模拟时，连"设备像素比"也按真机来（手机/平板通常是 2~3）。
// 否则在桌面（dpr=1）上模拟出来的清晰度和真机差一大截，等于没法复现问题。
const DEVICE_DPR = (() => {
  const real = Math.max(1, window.devicePixelRatio || 1)
  return DEVICE_OVERRIDE && IS_TOUCH ? Math.max(2, real) : real
})()
function effectivePixelRatio() {
  // 以"设备像素比"为基准（不超过档位上限），再乘自动画质的缩放系数。
  // 注意不能写成 min(max(dpr, ss), cap)：高像素比的平板上 dpr 永远大于 cap，
  // 那样倍率会被上限钉死，自动画质调 ss 完全不起作用（之前就是这个 bug）。
  const cap = Math.min(panelDprCap, levelDprCap(QUALITY))
  const base = Math.max(1, Math.min(DEVICE_DPR, cap))
  return Math.max(0.6, Math.min(base * ssCurrent, cap))
}
// 高 DPI 屏（手机/平板）的像素比下限：
// 这些设备 devicePixelRatio 通常是 2~3，如果倍率只有 1.0，等于只渲染物理像素的 1/4，
// 再拉伸 2~3 倍贴到屏幕上 —— 细线和文字立刻发虚。桌面屏 dpr=1，倍率 1.0 就是点对点，
// 所以同样的"低画质"在电脑上看着比平板清楚得多。
// 这里给触屏设备一个 1.5 倍下限（同时不超过屏幕自身的 dpr，避免白渲染）。
function levelDprCap(L) {
  if (!IS_TOUCH) return L.dprCap
  const dpr = DEVICE_DPR
  // 真实设备：不低于 1.5 倍，但也不超过屏幕自身的 dpr（dpr=1 的触屏设备没必要多渲染）
  // 用 ?device=phone/tablet 强制指定时按设备语义走 —— 否则在桌面（dpr=1）上没法复现手机/平板的样子
  const floor = DEVICE_OVERRIDE ? TOUCH_DPR_FLOOR : Math.min(TOUCH_DPR_FLOOR, dpr)
  return Math.max(L.dprCap, floor)
}
function applyQualityLevel(v) {
  const next = Math.max(QUALITY.ssMin, Math.min(QUALITY.ssMax, Math.round(v * 100) / 100))
  if (Math.abs(next - ssCurrent) < 0.001) return
  ssCurrent = next
  renderer.setPixelRatio(effectivePixelRatio())
  resize()
  pfModelSyncPixelRatio()   // 弹窗里的展品模型也跟着变
  console.log('[autoq] 渲染倍率调整为 ' + ssCurrent)
}
// 弹窗里的展品模型是与主场景**独立的渲染器**，像素比原来写死 1.0 ——
// 高档位下外面按 1.25~1.5 倍超采样渲染，弹窗里只有 1 倍，看着就比外面糊。
// 这里让它跟随当前画质档位的渲染倍率（尺寸重算交给 pfModelLayout）。
function pfModelSyncPixelRatio() {
  if (!pfModelCtx) return
  const r = pfModelCtx.renderer
  const next = effectivePixelRatio()
  if (Math.abs((r.getPixelRatio() || 1) - next) < 0.01) return
  r.setPixelRatio(next)
  try { pfModelLayout() } catch {}
}

// 档位文字说明（读数里显示用）
function levelFeats(L) {
  const f = [
    L.shadows && '阴影',
    L.bloom && ('泛光' + (L.bloomRes < 1 ? L.bloomRes + 'x' : '')),
    L.fxaa && 'FXAA',
  ].filter(Boolean)
  return f.length ? f.join('+') : '无特效'
}
function setShadowsEnabled(on) {
  on = !!on
  if (renderer.shadowMap.enabled === on && SHADOWS_ENABLED === on) return
  SHADOWS_ENABLED = on
  renderer.shadowMap.enabled = on
  try { baseDir.castShadow = on; envSunLight.castShadow = on } catch {}
  if (on) markShadowsDirty()
}
// 切到阶梯上的第 idx 档：渲染倍率、像素比上限、阴影、泛光、FXAA 一起改
function setQualityLevel(idx, reason) {
  idx = Math.max(0, Math.min(QUALITY_LEVELS.length - 1, idx))
  if (idx === currentLevel && QUALITY_LEVELS[idx].shadows === QUALITY.shadows &&
      QUALITY_LEVELS[idx].bloom === QUALITY.bloom) return
  const L = QUALITY_LEVELS[idx]
  const prevLevel = currentLevel
  currentLevel = idx
  Object.assign(QUALITY, {
    tier: L.name, index: idx, dprCap: L.dprCap, ssMin: L.ssMin, ssMax: L.ssMax,
    shadows: L.shadows, bloom: L.bloom, bloomRes: L.bloomRes, fxaa: L.fxaa,
  })
  FXAA_ENABLED = fxaaForLevel(L)
  // 需要后处理就按需把栈建起来（弱机器如果一直用不到就不建，省显存）。
  // 注意顺序：先把 FXAA_ENABLED 更新好，再建栈，否则新建的栈里会漏掉 FXAA 那一遍。
  if (L.bloom || FXAA_ENABLED) {
    ensureComposer()
    if (L.bloom) ensureBloomStack()
    if (FXAA_ENABLED) ensureFxaaPass()
  }
  if (aaPass) aaPass.enabled = FXAA_ENABLED
  setShadowsEnabled(L.shadows)
  BLOOM_RES_SCALE = L.bloomRes
  // 往高画质切时，把渲染倍率提到该档上限（否则从 low 切到 high 只会拿到"特效"、
  // 倍率还停在 1.0，等于没拿到高画质该有的清晰度）；往低画质切则保留当前倍率再夹住
  const promote = idx < prevLevel
  ssCurrent = promote ? L.ssMax : Math.max(L.ssMin, Math.min(L.ssMax, ssCurrent))
  renderer.setPixelRatio(effectivePixelRatio())
  pfModelSyncPixelRatio()   // 弹窗里的展品模型也跟着档位变
  try { fdPostApply() } catch {}   // 按新档位重算泛光那几个 pass 的开关
  resize()
  logQuality((reason ? reason + ' → ' : '') + '切到 ' + L.name + ' 档（' + levelFeats(L) + '，倍率 ' + ssCurrent.toFixed(2) + '）')
  syncQualityButtons()
}
function autoQualityTick(fpsNow) {
  if (!AUTO_QUALITY || qualityLocked) return
  if (document.visibilityState !== 'visible') { qGood = 0; qBad = 0; return }
  // 窗口没在前台 / 帧率低到不合理时不要判断：浏览器会对后台标签页限速，
  // 那种"1 帧"不是真卡，误判会把画质白降一档（泛光被关掉）。
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) { qGood = 0; qBad = 0; return }
  if (!fpsNow || fpsNow < 5) { qGood = 0; qBad = 0; return }
  if (qWarmup > 0) { qWarmup--; return }
  // 展品还没加载完之前帧率本来就低，不做判断（万一有模型没加载出来，120 秒后强制开始判断）
  if (hallExhibitObjs.size < 27 && (performance.now() - qPageStart) < 120000) { qGood = 0; qBad = 0; return }
  // 进厅后先给一段稳定时间（贴图还在陆续上传，偶发掉帧不代表机器不行）
  if (performance.now() - qPageStart < Q_START_DELAY_MS) { qGood = 0; qBad = 0; return }
  // 用最近 5 秒的中位数，抗单次抖动（阴影重算、贴图上传）
  fpsHist.push(fpsNow)
  if (fpsHist.length > 5) fpsHist.shift()
  if (fpsHist.length < 5) return
  const sorted = fpsHist.slice().sort((a, b) => a - b)
  const fpsMed = sorted[Math.floor(sorted.length / 2)]
  // 三条计时器：降档 / 恢复 / 升档
  qBad = fpsMed < Q_DOWN_FPS ? qBad + 1 : 0
  qRecover = fpsMed >= Q_RECOVER_FPS ? qRecover + 1 : 0
  qGood = fpsMed > Q_UP_FPS ? qGood + 1 : 0
  const nowMs = performance.now()

  // ① 降档（唯一一条）：连续 7 秒低于 10 帧 → 降一档
  if (qBad >= Q_DOWN_SEC && currentLevel < QUALITY_LEVELS.length - 1) {
    qBad = 0
    qRecover = 0
    qFailedLevels.set(currentLevel, nowMs)
    setQualityLevel(currentLevel + 1, '帧率 ' + fpsMed + ' 持续 ' + Q_DOWN_SEC + ' 秒低于 ' + Q_DOWN_FPS)
    qWarmup = 6
    return
  }
  // ② 恢复：在最低档且连续 5 秒 20 帧以上 → 抬回 mid2（刚失败过就多等一会儿再试）
  if (currentLevel > MID2_INDEX && qRecover >= Q_RECOVER_SEC) {
    const failedAt = qFailedLevels.get(MID2_INDEX) || 0
    const needSec = (nowMs - failedAt < Q_FAIL_COOLDOWN_MS) ? qCooldownSec : Q_RECOVER_SEC
    if (qRecover >= needSec) {
      qRecover = 0
      qWarmup = 6
      setQualityLevel(MID2_INDEX, '帧率 ' + fpsMed + ' 持续 ' + needSec + ' 秒高于 ' + Q_RECOVER_FPS)
    }
    return
  }
  // ③ 升档：连续 7 秒高于 30 帧 → 升一档（mid2 → mid → high）
  if (qGood >= Q_UP_SEC && currentLevel > 0) {
    const targetLevel = currentLevel - 1
    const failedAt = qFailedLevels.get(targetLevel) || 0
    const needSec = (nowMs - failedAt < Q_FAIL_COOLDOWN_MS) ? qCooldownSec : Q_UP_SEC
    if (qGood >= needSec) {
      qGood = 0
      qWarmup = 6
      setQualityLevel(targetLevel, '帧率 ' + fpsMed + ' 持续 ' + needSec + ' 秒高于 ' + Q_UP_FPS)
    }
  }
}
// 调试：?blooff=1 时，进页面先自动把泛光关掉，用来验证"帧率恢复后会不会自己把泛光还回来"
if ((new URLSearchParams(location.search).get('blooff') || '') === '1') {
  setTimeout(() => { try { setBloomAutoOff(true) } catch {} }, 3000)
}

// MSAA 默认关（实测很贵），需要对比时用 ?msaa=2
const MSAA_SAMPLES = (() => {
  const q = new URLSearchParams(location.search).get('msaa')
  if (q !== null) return Math.max(0, Math.min(8, Number(q) || 0))
  return 0
})()

// 临时性能实验开关（?perf=nobloom / noshadow / notrans / nosky），诊断完会删掉
const PERF_TEST = new URLSearchParams(location.search).get('perf') || ''
// 灯光模式（用来验证"只靠 HDRI 打光"够不够，可以任意组合试）：
//   ?lights=off           全关，只剩 HDRI 环境光照
//   ?lights=min           只关逐像素的点光/聚光，保留环境光/半球光/平行光
//   ?lights=sun           只留 envAmbient + envSunLight
//   ?lights=zone          留 envAmbient + envSunLight + 8 盏展区灯（= amb,sun,zones）
//   ?lights=amb,sun,zones 任意组合，可用名字：
//        amb=环境光 hemi=半球光 sky=天光半球 dir=主方向光 sun=太阳平行光
//        zones=8 盏展区点光 globe=中心球光 spot=中心聚光 rims=3 盏轮廓光 big=大范围蓝光
// 没登记的光（比如自己加的灯）在不含它们的模式下会被关掉。
const LIGHTS_MODE = (new URLSearchParams(location.search).get('lights') || '').toLowerCase()
const LIGHT_GROUP_KEYS = ['amb', 'hemi', 'sky', 'dir', 'sun', 'zones', 'globe', 'spot', 'rims', 'big']
// 返回"要保留哪几组"；返回 null 表示不干预（不认识的值）
function lightModeKeepGroups(mode) {
  if (mode === 'off') return []
  if (mode === 'sun') return ['amb', 'sun']
  if (mode === 'zone' || mode === 'zones') return ['amb', 'sun', 'zones']
  if (mode === 'min') return null   // min 单独处理：只关点光/聚光
  const parts = String(mode).split(/[,+ ]+/).filter(Boolean)
  if (parts.length && parts.every((p) => LIGHT_GROUP_KEYS.includes(p))) return parts
  return null
}
// 按灯光模式隐藏光源（用 visible=false 而不是删掉：three 只把 visible 的光源算进着色器，
// 而且隐藏后随时能还原，面板里的灯光编辑也不会因此报错）。
// 注意：强度设成 0 并不会省开销，必须 visible=false。
function applyLightMode() {
  if (!LIGHTS_MODE) return 0
  // 把每盏灯归到一组，方便按名字开关
  const groupOf = new Map()
  const tag = (key, val) => {
    for (const l of (Array.isArray(val) ? val : [val])) if (l) groupOf.set(l, key)
  }
  tag('amb', envAmbient); tag('hemi', envHemi); tag('sky', envSkyLight)
  tag('dir', baseDir); tag('sun', envSunLight)
  tag('zones', zoneLights); tag('globe', globeLightRef); tag('spot', envSpot)
  tag('rims', envRims); tag('big', envBig)
  let hidden = 0
  if (LIGHTS_MODE === 'min') {
    // 只关"逐像素"的点光/聚光，环境光/半球光/平行光保留
    scene.traverse((o) => {
      if (!o.isLight || !o.visible) return
      if (o.isPointLight || o.isSpotLight) { o.visible = false; hidden++ }
    })
    return hidden
  }
  const keepGroups = lightModeKeepGroups(LIGHTS_MODE)
  if (!keepGroups) return 0
  const keep = new Set(keepGroups)
  scene.traverse((o) => {
    if (!o.isLight || !o.visible) return
    const g = groupOf.get(o)
    if (g && keep.has(g)) return
    o.visible = false
    hidden++
  })
  return hidden
}

// 右下角 FPS 读数：正式页面默认关闭；调试时地址后加 ?fps=1 就会显示
// （会连带显示当前渲染倍率和泛光开/关，方便判断是哪一档被降了）
const SHOW_FPS_READOUT = (() => {
  const q = (new URLSearchParams(location.search).get('fps') || '').toLowerCase()
  return q === '1' || q === 'on'
})()

// 临时：?census=1 时在加载完成后打印一次"各分组三角面"清单（排查性能用，正式页面不会触发）
const CENSUS_ON = new URLSearchParams(location.search).get('census') === '1'

// ---------------------------------------------------------------------------
// 版本自检：服务端把"影响画面/布局的文件"算成一个版本号，页面每隔一会儿比对一次，
// 不一致就自动刷新。这样旧标签页/旧缓存的浏览器不会一直停在老版本上
// （之前的"旧浏览器打开还是老展品布局"就是这个原因）。
// ---------------------------------------------------------------------------
let buildVersionSeen = null
let buildVersionChecking = false
async function checkBuildVersion(isFirst) {
  if (buildVersionChecking) return
  buildVersionChecking = true
  try {
    const res = await fetch('/api/build-version?t=' + Date.now(), { cache: 'no-store' })
    if (!res.ok) return
    const info = await res.json()
    const v = info && info.version
    if (!v) return
    if (isFirst || buildVersionSeen === null) {
      buildVersionSeen = v
      console.log('[version] 当前版本 ' + v)
      return
    }
    if (v !== buildVersionSeen) {
      console.log('[version] 检测到新版本 ' + v + '（本地 ' + buildVersionSeen + '），自动刷新')
      try { showToast('检测到新版本，正在自动刷新…') } catch {}
      buildVersionSeen = v
      setTimeout(() => location.reload(), 800)
    }
  } catch (e) { /* 离线时忽略 */ } finally {
    buildVersionChecking = false
  }
}
checkBuildVersion(true)
setInterval(() => checkBuildVersion(false), 60000)
document.addEventListener('visibilitychange', () => {
  // 切回这个标签页时马上查一次（旧标签页挂着不动也能自己更新）
  if (document.visibilityState === 'visible') checkBuildVersion(false)
})

// ---------- 基础场景 ----------
const canvas = document.querySelector('#scene')
const renderer = new THREE.WebGLRenderer({ canvas, antialias: !IS_MOBILE, powerPreference: 'high-performance' })
renderer.setClearColor(0x050816)
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.0
renderer.outputColorSpace = THREE.SRGBColorSpace

// ---------------------------------------------------------------------------
// 画质：一条"能力阶梯"（index 越小画质越高），不再是三档固定配置。
// 老规则只按显卡型号猜，猜错了没法补救：强手机被压在中档拿不到高分辨率，
// GTX 480 / MX150 这类不在名单里的老卡反而被当成高档（实测只有 6~7 帧）。
// 新流程：① 按型号粗猜起始档（只是起点）→ ② 遮罩还盖着时实测一轮帧率重新定档
//        → ③ 进厅后继续双向微调（连续 7 秒 < 22 帧降档，连续 7 秒 > 45 帧升档）
// 想手动锁死、不许自动调：?q=high / mid / mid2 / low（也接受 0~3）
// ---------------------------------------------------------------------------
const GPU_NAME = (() => {
  try {
    const gl = renderer.getContext()
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    return String((dbg && gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || '')
  } catch { return '' }
})()
const QUALITY_LEVELS = [
  // FXAA 只在 high 档开：手机上看得出差别（细灯带/栏杆边缘），而 high 档的机器多这一遍
  // 全屏 pass 无所谓。中低档关掉换性能。想临时对比：?fxaa=1 强制开 / ?fxaa=0 强制关。
  { name: 'high', dprCap: 1.5, ssMin: 1.0, ssMax: 1.5, shadows: true, bloom: true, bloomRes: 1.0, fxaa: true },
  // mid：按要求"减掉阴影、保留分辨率"——和 high 同样的渲染倍率，只少一遍阴影贴图
  { name: 'mid', dprCap: 1.5, ssMin: 1.0, ssMax: 1.5, shadows: false, bloom: true, bloomRes: 1.0, fxaa: false },
  // mid2：分辨率上限降到 1.0，不再超采样
  { name: 'mid2', dprCap: 1.5, ssMin: 0.7, ssMax: 1.0, shadows: false, bloom: true, bloomRes: 1.0, fxaa: false },
  // low：像素比上限压到 1.0，且关掉泛光（最省的一档）
  { name: 'low', dprCap: 1.0, ssMin: 0.7, ssMax: 1.0, shadows: false, bloom: false, bloomRes: 0.5, fxaa: false },
]
const LEVEL_INDEX_BY_NAME = { high: 0, mid: 1, mid2: 2, 'mid-': 2, low: 3 }
const MID2_INDEX = 2   // "恢复到中档"的目标
// 自动画质的阈值
// 运行中的降档只有一条规则：连续 7 秒低于 10 帧 → 降一档
const Q_DOWN_FPS = 10
const Q_DOWN_SEC = 7
// 升档两条：连续 7 秒高于 30 帧 → 升一档；在最低档时连续 5 秒 20 帧以上 → 抬回 mid2
const Q_UP_FPS = 30
const Q_UP_SEC = 7
const Q_RECOVER_FPS = 20
const Q_RECOVER_SEC = 5
// 开机实测用的一次性定位阈值（只在遮罩后面测一轮，不参与运行中的反复判断）
const Q_PROBE_CRASH = 10     // 低于这个 → 直接最低档
const Q_PROBE_LOW = 22       // 低于这个 → 往下掉两档
// 防抖动：降档时记下"这个档刚才没扛住"，2 分钟内再想升回该档，要求连续 3 倍时长达标才试，
// 避免在阈值附近来回横跳。
const Q_FAIL_COOLDOWN_MS = 120000
const qFailedLevels = new Map()   // 档位 index -> 失败时间
const qCooldownSec = Q_UP_SEC * 3
const Q_START_DELAY_MS = 12000   // 进厅后多久开始判断（等贴图上传完、镜头稳定）
const QUALITY = (() => {
  const want = (new URLSearchParams(location.search).get('q') || '').toLowerCase()
  const g = GPU_NAME.toLowerCase()
  const software = /swiftshader|basic render|llvmpipe|softpipe|microsoft basic/.test(g)
  // 老卡名单（补上了 GTX 4xx~7xx 这类之前漏掉的）
  const weakGpu = /(^|[^0-9])(gt|gtx)?\s*(210|220|240|430|440|520|610|620|630|640|710|720|730|740)([^0-9]|$)|\b(gt|gtx)\s*(4|5|6|7)\d{2}\b|\bgt\s*10\d{2}\b|hd graphics (3000|4000|4200|4400|4600|5000|510|515|520|530|5500|6000)|radeon (hd|r[2-9])\s*\d{3}|mali-[4-9]\d\d|adreno \(tm\) [1-5]\d\d|powervr/.test(g)
  const strongGpu = /(rtx|gtx\s*(9|10|16|20|30|40|50)\d|radeon rx|apple m\d|arc a\d|quadro|tesla)/.test(g)
  // 笔记本入门独显（MX150/250/350/450/550、GT 1030 这类）：不弱到要直接给最低档，
  // 但也别给 high —— 从中档起步，跑起来再按实测升降
  const midGpu = /\b(mx|gt)\s*(1|2|3|4|5)\d{2}\b|radeon\s*(rx\s*5[0-6]\d|vega\s*\d+)/.test(g)
  const manualIdx = Object.prototype.hasOwnProperty.call(LEVEL_INDEX_BY_NAME, want)
    ? LEVEL_INDEX_BY_NAME[want]
    : (/^[0-4]$/.test(want) ? Number(want) : null)
  const lastIdx = QUALITY_LEVELS.length - 1   // 最低档（删掉 low+ 之后是 low=3）
  const guessIdx = software ? lastIdx
    : weakGpu ? lastIdx
    : midGpu ? 1
    : strongGpu ? 0
    : IS_TOUCH ? 1
    : 0
  const idx = manualIdx === null ? guessIdx : manualIdx
  return Object.assign({ tier: QUALITY_LEVELS[idx].name, index: idx, manual: manualIdx !== null, gpu: GPU_NAME }, QUALITY_LEVELS[idx])
})()
let currentLevel = QUALITY.index
let qualityLocked = QUALITY.manual   // 手动指定 ?q= 或点了画质按钮后，不再做自动调整
// FXAA 的开关（必须在 setQualityLevel 被调用之前初始化 —— 画质按钮启动时就会调它）
// 地址参数是"硬覆盖"，不受档位自动切换影响，方便现场 A/B 对比：?fxaa=1 强制开，?fxaa=0 强制关
const FXAA_URL = (new URLSearchParams(location.search).get('fxaa') || '').toLowerCase()
const fxaaForLevel = (L) => (FXAA_URL === '1' ? true : FXAA_URL === '0' ? false : !!L.fxaa)
let FXAA_ENABLED = fxaaForLevel(QUALITY)
// 后处理栈的变量必须在这里先声明：画质按钮在启动阶段就可能调用 setQualityLevel，
// 如果这些 let 还留在下面（后处理初始化那一段），就会撞上 TDZ 报错让整页崩掉。
let selectiveBloom = true
let SHADOWS_ENABLED = QUALITY.shadows
let composer = null
let ssaoPass = null
let bloomPass = null
let aaPass = null
let bloomComposer = null      // 只渲染发光件 + 泛光
let bloomPassOnly = null
let bloomMixPass = null       // 把泛光结果叠回主画面
console.log('[quality] 档位=' + QUALITY.tier + '  显卡=' + (GPU_NAME || '未知') +
  '  设备=' + (IS_PHONE ? '手机' : IS_TABLET ? '平板' : '电脑'))

// ---------------------------------------------------------------------------
// 顶栏的"画质"按钮：自动 / 高 / 中 / 低
//   · 自动（默认）：交给开机实测 + 帧率自动升降
//   · 手动点选：锁定该档位，并记在本机（下次打开还是这个选择）
//   · 地址参数 ?q= 优先级最高（调试用），带 ?q= 时按它锁定
// ---------------------------------------------------------------------------
const QUALITY_CHOICE_KEY = 'exhibit-quality-choice'
const QUALITY_CHOICE_LEVEL = { high: 0, mid: 1, low: 3 }
let qualityUserChoice = null
const qualityMenu = document.querySelector('#quality-menu')
const qualityTrigger = document.querySelector('#quality-trigger')
const qualityList = document.querySelector('#quality-list')
const qualityCurrent = document.querySelector('#quality-current')
const qualityBtns = Array.from(document.querySelectorAll('#quality-list button'))
const openQualityMenu = (open) => {
  if (!qualityList) return
  qualityList.classList.toggle('hidden', !open)
}
function syncQualityButtons() {
  // 自动模式下档位可能在 mid2 这类"按钮上没有"的中间档，按最接近的按钮高亮
  const byLevel = { high: 'high', mid: 'mid', mid2: 'mid', low: 'low' }
  const key = qualityLocked ? (qualityUserChoice || byLevel[QUALITY_LEVELS[currentLevel].name]) : 'auto'
  for (const b of qualityBtns) b.classList.toggle('active', b.dataset.q === key)
  // 收起状态的按钮上显示当前选择
  if (qualityCurrent) {
    // 走 i18n，别写死中文 —— 否则切到英文后这里还是"高/中/低"
    qualityCurrent.dataset.i18n = 'quality.' + key
    qualityCurrent.textContent = t('quality.' + key)
  }
  fitBrandBar()
}
function applyQualityChoice(q, reason) {
  if (q === 'auto') {
    qualityUserChoice = null
    qualityLocked = false
    try { localStorage.removeItem(QUALITY_CHOICE_KEY) } catch {}
    logQuality(reason || '已切回自动画质')
  } else if (q in QUALITY_CHOICE_LEVEL) {
    qualityUserChoice = q
    qualityLocked = true
    try { localStorage.setItem(QUALITY_CHOICE_KEY, q) } catch {}
    setQualityLevel(QUALITY_CHOICE_LEVEL[q], reason || ('手动选择' + t('quality.' + q) + '画质'))
  }
  syncQualityButtons()
}
for (const b of qualityBtns) {
  b.addEventListener('click', () => { applyQualityChoice(b.dataset.q); openQualityMenu(false) })
}
// 展开/收起：点触发按钮切换，点别处或按 Esc 收起
qualityTrigger?.addEventListener('click', (e) => {
  e.stopPropagation()
  openQualityMenu(qualityList?.classList.contains('hidden'))
})
document.addEventListener('click', (e) => {
  if (!qualityMenu || qualityList?.classList.contains('hidden')) return
  if (!qualityMenu.contains(e.target)) openQualityMenu(false)
})
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') openQualityMenu(false) })
// 启动时：带 ?q= 就按它；否则读本机记住的手动选择。
// 注意：这里只"记住"，真正切档要等场景就位后（finishLoadingIfReady）——
// 现在切档会调到 resize()，而相机/渲染器还没初始化，会直接抛错让整页崩掉。
let pendingQualityChoice = null
if (!QUALITY.manual) {
  let saved = null
  try { saved = localStorage.getItem(QUALITY_CHOICE_KEY) } catch {}
  if (saved && saved in QUALITY_CHOICE_LEVEL) {
    qualityUserChoice = saved
    qualityLocked = true
    pendingQualityChoice = saved
  }
}
syncQualityButtons()

// 把"档位 + 显卡型号"也放到屏幕上那行读数里（?fps=1 就能看到，不用开控制台）。
// 现场排查"某台老机器怎么这么卡"时，第一件事就是看这里落在哪一档、显卡识别出来没有。
const GPU_SHORT = (() => {
  const raw = GPU_NAME || ''
  if (!raw || /^webkit webgl$/i.test(raw.trim())) return '未识别（浏览器隐藏了显卡信息）'
  if (/swiftshader|llvmpipe|softpipe|basic render/i.test(raw)) return '软件渲染（SwiftShader 等）'
  let s = raw.replace(/^ANGLE\s*\(/i, '').replace(/\)\s*$/, '')
  s = s.replace(/\(0x[0-9a-f]+\)/gi, '')
    .replace(/\b(Direct3D\d*|vs_\d+_\d+|ps_\d+_\d+|D3D\d*|OpenGL\s*[\d.]*|Vulkan\s*[\d.]*)\b/gi, ' ')
    .replace(/\s+/g, ' ')
  const parts = s.split(',').map((x) => x.trim()).filter(Boolean)
  const pick = parts.find((x) => !/^(nvidia|amd|intel|apple|google|qualcomm|arm|microsoft)$/i.test(x)) || parts[0] || s
  return pick.replace(/^(NVIDIA|AMD|Intel|Apple)\s+/i, '').replace(/[,\s]+$/, '').slice(0, 44)
})()

// 按档位确定渲染倍率、泛光层分辨率和抗锯齿（都还能用地址参数覆盖，方便对比）
ssCurrent = Math.max(QUALITY.ssMin, Math.min(QUALITY.ssMax, SS_FLOOR))
BLOOM_RES_SCALE = (() => {
  const q = new URLSearchParams(location.search).get('bloomres')
  return q === null ? QUALITY.bloomRes : Math.max(0.25, Math.min(1, Number(q) || 1))
})()
renderer.setPixelRatio(effectivePixelRatio())
renderer.shadowMap.enabled = SHADOWS_ENABLED
// 性能诊断：关闭自动重置，改为每帧手动重置，这样统计到的是一整帧所有渲染遍的总和
renderer.info.autoReset = false
if (PERF_TEST === 'noshadow') renderer.shadowMap.enabled = false
// 静态场景的阴影优化：默认不每帧重算"阴影贴图"（它是全场景零件的深度图，非常贵），
// 只在场景变化后重算几帧，另外每 8 秒兜底重算一次，保证永远是对的。
renderer.shadowMap.autoUpdate = false
let shadowDirtyFrames = 3
// 重算阴影贴图那几帧会明显慢一点（要把全场零件重画进阴影层），
// 顺手让自动画质跳过这几帧，别把"阴影重算"误判成"机器不行"。
function markShadowsDirty() { shadowDirtyFrames = 3; qWarmup = Math.max(qWarmup, 4) }
RectAreaLightUniformsLib.init()

const scene = new THREE.Scene()
scene.background = new THREE.Color(0x050816)
scene.fog = new THREE.Fog(0xbfe6ff, 150, 1500)
const sceneFog = scene.fog

// 临时特效调试面板
const fogDebug = document.querySelector('#fog-debug')
const fdFogOn = document.querySelector('#fd-fog-on')
const fdNear = document.querySelector('#fd-near')
const fdFar = document.querySelector('#fd-far')
const fdR = document.querySelector('#fd-r')
const fdG = document.querySelector('#fd-g')
const fdB = document.querySelector('#fd-b')
const fdExp = document.querySelector('#fd-exp')
const fdSso = document.querySelector('#fd-ssao')
// 屏幕自发光参数（在"特效调试"面板里可调，属于默认参数档）
const fdScrI = document.querySelector('#fd-scr-i')
const fdScrI2 = document.querySelector('#fd-scr-i2')
const fdScrSol = document.querySelector('#fd-scr-sol')
const fdSk = document.querySelector('#fd-sk')
const fdBlo = document.querySelector('#fd-bloom')
const fdBs = document.querySelector('#fd-bs')
const fdBr = document.querySelector('#fd-br')
const fdBt = document.querySelector('#fd-bt')
const fdBloomSel = document.querySelector('#fd-bloom-sel')
const fdAmb = document.querySelector('#fd-amb')
const fdAmbColor = document.querySelector('#fd-amb-color')
const fdHemi = document.querySelector('#fd-hemi')
const fdHemiColor = document.querySelector('#fd-hemi-color')
const fdHemiGround = document.querySelector('#fd-hemi-ground')
const fdDir = document.querySelector('#fd-dir')
const fdDirColor = document.querySelector('#fd-dir-color')
const fdSpot = document.querySelector('#fd-spot')
const fdSpotColor = document.querySelector('#fd-spot-color')
const fdRim = document.querySelector('#fd-rim')
const fdRimColor = document.querySelector('#fd-rim-color')
const fdBig = document.querySelector('#fd-big')
const fdBigColor = document.querySelector('#fd-big-color')
const fdSkyTop = document.querySelector('#fd-sky-top')
const fdSkyHor = document.querySelector('#fd-sky-hor')
const fdEi = document.querySelector('#fd-ei')
const fdCloudOn = document.querySelector('#fd-cloud-on')
const fdCloud = document.querySelector('#fd-cloud')
const fdCloudH = document.querySelector('#fd-cloud-h')
const fdCloudSpd = document.querySelector('#fd-cloud-spd')
const fdWaterOn = document.querySelector('#fd-water-on')
const fdWaterRef = document.querySelector('#fd-water-ref')
const fdWaterRipple = document.querySelector('#fd-water-ripple')
const fdGroundDetail = document.querySelector('#fd-ground-detail')
const fdSkyYaw = document.querySelector('#fd-sky-yaw')
const fdSkyLift = document.querySelector('#fd-sky-lift')
const fdSkyZoom = document.querySelector('#fd-sky-zoom')

let cloudRebuildTimer = 0
// 地表细节强度（把模型里平铺的细节贴图接回山体）
function fdGroundDetailListener() {
  if (fdGroundDetail) terrainDetailStrength = Number(fdGroundDetail.value)
  for (const s of terrainDetailShaders) s.uniforms.uDetStrength.value = terrainDetailStrength
}
fdGroundDetail?.addEventListener('input', fdGroundDetailListener)
fdGroundDetail?.addEventListener('change', fdGroundDetailListener)

// 云彩 / 水面参数
function fdCloudApply() {
  const amt = fdCloud ? Number(fdCloud.value) : cloudAmount
  const hgt = fdCloudH ? Number(fdCloudH.value) : cloudHeight
  const needNewTexture = amt !== cloudAmount || hgt !== cloudHeight || !cloudTexture
  cloudEnabled = fdCloudOn ? fdCloudOn.checked : cloudEnabled
  cloudAmount = amt
  cloudHeight = hgt
  if (fdCloudSpd) cloudDrift = Number(fdCloudSpd.value)
  waterEnabled = fdWaterOn ? fdWaterOn.checked : waterEnabled
  if (fdWaterRef) waterReflect = Number(fdWaterRef.value)
  if (fdWaterRipple) waterRipple = Number(fdWaterRipple.value)

  ensureCloudDome()
  if (cloudDome) cloudDome.visible = cloudEnabled
  if (needNewTexture) {
    clearTimeout(cloudRebuildTimer)
    cloudRebuildTimer = setTimeout(() => {
      rebuildCloudTexture()
      refreshWaterSkyEnv()
    }, 200)
  }
  if (waterMesh) {
    waterMesh.material = waterEnabled ? matWater : waterOrigMaterial
    matWater.envMapIntensity = waterReflect
    matWater.normalScale.set(waterRipple, waterRipple)
    matWater.normalMap = waterRipple > 0.005 ? rippleTexture : null
    matWater.needsUpdate = true
    refreshWaterSkyEnv()
  }
}
function fdApply() {
  if (!sceneFog) return
  const near = Number(fdNear.value)
  const far = Number(fdFar.value)
  const r = Number(fdR.value)
  const g = Number(fdG.value)
  const b = Number(fdB.value)
  sceneFog.near = near
  sceneFog.far = far
  sceneFog.color.setRGB(r / 255, g / 255, b / 255)
  scene.fog = fdFogOn && fdFogOn.checked ? sceneFog : null
  document.querySelector('#fd-near-v').textContent = near
  document.querySelector('#fd-far-v').textContent = far
  document.querySelector('#fd-r-v').textContent = r
  document.querySelector('#fd-g-v').textContent = g
  document.querySelector('#fd-b-v').textContent = b
  document.querySelector('#fd-color').style.background = 'rgb(' + r + ',' + g + ',' + b + ')'
}
function fdPostApply() {
  renderer.toneMappingExposure = Number(fdExp.value)
  document.querySelector('#fd-exp-v').textContent = Number(fdExp.value).toFixed(2)
  if (ssaoPass) {
    ssaoPass.enabled = fdSso.checked
    ssaoPass.kernelRadius = Number(fdSk.value)
    document.querySelector('#fd-sk-v').textContent = fdSk.value
  }
  if (bloomPass) {
    // 交付模式（调试面板隐藏）下，泛光固定为"开 + 选择性泛光"：
    // 泛光那一遍现在只多 57 次绘制，几乎不增加面数，却是展厅最显眼的效果，
    // 不能让旧存档/旧参数档把辉光关掉（之前"突然没辉光且回不来"就是这个原因之一）。
    selectiveBloom = DEBUG_UI_ENABLED ? (fdBloomSel ? fdBloomSel.checked : true) : true
    const on = DEBUG_UI_ENABLED ? fdBlo.checked : true
    // 选择性泛光打开时：主合成器里的泛光关掉，改由独立的一层泛光再叠回去
    // QUALITY.bloom = 当前档位要不要泛光（高档位关掉泛光时整条泛光链都跳过）
    bloomPass.enabled = on && QUALITY.bloom && !selectiveBloom
    bloomPass.strength = Number(fdBs.value)
    bloomPass.radius = Number(fdBr.value)
    bloomPass.threshold = Number(fdBt.value)
    if (bloomPassOnly) {
      bloomPassOnly.enabled = on && QUALITY.bloom && selectiveBloom
      bloomPassOnly.strength = Number(fdBs.value)
      bloomPassOnly.radius = Number(fdBr.value)
      bloomPassOnly.threshold = Number(fdBt.value)
    }
    if (bloomMixPass) bloomMixPass.enabled = on && QUALITY.bloom && selectiveBloom
    document.querySelector('#fd-bs-v').textContent = Number(fdBs.value).toFixed(2)
    document.querySelector('#fd-br-v').textContent = Number(fdBr.value).toFixed(2)
    document.querySelector('#fd-bt-v').textContent = Number(fdBt.value).toFixed(2)
  }
}
// 屏幕自发光：HUD 屏亮度 / 主屏亮度 / 实体感（透明度）。
// 这几个值进"特效调试"面板，也进默认参数档，刷新后自动套用。
// 主屏额外加一点"实体感"：和其它屏共用 0.85 时，主屏看着还是偏透（用户反馈）。
// uSolid：0 = 允许半透明（能透出背景），1 = 完全不透。
const SCREEN_MAIN_EXTRA_SOLID = 0.1
function fdScreenApply() {
  const hud = fdScrI ? Number(fdScrI.value) : 1.3
  const main = fdScrI2 ? Number(fdScrI2.value) : 1.6
  const solid = fdScrSol ? Number(fdScrSol.value) : 0.85
  const v1 = document.querySelector('#fd-scr-i-v')
  const v2 = document.querySelector('#fd-scr-i2-v')
  const v3 = document.querySelector('#fd-scr-sol-v')
  if (v1) v1.textContent = hud.toFixed(2)
  if (v2) v2.textContent = main.toFixed(2)
  if (v3) v3.textContent = solid.toFixed(2)
  if (typeof screenFxMaterials === 'undefined' || !screenFxMaterials.length) return
  for (const m of screenFxMaterials) {
    if (!m.uniforms) continue
    const isMain = !!(m.userData && m.userData.isMain)
    m.uniforms.uDim.value = isMain ? main : hud
    // 主屏比其它屏再实一点（用户反馈主屏偏透）：0=能透出背景，1=完全不透
    if (m.uniforms.uSolid) m.uniforms.uSolid.value = Math.min(1, solid + (isMain ? SCREEN_MAIN_EXTRA_SOLID : 0))
  }
}
for (const el of [fdScrI, fdScrI2, fdScrSol]) {
  el?.addEventListener('input', fdScreenApply)
  el?.addEventListener('change', fdScreenApply)
}
function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')
}
function blendColor(a, b, t) {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  return rgbToHex(ca[0] + (cb[0] - ca[0]) * t, ca[1] + (cb[1] - ca[1]) * t, ca[2] + (cb[2] - ca[2]) * t)
}
function fdSkyApply() {
  skyTopColor = fdSkyTop.value
  skyHorizonColor = fdSkyHor.value
  skyMidColor = blendColor(skyTopColor, skyHorizonColor, 0.45)
  skyNearColor = blendColor(skyTopColor, skyHorizonColor, 0.8)
  applySkyEnvironment()
}
function fdLightApply() {
  if (envAmbient) {
    envAmbient.intensity = Number(fdAmb.value)
    envAmbient.color.set(fdAmbColor.value)
  }
  if (envHemi) {
    envHemi.intensity = Number(fdHemi.value)
    envHemi.color.set(fdHemiColor.value)
    envHemi.groundColor.set(fdHemiGround.value)
  }
  if (envDir) {
    envDir.intensity = Number(fdDir.value)
    envDir.color.set(fdDirColor.value)
  }
  if (envSpot) {
    envSpot.intensity = Number(fdSpot.value)
    envSpot.color.set(fdSpotColor.value)
  }
  const rimVal = Number(fdRim.value)
  envRims.forEach((rim) => {
    rim.intensity = (rim.userData.base || 1) * rimVal
    rim.color.set(fdRimColor.value)
  })
  if (envBig) {
    envBig.intensity = Number(fdBig.value)
    envBig.color.set(fdBigColor.value)
  }
  scene.environmentIntensity = Number(fdEi.value)
  document.querySelector('#fd-amb-v').textContent = Number(fdAmb.value).toFixed(2)
  document.querySelector('#fd-hemi-v').textContent = Number(fdHemi.value).toFixed(2)
  document.querySelector('#fd-dir-v').textContent = Number(fdDir.value).toFixed(2)
  document.querySelector('#fd-spot-v').textContent = fdSpot.value
  document.querySelector('#fd-rim-v').textContent = fdRim.value
  document.querySelector('#fd-big-v').textContent = fdBig.value
  document.querySelector('#fd-ei-v').textContent = Number(fdEi.value).toFixed(2)
}
fdNear?.addEventListener('input', fdApply)
fdFogOn?.addEventListener('change', fdApply)
fdFar?.addEventListener('input', fdApply)
fdR?.addEventListener('input', fdApply)
fdG?.addEventListener('input', fdApply)
fdB?.addEventListener('input', fdApply)
fdExp?.addEventListener('input', fdPostApply)
fdSso?.addEventListener('change', fdPostApply)
fdSk?.addEventListener('input', fdPostApply)
fdBloomSel?.addEventListener('change', fdPostApply)
fdBlo?.addEventListener('change', fdPostApply)
fdBs?.addEventListener('input', fdPostApply)
fdBr?.addEventListener('input', fdPostApply)
fdBt?.addEventListener('input', fdPostApply)
fdAmb?.addEventListener('input', fdLightApply)
fdAmbColor?.addEventListener('input', fdLightApply)
fdHemi?.addEventListener('input', fdLightApply)
fdHemiColor?.addEventListener('input', fdLightApply)
fdHemiGround?.addEventListener('input', fdLightApply)
fdDir?.addEventListener('input', fdLightApply)
fdDirColor?.addEventListener('input', fdLightApply)
fdSpot?.addEventListener('input', fdLightApply)
fdSpotColor?.addEventListener('input', fdLightApply)
fdRim?.addEventListener('input', fdLightApply)
fdRimColor?.addEventListener('input', fdLightApply)
fdBig?.addEventListener('input', fdLightApply)
fdBigColor?.addEventListener('input', fdLightApply)
fdEi?.addEventListener('input', fdLightApply)
fdSkyTop?.addEventListener('input', fdSkyApply)
fdSkyHor?.addEventListener('input', fdSkyApply)
const fdAces = document.querySelector('#fd-aces')
fdAces?.addEventListener('change', () => {
  renderer.toneMapping = fdAces.checked ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping
})
document.querySelector('#fd-close')?.addEventListener('click', () => fogDebug?.classList.add('hidden'))
fdApply()

// 材质调试面板
const mdGrass = document.querySelector('#md-grass')
const mdGroundOn = document.querySelector('#md-ground-on')
const mdGrassColor = document.querySelector('#md-grass-color')
const mdGroundH = document.querySelector('#md-ground-h')
const mdCenter = document.querySelector('#md-center')
const mdDark = document.querySelector('#md-dark')
const mdCorner = document.querySelector('#md-corner')
const mdZone = document.querySelector('#md-zone')
const mdPed = document.querySelector('#md-ped')
function mdSetColor(list, hex) {
  const c = new THREE.Color(hex)
  list.forEach((mat) => { if (mat) mat.color.copy(c) })
}
function mdApplyTileColor(list, hex) {
  list.forEach((mat) => {
    if (!mat || !mat.userData) return
    if (mat.userData.sideOnly || mat.userData.colorOnly) {
      // 厚地台的侧面/底面、以及不铺砖的素色地面：只跟色，不加砖纹
      mat.color.set(hex)
      mat.userData.__panelColor = mat.color.clone()
      mat.needsUpdate = true
      return
    }
    const glow = mat.userData.glow !== false
    const tex = makeFloorTileTexture(hex, glow)
    tex.repeat.set(mat.userData.repeatX || 1, mat.userData.repeatY || 1)
    if (mat.map) mat.map.dispose()
    mat.map = tex
    mat.color.setHex(0xffffff)
    mat.metalness = glow ? 0.35 : 0.25
    mat.envMapIntensity = glow ? 0.7 : 0.55
    mat.needsUpdate = true
  })
}
function mdApply() {
  if (matGrayFloor) {
    matGrayFloor.color.set(mdCenter.value)
    matGrayFloor.userData.__panelColor = matGrayFloor.color.clone()
    matGrayFloor.metalness = 0.3
    matGrayFloor.envMapIntensity = 0.7
  }
  if (matLeatherFloor) {
    matLeatherFloor.color.set(mdDark.value)
    matLeatherFloor.userData.__panelColor = matLeatherFloor.color.clone()
    matLeatherFloor.metalness = 0.25
    matLeatherFloor.envMapIntensity = 0.6
  }
  mdApplyTileColor(debugFloorMats.corner, mdCorner.value)
  mdApplyTileColor(debugFloorMats.light, mdCorner.value)
  mdApplyTileColor(debugFloorMats.dark, mdZone.value)
  mdApplyTileColor(debugFloorMats.darkFloor, mdDark.value)
  if (matPedestal) {
    matPedestal.color.set(mdPed.value)
    matPedestal.userData.__panelColor = matPedestal.color.clone()
    matPedestal.metalness = 0.3
    matPedestal.envMapIntensity = 0.8
  }
  if (mdGroundH) {
    infiniteGroundOffset = Number(mdGroundH.value)
    document.querySelector('#md-ground-h-v').textContent = infiniteGroundOffset.toFixed(1)
    if (infiniteGround) infiniteGround.position.y = hallFloorY + infiniteGroundOffset
    syncVegetationToGround()
  }
}
function mdGrassApply() {
  if (!infiniteGroundMat) return
  if (!mdGrass) return            // 新版改用「草地材质」预设按钮了
  const useMap = mdGrass.checked
  infiniteGroundMat.map = useMap ? infiniteColorTex : null
  infiniteGroundMat.normalMap = useMap ? infiniteNormalTex : null
  infiniteGroundMat.roughnessMap = useMap ? infiniteRoughTex : null
  infiniteGroundMat.color.setHex(useMap ? 0xffffff : parseInt(mdGrassColor.value.slice(1), 16))
  infiniteGroundMat.needsUpdate = true
}
// 无限地面开关（默认关闭）
function mdGroundOnApply() {
  const on = mdGroundOn ? mdGroundOn.checked : INFINITE_GROUND_ENABLED
  if (on) {
    addInfiniteGrassGround()
    // 用面板当前状态（含存档里恢复的槽位）刷新，别套回默认预设把用户选的槽位冲掉
    applyGroundFromUI()
  }
  if (infiniteGround) infiniteGround.visible = on
}
mdGroundOn?.addEventListener('change', () => {
  mdGroundOnApply()
  if (mdGroundOn.checked) showToast?.('已开启无限地面')
})
mdGrass?.addEventListener('change', mdGrassApply)
mdGrassColor?.addEventListener('input', () => {
  if (!mdGrass.checked && infiniteGroundMat) infiniteGroundMat.color.set(mdGrassColor.value)
})
mdGroundH?.addEventListener('input', () => {
  infiniteGroundOffset = Number(mdGroundH.value)
  document.querySelector('#md-ground-h-v').textContent = infiniteGroundOffset.toFixed(1)
  if (infiniteGround) infiniteGround.position.y = hallFloorY + infiniteGroundOffset
  syncVegetationToGround()
})
mdCenter?.addEventListener('input', () => {
  if (!matGrayFloor) return
  matGrayFloor.color.set(mdCenter.value)
  matGrayFloor.userData.__panelColor = matGrayFloor.color.clone()
  matGrayFloor.metalness = 0.3
  matGrayFloor.envMapIntensity = 0.7
})
mdDark?.addEventListener('input', () => {
  mdApplyTileColor(debugFloorMats.darkFloor, mdDark.value)
  if (matLeatherFloor) {
    matLeatherFloor.color.set(mdDark.value)
    matLeatherFloor.userData.__panelColor = matLeatherFloor.color.clone()
    matLeatherFloor.metalness = 0.25
    matLeatherFloor.envMapIntensity = 0.6
  }
})
mdCorner?.addEventListener('input', () => {
  mdApplyTileColor(debugFloorMats.corner, mdCorner.value)
  mdApplyTileColor(debugFloorMats.light, mdCorner.value)
})
mdZone?.addEventListener('input', () => mdApplyTileColor(debugFloorMats.dark, mdZone.value))
document.querySelector('#md-tile-size')?.addEventListener('input', applyFloorTileSize)
mdPed?.addEventListener('input', () => {
  if (!matPedestal) return
  matPedestal.color.set(mdPed.value)
  matPedestal.userData.__panelColor = matPedestal.color.clone()
  matPedestal.metalness = 0.3
  matPedestal.envMapIntensity = 0.8
})
document.querySelector('#md-close')?.addEventListener('click', () => document.querySelector('#mat-debug')?.classList.add('hidden'))
// 交付模式：把「调试」按钮和右下角 FPS 读数隐藏起来，避免误操作
// （需要自己调面板时，把下面的 DEBUG_UI_ENABLED 改成 true 即可）
if (!DEBUG_UI_ENABLED) {
  const hideEls = SHOW_FPS_READOUT ? ['#debug-toggle'] : ['#debug-toggle', '#fps']
  for (const sel of hideEls) {
    const el = document.querySelector(sel)
    if (el) el.style.display = 'none'
  }
  if (SHOW_FPS_READOUT) document.querySelector('#fps')?.classList.remove('hidden')
  for (const sel of ['#fog-debug', '#mat-debug', '#render-debug', '#light-debug', '#place-debug']) {
    document.querySelector(sel)?.classList.add('hidden')
  }
}
document.querySelector('#debug-toggle')?.addEventListener('click', () => {
  const matDebugEl = document.querySelector('#mat-debug')
  const renderDebugEl = document.querySelector('#render-debug')
  const lightDebugEl = document.querySelector('#light-debug')
  const placeDebugEl = document.querySelector('#place-debug')
  const anyVisible = !fogDebug?.classList.contains('hidden') || !matDebugEl?.classList.contains('hidden') ||
    !renderDebugEl?.classList.contains('hidden') || !lightDebugEl?.classList.contains('hidden') ||
    !placeDebugEl?.classList.contains('hidden')
  fogDebug?.classList.toggle('hidden', anyVisible)
  matDebugEl?.classList.toggle('hidden', anyVisible)
  renderDebugEl?.classList.toggle('hidden', anyVisible)
  lightDebugEl?.classList.toggle('hidden', anyVisible)
  placeDebugEl?.classList.toggle('hidden', anyVisible)
  document.querySelector('#fps')?.classList.toggle('hidden', anyVisible)
})

// 渲染 / 灯光 / 天空面板
const rdShadow = document.querySelector('#rd-shadow')
const rdShadowSize = document.querySelector('#rd-shadow-size')
const rdShadowBias = document.querySelector('#rd-shadow-bias')
const rdShadowNb = document.querySelector('#rd-shadow-nb')
const rdSsaoMin = document.querySelector('#rd-ssao-min')
const rdSsaoMax = document.querySelector('#rd-ssao-max')
const rdTonemap = document.querySelector('#rd-tonemap')
const rdDpr = document.querySelector('#rd-dpr')
panelDprCap = Number(rdDpr && rdDpr.value) || 1.5
const rdFov = document.querySelector('#rd-fov')
const rdDamp = document.querySelector('#rd-damp')
const rdZone = document.querySelector('#rd-zone')
const rdZoneColor = document.querySelector('#rd-zone-color')
const rdProd = document.querySelector('#rd-prod')
const rdProdColor = document.querySelector('#rd-prod-color')
const rdGlobe = document.querySelector('#rd-globe')
const rdGlobeColor = document.querySelector('#rd-globe-color')
const rdSpotAngle = document.querySelector('#rd-spot-angle')
const rdSpotPen = document.querySelector('#rd-spot-pen')
const rdRim = [document.querySelector('#rd-rim1'), document.querySelector('#rd-rim2'), document.querySelector('#rd-rim3')]
const rdBgMode = document.querySelector('#rd-bg-mode')
const rdBgColor = document.querySelector('#rd-bg-color')
const rdSkyMid = document.querySelector('#rd-sky-mid')
const rdSkyNear = document.querySelector('#rd-sky-near')
const rdCloud = document.querySelector('#rd-cloud')
const rdEnvSrc = document.querySelector('#rd-env-src')

function rdRenderApply() {
  const shadowsOn = rdShadow ? rdShadow.checked : true
  // 低档机器（软件渲染 / 老显卡）一律不开阴影，无论面板里怎么勾
  renderer.shadowMap.enabled = shadowsOn && QUALITY.shadows
  if (baseDir) {
    baseDir.castShadow = shadowsOn
    const size = Number(rdShadowSize.value)
    baseDir.shadow.mapSize.set(size, size)
    // 主光源（HDRI 太阳）也要跟着面板设置走，否则调"阴影贴图"完全看不出差别
    if (envSunLight) {
      envSunLight.castShadow = shadowsOn
      envSunLight.shadow.mapSize.set(size, size)
      if (envSunLight.shadow.map) {
        envSunLight.shadow.map.dispose()
        envSunLight.shadow.map = null
      }
      envSunLight.shadow.camera.updateProjectionMatrix()
    }
    baseDir.shadow.bias = Number(rdShadowBias.value)
    // normalBias：沿法线方向偏移阴影采样，专门治"平地上规则排列的小黑点"。
    // 阴影相机跨 1000 米、贴图 1024，一格约 1 米，所以这个值要给到 0.5~1.5 才有效
    const nb = rdShadowNb ? Number(rdShadowNb.value) : 0.8
    baseDir.shadow.normalBias = nb
    if (envSunLight) envSunLight.shadow.normalBias = nb
    if (baseDir.shadow.map) {
      baseDir.shadow.map.dispose()
      baseDir.shadow.map = null
    }
    baseDir.shadow.camera.updateProjectionMatrix()
  }
  document.querySelector('#rd-shadow-bias-v').textContent = Number(rdShadowBias.value).toFixed(4)
  if (rdShadowNb) document.querySelector('#rd-shadow-nb-v').textContent = Number(rdShadowNb.value).toFixed(2)
  if (ssaoPass) {
    ssaoPass.minDistance = Number(rdSsaoMin.value)
    ssaoPass.maxDistance = Number(rdSsaoMax.value)
    document.querySelector('#rd-ssao-min-v').textContent = Number(rdSsaoMin.value).toFixed(3)
    document.querySelector('#rd-ssao-max-v').textContent = Number(rdSsaoMax.value).toFixed(2)
  }
  const tmMap = {
    aces: THREE.ACESFilmicToneMapping,
    reinhard: THREE.ReinhardToneMapping,
    cineon: THREE.CineonToneMapping,
    linear: THREE.LinearToneMapping,
    none: THREE.NoToneMapping
  }
  renderer.toneMapping = tmMap[rdTonemap.value] ?? THREE.ACESFilmicToneMapping
  if (fdAces) fdAces.checked = rdTonemap.value === 'aces'
  const dpr = Number(rdDpr.value)
  panelDprCap = dpr || 1.5
  renderer.setPixelRatio(effectivePixelRatio())
  document.querySelector('#rd-dpr-v').textContent = dpr.toFixed(2)
  applyCameraFov()
  document.querySelector('#rd-fov-v').textContent = rdFov.value
  controls.dampingFactor = Number(rdDamp.value)
  document.querySelector('#rd-damp-v').textContent = Number(rdDamp.value).toFixed(2)
  resize()
}
function rdLightApply() {
  const zs = Number(rdZone.value)
  zoneLights.forEach((l) => {
    l.intensity = (l.userData.base || 1) * zs
    l.color.set(rdZoneColor.value)
  })
  document.querySelector('#rd-zone-v').textContent = zs.toFixed(2)
  const ps = Number(rdProd.value)
  productSpots.forEach((l) => {
    l.intensity = (l.userData.base || 1) * ps
    l.color.set(rdProdColor.value)
  })
  document.querySelector('#rd-prod-v').textContent = ps.toFixed(2)
  if (globeLightRef) {
    globeLightRef.intensity = Number(rdGlobe.value)
    globeLightRef.color.set(rdGlobeColor.value)
  }
  document.querySelector('#rd-globe-v').textContent = rdGlobe.value
  if (envSpot) {
    envSpot.angle = (Number(rdSpotAngle.value) * Math.PI) / 180
    envSpot.penumbra = Number(rdSpotPen.value)
  }
  document.querySelector('#rd-spot-angle-v').textContent = rdSpotAngle.value
  document.querySelector('#rd-spot-pen-v').textContent = Number(rdSpotPen.value).toFixed(2)
  envRims.forEach((rim, i) => {
    const el = rdRim[i]
    if (!el) return
    rim.intensity = Number(el.value)
    document.querySelector('#rd-rim' + (i + 1) + '-v').textContent = el.value
  })
}
function rdSkyApply() {
  skyBgMode = rdBgMode.value
  skySolidColor = rdBgColor.value
  skyMidColor = rdSkyMid.value
  skyNearColor = rdSkyNear.value
  skyCloudOpacity = Number(rdCloud.value)
  envSource = rdEnvSrc.value
  if (hdriSelect) setHdriFile(hdriSelect.value)
  document.querySelector('#rd-cloud-v').textContent = Number(rdCloud.value).toFixed(2)
  applySkyEnvironment()
}
for (const el of [rdShadow, rdShadowSize, rdShadowBias, rdShadowNb, rdSsaoMin, rdSsaoMax, rdTonemap, rdDpr, rdFov, rdDamp]) {
  el?.addEventListener('input', rdRenderApply)
  el?.addEventListener('change', rdRenderApply)
}
for (const el of [rdZone, rdZoneColor, rdProd, rdProdColor, rdGlobe, rdGlobeColor, rdSpotAngle, rdSpotPen, ...rdRim]) {
  el?.addEventListener('input', rdLightApply)
}
for (const el of [rdBgMode, rdBgColor, rdSkyMid, rdSkyNear, rdCloud, rdEnvSrc]) {
  el?.addEventListener('input', rdSkyApply)
  el?.addEventListener('change', rdSkyApply)
}
document.querySelector('#rd-close')?.addEventListener('click', () => document.querySelector('#render-debug')?.classList.add('hidden'))

// ---------- 自定义灯光编辑器 ----------
const LIGHT_KEY = 'exhibit-custom-lights-v1'
const customLights = []
const lightHelpers = []
let selectedLightEntry = null
let lightHelperVisible = false // 灯位标记默认不显示（调试时可在灯光面板里打开）
let lightSeq = 0
const ltAddButtons = document.querySelectorAll('#light-debug [data-add]')
const ltList = document.querySelector('#lt-list')
const ltIntensity = document.querySelector('#lt-intensity')
const ltColor = document.querySelector('#lt-color')
const ltDistance = document.querySelector('#lt-distance')
const ltDecay = document.querySelector('#lt-decay')
const ltAngle = document.querySelector('#lt-angle')
const ltPen = document.querySelector('#lt-pen')
const ltWidth = document.querySelector('#lt-width')
const ltHeight = document.querySelector('#lt-height')
const ltVisible = document.querySelector('#lt-visible')
const ltHelpers = document.querySelector('#lt-helpers')
const ltModeTranslate = document.querySelector('#lt-mode-translate')
const ltModeRotate = document.querySelector('#lt-mode-rotate')

function makeLightHelperMesh(entry) {
  // 交付模式（DEBUG_UI_ENABLED=false）：根本不生成灯位标记，
  // 那些橙色线框小球是调试用的，会永远画在物体上面，看起来像"灯的位置"漏出来了。
  if (!DEBUG_UI_ENABLED) {
    entry.helper = null
    return
  }
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 10, 8),
    new THREE.MeshBasicMaterial({ color: 0xffc35a, wireframe: true, transparent: true, opacity: 0.9, depthTest: false })
  )
  mesh.renderOrder = 999
  mesh.userData.lightEntry = entry
  entry.helper = mesh
  entry.pivot.add(mesh)
  lightHelpers.push(mesh)
}
function createCustomLight(type, data) {
  const d = data || {}
  const pivot = new THREE.Object3D()
  pivot.position.set(d.x ?? 0, d.y ?? 0, d.z ?? 0)
  if (d.rx || d.ry || d.rz) pivot.rotation.set(d.rx || 0, d.ry || 0, d.rz || 0)
  scene.add(pivot)
  let light
  if (type === 'point') {
    light = new THREE.PointLight(new THREE.Color(d.color || '#ffffff'), d.intensity ?? 60, d.distance ?? 30, d.decay ?? 2)
  } else if (type === 'spot') {
    light = new THREE.SpotLight(new THREE.Color(d.color || '#ffffff'), d.intensity ?? 200, d.distance ?? 40, ((d.angle ?? 30) * Math.PI) / 180, d.penumbra ?? 0.4, d.decay ?? 1.5)
    light.target.position.set(0, -1, -3)
    pivot.add(light.target)
  } else {
    light = new THREE.RectAreaLight(new THREE.Color(d.color || '#ffffff'), d.intensity ?? 6, d.width ?? 6, d.height ?? (type === 'line' ? 0.4 : 4))
  }
  light.userData.customType = type
  pivot.add(light)
  const entry = {
    id: 'L' + (++lightSeq) + '_' + Date.now(),
    type,
    builtin: false,
    label: '',
    pivot,
    light,
    visible: d.visible !== false,
    color: d.color || '#ffffff',
    intensity: light.intensity,
    distance: light.distance ?? 0,
    decay: light.decay ?? 2,
    angle: type === 'spot' ? (d.angle ?? 30) : 30,
    penumbra: type === 'spot' ? (d.penumbra ?? 0.4) : 0.4,
    width: light.width ?? (type === 'line' ? 12 : 6),
    height: light.height ?? (type === 'line' ? 0.4 : 4)
  }
  if (type === 'line') {
    light.width = d.width ?? 12
    light.height = d.height ?? 0.4
    entry.width = light.width
    entry.height = light.height
  }
  makeLightHelperMesh(entry)
  light.visible = entry.visible
  if (entry.helper) entry.helper.visible = entry.visible && lightHelperVisible
  customLights.push(entry)
  return entry
}
function registerBuiltinLight(light, type, label, id) {
  if (!light || customLights.some((e) => e.id === id)) return null
  const movable = type !== 'ambient' && type !== 'hemi'
  const entry = {
    id: id,
    label,
    type,
    builtin: true,
    light,
    pivot: movable ? light : null,
    helper: null,
    visible: light.visible !== false,
    color: '#' + light.color.getHexString(),
    intensity: light.intensity,
    distance: light.distance ?? 0,
    decay: light.decay ?? 2,
    angle: light.angle !== undefined ? (light.angle * 180) / Math.PI : 30,
    penumbra: light.penumbra ?? 0.4,
    width: light.width ?? 6,
    height: light.height ?? 4
  }
  entry.defaults = {
    intensity: entry.intensity, color: entry.color, distance: entry.distance,
    decay: entry.decay, angle: entry.angle, penumbra: entry.penumbra, visible: entry.visible,
    pos: movable ? light.position.clone() : null,
    rot: movable ? light.rotation.clone() : null
  }
  if (movable) makeLightHelperMesh(entry)
  customLights.push(entry)
  return entry
}
function registerBuiltinLights() {
  registerBuiltinLight(envAmbient, 'ambient', '环境光', 'BI_ambient')
  registerBuiltinLight(envHemi, 'hemi', '半球光', 'BI_hemi')
  registerBuiltinLight(envDir, 'dir', '主方向光', 'BI_dir')
  zoneLights.forEach((l, i) => registerBuiltinLight(l, 'point', '分区灯' + (i + 1), 'BI_zone_' + (i + 1)))
  productSpots.forEach((l, i) => registerBuiltinLight(l, 'spot', '展品聚光' + (i + 1), 'BI_prod_' + (i + 1)))
  registerBuiltinLight(globeLightRef, 'point', '球体光', 'BI_globe')
  registerBuiltinLight(envSpot, 'spot', '中心聚光', 'BI_spot')
  envRims.forEach((l, i) => registerBuiltinLight(l, 'point', '轮廓灯' + (i + 1), 'BI_rim_' + (i + 1)))
  registerBuiltinLight(envBig, 'point', '大范围蓝光', 'BI_big')
  ltRefreshList()
  applyStoredLightOverrides()
}
function applyLightDataToEntry(e, d) {
  if (!e || !d) return
  e.intensity = d.intensity ?? e.intensity
  e.color = d.color ?? e.color
  e.distance = d.distance ?? e.distance
  e.decay = d.decay ?? e.decay
  e.angle = d.angle ?? e.angle
  e.penumbra = d.penumbra ?? e.penumbra
  e.width = d.width ?? e.width
  e.height = d.height ?? e.height
  if (d.visible !== undefined) e.visible = !!d.visible
  const l = e.light
  l.intensity = e.intensity
  l.color.set(e.color)
  if (l.distance !== undefined) l.distance = e.distance
  if (l.decay !== undefined) l.decay = e.decay
  if (l.angle !== undefined) l.angle = (e.angle * Math.PI) / 180
  if (l.penumbra !== undefined) l.penumbra = e.penumbra
  if (l.width !== undefined) l.width = e.width
  if (l.height !== undefined) l.height = e.height
  l.visible = e.visible
  if (e.pivot && d.x !== undefined) e.pivot.position.set(d.x, d.y, d.z)
  if (e.pivot && d.rx !== undefined) e.pivot.rotation.set(d.rx, d.ry, d.rz)
  if (e.helper) e.helper.visible = e.visible && lightHelperVisible
}

function ltRefreshList() {
  if (!ltList) return
  ltList.innerHTML = ''
  customLights.forEach((e, i) => {
    const opt = document.createElement('option')
    opt.value = String(i)
    opt.textContent = (i + 1) + '. ' + (e.label || ({ point: '点光', spot: '聚光', area: '面光', line: '线灯' }[e.type] || e.type))
    ltList.appendChild(opt)
  })
}
function ltSyncInputs() {
  const e = selectedLightEntry
  const disabled = !e
  for (const el of [ltIntensity, ltColor, ltDistance, ltDecay, ltAngle, ltPen, ltWidth, ltHeight, ltVisible]) {
    if (el) el.disabled = disabled
  }
  if (!e) return
  const isSpot = e.type === 'spot'
  const isRect = e.type === 'area' || e.type === 'line'
  const isPointLike = e.type === 'point' || e.type === 'spot'
  ltIntensity.value = e.intensity
  ltColor.value = e.color
  ltDistance.value = e.distance
  ltDecay.value = e.decay
  ltAngle.value = e.angle
  ltPen.value = e.penumbra
  ltWidth.value = e.width
  ltHeight.value = e.height
  ltVisible.checked = e.visible
  document.querySelector('#lt-intensity-v').textContent = Number(e.intensity).toFixed(0)
  document.querySelector('#lt-distance-v').textContent = Number(e.distance).toFixed(0)
  document.querySelector('#lt-decay-v').textContent = Number(e.decay).toFixed(1)
  document.querySelector('#lt-angle-v').textContent = Number(e.angle).toFixed(0)
  document.querySelector('#lt-pen-v').textContent = Number(e.penumbra).toFixed(2)
  document.querySelector('#lt-width-v').textContent = Number(e.width).toFixed(1)
  document.querySelector('#lt-height-v').textContent = Number(e.height).toFixed(1)
  ltDistance.disabled = !isPointLike
  ltDecay.disabled = !isPointLike
  ltAngle.disabled = !isSpot
  ltPen.disabled = !isSpot
  ltWidth.disabled = !isRect
  ltHeight.disabled = !isRect
  ;['lt-intensity', 'lt-distance', 'lt-decay', 'lt-angle', 'lt-pen', 'lt-width', 'lt-height']
    .forEach((id) => syncNumFromRange(document.getElementById(id)))
}
function selectCustomLight(entry) {
  selectedLightEntry = entry || null
  transformHelper.visible = !!(entry && entry.pivot)
  if (entry && entry.pivot) {
    transformControls.attach(entry.pivot)
  } else {
    transformControls.detach()
  }
  if (entry) {
    const idx = customLights.indexOf(entry)
    if (idx >= 0 && ltList) ltList.value = String(idx)
  }
  ltSyncInputs()
}
function ltApply() {
  const e = selectedLightEntry
  if (!e) return
  const l = e.light
  e.intensity = Number(ltIntensity.value)
  e.color = ltColor.value
  e.distance = Number(ltDistance.value)
  e.decay = Number(ltDecay.value)
  e.angle = Number(ltAngle.value)
  e.penumbra = Number(ltPen.value)
  e.width = Number(ltWidth.value)
  e.height = Number(ltHeight.value)
  e.visible = ltVisible.checked
  l.intensity = e.intensity
  l.color.set(e.color)
  if (l.distance !== undefined) l.distance = e.distance
  if (l.decay !== undefined) l.decay = e.decay
  if (e.type === 'spot') {
    l.angle = (e.angle * Math.PI) / 180
    l.penumbra = e.penumbra
  }
  if (e.type === 'area' || e.type === 'line') {
    l.width = e.width
    l.height = e.height
  }
  l.visible = e.visible
  if (e.helper) {
    e.helper.material.color.set(0xffc35a)
    e.helper.visible = e.visible && lightHelperVisible
  }
  ltSyncInputs()
  saveCustomLights()
}
function serializeCustomLights() {
  return customLights.map((e) => ({
    builtin: !!e.builtin,
    id: e.builtin ? e.id : undefined,
    label: e.builtin ? e.label : undefined,
    type: e.type,
    x: e.pivot ? e.pivot.position.x : undefined,
    y: e.pivot ? e.pivot.position.y : undefined,
    z: e.pivot ? e.pivot.position.z : undefined,
    rx: e.pivot ? e.pivot.rotation.x : undefined,
    ry: e.pivot ? e.pivot.rotation.y : undefined,
    rz: e.pivot ? e.pivot.rotation.z : undefined,
    color: e.color, intensity: e.intensity, distance: e.distance, decay: e.decay,
    angle: e.angle, penumbra: e.penumbra, width: e.width, height: e.height, visible: e.visible
  }))
}
function saveCustomLights() {
  try { localStorage.setItem(LIGHT_KEY, JSON.stringify(serializeCustomLights())) } catch {}
}
let storedLightData = []
function loadCustomLights() {
  let list = []
  try { list = JSON.parse(localStorage.getItem(LIGHT_KEY) || '[]') || [] } catch {}
  storedLightData = list
  list.filter((d) => !d.builtin).forEach((d) => createCustomLight(d.type, d))
  ltRefreshList()
  if (customLights.length) selectCustomLight(customLights[customLights.length - 1])
}
function applyStoredLightOverrides() {
  if (!storedLightData.length) {
    try { storedLightData = JSON.parse(localStorage.getItem(LIGHT_KEY) || '[]') || [] } catch { storedLightData = [] }
  }
  storedLightData.filter((d) => d.builtin).forEach((d) => {
    const e = customLights.find((x) => x.id === d.id)
    if (e) applyLightDataToEntry(e, d)
  })
  ltRefreshList()
  ltSyncInputs()
}
function deleteSelectedLight() {
  const e = selectedLightEntry
  if (!e) return
  if (e.builtin) {
    const d = e.defaults
    applyLightDataToEntry(e, {
      intensity: d.intensity, color: d.color, distance: d.distance, decay: d.decay,
      angle: d.angle, penumbra: d.penumbra, visible: d.visible,
      x: d.pos ? d.pos.x : undefined, y: d.pos ? d.pos.y : undefined, z: d.pos ? d.pos.z : undefined,
      rx: d.rot ? d.rot.x : undefined, ry: d.rot ? d.rot.y : undefined, rz: d.rot ? d.rot.z : undefined
    })
    ltSyncInputs()
    saveCustomLights()
    return
  }
  transformControls.detach()
  transformHelper.visible = false
  scene.remove(e.pivot)
  if (e.light.dispose) e.light.dispose()
  const hi = lightHelpers.indexOf(e.helper)
  if (hi >= 0) lightHelpers.splice(hi, 1)
  const ci = customLights.indexOf(e)
  if (ci >= 0) customLights.splice(ci, 1)
  selectedLightEntry = null
  ltRefreshList()
  ltSyncInputs()
  saveCustomLights()
}
ltAddButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const pos = controls.target.clone().add(new THREE.Vector3(0, 4, 0))
    const entry = createCustomLight(btn.dataset.add, { x: pos.x, y: pos.y, z: pos.z })
    ltRefreshList()
    selectCustomLight(entry)
    saveCustomLights()
  })
})
ltList?.addEventListener('change', () => {
  const e = customLights[Number(ltList.value)]
  if (e) selectCustomLight(e)
})
for (const el of [ltIntensity, ltColor, ltDistance, ltDecay, ltAngle, ltPen, ltWidth, ltHeight, ltVisible]) {
  el?.addEventListener('input', ltApply)
  el?.addEventListener('change', ltApply)
}
ltHelpers?.addEventListener('change', () => {
  lightHelperVisible = ltHelpers.checked
  customLights.forEach((e) => { if (e.helper) e.helper.visible = e.visible && lightHelperVisible })
})
ltModeTranslate?.addEventListener('click', () => {
  transformControls.setMode('translate')
  ltModeTranslate.classList.add('active')
  ltModeRotate.classList.remove('active')
})
ltModeRotate?.addEventListener('click', () => {
  transformControls.setMode('rotate')
  ltModeRotate.classList.add('active')
  ltModeTranslate.classList.remove('active')
})
document.querySelector('#lt-delete')?.addEventListener('click', deleteSelectedLight)
document.querySelector('#lt-clear')?.addEventListener('click', () => {
  // 删除自定义灯
  const customs = customLights.filter((e) => !e.builtin)
  customs.forEach((e) => { selectCustomLight(e); deleteSelectedLight() })
  // 恢复内置灯默认
  customLights.filter((e) => e.builtin).forEach((e) => {
    const d = e.defaults
    applyLightDataToEntry(e, {
      intensity: d.intensity, color: d.color, distance: d.distance, decay: d.decay,
      angle: d.angle, penumbra: d.penumbra, visible: d.visible,
      x: d.pos ? d.pos.x : undefined, y: d.pos ? d.pos.y : undefined, z: d.pos ? d.pos.z : undefined,
      rx: d.rot ? d.rot.x : undefined, ry: d.rot ? d.rot.y : undefined, rz: d.rot ? d.rot.z : undefined
    })
  })
  saveCustomLights()
  ltSyncInputs()
})
document.querySelector('#lt-close')?.addEventListener('click', () => document.querySelector('#light-debug')?.classList.add('hidden'))

// 把所有滑杆右侧的数值标签升级为可手动输入的数字框
const DBG_RANGE_SELECTOR = '#fog-debug input[type="range"], #mat-debug input[type="range"], #render-debug input[type="range"], #light-debug input[type="range"]'
function syncNumFromRange(range) {
  if (!range || !range.id) return
  const num = document.getElementById(range.id + '-v')
  if (num && num.tagName === 'INPUT') num.value = range.value
}
function syncAllNumericInputs() {
  document.querySelectorAll(DBG_RANGE_SELECTOR).forEach(syncNumFromRange)
}
function enhanceNumericInputs() {
  document.querySelectorAll(DBG_RANGE_SELECTOR).forEach((range) => {
    const label = document.getElementById(range.id + '-v')
    if (!label || label.tagName === 'INPUT') return
    const num = document.createElement('input')
    num.type = 'number'
    num.className = 'fd-num'
    num.id = range.id + '-v'
    num.min = range.min
    num.max = range.max
    num.step = range.step || '1'
    num.value = range.value
    label.replaceWith(num)
    range.addEventListener('input', () => { num.value = range.value })
    const applyNum = () => {
      let v = Number(num.value)
      if (!isFinite(v)) { num.value = range.value; return }
      const min = Number(range.min)
      const max = Number(range.max)
      v = Math.min(max, Math.max(min, v))
      num.value = v
      range.value = v
      range.dispatchEvent(new Event('input', { bubbles: true }))
    }
    num.addEventListener('change', applyNum)
    num.addEventListener('keydown', (e) => { if (e.key === 'Enter') applyNum() })
  })
}
enhanceNumericInputs()

// 调试参数保存 / 恢复 / 导出
const DEBUG_KEY = 'exhibit-debug-params-v1'
// 17：默认天空从 qwantani 4K（48 MB）换成同一张的 2K 版（4.6 MB）；
//     同时把 HUD 大屏贴图默认切到 images/hud-sd/（2048px）。
//     —— 加这个数字，已经访问过的电脑才会丢掉旧存档、自动套用新的默认参数档。
// 16：屏幕自发光（HUD/主屏亮度 + 实体感）进默认参数档
// 15：Bloom 强度默认 0.3 → 0.25
// （改这个数字，别的电脑上的旧存档才会自动升级到新默认）
const DEBUG_VERSION = 17


// 小欧 / Aira 智能客服总开关。知识库（RAG assistant，本机 5000 端口）已接好：
// 前端 → /api/kb/ask → vite.config.js 代理（带密钥）→ 知识库 /api/ask。
// 关掉后：右下角气泡不显示、点中心发光球也不再弹对话。
// 2026-10-22：知识库已部署并验证 /api/kb/ask 正常，重新开启小欧。
const SUPPORT_ENABLED = true

const DEBUG_IDS = [
  'fd-fog-on', 'fd-near', 'fd-far', 'fd-r', 'fd-g', 'fd-b', 'fd-aces', 'fd-exp',
    'fd-ssao', 'fd-sk', 'fd-bloom', 'fd-bloom-sel', 'fd-bs', 'fd-br', 'fd-bt', 'fd-glow-i',
  'fd-scr-i', 'fd-scr-i2', 'fd-scr-sol',
  'fd-amb', 'fd-amb-color', 'fd-hemi', 'fd-hemi-color', 'fd-hemi-ground',
  'fd-sky', 'fd-sky-color', 'fd-sky-ground', 'fd-sun', 'fd-sun-color', 'fd-sun-az',
  'fd-cloud-on', 'fd-cloud', 'fd-cloud-h', 'fd-cloud-spd',
  'fd-sky-yaw', 'fd-sky-lift', 'fd-sky-zoom', 'fd-sky-bright', 'fd-hall-bright',
  'fd-water-on', 'fd-water-ref', 'fd-water-ripple',
  'fd-ground-detail',
  'fd-dir', 'fd-dir-color', 'fd-spot', 'fd-spot-color', 'fd-rim', 'fd-rim-color',
  'fd-big', 'fd-big-color', 'fd-sky-top', 'fd-sky-hor', 'fd-ei',
  'md-grass', 'md-grass-color', 'md-grass-preset', 'md-ground-bright', 'md-ground-h',
  'md-slot-1', 'md-slot-2', 'md-slot-3', 'md-slot-4',
  'md-mix-amount', 'md-ground-tile', 'md-mix-scale', 'md-parallax', 'md-relief',
  'md-tree-on', 'md-tree-count', 'md-tree-scale',
  'md-grass-on', 'md-grass-count', 'md-grass-scale', 'md-veg-radius',
  'md-tree-seed', 'md-shrub-on', 'md-shrub-count', 'md-shrub-scale', 'md-shrub-radius',
  'md-tree-a', 'md-tree-b', 'md-tree-c',
  'md-shrub-v1', 'md-shrub-v2', 'md-shrub-v3', 'md-shrub-v4', 'md-shrub-v5', 'md-shrub-v6', 'md-shrub-v7',
  'md-center', 'md-dark', 'md-corner', 'md-zone', 'md-ped', 'md-tile-size',
  'md-ground-on',
  'rd-shadow', 'rd-shadow-size', 'rd-shadow-bias', 'rd-shadow-nb', 'rd-ssao-min', 'rd-ssao-max',
  'rd-tonemap', 'rd-dpr', 'rd-fov', 'rd-damp',
  'rd-zone', 'rd-zone-color', 'rd-prod', 'rd-prod-color', 'rd-globe', 'rd-globe-color',
  'rd-spot-angle', 'rd-spot-pen', 'rd-rim1', 'rd-rim2', 'rd-rim3',
  'rd-bg-mode', 'rd-bg-color', 'rd-sky-mid', 'rd-sky-near', 'rd-cloud', 'rd-env-src', 'rd-hdri'
  , 'lt-helpers'
]
function collectDebugParams() {
  const out = {}
  out.__v = DEBUG_VERSION
  for (const id of DEBUG_IDS) {
    const el = document.querySelector('#' + id)
    if (!el) continue
    // 本地选的 HDRI 是临时 blob 链接，不写进存档（刷新后会失效）
    if (id === 'rd-hdri' && String(el.value || '').startsWith('blob:')) continue
    out[id] = el.type === 'checkbox' ? el.checked : el.value
  }
  return out
}
function saveDebugParams() {
  try { localStorage.setItem(DEBUG_KEY, JSON.stringify(collectDebugParams())) } catch {}
  saveCustomLights()
}
function readDebugParams() {
  try { return JSON.parse(localStorage.getItem(DEBUG_KEY) || 'null') } catch { return null }
}

// ---------- 默认参数档：public/data/default-params.json ----------
// 把面板「导出」得到的 {params, lights} 存成这个文件，就是整个项目的默认效果：
//   · 没存过参数的电脑 / 新访客打开 → 直接用这套；
//   · 本机存档的版本比代码旧 → 自动升级到这套（避免老存档把新默认压住）；
//   · 想回到这套：点面板上的「重置」（清掉本机存档后会自动再套一次）。
const DEFAULT_PROFILE_URL = 'data/default-params.json'
// 默认参数档开关：打开时，没存档的电脑会直接套用 public/data/default-params.json
// （就是面板「导出」得到的那份 = 用户调好的整套参数 + 灯光状态）。
const USE_DEFAULT_PROFILE = true
let defaultProfile = null
async function loadDefaultProfile() {
  if (defaultProfile !== null) return defaultProfile
  try {
    const res = await fetch(DEFAULT_PROFILE_URL + '?t=' + Date.now())
    defaultProfile = await res.json()
  } catch (e) {
    defaultProfile = false
    console.warn('[debug] 默认参数档读取失败', e)
  }
  return defaultProfile
}
async function applyDefaultProfile(reason) {
  const prof = await loadDefaultProfile()
  if (!prof || !prof.params) return false
  // 只套用参数（颜色/曝光/泛光/天空/地面…），不套用导出文件里的"灯光编辑器记录"：
  // 那份记录里分区灯/球体光/聚光/轮廓灯都是 0（=关），套上会把展厅弄黑、看不见模型。
  // 灯光统一由参数面板驱动（分区灯强度 / 球体点光 / 中心聚光 / 轮廓灯 / 大范围蓝光）。
  try {
    localStorage.removeItem(LIGHT_KEY)
    storedLightData = []
  } catch {}
  const params = Object.assign({}, prof.params, { __v: DEBUG_VERSION })
  applyDebugParams(params)
  try { localStorage.setItem(DEBUG_KEY, JSON.stringify(params)) } catch {}
  console.log('[debug] 已套用默认参数档（' + reason + '）')
  return true
}
function applyDebugParams(params) {
  if (!params) return
  // 旧存档迁移：早期版本没有 HDRI 选项，存下来的环境是"蓝天"，
  // 会把新加的 HDRI 默认值覆盖掉，导致看不到。这里只迁移一次。
  if (!params.__v || params.__v < 5) {
    const es = document.querySelector('#rd-env-src')
    const bm = document.querySelector('#rd-bg-mode')
    if (es) es.value = 'hdri'
    if (bm) bm.value = 'hdri'
    // v3：SSAO 默认关闭（它会把树/草的细密枝叶算出一堆暗块）
    const ss = document.querySelector('#fd-ssao')
    if (ss) ss.checked = false
    // v5：阴影贴图 1024、植被分布半径 400 米
    const szs = document.querySelector('#rd-shadow-size')
    const vr = document.querySelector('#md-veg-radius')
    if (szs) szs.value = '1024'
    if (vr) vr.value = 400
    // v4：光照配比——天光降低、阳光提高、环境反射降低，让影子有对比（阳光强度按你调的 3.5）
    const sk = document.querySelector('#fd-sky')
    const su = document.querySelector('#fd-sun')
    const ei = document.querySelector('#fd-ei')
    if (sk) sk.value = 0.7
    if (su) su.value = 3.5
    if (ei) ei.value = 0.5
    console.log('[debug] 旧参数迁移到 v5：HDRI 背景、SSAO 关闭、光照配比（天光0.7/阳光3.5/环境反射0.5）')
  }
  for (const id of DEBUG_IDS) {
    const el = document.querySelector('#' + id)
    if (!el || params[id] === undefined) continue
    if ((id === 'rd-env-src' || id === 'rd-bg-mode') &&
        (!params.__v || params.__v < DEBUG_VERSION)) continue
    if (el.type === 'checkbox') el.checked = !!params[id]
    else el.value = params[id]
  }
  // v6：草地拼接改成 4 槽位，混拼强度默认提到 0.6
  // （必须放在存档还原"之后"，否则会被存档里的旧值 0 覆盖掉）
  if (!params.__v || params.__v < 6) {
    const ma = document.querySelector('#md-mix-amount')
    if (ma) ma.value = 0.6
    console.log('[debug] 参数迁移到 v6：草地 4 槽位拼接已启用，混拼强度 0.6')
  }
  // v7：泛光阈值旧默认 0.85 太高，青色灯带（亮度≈0.79）刚好被切掉、怎么调都没反应。
  // 默认降到 0.55，让灯带/灯圈/发光球真的会辉光（不是旧默认值的就尊重用户自己的设置）。
  if (!params.__v || params.__v < 7) {
    const bt = document.querySelector('#fd-bt')
    if (bt && (params.__v === undefined || Number(bt.value) >= 0.8)) bt.value = '0.55'
    const bs = document.querySelector('#fd-bs')
    if (bs && Number(bs.value) < 0.3) bs.value = '0.6'
    console.log('[debug] 参数迁移到 v7：泛光阈值 0.55、强度 0.6（灯带/光球才会辉光）')
  }
  // v8：泛光半径旧默认 1.0 太大，光晕摊得太开，整屏会像蒙了一层雾（看着"半透明"）。
  // 默认收到 0.3：光晕贴着灯带/光球，不再糊住整个画面。
  if (!params.__v || params.__v < 8) {
    const br = document.querySelector('#fd-br')
    if (br && (params.__v === undefined || Number(br.value) >= 0.8)) br.value = '0.3'
    console.log('[debug] 参数迁移到 v8：泛光半径 0.3（光晕收紧，不再整屏发雾）')
  }
  // v9：让"发光件的泛光能溢到周边"——发光件本身先亮一档，泛光再铺开一些。
  // 只迁移还停留在旧默认值的项，用户自己调过的不动。
  if (!params.__v || params.__v < 9) {
    const gi = document.querySelector('#fd-glow-i')
    const bs = document.querySelector('#fd-bs')
    const bt = document.querySelector('#fd-bt')
    const br = document.querySelector('#fd-br')
    if (gi && Number(gi.value) <= 1) gi.value = '2'
    if (bs && Number(bs.value) <= 0.6) bs.value = '0.9'
    if (bt && Number(bt.value) >= 0.5) bt.value = '0.45'
    if (br && Number(br.value) <= 0.3) br.value = '0.5'
    console.log('[debug] 参数迁移到 v9：发光强度 2、Bloom 强度 0.9、阈值 0.45、半径 0.5（泛光溢到周边）')
  }
  fdApply()
  fdPostApply()
  fdSkyApply()
  const safe = (fn) => { try { fn() } catch {} }
  safe(fdScreenApply)
  safe(fdLightApply)
  safe(fdSkyLightApply)
  safe(fdCloudApply)
  safe(fdTerrainDetailApply)
  safe(fdSkyDomeApply)
  safe(mdGrassApply)
  safe(applyGroundFromUI)
  safe(applyGroundBright)
  safe(applyGroundShaderParams)
  safe(applyHallMaterialBright)
  safe(applyVegetationParams)
  safe(mdGroundOnApply)
  safe(mdApply)
  safe(applyFloorTileSize)
  safe(applyGlowBoost)
  safe(rdRenderApply)
  safe(rdLightApply)
  safe(rdSkyApply)
  safe(() => {
    if (!ltHelpers) return
    lightHelperVisible = ltHelpers.checked
    customLights.forEach((e) => { if (e.helper) e.helper.visible = e.visible && lightHelperVisible })
  })
  safe(syncAllNumericInputs)
}
// 四个调试面板里的改动都自动存到本机浏览器（原来"渲染/灯光/天空"和"灯光编辑"两栏漏了，
// 改完不点保存、刷新就丢）
for (const sel of ['#fog-debug', '#mat-debug', '#render-debug', '#light-debug']) {
  const el = document.querySelector(sel)
  el?.addEventListener('input', saveDebugParams)
  el?.addEventListener('change', saveDebugParams)
}
document.querySelector('#fd-save')?.addEventListener('click', () => {
  saveDebugParams()
  showToast('参数已保存到本机浏览器')
})
document.querySelector('#fd-export')?.addEventListener('click', () => {
  const text = JSON.stringify({ params: collectDebugParams(), lights: serializeCustomLights() }, null, 2)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => showToast('参数已复制到剪贴板')).catch(() => showToast(text.slice(0, 60)))
  } else {
    showToast(text.slice(0, 60))
  }
})
document.querySelector('#fd-reset')?.addEventListener('click', () => {
  try { localStorage.removeItem(DEBUG_KEY) } catch {}
  try { localStorage.removeItem(LIGHT_KEY) } catch {}
  location.reload()
})
// 星空粒子：深蓝背景上的星尘，远近都能带一点氛围
function makeStarfield() {
  const count = 4000
  const positions = new Float32Array(count * 3)
  const center = new THREE.Vector3(0, 30, -20)
  for (let i = 0; i < count; i++) {
    // 球壳随机方向 + 半径分层，避免过于均匀
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)
    const r = 240 + Math.pow(Math.random(), 0.7) * 260
    positions[i * 3] = center.x + r * Math.sin(phi) * Math.cos(theta)
    positions[i * 3 + 1] = center.y + r * Math.cos(phi)
    positions[i * 3 + 2] = center.z + r * Math.sin(phi) * Math.sin(theta)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const mat = new THREE.PointsMaterial({
    color: 0xd8f4ff,
    size: 0.55,
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
    fog: false
  })
  const stars = new THREE.Points(geo, mat)
  stars.frustumCulled = false
  scene.add(stars)
  return stars
}
// 室外蓝天场景不需要星空，注释掉保留函数
// makeStarfield()

// 蓝天白云环境：程序生成等距柱状天空图（可调色，取消外部 HDRI）
let skyTopColor = '#7ec8ff'
let skyMidColor = '#bfe6ff'
let skyNearColor = '#e8f7ff'
let skyHorizonColor = '#f4fbff'
let skyEnvTexture = null
let skyCloudOpacity = 0.9
let skyBgMode = 'hdri'
let skySolidColor = '#050816'
let envSource = 'hdri'
let roomEnvTexture = null
// 天光：交付默认档（public/data/default-params.json）用 2K 版 qwantani moonrise，
// 跟原来的 4K 版是同一张天空，只是分辨率减半 —— 4K 那张 48 MB，2K 只有 4.6 MB，
// 而天空在入口就能看见、属于"遮罩必须等"的关键路径。
// 备选：env/017-2k.hdr（5.6 MB）、env/017-1k.hdr（1.44 MB，手机/平板用）、
//       env/017.hdr（原图 13.79 MB）。地址参数可临时切换：?hdri=hd / ?hdri=1k / ?hdri=2k
let HDRI_URL = 'env/qwantani_moonrise_puresky_update_2k.exr'
const hdriSelect = document.querySelector('#rd-hdri')
if (hdriSelect) HDRI_URL = hdriSelect.value
// 启动时先看"本机存档/参数档里选的是哪张环境贴图"，直接加载那一张，
// 避免先加载默认 017.hdr、再切换到目标贴图（画面会闪一下）。
try {
  const savedForHdri = JSON.parse(localStorage.getItem(DEBUG_KEY) || 'null')
  const wantHdri = savedForHdri && savedForHdri['rd-hdri']
  if (wantHdri && !String(wantHdri).startsWith('blob:')) HDRI_URL = wantHdri
} catch (e) { /* 没存档就按默认 */ }
// 地址参数优先级最高，方便现场临时切换
{
  const want = (new URLSearchParams(location.search).get('hdri') || '').toLowerCase()
  if (want === '4k') HDRI_URL = 'env/qwantani_moonrise_puresky_update_4k.exr'   // 原来的 48 MB 那张
  else if (want === 'hd') HDRI_URL = 'env/017.hdr'                             // 017 原图
  else if (want === '2k') HDRI_URL = 'env/017-2k.hdr'
  else if (want === '1k') HDRI_URL = 'env/017-1k.hdr'
}
HDRI_URL = hdriUrlForDevice(HDRI_URL) // 手机/平板换成 2K 版，省几十 MB 下载
let hdriEnvTexture = null      // PMREM 之后用于照明
let hdriRawTexture = null      // 原始等距柱状图，用于当背景
let hdriState = 'idle'         // idle | loading | ready | error
let hdriIsExr = /\.exr($|\?)/i.test(HDRI_URL) // .exr 要用 EXRLoader（按上面定好的那张判断）

// ---------- 天空球：让 HDRI 可以调 转向 / 高低 / 缩放 ----------
const SKY_RADIUS = 8000
let skyDome = null
let skyDomeMat = null
let skyYaw = 0        // 转向（度）
let skyLift = 0       // 高低（米）
let skyZoom = 1       // 缩放
function ensureSkyDome() {
  if (skyDome) return skyDome
  skyDomeMat = new THREE.MeshBasicMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  })
  skyDome = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 40), skyDomeMat)
  skyDome.name = 'SkyDome'
  skyDome.frustumCulled = false
  skyDome.renderOrder = -100
  skyDome.visible = false
  scene.add(skyDome)
  return skyDome
}
function applySkyDome() {
  if (!skyDome) return
  skyDome.rotation.y = THREE.MathUtils.degToRad(skyYaw)
  skyDome.position.y = skyLift
  skyDome.scale.setScalar(SKY_RADIUS)
  // 真正的"缩放"：缩放贴图本身（球放大只是变远，画面不会变）
  const map = skyDomeMat && skyDomeMat.map
  if (map) {
    const z = Math.max(0.15, skyZoom)
    map.wrapS = THREE.RepeatWrapping
    map.wrapT = THREE.ClampToEdgeWrapping
    map.repeat.set(1 / z, 1 / z)
    map.offset.set(0.5 - 0.5 / z, 0.5 - 0.5 / z)
    map.needsUpdate = true
  }
  // 环境照明跟着转（高低/缩放对光照没有意义，只有转向能影响光的方向）
  try {
    scene.environmentRotation = new THREE.Euler(0, THREE.MathUtils.degToRad(skyYaw), 0)
  } catch (e) { /* 老版本 three 没有这个属性 */ }
}
function fdSkyDomeApply() {
  if (fdSkyYaw) skyYaw = Number(fdSkyYaw.value)
  if (fdSkyLift) skyLift = Number(fdSkyLift.value)
  if (fdSkyZoom) skyZoom = Number(fdSkyZoom.value)
  applySkyDome()
}

// 可见天空的亮度：只改"你看到的那层天空"，不参与物体受光
// （物体的 HDRI 受光强度是光照面板里的「环境反射」，两者互不影响）
let skyBright = 1
function applySkyBright() {
  const el = document.querySelector('#fd-sky-bright')
  if (el) {
    skyBright = Math.max(0, Number(el.value) || 0)
    const v = document.querySelector('#fd-sky-bright-v')
    if (v) v.textContent = skyBright.toFixed(2)
  }
  // ① HDRI 背景：看的其实是那个天空球
  if (skyDomeMat) skyDomeMat.color.setScalar(skyBright)
  // ② 天空渐变背景：用 three 自带的背景亮度
  if (typeof scene.backgroundIntensity === 'number') scene.backgroundIntensity = skyBright
  // ③ 纯色背景：backgroundIntensity 对纯色不生效，这里自己乘
  if (scene.background && scene.background.isColor) {
    scene.background.set(skySolidColor).multiplyScalar(skyBright)
  }
}
document.querySelector('#fd-sky-bright')?.addEventListener('input', applySkyBright)
document.querySelector('#fd-sky-bright')?.addEventListener('change', applySkyBright)

// 展厅本体材质的整体亮度：同时压暗基色 + 按同比例削减环境反射（解决白墙发亮、反光过强）
let hallMatBright = 0.85
function applyHallMaterialBright() {
  const el = document.querySelector('#fd-hall-bright')
  if (el) {
    hallMatBright = Number(el.value)
    const v = document.querySelector('#fd-hall-bright-v')
    if (v) v.textContent = hallMatBright.toFixed(2)
  }
  if (!modelRoot) return
  const seen = new Set()
  modelRoot.traverse((o) => {
    if (!o.isMesh || !o.material) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) {
      if (seen.has(m)) continue
      seen.add(m)
      if (!m.color || !(m.isMeshStandardMaterial || m.isMeshPhysicalMaterial)) continue
      if (!m.userData.__origColor) m.userData.__origColor = m.color.clone()
      if (m.userData.__origEnv === undefined) {
        m.userData.__origEnv = typeof m.envMapIntensity === 'number' ? m.envMapIntensity : 1
      }
      // 地面颜色是调试面板定的，压暗要以面板色为基准，否则调完亮度地面颜色会被打回默认
      m.color.copy(m.userData.__panelColor || m.userData.__origColor).multiplyScalar(hallMatBright)
      m.envMapIntensity = m.userData.__origEnv * hallMatBright
      m.needsUpdate = true
    }
  })
}
document.querySelector('#fd-hall-bright')?.addEventListener('input', applyHallMaterialBright)
document.querySelector('#fd-hall-bright')?.addEventListener('change', applyHallMaterialBright)
for (const el of ['fd-sky-yaw', 'fd-sky-lift', 'fd-sky-zoom']) {
  const node = document.querySelector('#' + el)
  node?.addEventListener('input', fdSkyDomeApply)
  node?.addEventListener('change', fdSkyDomeApply)
}
function setHdriFile(file) {
  if (!file || file === HDRI_URL) return
  HDRI_URL = hdriUrlForDevice(file)   // 手机/平板自动换 2K 版
  hdriIsExr = /\.exr($|\?)/i.test(file)
  if (hdriEnvTexture) { hdriEnvTexture.dispose(); hdriEnvTexture = null }
  if (hdriRawTexture) { hdriRawTexture.dispose(); hdriRawTexture = null }
  hdriState = 'idle'
  loadHdriEnv()
  console.log('[hdri] 切换到', HDRI_URL + (HDRI_URL !== file ? '（已按设备换成轻量版）' : ''))
}
// 用本地文件当环境贴图（blob URL，只在本次会话有效）
function setHdriFromLocalFile(file) {
  if (!file) return
  const url = URL.createObjectURL(file)
  hdriIsExr = /\.exr$/i.test(file.name)
  HDRI_URL = url
  if (hdriEnvTexture) { hdriEnvTexture.dispose(); hdriEnvTexture = null }
  if (hdriRawTexture) { hdriRawTexture.dispose(); hdriRawTexture = null }
  hdriState = 'idle'
  loadHdriEnv()
  if (hdriSelect) {
    const opt = document.createElement('option')
    opt.value = url
    opt.textContent = '本地：' + file.name
    hdriSelect.appendChild(opt)
    hdriSelect.value = url
  }
  console.log('[hdri] 使用本地文件', file.name)
  try { showToast('已加载本地 HDRI：' + file.name) } catch {}
}
document.querySelector('#rd-hdri-file')?.addEventListener('change', (e) => {
  const f = e.target.files && e.target.files[0]
  if (f) setHdriFromLocalFile(f)
})
// 下拉换环境贴图（原来漏了这个监听，选了下拉不生效）
if (hdriSelect) {
  const onHdriPick = () => {
    setHdriFile(hdriSelect.value)
    try { saveDebugParams() } catch {}
  }
  hdriSelect.addEventListener('change', onHdriPick)
  hdriSelect.addEventListener('input', onHdriPick)
}
// 下拉列表：从 /api/hdr 动态填充 public/env 里的所有 hdr/exr
async function refreshHdriOptions() {
  if (!hdriSelect) return
  try {
    const list = await fetch('/api/hdr').then((r) => r.json())
    if (!Array.isArray(list) || !list.length) return
    const saved = readDebugParams()
    const want = (saved && saved['rd-hdri']) || HDRI_URL
    hdriSelect.innerHTML = ''
    list.forEach((f) => {
      const opt = document.createElement('option')
      opt.value = 'env/' + f
      opt.textContent = f.replace(/\.(hdr|exr)$/i, '')
      hdriSelect.appendChild(opt)
    })
    const values = [...hdriSelect.options].map((o) => o.value)
    const pick = values.indexOf(want) >= 0 ? want : values[0]
    hdriSelect.value = pick
    if (pick !== HDRI_URL) setHdriFile(pick)
    console.log('[hdri] 可选环境贴图', list.length, '张:', list.join(', '))
  } catch (err) {
    console.warn('[hdri] 列表读取失败', err)
  }
}
refreshHdriOptions()
function loadHdriEnv() {
  if (hdriState !== 'idle') return
  hdriState = 'loading'
  console.log('[hdri] 开始加载', HDRI_URL)
  const Loader = hdriIsExr ? EXRLoader : HDRLoader
  // 本地选的 blob: / data: 地址不加版本号，网络地址才加
  new Loader().load(/^(blob:|data:|https?:)/i.test(HDRI_URL) ? HDRI_URL : withVersion(HDRI_URL), (tex) => {
    tex.mapping = THREE.EquirectangularReflectionMapping
    hdriRawTexture = tex
    const pmrem = new THREE.PMREMGenerator(renderer)
    hdriEnvTexture = pmrem.fromEquirectangular(tex).texture
    pmrem.dispose()
    hdriState = 'ready'
    console.log('[hdri] 加载完成，已生成环境贴图')
    applySkyEnvironment()
    refreshWaterSkyEnv()
  }, undefined, (err) => {
    hdriState = 'error'
    console.warn('[hdri] 加载失败，回退程序天空', err)
    applySkyEnvironment()
  })
}
function makeSkyEquirectTexture() {
  const w = 2048
  const h = 1024
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  const g = ctx.createLinearGradient(0, 0, 0, h)
  g.addColorStop(0, skyTopColor)
  g.addColorStop(0.55, skyMidColor)
  g.addColorStop(0.78, skyNearColor)
  g.addColorStop(1, skyHorizonColor)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, w, h)
  // 白云：近地平线大片 + 高空小片
  ctx.fillStyle = 'rgba(255,255,255,' + skyCloudOpacity + ')'
  const cloud = (x, y, s) => {
    ctx.beginPath()
    ctx.ellipse(x, y, s, s * 0.32, 0, 0, Math.PI * 2)
    ctx.fill()
  }
  cloud(w * 0.16, h * 0.82, 130)
  cloud(w * 0.22, h * 0.84, 180)
  cloud(w * 0.38, h * 0.78, 110)
  cloud(w * 0.5, h * 0.86, 220)
  cloud(w * 0.64, h * 0.8, 140)
  cloud(w * 0.78, h * 0.85, 200)
  cloud(w * 0.92, h * 0.8, 120)
  ctx.fillStyle = 'rgba(255,255,255,' + (skyCloudOpacity * 0.7).toFixed(2) + ')'
  cloud(w * 0.3, h * 0.55, 60)
  cloud(w * 0.55, h * 0.5, 80)
  cloud(w * 0.82, h * 0.58, 50)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.mapping = THREE.EquirectangularReflectionMapping
  return tex
}
function applySkyEnvironment() {
  const skyTex = makeSkyEquirectTexture()
  if (envSource === 'room') {
    if (!roomEnvTexture) {
      const pmrem = new THREE.PMREMGenerator(renderer)
      roomEnvTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
      pmrem.dispose()
    }
    scene.environment = roomEnvTexture
  } else if (envSource === 'hdri') {
    // 真实天光：用 Blender 模型里那张 HDRI 做环境照明
    if (!hdriEnvTexture) loadHdriEnv()
    scene.environment = hdriEnvTexture || skyEnvTexture || null
    if (!scene.environment) {
      const pmrem = new THREE.PMREMGenerator(renderer)
      if (skyEnvTexture) skyEnvTexture.dispose()
      skyEnvTexture = pmrem.fromEquirectangular(skyTex).texture
      pmrem.dispose()
      scene.environment = skyEnvTexture
    }
  } else {
    const pmrem = new THREE.PMREMGenerator(renderer)
    if (skyEnvTexture) skyEnvTexture.dispose()
    skyEnvTexture = pmrem.fromEquirectangular(skyTex).texture
    pmrem.dispose()
    scene.environment = skyEnvTexture
  }
  if (skyBgMode === 'solid') {
    scene.background = new THREE.Color(skySolidColor)
  } else if (skyBgMode === 'hdri') {
    if (!hdriRawTexture) loadHdriEnv()
    // 用天空球显示 HDRI（这样可以调高低/转向/缩放），背景就不再重复画一层
    ensureSkyDome()
    skyDomeMat.map = hdriRawTexture || skyTex
    skyDomeMat.needsUpdate = true
    skyDome.visible = true
    scene.background = null
    applySkyDome()
  } else {
    if (skyDome) skyDome.visible = false
    scene.background = skyTex
  }
  if (typeof scene.environmentIntensity !== 'number') scene.environmentIntensity = 0.9
  applySkyBright()   // 换背景/换 HDRI 后保持天空亮度设置
  try { fdSkyLightApply() } catch (e) { /* 灯光尚未创建 */ }
}
applySkyEnvironment()

// 远裁剪面放到 2 万米，容得下半径 8000 的天空球
const camera = new THREE.PerspectiveCamera(50, 1, 0.5, 20000)
// 镜头按"16:9 构图"对齐：屏幕比 16:9 窄（平板竖屏、方形窗口）时自动放大垂直视角，
// 保证左右两侧看到的内容和电脑上一样多，不会把展厅裁掉。
const DESIGN_ASPECT = 16 / 9
function applyCameraFov() {
  const base = Number(rdFov ? rdFov.value : 50) || 50
  const a = camera.aspect || DESIGN_ASPECT
  // 只在手机/平板上做这个补偿：电脑端保持原来的构图（你调好的入场画面不会变）
  camera.fov = (IS_TOUCH && a < DESIGN_ASPECT)
    ? THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(base) / 2) * (DESIGN_ASPECT / a)))
    : base
  camera.updateProjectionMatrix()
}
camera.position.set(0, 2, 8)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.08
controls.minDistance = 0.5
controls.maxDistance = 300
controls.maxPolarAngle = Math.PI * 0.95
// 鼠标按键：左键旋转、**中键平移**、右键也平移（默认中键是缩放，滚轮已经能缩放）
controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN }
// OrbitControls 只对滚轮/右键菜单做了 preventDefault，中键按下会触发浏览器的"自动滚动"小圆盘，
// 这里单独挡掉中键的默认行为（不影响左键、右键和滚轮）
canvas.addEventListener('mousedown', (e) => { if (e.button === 1) e.preventDefault() })
// 相机防穿模用的射线（和场景点击用的 raycaster 分开，互不影响）
const camRay = new THREE.Raycaster()
const camDir = new THREE.Vector3()
let camCollideAt = -1e9 // 最近一次"防穿模"生效的时间（调试读数里会显示）
let camZoomAt = -1e9    // 最近一次滚轮/双指缩放的时间
let camLastDist = 0
canvas.addEventListener('wheel', () => { camZoomAt = performance.now() }, { passive: true })
canvas.addEventListener('touchmove', () => { camZoomAt = performance.now() }, { passive: true })

// 自定义灯光的移动/旋转手柄
const transformControls = new TransformControls(camera, renderer.domElement)
transformControls.setMode('translate')
transformControls.setSize(0.8)
const transformHelper = transformControls.getHelper()
transformHelper.visible = false
scene.add(transformHelper)
transformControls.addEventListener('dragging-changed', (e) => {
  controls.enabled = !e.value
})
// 恢复上次保存的自定义灯（此时 TransformControls 已就绪）
loadCustomLights()

// 导航状态：null = 入口界面；非 null = 已飞入某个展区
let currentZone = null
let isFlying = false

// 后处理用的贴图：显式带上 MSAA 采样，否则后处理会让画面失去抗锯齿
function makePostTarget(w, h, samples) {
  return new THREE.WebGLRenderTarget(Math.max(1, Math.round(w)), Math.max(1, Math.round(h)), {
    type: THREE.HalfFloatType,
    samples,
  })
}
// 后处理栈改成"按需创建"：画质档位在运行时可以升降，所以创建时机不能写死在启动那一刻。
// 弱机器一开始就落在 low 档时，这里不会白分配一堆全屏 render target。
function ensureComposer() {
  if (composer) return
  const db0 = renderer.getDrawingBufferSize(new THREE.Vector2())
  composer = new EffectComposer(renderer, makePostTarget(db0.x, db0.y, MSAA_SAMPLES))
  composer.addPass(new RenderPass(scene, camera))
  // 电影级：SSAO 增加接缝暗部 → 克制的高阈值 bloom
  ssaoPass = new SSAOPass(scene, camera, window.innerWidth, window.innerHeight)
  ssaoPass.kernelRadius = 8
  ssaoPass.minDistance = 0.005
  ssaoPass.maxDistance = 0.1
  composer.addPass(ssaoPass)
  bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.4, 0.5, 0.85)
  composer.addPass(bloomPass)
  composer.addPass(new OutputPass())
}

// ---------------------------------------------------------------------------
// 选择性泛光：只有"自发光的件"（灯带 / 屏幕 / 星空球 / 光轨）参与泛光。
// 原来的做法是把整幅画面都丢给泛光，白墙、展品被照亮的地方也会跟着发光，
// 看着就是"白色材质在发亮"。这里改成：单独把发光件渲染成一层拿去泛光，再叠回画面。
// ---------------------------------------------------------------------------
const BLOOM_LAYER = 1
function ensureBloomStack() {
  if (bloomComposer) return
  if (!composer) ensureComposer()
  const bs0 = BLOOM_RES_SCALE
  const db0b = renderer.getDrawingBufferSize(new THREE.Vector2())
  bloomComposer = new EffectComposer(renderer, makePostTarget(db0b.x * bs0, db0b.y * bs0, MSAA_SAMPLES))
  // 关键：泛光这一遍必须"渲染到贴图"而不是渲染到屏幕。
  // EffectComposer 默认 renderToScreen = true，会让最后的泛光 pass 直接画到屏幕上，
  // renderTarget2 里只剩"发光件那一层的原图"，混合步骤拿不到泛光结果 ——
  // 表现就是：阈值/强度怎么调都没变化，发光件只有原图亮度、没有光晕。
  bloomComposer.renderToScreen = false
  bloomComposer.addPass(new RenderPass(scene, camera))
  bloomPassOnly = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.4, 0.5, 0.85)
  bloomComposer.addPass(bloomPassOnly)
  bloomMixPass = new ShaderPass({
    uniforms: {
      baseTexture: { value: null },
      bloomTexture: { value: bloomComposer.renderTarget2.texture },
      bloomStrength: { value: 1.0 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform sampler2D baseTexture;
      uniform sampler2D bloomTexture;
      uniform float bloomStrength;
      varying vec2 vUv;
      void main() {
        vec4 base = texture2D(baseTexture, vUv);
        vec4 bloom = texture2D(bloomTexture, vUv);
        gl_FragColor = vec4(base.rgb + bloom.rgb * bloomStrength, base.a);
      }`,
  }, 'baseTexture')
  bloomMixPass.needsSwap = true
  // 关键：ShaderPass 内部会用 cloneUniforms 复制 uniform，而"渲染目标贴图"会被置成 null
  // （控制台那条 "Textures of render targets cannot be cloned" 警告就是这里的），
  // 必须在创建之后手动把泛光贴图接回去，否则混合拿到的是一张空图、泛光等于没开。
  bloomMixPass.uniforms.bloomTexture.value = bloomComposer.renderTarget2.texture
  // 插在 OutputPass（色调映射）之前，让泛光和画面一起做色调映射
  const outPass = composer.passes[composer.passes.length - 1]
  composer.removePass(outPass)
  composer.addPass(bloomMixPass)
  composer.addPass(outPass)
  // 抗锯齿放在最末尾（色调映射之后）：细线、栏杆、灯带边缘的锯齿会顺下来
  if (FXAA_ENABLED) ensureFxaaPass()
}
// FXAA 那一遍单独抽出来：档位在运行时升到"带 FXAA"的档时，栈可能已经建好了但没有这一遍
function ensureFxaaPass() {
  if (aaPass || !composer) return
  aaPass = new FXAAPass()
  aaPass.renderToScreen = false // 由 EffectComposer 自动决定最后一步是否上屏
  composer.addPass(aaPass)
}
// 起始档需要泛光/抗锯齿才创建后处理栈（和原来的行为一致：low 档不创建）
if (QUALITY.bloom) {
  ensureComposer()
  ensureBloomStack()
}

// 泛光层的"遮挡板"：渲染泛光那一遍时，把非发光件临时换成纯黑材质。
// 这样泛光层里也有正确的遮挡关系 —— 被墙、展品、地板挡住的灯带不会透出来
// （之前只渲染发光件、没有挡板，被挡住的部分也会叠到画面上，看着就像模型透明）。
const bloomLayerOnly = new THREE.Layers()
bloomLayerOnly.set(BLOOM_LAYER)
const BLOOM_MASK_OPAQUE = new THREE.MeshBasicMaterial({ color: 0x000000, toneMapped: false })
const BLOOM_MASK_GLASS = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.25, toneMapped: false, depthWrite: false })
const bloomSwapped = []
function bloomDarkenNonGlow() {
  bloomSwapped.length = 0
  bloomSkipped.length = 0
  // 1) 展品：泛光层里不重画（它们占了每帧 2000 万面里的绝大部分）
  if (BLOOM_OCC_MODE !== 'full') {
    const roots = []
    scene.traverse((o) => { if (o.name && /^HallExhibit_/i.test(o.name)) roots.push(o) })
    for (const root of roots) {
      root.traverse((o) => {
        if (!o.isMesh || !o.material) return
        if (o.layers.test(bloomLayerOnly)) return // 展品上的发光件仍要参与泛光
        if (o.visible === false) return
        bloomSkipped.push([o, o.visible])
        o.visible = false
      })
    }
    if (bloomProxyMesh && BLOOM_OCC_MODE === 'box') bloomProxyMesh.visible = true
  }
  // 2) 其余不发光件：照旧换成纯黑材质当挡板（墙、顶棚、地面、展台都留着）
  scene.traverse((o) => {
    if (!o.isMesh || !o.material) return
    if (o.visible === false) return // 上面已经跳过的展品不用再换材质
    if (o.layers.test(bloomLayerOnly)) return // 发光件保持原样
    bloomSwapped.push([o, o.material])
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    const dark = mats.map((m) => (m && m.transparent ? BLOOM_MASK_GLASS : BLOOM_MASK_OPAQUE))
    o.material = Array.isArray(o.material) ? dark : dark[0]
  })
}
function bloomRestoreMaterials() {
  for (const [o, m] of bloomSwapped) o.material = m
  bloomSwapped.length = 0
  for (const [o, v] of bloomSkipped) o.visible = v
  bloomSkipped.length = 0
  if (bloomProxyMesh) bloomProxyMesh.visible = false
}

// ---------------------------------------------------------------------------
// 泛光层的"挡板"策略（性能关键）
//   老做法：泛光那一遍把每个零件都换成黑材质、整场景重画一次。
//           实测电脑端每帧因此多提交 20.7M 三角面 + 352 次绘制，平板直接扛不住，
//           结果就是"平板自动降画质、最后连泛光都被关掉"。
//   新做法（默认）：泛光那一遍只画"发光件 + 墙/地面/展台/顶棚这些便宜的大件"，
//           展品用一个世界包围盒（每个 12 个三角面）当挡板 —— 遮挡关系几乎一样，
//           但每帧少提交约 2000 万面。
//   想对比可以加地址参数：
//           ?bloomocc=full  老做法（最准最贵）
//           ?bloomocc=hide  展品在泛光层里完全不画（最省，少数角度可能漏一点光）
const BLOOM_OCC_MODE = (() => {
  const q = (new URLSearchParams(location.search).get('bloomocc') || '').toLowerCase()
  return (q === 'hide' || q === 'full') ? q : 'box'
})()
const bloomSkipped = []        // [对象, 原本的 visible]
let bloomProxyMesh = null
let bloomProxyBuiltAt = -1e9
let bloomProxyExhibitCount = -1 // 上次生成挡板时场上有几个展品（展品是陆续加载进来的）
let bloomProxyLoggedCount = -1
// 求一个展品"当挡板用"的包围盒。
// 不能直接用 Box3.setFromObject：86 号模型里有一个零件自身的包围盒就是 18×60×10 米
// （其余零件都正常），泛光层里会因此出现一块巨大的黑板，把灯带的光整片挡掉 ——
// 这就是"泛光突然没了、而且回不来"的原因。
// 所以优先用配置文件里登记的尺寸（app 摆位用的就是这套数据，稳定可靠），
// 中心点取各零件中心的中位数（同样不受个别零件跑飞影响）。
function exhibitOccluderBox(root) {
  const med = (arr) => { const s = arr.slice().sort((a, b) => a - b); return s[Math.floor(s.length / 2)] }
  // 收集每个零件的世界包围盒
  const parts = []
  const cs = []
  root.updateWorldMatrix(true, true)
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return
    let b = null
    try { b = new THREE.Box3().setFromObject(o) } catch { b = null }
    if (!b || b.isEmpty()) return
    parts.push(b)
    cs.push(b.getCenter(new THREE.Vector3()))
  })
  if (!parts.length) return null
  // 各零件中心的中位数 = 稳健的中心点
  const cx = med(cs.map((c) => c.x))
  const cy = med(cs.map((c) => c.y))
  const cz = med(cs.map((c) => c.z))
  // 配置文件里登记的尺寸（可能是旧模型留下的，所以只当"最小覆盖范围"用）
  const id = String(root.name || '').replace(/^HallExhibit_/i, '')
  const cfg = hallExhibitCfg && hallExhibitCfg.exhibits ? hallExhibitCfg.exhibits[id] : null
  const info = cfg && cfg._info
  const sz = info && info.size
  let declared = null
  if (sz && sz.length === 3 && Number(sz[0]) > 0.05 && Number(sz[1]) > 0.05 && Number(sz[2]) > 0.05) {
    let sx = Number(sz[0]), sy = Number(sz[1]), s2 = Number(sz[2])
    const rot = Math.abs(Math.round((((cfg.rotY || 0) % 360) + 360) % 360))
    if (rot === 90 || rot === 270) { const t = sx; sx = s2; s2 = t }
    declared = new THREE.Box3(
      new THREE.Vector3(cx - sx / 2, cy - sy / 2, cz - s2 / 2),
      new THREE.Vector3(cx + sx / 2, cy + sy / 2, cz + s2 / 2)
    )
  }
  // 用"登记尺寸的对角线 ×3"当上限，剔掉尺寸跑飞的零件
  // （86 号里那个 18×60×10 米的东西就是这么被挡掉的；正常零件都远小于这个上限）
  const refDiag = declared
    ? declared.getSize(new THREE.Vector3()).length()
    : med(parts.map((b) => b.getSize(new THREE.Vector3()).length())) * 3
  const limit = Math.max(refDiag * 3, 4)
  const kept = parts.filter((b) => b.getSize(new THREE.Vector3()).length() <= limit)
  const use = kept.length ? kept : parts
  const out = use[0].clone()
  for (let i = 1; i < use.length; i++) out.union(use[i])
  // 再和登记尺寸取并集：模型换过（变大）时挡板盒跟着长；模型正常时也不比登记尺寸小
  if (declared) out.union(declared)
  // 兜底：展品机器没有超过 8 米高的，超过就当异常截掉
  if (out.max.y - out.min.y > 8) out.max.y = out.min.y + 8
  return out
}
function bloomBuildProxy() {
  const roots = []
  scene.traverse((o) => { if (o.name && /^HallExhibit_/i.test(o.name)) roots.push(o) })
  const boxes = []
  for (const root of roots) {
    let box = null
    try { box = exhibitOccluderBox(root) } catch { box = null }
    if (box && !box.isEmpty()) boxes.push(box)
  }
  // 加载过程中展品是一个一个进来的，这里只在"数量变了"时打一行，避免刷屏
  if (boxes.length !== bloomProxyLoggedCount) {
    bloomProxyLoggedCount = boxes.length
    console.log('[bloom] 泛光遮挡盒：' + boxes.length + ' 个展品包围盒（模式 ' + BLOOM_OCC_MODE + '）')
  }
  bloomProxyBuiltAt = performance.now()
  bloomProxyExhibitCount = roots.length
  if (bloomProxyMesh) {
    if (bloomProxyMesh.parent) bloomProxyMesh.parent.remove(bloomProxyMesh)
    bloomProxyMesh.geometry.dispose()
    bloomProxyMesh = null
  }
  if (!boxes.length) return
  const parts = []
  for (const b of boxes) {
    const g = new THREE.BoxGeometry(
      Math.max(0.01, b.max.x - b.min.x),
      Math.max(0.01, b.max.y - b.min.y),
      Math.max(0.01, b.max.z - b.min.z)
    )
    g.translate((b.min.x + b.max.x) / 2, (b.min.y + b.max.y) / 2, (b.min.z + b.max.z) / 2)
    parts.push(g)
  }
  const merged = parts.length === 1 ? parts[0] : mergeGeometries(parts, false)
  if (merged !== parts[0]) parts.forEach((g) => g.dispose())
  bloomProxyMesh = new THREE.Mesh(merged, BLOOM_MASK_OPAQUE)
  bloomProxyMesh.name = 'BloomOccluderProxy'
  bloomProxyMesh.matrixAutoUpdate = false
  bloomProxyMesh.visible = false
  scene.add(bloomProxyMesh)
  console.log('[bloom] 泛光遮挡盒已生成：' + boxes.length + ' 个展品包围盒（模式 ' + BLOOM_OCC_MODE + '）')
}

// 测试用：一次性隐藏"发光件集合"（泛光层里的所有对象），用来确认灯带/发光球到底在不在集合里。
// 集合是通过 layers 标记的，所以这里是"按集合"操作，不是逐个点名。
const fdHideGlow = document.querySelector('#fd-hide-glow')
let glowHidden = false
function applyHideGlowLayer() {
  const hide = !!(fdHideGlow && fdHideGlow.checked)
  if (hide === glowHidden) return
  glowHidden = hide
  scene.traverse((o) => {
    if (!o.material) return
    if (!o.layers.test(bloomLayerOnly)) return
    if (o.userData.__glowVisibleOrig === undefined) o.userData.__glowVisibleOrig = o.visible
    o.visible = hide ? false : o.userData.__glowVisibleOrig
  })
  console.log('[bloom] 发光件集合 ' + (hide ? '已隐藏（测试）' : '已恢复显示'))
}
fdHideGlow?.addEventListener('change', applyHideGlowLayer)


/** 给"自发光的件"打上泛光层标记（其余物体不参与泛光） */
function tagBloomObjects() {
  if (!QUALITY.bloom) return 0
  let n = 0
  const tagged = []
  scene.traverse((o) => {
    // 点云（发光球的星点壳）是 isPoints，不是 isMesh —— 之前只判断了 mesh，星点被漏掉了
    if ((!o.isMesh && !o.isPoints && !o.isSprite) || !o.material) return
    const name = o.name || ''
    if (/^(SkyDome|CloudDome|InfiniteGround|Vegetation)/i.test(name)) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    // 屏幕（6 个 HUD + 主屏）要"自发光但不泛光"：这里显式挡掉，
    // 保证以后不管怎么改注册逻辑，屏幕都不会被并进灯带那个泛光组。
    if (/^Screen_/i.test(name)) return
    if (mats.some((m) => m && typeof screenFxMaterials !== 'undefined' && screenFxMaterials.indexOf(m) >= 0)) return
    // 只把"我们自己登记的发光件材质"（灯带 / 灯圈 / 发光球）放进泛光层。
    // 不能用"toneMapped:false / 加色混合"这种泛化判断——展品模型里带无光照材质的网格
    // 也会命中，会被整片叠到主画面上（没有遮挡关系），看起来像"整体半透明/糊了一层雾"。
    const isGlowMat = mats.some((m) => glowBoostMats.some((e) => e.mat === m))
    if (!(isGlowMat || /^(LightStrip|Light_Circle)/i.test(name))) return
    // 再筛一遍大小：模型里有些"壳体"被归在 LightStrip_* 节点下面（比如 90×54 米的顶棚壳），
    // 它们会被刷成灯带材质、跟着进泛光层——大面积的东西叠到主画面上就会糊成一层雾。
    // 用"真实表面积"判断：灯带/灯圈/光环都是细长的（几十~几百平米），壳体是好几千平米。
    if (o.isMesh) {
      try {
        const box = new THREE.Box3().setFromObject(o)
        const stat = meshSurfaceStats(o, box, null)
        if (stat && stat.area > 600) return
      } catch (e) { /* 量不出来就按老的来 */ }
    }
    {
      o.layers.enable(BLOOM_LAYER)
      if (tagged.length < 14) {
        const mt = (Array.isArray(o.material) ? o.material[0] : o.material) || {}
        const parent = (o.parent && o.parent.name) ? o.parent.name : '-'
        tagged.push((name || '(未命名)') + '［' + String(mt.type || '?').replace('Material', '') + ' @' + parent + '］')
      }
      n++
    }
  })
  if (tagged.length) console.log('[bloom] 泛光层内容：' + tagged.join(', '))
  return n
}

// 光照：科技蓝环境色调，环境光压低（≤0.3），展品由 Light_Product 聚光灯单独照亮
let envAmbient = new THREE.AmbientLight(0x3a6ea5, 0.22)
let envHemi = new THREE.HemisphereLight(0x1a3a6a, 0x000000, 0.45)
let envSpot = null
let envBig = null
const envRims = []
scene.add(envAmbient)
scene.add(envHemi)

// 真天光：配合 HDRI 的半球天光 + 阳光。用 HDRI 时启用，用程序天空时关闭。
const fdSkyColor = document.querySelector('#fd-sky-color')
const fdSkyGround = document.querySelector('#fd-sky-ground')
const fdSky = document.querySelector('#fd-sky')
const fdSunColor = document.querySelector('#fd-sun-color')
const fdSun = document.querySelector('#fd-sun')
const fdSunAz = document.querySelector('#fd-sun-az')
var envSkyLight = new THREE.HemisphereLight(0xcfe6ff, 0x4a4438, 0)
envSkyLight.name = 'skyLight'
var envSunLight = new THREE.DirectionalLight(0xfff4e2, 0)
envSunLight.name = 'sunLight'
envSunLight.position.set(-60, 90, -45)
// 主光源（HDRI 的太阳）也投影：原来只有一盏很弱的基础方向光投影，
// 在强环境光下影子几乎看不见，所以树看起来"没有阴影"
envSunLight.castShadow = SHADOWS_ENABLED
envSunLight.shadow.mapSize.set(2048, 2048)
envSunLight.shadow.camera.left = -500
envSunLight.shadow.camera.right = 500
envSunLight.shadow.camera.top = 500
envSunLight.shadow.camera.bottom = -500
envSunLight.shadow.camera.near = 1
envSunLight.shadow.camera.far = 1400
envSunLight.shadow.bias = -0.0006
envSunLight.shadow.normalBias = 0.03
envSunLight.shadow.camera.updateProjectionMatrix()
scene.add(envSkyLight)
scene.add(envSunLight)
function fdSkyLightApply() {
  if (!envSkyLight || !envSunLight) return   // 灯光还没建好时的早调用
  if (fdSkyColor) envSkyLight.color.set(fdSkyColor.value)
  if (fdSkyGround) envSkyLight.groundColor.set(fdSkyGround.value)
  if (fdSky) {
    envSkyLight.intensity = Number(fdSky.value)
    const v = document.querySelector('#fd-sky-v')
    if (v) v.textContent = Number(fdSky.value).toFixed(2)
  }
  if (fdSunColor) envSunLight.color.set(fdSunColor.value)
  if (fdSun) {
    envSunLight.intensity = Number(fdSun.value)
    const v = document.querySelector('#fd-sun-v')
    if (v) v.textContent = Number(fdSun.value).toFixed(2)
  }
  if (fdSunAz) {
    const az = Number(fdSunAz.value) * Math.PI / 180
    const r = 110
    envSunLight.position.set(Math.sin(az) * r * -1, 95, Math.cos(az) * r * -1)
    const v = document.querySelector('#fd-sun-az-v')
    if (v) v.textContent = fdSunAz.value
  }
  // 天光只在 HDRI 模式下生效，避免和原来的程序天空重复打光
  const on = envSource === 'hdri'
  envSkyLight.visible = on
  envSunLight.visible = on
}
for (const el of [fdSkyColor, fdSkyGround, fdSky, fdSunColor, fdSun, fdSunAz]) {
  el?.addEventListener('input', fdSkyLightApply)
  el?.addEventListener('change', fdSkyLightApply)
}
fdSkyLightApply()
const baseDir = new THREE.DirectionalLight(0x9cc6ff, 0.5)
const envDir = baseDir
baseDir.position.set(10, 20, 8)
// 方向光投影（可开关，影响 FPS）
baseDir.castShadow = SHADOWS_ENABLED
if (SHADOWS_ENABLED) {
  baseDir.shadow.mapSize.set(2048, 2048)
  // 阴影范围放大到能覆盖周围的树/草（原来只有 ±55 米，树都在 85 米外 → 完全没有影子）
  baseDir.shadow.camera.left = -500
  baseDir.shadow.camera.right = 500
  baseDir.shadow.camera.top = 500
  baseDir.shadow.camera.bottom = -500
  baseDir.shadow.camera.near = 0.5
  baseDir.shadow.camera.far = 1400
  baseDir.shadow.bias = -0.0005
  baseDir.shadow.normalBias = 0.02
  // 改了阴影相机的范围后必须手动刷新投影矩阵，否则新范围不生效（树就一直没有影子）
  baseDir.shadow.camera.updateProjectionMatrix()
}
scene.add(baseDir)
// 启动时恢复已保存的调试参数（材质/灯光的应用会在模型加载完成后再执行一次）
applyDebugParams(readDebugParams())

// ---------- 模型加载 ----------
// ---------- 天空白云 + 镜面水面 ----------
// 云层是一张程序生成的等距柱状贴图，贴在一个跟着相机走的大球上（相当于无限远的云层）；
// 同一片云也会参与水面反射的环境贴图，所以"天上的云"和"水里的云"是同一片。
let cloudEnabled = false
let cloudAmount = 0.55
let cloudHeight = 900
let cloudDrift = 0.15
let waterEnabled = false
let waterReflect = 2.0
let waterRipple = 0.22

let cloudTexture = null
let cloudDome = null
let cloudDomeMat = null
let waterSkyRT = null
let waterMesh = null
let waterOrigMaterial = null

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// 分形值噪声：云层的基础
function hash2i(x, y, s) {
  let hh = Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(s, 83492791)
  hh = Math.imul(hh ^ (hh >>> 13), 1274126177)
  return ((hh ^ (hh >>> 16)) >>> 0) / 4294967296
}
function valueNoise2(x, y, s) {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const tx = x - x0
  const ty = y - y0
  const sx = tx * tx * (3 - 2 * tx)
  const sy = ty * ty * (3 - 2 * ty)
  const a = hash2i(x0, y0, s)
  const b = hash2i(x0 + 1, y0, s)
  const c = hash2i(x0, y0 + 1, s)
  const d = hash2i(x0 + 1, y0 + 1, s)
  return (a * (1 - sx) + b * sx) * (1 - sy) + (c * (1 - sx) + d * sx) * sy
}
function fbm2(x, y, oct, s) {
  let f = 1
  let amp = 0.5
  let sum = 0
  let norm = 0
  for (let i = 0; i < oct; i++) {
    // 每个倍频旋转一个角度，避免出现明显的条带/重复感
    const ang = i * 1.7 + s * 0.13
    const ca = Math.cos(ang)
    const sa = Math.sin(ang)
    const xx = (x * ca - y * sa) * f
    const yy = (x * sa + y * ca) * f
    sum += amp * valueNoise2(xx, yy, s + i * 17)
    norm += amp
    f *= 2.03
    amp *= 0.5
  }
  return sum / norm
}

// 程序生成云层：把每一条视线投影到高度 H 的云层平面上求分形密度，
// 再按密度梯度做明暗（云的受光面/背光面），得到有体积感的积云。
function makeCloudCanvas(amount = 0.55, seed = 20260915) {
  const w = 2048
  const h = 1024
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  ctx.clearRect(0, 0, w, h)
  const H0 = Math.max(200, cloudHeight)     // 云层高度（米）
  const SCALE = 1300                        // 云团尺度（米）
  const thresh = 0.705 - 0.102 * amount - 0.1056 * amount * amount
  const soft = 0.085
  // 只算天空那一半：1024 x 256，再放大铺到 2048 x 1024
  const CW = 1024
  const CH = 256
  const small = document.createElement('canvas')
  small.width = CW
  small.height = CH
  const sctx = small.getContext('2d')
  const img = sctx.createImageData(CW, CH)
  const d = img.data
  // 光源方向（与 HDRI 的太阳大致同侧）
  const sunAz = 296 * Math.PI / 180
  const sunEl = 24 * Math.PI / 180
  const Lx = Math.cos(sunEl) * Math.cos(sunAz)
  const Ly = Math.sin(sunEl)
  const Lz = Math.cos(sunEl) * Math.sin(sunAz)
  // 沿太阳方向的自阴影取样偏移（云层厚度）
  const sunOx = (Lx / Math.max(0.3, Ly)) * 0.26
  const sunOz = (Lz / Math.max(0.3, Ly)) * 0.26
  const smooth = (a, b, x) => {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
  }
  for (let Y = 0; Y < CH; Y++) {
    // 画布 y=0 是天顶，y=h/2 是地平线
    const v = 1 - (Y + 0.5) / (2 * CH)      // 0.5 ~ 1
    const el = (v - 0.5) * Math.PI
    const zenithFade = 1 - 0.62 * (() => {
      const t = Math.max(0, Math.min(1, (el * 180 / Math.PI - 58) / 30))
      return t * t * (3 - 2 * t)
    })()
    const sinEl = Math.max(0.035, Math.sin(el))
    const dist = H0 / sinEl
    for (let X = 0; X < CW; X++) {
      const az = (X / CW) * Math.PI * 2
      const px = Math.cos(az) * dist
      const pz = Math.sin(az) * dist
      const qx = px / SCALE
      const qz = pz / SCALE
      const dens = fbm2(qx, qz, 5, seed)
      let alpha = smooth(thresh, thresh + soft, dens)
      let r = 255
      let g = 255
      let b = 255
      if (alpha > 0.002) {
        // 沿着太阳方向再采样一次：朝光一侧更厚 = 这里在阴影里（云的自阴影）
        const densSun = fbm2(qx + sunOx, qz + sunOz, 4, seed)
        let lit = 0.60 + (dens - densSun) * 3.0
        lit = Math.max(0.24, Math.min(1.16, lit))
        // 薄的边缘透光，稍微提亮（银边）
        const thin = 1 - smooth(thresh, thresh + soft * 1.2, dens)
        lit = Math.min(1.22, lit + thin * 0.22)
        r = Math.min(255, 245 * lit + 22)
        g = Math.min(255, 247 * lit + 24)
        b = Math.min(255, 252 * lit + 30)
        // 边缘半透明，云才有"消散感"
        alpha *= 0.30 + 0.70 * smooth(thresh + soft * 0.1, thresh + soft * 1.5, dens)
        // 正头顶（贴图最上端）云量减少，避免极区拉伸感
        if (zenithFade < 1) alpha *= zenithFade
      } else {
        alpha = 0
      }
      const i4 = (Y * CW + X) * 4
      d[i4] = r
      d[i4 + 1] = g
      d[i4 + 2] = b
      d[i4 + 3] = Math.round(Math.min(1, alpha) * 255)
    }
  }
  sctx.putImageData(img, 0, 0)
  ctx.imageSmoothingEnabled = true
  ctx.drawImage(small, 0, 0, w, h * 0.5)

  // 贴近地平线的部分淡出，云不会硬压在地平线上
  const g = ctx.createLinearGradient(0, h * 0.5, 0, h * 0.462)
  g.addColorStop(0, 'rgba(0,0,0,1)')
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.globalCompositeOperation = 'destination-out'
  ctx.fillStyle = g
  ctx.fillRect(0, h * 0.462, w, h * 0.538)
  ctx.globalCompositeOperation = 'source-over'
  return c
}

function rebuildCloudTexture() {
  if (cloudTexture) cloudTexture.dispose()
  cloudTexture = new THREE.CanvasTexture(makeCloudCanvas(cloudAmount))
  cloudTexture.colorSpace = THREE.SRGBColorSpace
  cloudTexture.wrapS = THREE.RepeatWrapping
  cloudTexture.wrapT = THREE.ClampToEdgeWrapping
  cloudTexture.anisotropy = 4
  if (cloudDomeMat) {
    cloudDomeMat.map = cloudTexture
    cloudDomeMat.needsUpdate = true
  }
}

// 云层球：半径 1400（在相机远裁剪面内），挂在相机上，永远铺满天空
function ensureCloudDome() {
  if (cloudDome) return cloudDome
  if (cloudEnabled && !cloudTexture) rebuildCloudTexture()
  cloudDomeMat = new THREE.MeshBasicMaterial({
    map: cloudTexture,
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    fog: false,
    toneMapped: true,
  })
  cloudDome = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 40), cloudDomeMat)
  cloudDome.scale.setScalar(1400)
  cloudDome.renderOrder = -10
  cloudDome.frustumCulled = false
  cloudDome.name = 'CloudSky'
  cloudDome.visible = cloudEnabled
  scene.add(camera)
  camera.add(cloudDome)
  return cloudDome
}

// 水面：低粗糙度镜面 + 波纹法线，把天空的白云映到水里
function makeRippleNormalMap(size = 512, repeat = 220) {
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')
  const img = ctx.createImageData(size, size)
  const rnd = mulberry32(97531)
  const waves = []
  for (let i = 0; i < 9; i++) {
    const ang = rnd() * Math.PI * 2
    const f = 2 + Math.floor(rnd() * 6)      // 整数波数 -> 贴图可无缝平铺
    waves.push({
      fx: Math.cos(ang) * f,
      fy: Math.sin(ang) * f,
      a: (0.35 + rnd() * 0.65) / (1 + f * 0.45),
      ph: rnd() * Math.PI * 2,
    })
  }
  const hgt = new Float32Array(size * size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x / size) * Math.PI * 2
      const v = (y / size) * Math.PI * 2
      let s = 0
      for (const w of waves) s += w.a * Math.sin(u * w.fx + v * w.fy + w.ph)
      hgt[y * size + x] = s
    }
  }
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x
      const dx = hgt[y * size + ((x + 1) % size)] - hgt[y * size + ((x - 1 + size) % size)]
      const dy = hgt[((y + 1) % size) * size + x] - hgt[((y - 1 + size) % size) * size + x]
      const nx = -dx * 2.4
      const ny = -dy * 2.4
      const nz = 1
      const l = Math.hypot(nx, ny, nz) || 1
      img.data[i * 4] = ((nx / l) * 0.5 + 0.5) * 255
      img.data[i * 4 + 1] = ((ny / l) * 0.5 + 0.5) * 255
      img.data[i * 4 + 2] = ((nz / l) * 0.5 + 0.5) * 255
      img.data[i * 4 + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)
  const t = new THREE.CanvasTexture(c)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.repeat.set(repeat, repeat)
  t.anisotropy = 4
  return t
}

const rippleTexture = makeRippleNormalMap()
// 调试：把云层/波纹贴图生成器暴露出来，方便离线检查贴图效果
window.__opt2 = Object.assign(window.__opt2 || {}, {
  scene, camera, renderer, controls,
  makeCloudCanvas, makeRippleNormalMap, ensureCloudDome,
  cloudInfo: () => ({
    enabled: cloudEnabled, amount: cloudAmount, height: cloudHeight,
    domeVisible: !!(cloudDome && cloudDome.visible),
  }),
  terrainDetailInfo: () => ({
    texA: !!terrainDetailA, texB: !!terrainDetailB,
    shaders: terrainDetailShaders.length, strength: terrainDetailStrength,
  }),
  terrainDetailDump: () => ({
    a: terrainDetailA ? terrainDetailA.image.toDataURL('image/png') : null,
    b: terrainDetailB ? terrainDetailB.image.toDataURL('image/png') : null,
  }),
  terrainDetailTex: () => ({ a: terrainDetailA, b: terrainDetailB }),
})
const matWater = new THREE.MeshPhysicalMaterial({
  color: 0x0a2632,
  roughness: 0.09,
  metalness: 0.0,
  ior: 1.33,
  envMapIntensity: waterReflect,
  clearcoat: 1.0,
  clearcoatRoughness: 0.05,
  normalMap: rippleTexture,
  normalScale: new THREE.Vector2(waterRipple, waterRipple),
})
matWater.name = 'M_Water_Reflective'
// 云层球一开始就建好（只依赖相机），这样即使模型还没加载完，天空里也已经有云
ensureCloudDome()

// 水面缺少 UV，用平面投影补一套（约 9 米一个波纹循环）
function ensureWaterUV(geo) {
  if (geo.attributes.uv) return
  geo.computeBoundingBox()
  const bb = geo.boundingBox
  const span = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) || 1
  const pos = geo.attributes.position
  const uv = new Float32Array(pos.count * 2)
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = (pos.getX(i) - bb.min.x) / span
    uv[i * 2 + 1] = (pos.getZ(i) - bb.min.z) / span
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
}

function applyWaterToMesh(o) {
  waterMesh = o
  if (!waterOrigMaterial) waterOrigMaterial = o.material
  ensureWaterUV(o.geometry)
  o.material = waterEnabled ? matWater : waterOrigMaterial
  o.userData.noCast = true
  o.castShadow = false
  o.receiveShadow = false
  o.renderOrder = 1
  refreshWaterSkyEnv()
}

// 用"HDRI 天空 + 云层"生成水面反射用的环境贴图，保证水里映出的就是天上的云
function refreshWaterSkyEnv() {
  if (!waterMesh || !waterEnabled) return
  if (!hdriRawTexture) return
  const skyScene = new THREE.Scene()
  skyScene.background = hdriRawTexture
  let dome = null
  if (cloudEnabled && cloudTexture) {
    dome = new THREE.Mesh(
      new THREE.SphereGeometry(1, 48, 32),
      new THREE.MeshBasicMaterial({
        map: cloudTexture,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        fog: false,
      })
    )
    dome.scale.setScalar(1000)
    dome.frustumCulled = false
    skyScene.add(dome)
  }
  const pmrem = new THREE.PMREMGenerator(renderer)
  const rt = pmrem.fromScene(skyScene, 0.02, 1, 3000)
  pmrem.dispose()
  if (dome) {
    dome.geometry.dispose()
    dome.material.dispose()
  }
  if (waterSkyRT) waterSkyRT.dispose()
  waterSkyRT = rt
  matWater.envMap = rt.texture
  matWater.needsUpdate = true
}

// 地形细节层：模型的材质里 Texture01 平铺 26/58 倍、Rock detail 平铺 11 倍，
// 这些高频细节在烘焙成整张 UV 贴图时会被平均掉，这里单独作为平铺细节层接回来。
const TERRAIN_DETAIL_URLS = {
  a: withVersion('textures/terrain/terrain_detail01.jpg'),   // Texture01，平铺 26 倍（凹凸）
  b: withVersion('textures/terrain/terrain_detail02.jpg'),   // Rock detail，平铺 11 倍（凹凸）
}
let terrainDetailA = null
let terrainDetailB = null
let terrainDetailStrength = 0.6
const terrainDetailShaders = []
const terrainDetailPending = []

function maxAniso() {
  try { return renderer.capabilities.getMaxAnisotropy() } catch { return 4 }
}

// 把灰度高度贴图转成法线贴图，用于细节凹凸
function heightToNormalTexture(image, size = 512, gain = 2.4) {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')
  ctx.drawImage(image, 0, 0, size, size)
  const src = ctx.getImageData(0, 0, size, size)
  const hgt = new Float32Array(size * size)
  for (let i = 0; i < size * size; i++) {
    hgt[i] = (src.data[i * 4] * 0.299 + src.data[i * 4 + 1] * 0.587 + src.data[i * 4 + 2] * 0.114) / 255
  }
  const out = ctx.createImageData(size, size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const xl = hgt[y * size + ((x - 1 + size) % size)]
      const xr = hgt[y * size + ((x + 1) % size)]
      const yu = hgt[((y - 1 + size) % size) * size + x]
      const yd = hgt[((y + 1) % size) * size + x]
      const nx = -(xr - xl) * gain
      const ny = -(yd - yu) * gain
      const nz = 1
      const l = Math.hypot(nx, ny, nz) || 1
      const i4 = (y * size + x) * 4
      out.data[i4] = ((nx / l) * 0.5 + 0.5) * 255
      out.data[i4 + 1] = ((ny / l) * 0.5 + 0.5) * 255
      out.data[i4 + 2] = ((nz / l) * 0.5 + 0.5) * 255
      out.data[i4 + 3] = 255
    }
  }
  ctx.putImageData(out, 0, 0)
  const t = new THREE.CanvasTexture(c)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.anisotropy = maxAniso()
  return t
}

// 首次真正遇到 M_Terrain 材质时才去下载/生成这两张细节层，避免模型里没有地形时白下 2.7 MB、
// 还白跑两次 2048×2048 的逐像素法线图转换（那两次转换在手机上要卡几百毫秒，正好压在加载页上）。
let terrainDetailRequested = false
function loadTerrainDetail() {
  if (terrainDetailRequested) return
  terrainDetailRequested = true
  const loader = new THREE.TextureLoader()
  const load = (url, cb) => loader.load(url, (tex) => {
    tex.colorSpace = THREE.NoColorSpace
    cb(tex.image)
  }, undefined, () => console.warn('[terrain-detail] 加载失败', url))
  load(TERRAIN_DETAIL_URLS.a, (img) => {
    terrainDetailA = heightToNormalTexture(img, 2048, 3.6)
    console.log('[terrain-detail] Texture01 细节层就绪')
    flushTerrainDetail()
  })
  load(TERRAIN_DETAIL_URLS.b, (img) => {
    terrainDetailB = heightToNormalTexture(img, 2048, 2.4)
    console.log('[terrain-detail] Rock detail 细节层就绪')
    flushTerrainDetail()
  })
}

function injectTerrainDetail(mat) {
  if (!terrainDetailA || !terrainDetailB) {
    if (!terrainDetailPending.includes(mat)) terrainDetailPending.push(mat)
    return
  }
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDetA = { value: terrainDetailA }
    shader.uniforms.uDetB = { value: terrainDetailB }
    shader.uniforms.uDetRepA = { value: 26.0 }
    shader.uniforms.uDetRepB = { value: 11.0 }
    shader.uniforms.uDetStrength = { value: terrainDetailStrength }
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform sampler2D uDetA;
        uniform sampler2D uDetB;
        uniform float uDetRepA;
        uniform float uDetRepB;
        uniform float uDetStrength;`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        #ifdef USE_NORMALMAP_TANGENTSPACE
          vec3 detN1 = texture2D( uDetA, vMapUv * uDetRepA ).xyz * 2.0 - 1.0;
          vec3 detN2 = texture2D( uDetB, vMapUv * uDetRepB ).xyz * 2.0 - 1.0;
          vec2 detXY = ( detN1.xy * 0.85 + detN2.xy * 0.55 ) * uDetStrength;
          normal = normalize( normal + tbn * vec3( detXY, 0.0 ) );
        #endif`)
    terrainDetailShaders.push(shader)
  }
  mat.needsUpdate = true
  console.log('[terrain-detail] 已接入地形材质')
}

function flushTerrainDetail() {
  if (!terrainDetailA || !terrainDetailB || !terrainDetailPending.length) return
  const list = terrainDetailPending.splice(0, terrainDetailPending.length)
  for (const m of list) injectTerrainDetail(m)
}

function registerTerrainMaterial(mat) {
  if (!mat || mat.userData.terrainDetailDone) return
  mat.userData.terrainDetailDone = true
  loadTerrainDetail()   // 现在这里才是唯一的触发点：有地形材质才加载细节层
  injectTerrainDetail(mat)
}

function fdTerrainDetailApply() {
  if (fdGroundDetail) terrainDetailStrength = Number(fdGroundDetail.value)
  for (const s of terrainDetailShaders) s.uniforms.uDetStrength.value = terrainDetailStrength
}
// 注意：这里以前是无条件调用 loadTerrainDetail()。当前 hall.glb 没有任何 M_Terrain 材质，
// 所以两张地形细节图（约 2.7 MB）下载完只会被丢掉。现在改成由 registerTerrainMaterial() 按需触发。

// 贴图清晰度：地面/山体这类大面积贴图在斜视角下会被压成低精度 mipmap，
// 打开各向异性过滤（通常是 16x）能明显变清楚。
const MAX_ANISO = (() => {
  try { return renderer.capabilities.getMaxAnisotropy() } catch { return 4 }
})()
const TEX_SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap',
  'emissiveMap', 'specularMap', 'clearcoatMap', 'clearcoatRoughnessMap',
  'sheenColorMap', 'alphaMap', 'bumpMap', 'displacementMap', 'lightMap']
function boostTextureSharpness(root) {
  if (!root) return 0
  const seen = new Set()
  let n = 0
  root.traverse((o) => {
    if (!o.isMesh || !o.material) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) {
      if (!m) continue
      for (const k of TEX_SLOTS) {
        const t = m[k]
        if (!t || !t.isTexture || seen.has(t)) continue
        seen.add(t)
        if (!t.anisotropy || t.anisotropy < MAX_ANISO) t.anisotropy = MAX_ANISO
        t.needsUpdate = true
        n++
      }
    }
  })
  return n
}

// 调试句柄：方便从浏览器控制台或自动化脚本检查场景
function exposeDebugHandles() {
  if (!window.__opt2) window.__opt2 = {}
  Object.assign(window.__opt2, {
    scene, camera, renderer, controls, modelRoot,
    // 自动化核对用：机位还原相关
    camState: () => ({
      pos: camera.position.toArray().map((v) => +v.toFixed(3)),
      target: controls.target.toArray().map((v) => +v.toFixed(3)),
      saved: camBeforeProduct ? {
        pos: camBeforeProduct.pos.toArray().map((v) => +v.toFixed(3)),
        target: camBeforeProduct.target.toArray().map((v) => +v.toFixed(3)),
      } : null,
    }),
    openProduct, closeProductFull, restoreCameraBeforeProduct,
    gsap,
  })
}

const loader = new GLTFLoader()

// ---------------------------------------------------------------------------
// 加载遮罩：等"展厅模型 + 全部展品"都就位再揭开。
// 之前是展厅模型一读完就揭开，展品还在一个个加载，所以能看到零件逐个蹦出来。
// ---------------------------------------------------------------------------
const loadingState = { hall: false, exhibits: false }
// 进度条：真实进度用 loadingTarget，界面显示用 loadingShown。
// 显示值会平滑追赶真实值，并且在"等待中"也会缓慢往前爬，避免看起来卡死不动。
let loadingTarget = 0
let loadingShown = 0
let loadingLabel = '正在加载展厅模型…'
let loadingTick = null
function setLoadingProgress(pct, text) {
  if (typeof pct === 'number') loadingTarget = Math.max(loadingTarget, Math.max(0, Math.min(100, pct)))
  if (text) loadingLabel = text
  if (loadingTick) return
  loadingTick = setInterval(() => {
    if (loadingShown < loadingTarget) {
      loadingShown = Math.min(loadingTarget, loadingShown + Math.max(0.5, (loadingTarget - loadingShown) * 0.2))
    } else if (loadingTarget < 100) {
      // 等待阶段：慢慢往目标上方爬一点，最多爬到 96%，别让进度条看着是死的
      const ceil = Math.min(96, loadingTarget + 10)
      if (loadingShown < ceil) loadingShown = Math.min(ceil, loadingShown + 0.35)
    } else {
      loadingShown = 100
    }
    const fill = document.querySelector('#loading-fill')
    if (fill) fill.style.width = loadingShown.toFixed(1) + '%'
    // 界面只留进度条，不显示文字（出错时下面那个 handler 会再把文字露出来）
    const t = document.querySelector('#loading-text')
    if (t) t.style.display = 'none'
    if (loadingShown >= 100) {
      clearInterval(loadingTick)
      loadingTick = null
    }
  }, 100)
}
// 预编译着色器 + 预热贴图 —— 都放在遮罩还盖着的时候做完。
// 为什么值得做：three 是"第一次渲染某个材质时才编译着色器、才上传贴图"，而渲染循环只画
// 当前视锥内的东西。入口机位下，展区里的展品大多在视锥外，于是它们的着色器要等用户点分区
// 飞进去时才第一次编译 —— 那一瞬间就是"飞入途中顿一下"的来源。compileAsync 会把整个场景
// 的材质都先编译好，initTexture 把贴图先塞进显存。
// 想看对比：地址后加 ?warmup=0 关掉这段。
const WARMUP_ENABLED = new URLSearchParams(location.search).get('warmup') !== '0'
const WARMUP_MAX_MS = 5000   // 兜底：预热最多占 5 秒，宁可直接揭开也不能卡住遮罩
// 开机实测：趁遮罩还盖着量一轮真实帧率，用来定初始档位（"先测再分配"）。
// 注意显示器垂直同步会把帧间隔钉在 16.7ms(60Hz) / 8.3ms(120Hz)，
// 所以实测只能判断"这台机器扛不扛得住当前配置"：扛不住就往下掉（最多两档），
// 富余就升一档；之后由进厅后的双向微调继续收敛。
function probeQualityLevel() {
  return new Promise((resolve) => {
    const t0 = performance.now()
    const gaps = []
    let last = t0
    const step = () => {
      const now = performance.now()
      gaps.push(now - last)
      last = now
      if (gaps.length >= 30 || now - t0 > 1800) {
        const sorted = gaps.slice().sort((a, b) => a - b)
        const med = sorted[Math.floor(sorted.length / 2)]
        resolve(1000 / Math.max(1, med))
      } else {
        requestAnimationFrame(step)
      }
    }
    requestAnimationFrame(step)
  })
}
async function applyProbeResult() {
  if (!AUTO_QUALITY || qualityLocked) return
  let fps = 0
  try { fps = await probeQualityLevel() } catch {}
  if (!fps) return
  let target = currentLevel
  if (fps < Q_PROBE_CRASH) target = QUALITY_LEVELS.length - 1        // 崩盘级 → 直接最低档
  else if (fps < Q_PROBE_LOW) target = Math.min(QUALITY_LEVELS.length - 1, currentLevel + 2)
  else if (fps < 30) target = Math.min(QUALITY_LEVELS.length - 1, currentLevel + 1)
  else if (fps > Q_UP_FPS) target = Math.max(0, currentLevel - 1)
  if (target !== currentLevel) {
    setQualityLevel(target, '开机实测 ' + Math.round(fps) + ' 帧')
  } else {
    logQuality('开机实测 ' + Math.round(fps) + ' 帧 → 保持 ' + QUALITY_LEVELS[target].name + ' 档')
  }
}
async function warmUpRenderer() {
  const t0 = performance.now()
  try {
    if (typeof renderer.compileAsync === 'function') await renderer.compileAsync(scene, camera)
    else renderer.compile(scene, camera)
  } catch (e) { console.warn('[warmup] 预编译失败', e) }
  let texCount = 0
  try {
    const warmedMats = new Set()
    scene.traverse((o) => {
      const m = o.material
      if (!m) return
      for (const mat of (Array.isArray(m) ? m : [m])) {
        if (!mat || warmedMats.has(mat)) continue
        warmedMats.add(mat)
        for (const slot of TEX_SLOTS) {
          const tex = mat[slot]
          if (!tex || !tex.isTexture || !tex.image) continue
          if (!(tex.image.width || tex.image.videoWidth)) continue   // 还没加载完的先跳过
          try { renderer.initTexture(tex); texCount++ } catch {}
        }
      }
    })
  } catch (e) { console.warn('[warmup] 贴图预热失败', e) }
  console.log('[warmup] 预编译 + 贴图预热完成，耗时 ' + Math.round(performance.now() - t0) + ' ms，预热贴图 ' + texCount + ' 张')
}

function finishLoadingIfReady() {
  if (!loadingState.hall || !loadingState.exhibits) return
  setLoadingProgress(100, '加载完成')
  // 本机记住的手动画质，等场景就位后再套用（场景没就位时切档会碰到还没初始化的相机）
  const applyPendingQualityChoice = () => {
    if (!pendingQualityChoice) return
    const q = pendingQualityChoice
    pendingQualityChoice = null
    setQualityLevel(QUALITY_CHOICE_LEVEL[q], '本机记住的画质选择：' + ({ high: '高', mid: '中', low: '低' }[q]))
  }
  // 让进度条走到 100% 再揭开，避免"差一点就跳走"的观感。
  // 揭开前先把着色器编译和贴图上传做掉（用户看不到），做完再揭开。
  const reveal = () => setTimeout(() => document.querySelector('#loading')?.classList.add('hidden'), 450)
  if (WARMUP_ENABLED) {
    const run = async () => {
      applyPendingQualityChoice()
      await applyProbeResult()   // 先测——按实测结果定初始档位
      await warmUpRenderer()     // 再按最终档位预编译着色器、预热贴图
    }
    Promise.race([
      run().catch(() => {}),
      new Promise((r) => setTimeout(r, WARMUP_MAX_MS + 2500)),
    ]).then(reveal, reveal)
  } else {
    applyPendingQualityChoice()
    reveal()
  }
  // 调试：地址里带 ?pf=41 时，加载完成后自动打开该展品弹窗（只用于核对弹窗布局，不影响正常访问）
  const pfAutoOpen = new URLSearchParams(location.search).get('pf')
  if (pfAutoOpen) setTimeout(() => pfDebugOpen(pfAutoOpen), 900)
  // 调试：?lb=1 自动打开 HUD 图集灯箱（用于核对"打开后被合成点击秒关"这个问题）
  if (new URLSearchParams(location.search).get('lb') === '1') setTimeout(() => { try { lbShowHud(0) } catch {} }, 1200)
}
// 加载画面只留进度条，文字先藏起来（出错时才显示）
document.querySelector('#loading-text')?.style.setProperty('display', 'none')
// 兜底：无论什么原因（某个模型一直不回调、网络卡住），最多 150 秒一定揭开遮罩，
// 绝不让人卡在加载页上。
setTimeout(() => {
  if (!loadingState.hall || !loadingState.exhibits) {
    console.warn('[loading] 等待超时，强制揭开遮罩 (hall=' + loadingState.hall + ' exhibits=' + loadingState.exhibits + ')')
    loadingState.hall = true
    loadingState.exhibits = true
    finishLoadingIfReady()
  }
}, 150000)

// 临时探针：?zones=1 打印各展区的"镜头-目标"距离；?fly=5 自动飞入 5 号展厅并记录距离变化
const ZONES_PROBE = new URLSearchParams(location.search).get('zones')
const FLY_PROBE = new URLSearchParams(location.search).get('fly')
if (ZONES_PROBE || FLY_PROBE) {
  setTimeout(() => {
    // 顺带确认屏幕参数确实落到材质上了（面板 → 材质 这条链路）
    try {
      console.log('[probe] 屏幕材质 ' + screenFxMaterials.length + ' 个：' + screenFxMaterials.map((m) =>
        'uDim=' + (m.uniforms.uDim ? m.uniforms.uDim.value.toFixed(2) : '?') +
        ' uSolid=' + (m.uniforms.uSolid ? m.uniforms.uSolid.value.toFixed(2) : '?')).join(' | '))
      console.log('[probe] 面板值 HUD=' + (fdScrI && fdScrI.value) + ' 主屏=' + (fdScrI2 && fdScrI2.value) +
        ' 实体感=' + (fdScrSol && fdScrSol.value))
    } catch (e) { console.log('[probe] 屏幕参数读取失败 ' + e.message) }
    for (const [z, info] of Object.entries(ZONES)) {
      const p = camPoints[info.camPos]
      const t = camPoints[info.camTarget]
      if (!p || !t) continue
      const d = p.distanceTo(t)
      const dir = new THREE.Vector3().subVectors(p, t)
      const polar = Math.acos(Math.max(-1, Math.min(1, dir.y / (dir.length() || 1))))
      console.log('[zones] 展区' + z + ' 镜头距离=' + d.toFixed(2) + 'm 极角=' + (polar * 180 / Math.PI).toFixed(1) +
        '° 镜头Y=' + p.y.toFixed(2) + ' 目标Y=' + t.y.toFixed(2) +
        ' 地面Y=' + (hallFloorY + infiniteGroundOffset + 1.5).toFixed(2))
    }
    if (FLY_PROBE) {
      console.log('[fly] 飞入展区 ' + FLY_PROBE)
      flyToZone(FLY_PROBE)
      let n = 0
      const timer = setInterval(() => {
        n++
        const d = camera.position.distanceTo(controls.target)
        console.log('[fly] t=' + (n * 0.7).toFixed(1) + 's 距离=' + d.toFixed(2) +
          ' 相机Y=' + camera.position.y.toFixed(2) + ' min=' + controls.minDistance + ' max=' + controls.maxDistance +
          (n >= 6 ? '  (结束)' : ''))
        if (n >= 6) clearInterval(timer)
      }, 700)
    }
  }, 10000)
}
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/')
loader.setDRACOLoader(dracoLoader)
loader.setMeshoptDecoder(MeshoptDecoder)

let modelRoot = null
const hitboxes = [] // { name, id, mesh, center }
const screens = [] // 视频屏幕 mesh
const zoneMarkers = [] // 入口界面可点击的分区平面
let camPoints = {} // CamPos1 / CamPos11 / CamTarget1 / CamTarget11 等

loader.load(
  // 手机/平板也用同一套客厅模型：原来手机端用的是 hall-mobile.glb
  // （46 MB / 3031 网格 / 767 万面），比电脑版（1.65 MB / 60 网格 / 4.8 万面）
  // 重一百多倍，是手机端卡和慢的主因。
  withVersion('models/hall.glb'),
  (gltf) => {
    modelRoot = gltf.scene
    scene.add(modelRoot)
    glowBoostMats.length = 0 // 换模型后重新登记发光件材质（灯带/灯圈/发光球都是每轮新建的）
    modelRoot.traverse((o) => {
      const n = o.name || ''
      if (o.isMesh && /^Hitbox_Product_/i.test(n)) {
        const id = n.match(/^Hitbox_Product_(\d+)/i)?.[1] || ''
        hitboxes.push({ name: n, id, mesh: o })
      } else if (o.isMesh && /^Screen_Product_/i.test(n)) {
        screens.push(o)
      } else if (o.isMesh && (/^water$/i.test(n) || /M_Water/i.test((o.material && o.material.name) || ''))) {
        applyWaterToMesh(o)
      } else if (o.isMesh && /M_Terrain/i.test((o.material && o.material.name) || '')) {
        registerTerrainMaterial(o.material)
      }
    })
    hitboxes.forEach((h) => {
      h.mesh.visible = false // 隐藏但不影响 Raycaster 点击
      const box = new THREE.Box3().setFromObject(h.mesh)
      h.center = box.getCenter(new THREE.Vector3())
      box.getSize(h.size = new THREE.Vector3())
      h.radius = Math.max(h.size.x, h.size.y, h.size.z)
    })
    setupCameras()
    if (PRODUCT_SPOTLIGHTS_ENABLED) setupProductLights()
    if (ZONE_FILL_LIGHTS_ENABLED) setupZoneLights()
    setupScreens()
    updateMapZoneTitles()
    setupZoneClickTargets()
    setupHoloEffects()
    applyVisualEffects()
    ensureCloudDome()
    console.log(`[sharp] 各向异性过滤已应用到 ${boostTextureSharpness(modelRoot)} 张贴图 (max ${MAX_ANISO}x)`)
    // 模型材质/灯光已就绪：再次应用已保存的调试参数
    // 本机有存档且版本相同 → 用本机的；没存档或存档陈旧 → 回到代码默认状态
    {
      const savedParams = readDebugParams()
      if (!savedParams || !savedParams.__v || savedParams.__v < DEBUG_VERSION) {
        if (USE_DEFAULT_PROFILE) {
          applyDefaultProfile(savedParams ? '本机存档升级到 v' + DEBUG_VERSION : '首次打开')
        } else {
          // 清掉旧存档 + 灯光覆盖记录，直接用代码里的默认参数与内置灯光（回到"干净"状态）
          try { localStorage.removeItem(DEBUG_KEY); localStorage.removeItem(LIGHT_KEY) } catch {}
          storedLightData = []
          console.log('[debug] 已回到代码默认参数（清掉旧存档与灯光覆盖）')
        }
      } else {
        applyDebugParams(savedParams)
      }
    }
    // 内置灯光注册到灯光编辑器（含已保存的覆盖值）
    registerBuiltinLights()
    exposeDebugHandles()
    // 展厅本体就绪，但展品还没加载：先别揭开遮罩，等展品也全部就位
    // （否则会看到一个一个蹦出来的过程）
    loadingState.hall = true
    finishLoadingIfReady()
    // 左下角显示当前加载的展厅模型规模：换过模型后用它确认浏览器有没有吃到新文件
    try {
      let meshes = 0
      let tris = 0
      modelRoot.traverse((o) => {
        if (!o.isMesh || !o.geometry) return
        meshes++
        const g = o.geometry
        if (g.index) tris += g.index.count / 3
        else if (g.attributes.position) tris += g.attributes.position.count / 3
      })
      const el = document.querySelector('#fps-model')
      if (el) el.textContent = '  · 展厅 ' + meshes + ' 网格 / ' + Math.round(tris / 10000) + ' 万面'
      const pdInfo = document.querySelector('#pd-hall-info')
      if (pdInfo) pdInfo.textContent = '展厅模型：' + meshes + ' 网格 / ' + Math.round(tris / 10000) + ' 万面（' + MODEL_VERSION + '）'
      console.log('[hall] 展厅模型 ' + meshes + ' 网格 / ' + Math.round(tris) + ' 三角面（版本 ' + MODEL_VERSION + '）')
    } catch (e) { /* 忽略 */ }
    // 展品按坐标摆进 hall（配置：public/data/hall-exhibits.json）
    applyHallMaterialBright()   // 按面板设置压暗展厅本体材质
    // 给"自发光的件"打泛光标记：只有它们参与泛光，白墙和展品不会发光
    try {
      const lit = tagBloomObjects()
      console.log('[bloom] 参与泛光的自发光件 ' + lit + ' 个')
    } catch (e) { console.warn('[bloom] 标记失败', e) }
    // 热区如果被改动过（例如在 Blender 里挪了位置），展品跟着一起挪
    try {
      const moved = syncExhibitsToHitboxes()
      if (moved) console.log('[hall-exhibits] ' + moved + ' 个展品按新热区位置校正')
    } catch (e) { console.warn('[hall-exhibits] 热区校正失败', e) }
    loadHallExhibits()
    // 纯 HDRI 打光对比：?lights=off 关掉全部灯光，?lights=min 只关点光/聚光
    {
      const hidden = applyLightMode()
      if (hidden) {
        console.log('[lights] 模式 ' + LIGHTS_MODE + '：已隐藏 ' + hidden + ' 个光源')
        setTimeout(() => applyLightMode(), 5000)   // 兜底：晚创建的光也一起关
      }
    }
  },
  (e) => {
    if (e.total) {
      // 展厅模型占前 40%，展品占后 60%
      const p = Math.round((e.loaded / e.total) * 40)
      setLoadingProgress(p, '正在加载展厅模型')
    }
  },
  (err) => {
    const t = document.querySelector('#loading-text')
    if (t) {
      t.textContent = '加载失败：' + (err && err.message ? err.message : err)
      t.style.display = 'block' // 只在出错时把文字露出来
    }
    setLoadingProgress(100)
  }
)

function findObject(name) {
  return modelRoot ? modelRoot.getObjectByName(name) : null
}

// ---------- 相机与飞行 ----------
function setupCameras() {
  // 读取所有相机标记：入口 CamPos1/CamTarget1 + 各展区 CamPos11..18/CamTarget11..18
  for (const n of ['CamPos1', 'CamTarget1']) {
    const o = findObject(n)
    if (o) camPoints[n] = o.getWorldPosition(new THREE.Vector3())
  }
  for (let z = 1; z <= 8; z++) {
    const pn = 'CamPos1' + z
    const tn = 'CamTarget1' + z
    const po = findObject(pn)
    const to = findObject(tn)
    if (po) camPoints[pn] = po.getWorldPosition(new THREE.Vector3())
    if (to) camPoints[tn] = to.getWorldPosition(new THREE.Vector3())
    if (po && to) {
      ZONES[z] = { name: z + '号展区', camPos: pn, camTarget: tn }
    }
  }
  // 初始镜头 = 导航界面起始位置
  if (camPoints.CamPos1 && camPoints.CamTarget1) {
    camera.position.copy(camPoints.CamPos1)
    controls.target.copy(camPoints.CamTarget1)
    camera.lookAt(controls.target)
  }
}

// 每个展区中心上方加一盏均匀白色填充光，解决展区过暗
function setupZoneLights() {
  // 先看灯光模式：?lights=off 时所有灯都不建，?lights=min 时只跳过点光/聚光
  // 不需要展区灯就别建（省一点 CPU/显存；真正可见性统一由 applyLightMode 收口）
  const zoneKeep = lightModeKeepGroups(LIGHTS_MODE)
  if (LIGHTS_MODE === 'off' || (zoneKeep && !zoneKeep.includes('zones'))) return
  // 各展区差异化亮度：让不同区有明有暗（效果图的分区层次感）
  const zoneIntensity = {
    1: 30, 2: 46, 3: 34, 4: 22,
    5: 26, 6: 38, 7: 30, 8: 20
  }
  for (let z = 1; z <= 8; z++) {
    const t = camPoints['CamTarget1' + z]
    if (!t) continue
    const light = new THREE.PointLight(0xa8ccff, zoneIntensity[z] || 28, 60, 2)
    light.userData.base = zoneIntensity[z] || 28
    light.userData.baseColor = 0xa8ccff
    zoneLights.push(light)
    if (z === 3) {
      // 3号展区：灯放到展区中心并抬高，覆盖更均匀
      const p = camPoints['CamPos1' + z]
      light.position.set((t.x + p.x) / 2, t.y + 18, (t.z + p.z) / 2)
    } else {
      light.position.set(t.x, t.y + 8, t.z)
    }
    scene.add(light)
  }
}

// 入口界面：每个展区放置一个隐形可点击平面，点分区直接飞入
function setupZoneClickTargets() {
  for (let z = 1; z <= 8; z++) {
    const info = ZONES[z]
    if (!info || !camPoints[info.camTarget]) continue
    const t = camPoints[info.camTarget]
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 16),
      new THREE.MeshBasicMaterial({ color: 0x00aaff })
    )
    plane.rotation.x = -Math.PI / 2
    plane.position.set(t.x, t.y, t.z)
    plane.visible = false
    plane.userData.zone = z
    scene.add(plane)
    zoneMarkers.push(plane)
  }
}

function animateCameraTo(pos, target, duration = 2.2, onComplete = null) {
  controls.enabled = false
  // 同一时刻只允许一个镜头动画：否则两个补间会同时改相机位置互相打架
  // （例如推近动画还没跑完就关闭弹窗，还原会被推近覆盖掉）
  if (typeof gsap.killTweensOf === 'function') {
    gsap.killTweensOf(camera.position)
    gsap.killTweensOf(controls.target)
  }
  gsap.to(camera.position, {
    x: pos.x, y: pos.y, z: pos.z,
    duration,
    ease: 'power2.inOut'
  })
  gsap.to(controls.target, {
    x: target.x, y: target.y, z: target.z,
    duration,
    ease: 'power2.inOut',
    onUpdate: () => {
      camera.lookAt(controls.target)
    },
    onComplete: () => {
      controls.enabled = true
      if (onComplete) onComplete()
    }
  })
}

// ---------- 平面图热区（8 分区，相机名由模型动态填充） ----------
const ZONES = {}
const titleSprites = []
const hud1Meshes = []
const hud2Meshes = []
const hud3Meshes = []
const hud4Meshes = []
const hud5Meshes = []
const hud6Meshes = []
const TITLE_EN = {
  '一体机展厅': 'Integrated Units',
  '企业介绍': 'Company Introduction',
  '企业视频介绍': 'Company Video',
  '其他末端产品及水力模块展厅': 'Terminal Products & Hydraulic Module',
  '商用空气能产品展厅': 'Commercial Air-source Products',
  '末端产品（水系统）展厅': 'Air-side',
  '离心机及其他主机展厅': 'Centrifugal & Main Units',
  '螺杆机展厅': 'Screw Units'
}

// 展区标题（3D 悬浮标题按当前语言只显示一种语言）
const ZONE_TITLE_ZH = {
  1: '离心机展厅',
  2: '一体机展厅',
  3: '直膨机及水力模块展厅',
  4: '末端产品展厅',
  5: '厂容厂貌',
  6: '企业介绍',
  7: '涡旋机展厅',
  8: '螺杆机展厅'
}
const ZONE_TITLE_EN = {
  1: 'Centrifugal Units',
  2: 'Integrated Units',
  3: 'DX Units & Hydraulic Modules',
  4: 'Air-side',
  5: 'Plant Tour',
  6: 'Company Introduction',
  7: 'Scroll Units',
  8: 'Screw Units'
}

function updateMapZoneTitles() {
  document.querySelectorAll('.map-body polygon[data-zone]').forEach((poly) => {
    const z = Number(poly.dataset.zone)
    poly.setAttribute('title', '前往' + (ZONES[z] ? ZONES[z].name : z + '号展区'))
  })
}
document.querySelectorAll('.map-body polygon[data-zone]').forEach((poly) => {
  poly.addEventListener('click', () => flyToZone(poly.dataset.zone))
})

function flyToZone(zoneId) {
  const z = ZONES[zoneId]
  if (!z) return
  if (!z.camPos || !camPoints[z.camPos] || !camPoints[z.camTarget]) {
    showToast(z.name + ' 镜头尚未配置')
    return
  }
  if (isFlying) return
  showToast('正在前往' + z.name)
  isFlying = true
  animateCameraTo(camPoints[z.camPos], camPoints[z.camTarget], 2.2, () => {
    currentZone = zoneId
    isFlying = false
    updateNavUI()
    updateTitleHighlights(zoneId)
  })
}

function flyToEntrance() {
  if (isFlying) return
  if (!camPoints.CamPos1 || !camPoints.CamTarget1) return
  isFlying = true
  animateCameraTo(camPoints.CamPos1, camPoints.CamTarget1, 2.0, () => {
    currentZone = null
    isFlying = false
    updateNavUI()
    updateTitleHighlights(null)
  })
}

function updateNavUI() {
  document.querySelector('#btn-home-float').classList.toggle('show', currentZone !== null)
  // 平面图「落章」：当前展区以警示琥珀描边标出（见 option2/DESIGN.md）
  document.querySelectorAll('.map-body polygon[data-zone]').forEach((poly) => {
    poly.toggleAttribute('data-current', String(poly.dataset.zone) === String(currentZone))
  })
  document.querySelectorAll('.map-body [data-zone-name]').forEach((txt) => {
    txt.toggleAttribute('data-current', Number(txt.dataset.zoneName) === Number(currentZone))
  })
}

document.querySelector('#btn-home').addEventListener('click', flyToEntrance)
document.querySelector('#btn-home-float').addEventListener('click', flyToEntrance)

// ---------- 光照隔离：按 Light_Product_xx 放置聚光灯 ----------
function setupProductLights() {
  if (!modelRoot) return
  modelRoot.traverse((o) => {
    // three.js 读 glTF 时会把名字里的空格换成下划线、去掉点，
    // 所以像 "  Floor_Side" 会变成 "__Floor_Side"，这里先还原再匹配规则
    const n = (o.name || '').replace(/^[_ .]+/, '')
    if (!/^Light_Product_(\d+)/i.test(n)) return
    const id = n.match(/^Light_Product_(\d+)/i)?.[1] || ''
    const pos = o.getWorldPosition(new THREE.Vector3())
    const hit = hitboxes.find((h) => h.id === id)
    const target = new THREE.Vector3()
    if (hit) {
      target.copy(hit.center)
    } else {
      // 无对应 Hitbox 时朝空物体下方照射
      target.copy(pos).y -= 1.5
    }
    const light = new THREE.SpotLight(0xe8f4ff, 42, 25, Math.PI / 6, 0.5, 2)
    light.userData.base = 42
    light.userData.baseColor = 0xe8f4ff
    productSpots.push(light)
    light.position.copy(pos)
    // 聚光灯只照"就近"的展品：与对应 Hitbox 距离近才瞄准它，否则照向自己正下方
    if (hit && hit.center.distanceTo(pos) < 6) {
      light.target.position.copy(hit.center)
    } else {
      light.target.position.copy(pos).y -= 1.5
    }
    light.castShadow = false // 聚光灯投影暂取消
    scene.add(light)
    scene.add(light.target)
  })
}

// ---------- 全息特效：展台全息屏 + 中央星星球 ----------
let starGlobeGroup = null
let starGlobeOuter = null
let starGlobeInner = null
let starGlobeBaseY = 0
let starLogoHolder = null
let starLogo = null
let starLogoHalo = null
let starLogoBaseY = 0
const starGlobeWorld = new THREE.Vector3()
const hallCenter = new THREE.Vector3()
let hallFloorY = 0
let fxRingFloorY = 0
let fxRingCount = 0

function setupHoloEffects() {
  if (!modelRoot) return
  const bbox = new THREE.Box3().setFromObject(modelRoot)
  bbox.getCenter(hallCenter)
  hallFloorY = bbox.min.y

  // 星星球：读取模型中的 StarGlobe 空物体位置
  const globeRadius = 3.2
  const globeMarker = findObject('StarGlobe')
  const globeCenter = globeMarker
    ? globeMarker.getWorldPosition(new THREE.Vector3())
    : new THREE.Vector3(hallCenter.x, bbox.min.y + 3.6, hallCenter.z)
  starGlobeBaseY = globeCenter.y + 2 // 抬高 2 米
  starGlobeWorld.set(globeCenter.x, globeCenter.y + 2, globeCenter.z)
  starGlobeGroup = new THREE.Group()
  starGlobeGroup.position.copy(globeCenter)

  starGlobeOuter = makeStarShell(1600, globeRadius, 0x88ffff, 0.16)
  starGlobeInner = makeStarShell(700, globeRadius * 0.72, 0x00e5ff, 0.10)
  registerGlowMat(starGlobeOuter.material)
  registerGlowMat(starGlobeInner.material)
  starGlobeGroup.add(starGlobeOuter)
  starGlobeGroup.add(starGlobeInner)

  // 线框球壳：定义星星球轮廓
  const wire = new THREE.Mesh(
    new THREE.IcosahedronGeometry(globeRadius, 2),
    registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00e5ff, wireframe: true, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false }))
  )
  starGlobeGroup.add(wire)

  // 微弱发光球壳
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(globeRadius * 0.98, 32, 24),
    registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide }))
  )
  starGlobeGroup.add(glow)

  // 底部全息光环
  const baseRing = new THREE.Mesh(
    new THREE.TorusGeometry(globeRadius, 0.02, 12, 96),
    registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00e5ff }))
  )
  baseRing.rotation.x = Math.PI / 2
  baseRing.position.y = -5.6
  starGlobeGroup.add(baseRing)

  const midRing = new THREE.Mesh(
    new THREE.TorusGeometry(globeRadius + 0.2, 0.012, 12, 96),
    registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.5 }))
  )
  midRing.rotation.x = Math.PI / 2
  midRing.position.y = -5.45
  starGlobeGroup.add(midRing)

  scene.add(starGlobeGroup)
  // 中心球入口：不可见的点击球，点击打开智能客服
  supportEntry = new THREE.Mesh(
    new THREE.SphereGeometry(globeRadius + 1.0, 20, 16),
    new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0, depthWrite: false })
  )
  supportEntry.castShadow = false
  supportEntry.receiveShadow = false
  supportEntry.visible = SUPPORT_ENABLED
  starGlobeGroup.add(supportEntry)

  // 中心星星球蓝色主光（参考图：中心发光体自带光源氛围）
  const globeLight = new THREE.PointLight(0x66c8ff, 50, 25, 2)
  globeLightRef = globeLight
  globeLight.position.set(globeCenter.x, globeCenter.y + 2, globeCenter.z)
  scene.add(globeLight)

  // 中心光柱：从光球正上方斜打下来，形成中央亮、四周暗的电影感
  envSpot = new THREE.SpotLight(0xdff2ff, 220, 70, Math.PI / 7, 0.55, 1.4)
  const centerSpot = envSpot
  centerSpot.position.set(globeCenter.x, globeCenter.y + 16, globeCenter.z)
  const centerTarget = new THREE.Object3D()
  centerTarget.position.set(globeCenter.x, hallFloorY + 0.5, globeCenter.z)
  scene.add(centerTarget)
  centerSpot.target = centerTarget
  scene.add(centerSpot)

  // 外围极暗的蓝色轮廓补光
  const rimDefs = [
    [globeCenter.x + 34, 6, globeCenter.z - 18, 0x1f5fff, 22],
    [globeCenter.x - 34, 6, globeCenter.z - 18, 0x0f8fff, 30],
    [globeCenter.x, 5, globeCenter.z + 30, 0x4a6fff, 18]
  ]
  for (const [rx, ry, rz, color, inten] of rimDefs) {
    const rim = new THREE.PointLight(color, inten, 55, 2)
    rim.userData.base = inten
    envRims.push(rim)
    rim.position.set(rx, ry, rz)
    scene.add(rim)
  }
  // 大范围低强度蓝光：把整个场景的暗部微微提亮（不压掉层次）
  envBig = new THREE.PointLight(0x2f6fff, 42, 220, 2)
  const bigBlue = envBig
  bigBlue.position.set(globeCenter.x, globeCenter.y + 26, globeCenter.z - 8)
  scene.add(bigBlue)

  // 中央星星球正上方的发光企业 Logo
  // Logo 中心高度：球心上方 6m
  starLogoBaseY = globeCenter.y + 6
  starLogoHolder = new THREE.Group()
  starLogoHolder.position.set(globeCenter.x, starLogoBaseY, globeCenter.z)
  scene.add(starLogoHolder)

  // 企业 Logo：平面始终面向相机，用着色器做周期性扫光
  const sweepUniforms = {
    uMap: { value: null },
    uTime: { value: 0 }
  }
  const sweepVertex = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `
  const sweepFragment = `
    uniform sampler2D uMap;
    uniform float uTime;
    varying vec2 vUv;
    void main() {
      vec4 t = texture2D(uMap, vUv);
      if (t.a < 0.02) discard;
      float pos = fract(uTime / 4.5) * 1.35 - 0.15; // 每 4.5s 从左到右扫一次
      float band = 1.0 - smoothstep(0.015, 0.2, abs(vUv.x - pos));
      vec3 base = t.rgb * (0.92 + 0.18 * band);
      vec3 scan = vec3(0.75, 0.96, 1.0) * band * 0.85;
      vec3 col = base + scan;
      gl_FragColor = vec4(col, t.a);
    }
  `
  new THREE.TextureLoader().load('images/logo.png', (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 4
    sweepUniforms.uMap.value = tex
    const aspect = (tex.image.width || 1558) / (tex.image.height || 497)
    // Logo 尺寸（横竖等比）：原始宽 5.2 米，这里放大一倍 → 10.4 米
    const LOGO_W = 10.4
    const LOGO_H = LOGO_W / aspect
    starLogo = new THREE.Mesh(
      new THREE.PlaneGeometry(LOGO_W, LOGO_H),
      new THREE.ShaderMaterial({
        uniforms: sweepUniforms,
        vertexShader: sweepVertex,
        fragmentShader: sweepFragment,
        transparent: true,
        depthWrite: false
      })
    )
    starLogo.frustumCulled = false
    // 再上移半个身位（= Logo 自身高度的一半）
    starLogo.position.y = LOGO_H / 2
    starLogoHolder.add(starLogo)
    // 调试：Logo 纹理就绪后可从外部调整
    if (!window.__opt2) window.__opt2 = {}
    window.__opt2.starLogoMesh = starLogo
  })

  // 调试信息：方便定位星星球
  window.__opt2 = Object.assign(window.__opt2 || {}, {
    hallCenter: hallCenter.toArray(),
    starGlobePos: globeCenter.toArray(),
    hitboxes: hitboxes.length
  })
  console.log('[option2] star globe at', globeCenter.toArray(), 'hitboxes', hitboxes.length)
}

function makeStarShell(count, radius, color, size) {
  const positions = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    // 均匀分布在球壳上（高斯法向）
    let x = 0, y = 0, z = 0
    do {
      x = Math.random() * 2 - 1
      y = Math.random() * 2 - 1
      z = Math.random() * 2 - 1
    } while (x * x + y * y + z * z > 1 || x * x + y * y + z * z < 0.02)
    const len = Math.sqrt(x * x + y * y + z * z)
    const r = radius * (0.92 + Math.random() * 0.16)
    positions[i * 3] = (x / len) * r
    positions[i * 3 + 1] = (y / len) * r
    positions[i * 3 + 2] = (z / len) * r
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const mat = new THREE.PointsMaterial({
    color,
    size,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true
  })
  const points = new THREE.Points(geo, mat)
  points.userData.axis = new THREE.Vector3(Math.random() - 0.5, 1, Math.random() - 0.5).normalize()
  points.userData.speed = 0.1 + Math.random() * 0.12
  return points
}

// ---------- 视频投射：先占位，video 路径就绪后自动切换 ----------
function makeVideoPlaceholderTexture() {
  const c = document.createElement('canvas')
  c.width = 1280
  c.height = 720
  const ctx = c.getContext('2d')
  ctx.fillStyle = '#06121f'
  ctx.fillRect(0, 0, 1280, 720)
  ctx.strokeStyle = 'rgba(55, 213, 255, 0.18)'
  ctx.lineWidth = 1
  for (let x = 0; x < 1280; x += 64) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 720); ctx.stroke()
  }
  for (let y = 0; y < 720; y += 64) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(1280, y); ctx.stroke()
  }
  ctx.fillStyle = '#3d9cff'
  ctx.font = 'bold 52px Inter, "Microsoft YaHei", sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('VIDEO · 待接入', 640, 360)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function setupScreens() {
  const placeholder = makeVideoPlaceholderTexture()
  screens.forEach((mesh) => {
    mesh.material = new THREE.MeshBasicMaterial({ map: placeholder, toneMapped: false, side: THREE.DoubleSide })
  })
}

// video 路径配置后调用：applyProductVideo(screenMesh, videoUrl)
const videoElements = []
const videoButtons = [] // 视频屏幕上的播放/暂停、重播按钮 Sprite
const videoScreenCache = {} // 按网格名复用同一视频/纹理（避免同屏重复网格各播一份视频）

// 生成视频控制按钮纹理（canvas 图标）
function makeVideoButtonTexture(type) {
  const s = 256
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  // 圆形底
  ctx.beginPath()
  ctx.arc(s / 2, s / 2, 104, 0, Math.PI * 2)
  ctx.lineWidth = 10
  ctx.strokeStyle = '#3d9cff'
  ctx.stroke()
  ctx.fillStyle = '#dff6ff'
  if (type === 'replay') {
    // 重播：圆形箭头 + 三角
    ctx.lineWidth = 14
    ctx.strokeStyle = '#dff6ff'
    ctx.beginPath()
    ctx.arc(s / 2, s / 2, 52, -0.4, Math.PI * 1.5, false)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(s / 2 + 6, s / 2 - 66)
    ctx.lineTo(s / 2 + 6, s / 2 - 26)
    ctx.lineTo(s / 2 + 46, s / 2 - 46)
    ctx.closePath()
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(s / 2 + 24, s / 2 + 6)
    ctx.lineTo(s / 2 + 60, s / 2 + 30)
    ctx.lineTo(s / 2 + 24, s / 2 + 56)
    ctx.closePath()
    ctx.fill()
  } else if (type === 'pause') {
    // 暂停：两条竖线
    ctx.fillRect(s / 2 - 34, s / 2 - 52, 22, 104)
    ctx.fillRect(s / 2 + 12, s / 2 - 52, 22, 104)
  } else {
    // 播放：三角
    ctx.beginPath()
    ctx.moveTo(s / 2 - 18, s / 2 - 52)
    ctx.lineTo(s / 2 - 18, s / 2 + 52)
    ctx.lineTo(s / 2 + 36, s / 2)
    ctx.closePath()
    ctx.fill()
  }
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// 给视频屏幕加控制按钮（播放/暂停 + 重播），并让屏幕后绘制以遮挡置顶标题
function addVideoControls(mesh, video) {
  mesh.userData.video = video
  const box = new THREE.Box3().setFromObject(mesh)
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const normal = new THREE.Vector3()
  // 估算屏幕朝向：取包围盒最小厚度轴为法线方向
  if (size.x <= size.y && size.x <= size.z) normal.set(1, 0, 0)
  else if (size.y <= size.x && size.y <= size.z) normal.set(0, 1, 0)
  else normal.set(0, 0, 1)
  // 面向相机方向的屏幕外侧（相机在法线同侧则用正法线，否则取反）
  const toCam = camera.position.clone().sub(center)
  if (toCam.dot(normal) < 0) normal.multiplyScalar(-1)
  // 按钮沿世界 X 轴水平排列
  const horizontal = new THREE.Vector3(1, 0, 0)
  // 屏幕下缘：中心沿法线平移到屏幕面，再往下移屏幕高度一半 + 间距
  const bottomY = center.y - (Math.abs(size.y) / 2) - 0.7 + 1.5 - 0.5 - 0.2
  const playBtn = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeVideoButtonTexture('play'), transparent: true, depthTest: true }))
  const replayBtn = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeVideoButtonTexture('replay'), transparent: true, depthTest: true }))
  playBtn.scale.set(0.22, 0.22, 1)
  replayBtn.scale.set(0.22, 0.22, 1)
  const offset = normal.clone().multiplyScalar(0.5)
  playBtn.position.copy(center).add(offset)
  playBtn.position.y = bottomY
  playBtn.position.add(horizontal.clone().multiplyScalar(-0.3))
  replayBtn.position.copy(center).add(offset)
  replayBtn.position.y = bottomY
  replayBtn.position.add(horizontal.clone().multiplyScalar(0.3))
  playBtn.userData.video = video
  playBtn.userData.btnType = 'toggle'
  playBtn.userData.texPlay = makeVideoButtonTexture('play')
  playBtn.userData.texPause = makeVideoButtonTexture('pause')
  replayBtn.userData.video = video
  replayBtn.userData.btnType = 'replay'
  replayBtn.userData.toggleBtn = playBtn
  // 视频默认不自动播放（点击后才播放），初始图标显示播放键
  playBtn.material.map = playBtn.userData.texPlay
  playBtn.material.needsUpdate = true
  scene.add(playBtn)
  scene.add(replayBtn)
  videoButtons.push(playBtn, replayBtn)
}

// 屏幕网格若无 UV，按包围盒自动生成平面 UV（世界地图/HUD/视频都能正确贴图）
function ensureScreenUV(mesh) {
  const geo = mesh.geometry
  if (geo.attributes.uv) return
  const pos = geo.attributes.position
  const box = new THREE.Box3().setFromBufferAttribute(pos)
  const size = box.getSize(new THREE.Vector3())
  // 根据最小厚度轴判断屏幕所在平面，保证纹理方向正确（u=宽，v=高）
  let uKey = 'x'
  let vKey = 'y'
  if (size.y <= size.x && size.y <= size.z) {
    uKey = 'x'; vKey = 'z' // 平面在 XZ（竖墙屏）
  } else if (size.z <= size.x && size.z <= size.y) {
    uKey = 'x'; vKey = 'y' // 平面在 XY（默认平面）
  } else {
    uKey = 'y'; vKey = 'z' // 平面在 YZ
  }
  const arr = new Float32Array(pos.count * 2)
  const getAxis = (axis, i) => (axis === 'x' ? pos.getX(i) : axis === 'y' ? pos.getY(i) : pos.getZ(i))
  for (let i = 0; i < pos.count; i++) {
    arr[i * 2] = (getAxis(uKey, i) - box.min[uKey]) / (size[uKey] || 1)
    arr[i * 2 + 1] = (getAxis(vKey, i) - box.min[vKey]) / (size[vKey] || 1)
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(arr, 2))
}

function applyProductVideo(mesh, url) {
  // 同名网格（如屏幕双面/重复导出）复用同一视频材质，只创建一组按钮
  const cacheKey = mesh.name
  if (videoScreenCache[cacheKey]) {
    mesh.material = videoScreenCache[cacheKey]
    mesh.renderOrder = 100
    return
  }
  const video = document.createElement('video')
  video.src = url
  video.loop = true
  video.muted = true
  video.playsInline = true
  video.setAttribute('playsinline', '')
  video.setAttribute('webkit-playsinline', '')
  video.crossOrigin = 'anonymous'
  ensureScreenUV(mesh)
  const tex = new THREE.VideoTexture(video)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.repeat.x = -1 // 视频镜像，投到正面
  tex.offset.x = 1
  // transparent:true 让屏幕进入透明渲染组，配合 renderOrder 后绘制，可覆盖 depthTest:false 的置顶标题
  const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, side: THREE.DoubleSide, transparent: true, opacity: 1 })
  mat.renderOrder = 100 // 后绘制，遮挡置顶标题（标题 depthTest:false 会穿透屏幕）
  mesh.material = mat
  mesh.renderOrder = 100
  videoScreenCache[cacheKey] = mat
  videoElements.push(video)
  addVideoControls(mesh, video)
  // 不自动播放：预加载以便点击后快速开始，并尝试显示首帧
  video.preload = 'auto'
  video.load()
  video.addEventListener('loadedmetadata', () => {
    try { video.currentTime = 0.001 } catch {}
  })
}

// ---------- 5 号展区「厂容厂貌」图片大屏 ----------
const plantScreens = []
let plantImageUrls = []
let plantTextures = []
let plantIdx = 0
let plantTimer = null
// 厂区图片清单接口不能沿用旧 CDN 缓存：2026-10-20 图片从 jpg 换成 webp 后，
// 旧缓存里的 jpg 地址会让 .net 从第 9 张开始 404。换清单版本即可立即绕过旧缓存。
const PLANT_IMAGES_API = '/api/plant-images?v=20261022a'

function registerPlantScreen(mesh) {
  ensureScreenUV(mesh)
  if (mesh.userData.plantMat) return
  const mat = new THREE.MeshBasicMaterial({ color: 0x0a1020, side: THREE.DoubleSide })
  mesh.material = mat
  mesh.userData.plantMat = mat
  mesh.renderOrder = 100
  plantScreens.push(mesh)
}

function applyPlantTexture() {
  if (!plantScreens.length || !plantTextures.length) return
  const tex = plantTextures[plantIdx % plantTextures.length]
  for (const s of plantScreens) {
    s.userData.plantMat.map = tex
    s.userData.plantMat.color.setHex(0xffffff) // 底色改白，避免贴图被深色底乘暗
    s.userData.plantMat.needsUpdate = true
  }
}

function startPlantSlideshow() {
  if (plantTimer || plantTextures.length < 2) return
  plantTimer = setInterval(() => {
    plantIdx = (plantIdx + 1) % plantTextures.length
    applyPlantTexture()
  }, 6000)
}

async function loadPlantImages() {
  try {
    const res = await fetch(PLANT_IMAGES_API, { cache: 'no-cache' })
    const list = await res.json()
    if (!Array.isArray(list) || !list.length) return
    plantImageUrls = list
    // 大屏只预载前 8 张并降采样到 1024px：原图很多是 10–30MB，全部解码会把内存占满。
    // 1024 而不是 2048：厂容厂貌是远处看的照片墙，8 张 2048 要占 171 MB 显存，
    // 降到 1024 只占约 43 MB —— 对 1–2 GB 显存的弱卡（GT730 这类）是生死线。
    const loaded = await Promise.all(list.slice(0, 8).map((url) => makeScreenTexture(url, 1024)))
    plantTextures = loaded.filter(Boolean)
    plantIdx = 0
    applyPlantTexture()
    startPlantSlideshow()
    console.log('[plant] screen textures:', plantTextures.length, '/ total', list.length)
  } catch {}
}

function makeScreenTexture(url, maxSize = 2048) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const scale = Math.min(1, maxSize / Math.max(img.width || 1, img.height || 1))
        const w = Math.max(1, Math.round((img.width || 1) * scale))
        const h = Math.max(1, Math.round((img.height || 1) * scale))
        const c = document.createElement('canvas')
        c.width = w
        c.height = h
        c.getContext('2d').drawImage(img, 0, 0, w, h)
        const tex = new THREE.CanvasTexture(c)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.wrapS = THREE.RepeatWrapping
        tex.repeat.x = -1 // 镜像到正面
        tex.offset.x = 1
        resolve(tex)
      } catch {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = url
  })
}

function openPlantGallery() {
  if (!plantImageUrls.length) {
    showToast(getLang() === 'en' ? 'Plant photos not available yet' : '厂容厂貌图片待补充')
    return
  }
  lbShowGallery(plantImageUrls, 0)
}

// ---------- 视觉元素处理（按网格名自动应用参考图风格） ----------
let screenFxMaterials = []
let glassFloorHoloMat = null
let matPedestal = null
let matGrayFloor = null
let matLeatherFloor = null
const debugFloorMats = { corner: [], light: [], dark: [], side: [], darkFloor: [] }
const zoneLights = []
const productSpots = []
let globeLightRef = null
const lightTrailMats = []
let trailSwapTimer = 2 + Math.random() * 3
let lastAnimT = 0
const PRODUCT_SPOTLIGHTS_ENABLED = true // 展品聚光灯（参考图风格：窄角冷光）
const ZONE_FILL_LIGHTS_ENABLED = true // 展区填充点光（暗档，用于提亮地面）
const RING_HALO_ENABLED = true // 光环粒子系统
const CLICK_OCCLUSION_ENABLED = false // 点击遮挡检测，暂关闭（排查性能）
const VIDEO_ENABLED = true // 视频加载/播放

function makeMainScreenTexture() {
  const w = 2048
  const h = 1024
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace

  let holoMapLayer = null
  const draw = () => {
    // 背景：深蓝渐变（半透明，黑色区域可透出场景）
    const g = ctx.createLinearGradient(0, 0, 0, h)
    g.addColorStop(0, 'rgba(4,22,38,0.4)')
    g.addColorStop(1, 'rgba(2,10,20,0.32)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, w, h)
    // 经纬网格
    ctx.strokeStyle = 'rgba(55,213,255,0.14)'
    ctx.lineWidth = 1
    for (let i = 0; i <= 24; i++) {
      const x = (i / 24) * w
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke()
    }
    for (let j = 0; j <= 12; j++) {
      const y = (j / 12) * h
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke()
    }
    // 全息地图层（黑色→透明，白色→全息蓝）
    if (holoMapLayer) {
      ctx.drawImage(holoMapLayer, 0, 0, w, h)
    }
    // 顶部压暗，保证标题可读
    const tg = ctx.createLinearGradient(0, 0, 0, 150)
    tg.addColorStop(0, 'rgba(2,8,16,0.85)')
    tg.addColorStop(1, 'rgba(2,8,16,0)')
    ctx.fillStyle = tg
    ctx.fillRect(0, 0, w, 150)
    // 标题
    ctx.textAlign = 'center'
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 46px Inter, sans-serif'
    ctx.fillText('COMPANY STRENGTH ZONE', w / 2, 78)
    ctx.fillStyle = '#3d9cff'
    ctx.font = '26px Inter, "Microsoft YaHei", sans-serif'
    ctx.fillText('公司实力 · 全球布局', w / 2, 118)
  }
  draw()

  // 异步加载地图并转全息
  const img = new Image()
  img.onload = () => {
    console.log('[worldmap] loaded', img.width + 'x' + img.height)
    const lw = img.width
    const lh = img.height
    const layer = document.createElement('canvas')
    layer.width = lw
    layer.height = lh
    const lctx = layer.getContext('2d')
    lctx.drawImage(img, 0, 0, lw, lh)
    const data = lctx.getImageData(0, 0, lw, lh)
    const d = data.data
    for (let i = 0; i < d.length; i += 4) {
      const l = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / 255
      d[i] = Math.round(30 + l * 140)
      d[i + 1] = Math.round(140 + l * 115)
      d[i + 2] = 255
      d[i + 3] = Math.round(l * 255)
    }
    lctx.putImageData(data, 0, 0)
    holoMapLayer = layer
    draw()
    tex.needsUpdate = true
  }
  img.onerror = () => console.log('[worldmap] load FAILED: images/worldmap.jpg')
  img.src = 'images/worldmap.jpg'
  return tex
}

function makeHUDScreenTexture() {
  // HUD 面板内容集中在这里，方便随时改文字/数据
  const content = {
    title: 'SYSTEM DATA',
    subTitle: '运行数据监控',
    ringLabel: '设备利用率',
    ringValue: '82%',
    ringPct: 0.7,
    bars: [0.5, 0.72, 0.6, 0.9, 0.68, 0.82, 0.75],
    rows: [
      ['运行状态', '正常'],
      ['能耗', '23.4 kWh'],
      ['新风量', '18,000 m³/h'],
      ['温度', '24.6 ℃'],
      ['湿度', '52 %']
    ]
  }
  const w = 1024
  const h = 1536
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  ctx.fillStyle = '#04101f'
  ctx.fillRect(0, 0, w, h)
  ctx.strokeStyle = 'rgba(55,213,255,0.6)'
  ctx.lineWidth = 4
  ctx.strokeRect(24, 24, w - 48, h - 48)
  ctx.strokeStyle = 'rgba(55,213,255,0.25)'
  ctx.lineWidth = 2
  ctx.strokeRect(44, 44, w - 88, h - 88)
  ctx.textAlign = 'center'
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 44px Inter, sans-serif'
  ctx.fillText(content.title, w / 2, 110)
  ctx.fillStyle = '#3d9cff'
  ctx.font = '24px Inter, "Microsoft YaHei", sans-serif'
  ctx.fillText(content.subTitle, w / 2, 152)
  // 环形图
  const ringCx = w / 2
  const ringCy = 430
  const ringR = 200
  ctx.strokeStyle = 'rgba(55,213,255,0.15)'
  ctx.lineWidth = 26
  ctx.beginPath(); ctx.arc(ringCx, ringCy, ringR, 0, Math.PI * 2); ctx.stroke()
  ctx.strokeStyle = '#3d9cff'
  ctx.beginPath(); ctx.arc(ringCx, ringCy, ringR, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * content.ringPct); ctx.stroke()
  ctx.fillStyle = '#cfe9ff'
  ctx.font = 'bold 52px Inter, sans-serif'
  ctx.fillText(content.ringValue, ringCx, ringCy + 18)
  ctx.fillStyle = '#7fb8d8'
  ctx.font = '22px Inter, "Microsoft YaHei", sans-serif'
  ctx.fillText(content.ringLabel, ringCx, ringCy + 220)
  // 柱状图
  const bars = content.bars
  const bw = 70
  const gap = 26
  const bx0 = (w - bars.length * (bw + gap)) / 2
  const by = 900
  for (let i = 0; i < bars.length; i++) {
    const bh = bars[i] * 260
    const g = ctx.createLinearGradient(0, by, 0, by - bh)
    g.addColorStop(0, '#3d9cff')
    g.addColorStop(1, 'rgba(55,213,255,0.15)')
    ctx.fillStyle = g
    ctx.fillRect(bx0 + i * (bw + gap), by - bh, bw, bh)
    ctx.fillStyle = '#7fb8d8'
    ctx.font = '20px Inter, sans-serif'
    ctx.fillText('D' + (i + 1), bx0 + i * (bw + gap) + bw / 2, by + 34)
  }
  // 数据文本
  ctx.textAlign = 'left'
  ctx.font = '26px Inter, "Microsoft YaHei", sans-serif'
  const rows = content.rows
  rows.forEach((r, i) => {
    const y = 1180 + i * 62
    ctx.fillStyle = 'rgba(55,213,255,0.9)'
    ctx.fillText(r[0], 120, y)
    ctx.fillStyle = '#ffffff'
    ctx.fillText(r[1], 480, y)
  })
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function makeScreenFxMaterial(mapTex) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: mapTex },
      uTime: { value: 0 },
      uFlipX: { value: 0 },
      uFlipY: { value: 0 },
      uRot: { value: 0 },
      uTest: { value: 0 },
      uFit: { value: 1 },
      uChamfer: { value: 0 },
      uOpaque: { value: 0 },
      uDim: { value: 1 },
      uWhiteAlpha: { value: 0 },
      uBlackAlpha: { value: 0 },
      uSolid: { value: 0 }, // 屏幕"实体感"：0=允许半透明（能透出背景），1=完全不透
      uChamferPts: { value: Array.from({ length: 12 }, () => new THREE.Vector2()) },
      uChamferN: { value: 0 },
      uOffsetY: { value: 0 },
      uScaleX: { value: 1 },
      uOffsetX: { value: 0 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uMap;
      uniform float uTime;
      uniform float uFlipX;
      uniform float uFlipY;
      uniform float uRot;
      uniform float uTest;
      uniform float uFit;
      uniform float uChamfer;
      uniform float uOpaque;
      uniform float uDim;
      uniform float uWhiteAlpha;
      uniform float uBlackAlpha;
      uniform float uSolid;
      uniform vec2 uChamferPts[12];
      uniform float uChamferN;
      uniform float uOffsetY;
      uniform float uScaleX;
      uniform float uOffsetX;
      varying vec2 vUv;
      float sdSeg(vec2 p, vec2 a, vec2 b) {
        vec2 pa = p - a;
        vec2 ba = b - a;
        float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
        return length(pa - ba * h);
      }
      float sdPolyBorder(vec2 p) {
        float d = 1e9;
        int n = int(uChamferN);
        for (int i = 0; i < 12; i++) {
          if (i >= n) break;
          vec2 a = uChamferPts[i];
          vec2 b = uChamferPts[(i + 1) % n];
          d = min(d, sdSeg(p, a, b));
        }
        return d;
      }
      void main() {
        vec2 uv = vUv;
        uv.y = (uv.y - 0.5) * uFit + 0.5; // 等比适配，避免垂直拉伸
        uv.y += uOffsetY; // 纵向偏移
        uv.x = (uv.x - 0.5) / uScaleX + 0.5 + uOffsetX; // 横向缩放（加宽）+ 横向偏移
        uv.x = mix(uv.x, 1.0 - uv.x, uFlipX);
        uv.y = mix(uv.y, 1.0 - uv.y, uFlipY);
        float r = mod(uRot, 4.0);
        if (r == 1.0) uv = vec2(1.0 - uv.y, uv.x);
        else if (r == 2.0) uv = vec2(1.0 - uv.x, 1.0 - uv.y);
        else if (r == 3.0) uv = vec2(uv.y, 1.0 - uv.x);
        if (uTest > 0.5) {
          // 测试画面：红蓝棋盘格 + 白色对角线
          vec2 cell = floor(uv * vec2(6.0, 6.0));
          float chk = mod(cell.x + cell.y, 2.0);
          vec3 col = chk > 0.5 ? vec3(1.0, 0.05, 0.05) : vec3(0.05, 0.05, 1.0);
          if (abs(uv.x - uv.y) < 0.02) col = vec3(1.0);
          gl_FragColor = vec4(col, 1.0);
          return;
        }
        vec4 t = texture2D(uMap, uv);
        // 屏幕贴图是 sRGB 编码的，但这里输出到的是线性缓冲（最后由 OutputPass 统一做色调映射）。
        // 原来直接把 sRGB 当线性值乘 uDim —— 亮部被压暗、暗部被抬灰，
        // 所以屏幕怎么调都是"发灰的暗"，不像发光的显示屏。先转回线性再乘亮度。
        // 末尾乘 2 是为了抵消后期曝光 0.5：这样 uDim≈1 就是"原图亮度"，好调。
        vec3 col = pow(max(t.rgb, 0.0), vec3(2.2)) * uDim * 2.0;
        float scanY = fract(uTime * 0.1);
        float scan = smoothstep(0.0, 0.035, abs(uv.y - scanY));
        col += vec3(0.0, 0.9, 1.0) * (1.0 - scan) * 0.28;
        // 屏幕边框（一圈青色发光框，主屏带切角）
        // 边框使用物理 UV（vUv），与等比缩放/翻转无关，保证贴合屏幕真实边缘
        float b = 0.006;
        float borderMask;
        if (uChamfer > 0.5) {
          borderMask = 1.0 - smoothstep(0.0, b, sdPolyBorder(vUv));
        } else {
          borderMask = 1.0 - step(b, vUv.x) * step(b, vUv.y) * step(b, 1.0 - vUv.x) * step(b, 1.0 - vUv.y);
        }
        vec3 frameCol = vec3(0.0, 0.9, 1.0);
        col = mix(frameCol, col, 1.0 - borderMask);
        float baseA = 1.0;
        float lum = dot(t.rgb, vec3(0.299, 0.587, 0.114));
        if (uOpaque < 0.5) {
          float aWhite = uWhiteAlpha > 0.0 ? clamp(1.0 - lum * uWhiteAlpha, 0.6, 1.0) : 1.0;
          float aBlack = uBlackAlpha > 0.0 ? clamp(lum * uBlackAlpha, 0.6, 1.0) : 1.0;
          baseA = clamp(aWhite * aBlack, 0.6, 1.0);
          // 屏幕实体感：把透明度往"完全不透"拉。原来亮的地方只有 0.7 的不透明度，
          // 背景会透进来 30%，屏幕看着发虚。uSolid≈0.85 基本就是实体屏了。
          baseA = mix(baseA, 1.0, uSolid);
        }
        float a = mix(1.0, baseA, 1.0 - borderMask);
        gl_FragColor = vec4(col, a);
      }
    `,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide
  })
}

// 从网格 UV 计算凸包顶点（切角边框自动贴合屏幕形状）
function getUVCorners(mesh) {
  const attr = mesh.geometry.attributes.uv
  if (!attr) return []
  const pts = []
  for (let i = 0; i < attr.count; i++) pts.push({ x: attr.getX(i), y: attr.getY(i) })
  pts.sort((a, b) => a.x - b.x || a.y - b.y)
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const lower = []
  const upper = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  lower.pop()
  upper.pop()
  return lower.concat(upper).map((p) => new THREE.Vector2(p.x, p.y))
}

function setChamferFromMesh(mat, mesh) {
  const corners = getUVCorners(mesh)
  if (corners.length < 4) return
  const arr = mat.uniforms.uChamferPts.value
  for (let i = 0; i < 12; i++) {
    if (i < corners.length) arr[i].copy(corners[i])
  }
  mat.uniforms.uChamferN.value = corners.length
  mat.uniforms.uChamfer.value = 1
}

// ---------- Screen_Data 屏幕 ----------
// 模型里的 Screen_Data_xx 载体保留但不显示（见 setupHallVisuals 里的 m.visible = false）。
// 原来这里有一套"八图横向拼成 8192px 条带 + 逐张抠背景"的贴图逻辑，因为载体已隐藏、
// 图片素材也已移除，整套贴图代码（连同那 8 张屏图）一并清掉了。

// 银色皮革纹理（程序生成，可替换成你的贴图）
function makeSilverLeatherTexture() {
  const s = 512
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  const g = ctx.createLinearGradient(0, 0, 0, s)
  g.addColorStop(0, '#ccd2d9')
  g.addColorStop(0.5, '#b9c0c8')
  g.addColorStop(1, '#a7aeb6')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, s, s)
  const img = ctx.getImageData(0, 0, s, s)
  const d = img.data
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 18
    d[i] += n
    d[i + 1] += n
    d[i + 2] += n
  }
  ctx.putImageData(img, 0, 0)
  ctx.strokeStyle = 'rgba(90,100,112,0.10)'
  ctx.lineWidth = 1
  for (let i = 0; i < 60; i++) {
    ctx.beginPath()
    const x = Math.random() * s
    const y = Math.random() * s
    ctx.moveTo(x, y)
    ctx.quadraticCurveTo(
      x + (Math.random() - 0.5) * 40,
      y + (Math.random() - 0.5) * 40,
      x + (Math.random() - 0.5) * 90,
      y + (Math.random() - 0.5) * 90
    )
    ctx.stroke()
  }
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  // 各向异性过滤：地面是斜着看的，关掉的话远处砖缝会闪、会起摩尔纹
  tex.anisotropy = maxAniso()
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  return tex
}

// 全息折线纹理（仅用于主地面 FLoor）
function makeHoloLinesTexture(floorW, floorH) {
  const cw = 2048
  const ch = Math.max(512, Math.min(2048, Math.round(cw * (floorH / floorW))))
  const c = document.createElement('canvas')
  c.width = cw
  c.height = ch
  const ctx = c.getContext('2d')
  const count = 20 + Math.floor(Math.random() * 13)
  for (let i = 0; i < count; i++) {
    let x = 20 + Math.random() * (cw - 40)
    let y = 20 + Math.random() * (ch - 40)
    ctx.beginPath()
    ctx.moveTo(x, y)
    const segs = 3 + Math.floor(Math.random() * 4)
    for (let s = 0; s < segs; s++) {
      const ang = Math.random() * Math.PI * 2
      const len = 90 + Math.random() * 240
      x = Math.min(cw - 10, Math.max(10, x + Math.cos(ang) * len))
      y = Math.min(ch - 10, Math.max(10, y + Math.sin(ang) * len))
      ctx.lineTo(x, y)
    }
    const bright = 0.55 + Math.random() * 0.45
    ctx.strokeStyle = `rgba(${Math.round(120 + bright * 135)}, ${Math.round(225 + bright * 30)}, 255, ${0.7 + Math.random() * 0.3})`
    ctx.lineWidth = 2 + Math.random() * 3
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.shadowColor = 'rgba(55,213,255,0.95)'
    ctx.shadowBlur = 14 + Math.random() * 14
    ctx.stroke()
    ctx.fillStyle = 'rgba(190,240,255,0.95)'
    ctx.beginPath()
    ctx.arc(x, y, 3 + Math.random() * 2, 0, Math.PI * 2)
    ctx.fill()
  }
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// 地砖贴图：单块方格；glow=true 时砖缝才发光（仅上下左右四向地面）
function makeFloorTileTexture(baseColor, glow = true) {
  const s = 256
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  ctx.fillStyle = baseColor || '#12294a'
  ctx.fillRect(0, 0, s, s)
  // 轻微噪点
  const img = ctx.getImageData(0, 0, s, s)
  const d = img.data
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 8
    d[i] += n
    d[i + 1] += n
    d[i + 2] += n
  }
  ctx.putImageData(img, 0, 0)
  if (glow) {
    // 暗底 + 细霓虹缝：克制的发光，不整片亮
    ctx.fillStyle = 'rgba(0, 220, 255, 0.28)'
    ctx.fillRect(0, 0, 9, s)
    ctx.fillRect(0, 0, s, 9)
    ctx.fillStyle = 'rgba(0, 255, 255, 0.55)'
    ctx.fillRect(2.5, 0, 3, s)
    ctx.fillRect(0, 2.5, s, 3)
  } else {
    // 四角浅灰地面：普通砖缝，不发光
    ctx.fillStyle = 'rgba(60, 72, 90, 0.22)'
    ctx.fillRect(0, 0, 4, s)
    ctx.fillRect(0, 0, s, 4)
  }
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// 地砖材质：磨砂雾面反光 + 接收阴影；glow 控制砖缝是否发光
function makeFloorTileMaterial(repeatX, repeatY, baseColor, glow) {
  // 兼容旧调用：前两个参数不再用（地面 UV 改成"世界米"，平铺密度由 floorTileMeters 统一控制）
  const tex = makeFloorTileTexture(baseColor, glow)
  const r = floorTileRepeat()
  tex.repeat.set(r, r)
  const mat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: tex,
    roughness: glow === false ? 0.5 : 0.4,
    metalness: glow === false ? 0.3 : 0.5,
    envMapIntensity: glow === false ? 0.8 : 1.2,
    clearcoat: glow === false ? 0.2 : 0.25,
    clearcoatRoughness: glow === false ? 0.4 : 0.35,
    receiveShadow: true
  })
  mat.userData = { glow: glow !== false, repeatX: r, repeatY: r, base: baseColor }
  return mat
}

// ---------- 地面统一处理：格子方向对齐 + 砖尺寸可调 ----------
let floorTileMeters = 1 // 一格地砖多少米（默认 1 米＝原来的大小，面板可调）
function floorTileRepeat() { return 1 / Math.max(floorTileMeters, 0.1) }

// 一块地面用"世界坐标 XZ（单位：米）"当 UV：
// 所有地面的格子方向、网格线都对齐同一套世界网格，不会一块歪一块正、接缝也接不上。
function applyWorldFloorUV(mesh) {
  const geo = mesh.geometry
  const pos = geo && geo.attributes && geo.attributes.position
  if (!pos) return
  mesh.updateWorldMatrix(true, false)
  const m = mesh.matrixWorld
  const arr = new Float32Array(pos.count * 2)
  const v = new THREE.Vector3()
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i).applyMatrix4(m)
    arr[i * 2] = v.x
    arr[i * 2 + 1] = v.z
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(arr, 2))
}

// 厚地台的侧面：同色不带纹理的材质（避免砖纹被拉成长条）
function makeFloorSideMaterial(baseColor) {
  const c = new THREE.Color(baseColor || '#12294a').multiplyScalar(0.85)
  const mat = new THREE.MeshPhysicalMaterial({ color: c, metalness: 0.35, roughness: 0.55, envMapIntensity: 0.9 })
  mat.userData = { sideOnly: true }
  return mat
}

// 不铺砖的素色地面（颜色跟面板色板走，但没有方格纹理）
function makeFloorPlainMaterial(hex) {
  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(hex || '#6b6b6b'),
    metalness: 0.35,
    roughness: 0.5,
    envMapIntensity: 1.0
  })
  mat.userData = { colorOnly: true }
  return mat
}

// 模型里同一个 geometry 被多个 mesh 用的时候不要拆面（会影响别人）
let floorGeoUse = null
function geomUseCount() {
  if (floorGeoUse) return floorGeoUse
  floorGeoUse = new Map()
  if (modelRoot) {
    modelRoot.traverse((o) => {
      if (o.isMesh && o.geometry) floorGeoUse.set(o.geometry, (floorGeoUse.get(o.geometry) || 0) + 1)
    })
  }
  return floorGeoUse
}

// 给一块地面铺砖：世界 UV + 砖材质；厚地台只在朝上的大面铺砖，侧面用同色纯材质
function paintFloorMesh(mesh, baseColor, glow, list) {
  applyWorldFloorUV(mesh)
  const tileMat = makeFloorTileMaterial(baseColor, glow)
  let thick = false
  try {
    const box = new THREE.Box3().setFromObject(mesh)
    const s = box.getSize(new THREE.Vector3())
    thick = s.y > 0.25
  } catch (e) { /* 忽略 */ }
  if (thick && geomUseCount().get(mesh.geometry) === 1 && splitFloorPlateFaces(mesh)) {
    const sideMat = makeFloorSideMaterial(baseColor)
    debugFloorMats.side.push(sideMat)
    mesh.material = [tileMat, sideMat]
  } else {
    mesh.material = tileMat
  }
  ;(list || debugFloorMats.dark).push(tileMat)
  mesh.userData.isFloor = true
  return tileMat
}

// 面板：地砖尺寸（米）
function applyFloorTileSize() {
  const el = document.querySelector('#md-tile-size')
  if (el) floorTileMeters = Math.max(0.2, Number(el.value) || 2)
  const v = document.querySelector('#md-tile-size-v')
  if (v) v.textContent = floorTileMeters.toFixed(2)
  const r = floorTileRepeat()
  const lists = [debugFloorMats.corner, debugFloorMats.light, debugFloorMats.dark, debugFloorMats.darkFloor]
  for (const list of lists) {
    for (const mat of list) {
      if (!mat || !mat.userData || !mat.map) continue
      mat.userData.repeatX = r
      mat.userData.repeatY = r
      mat.map.repeat.set(r, r)
      mat.map.needsUpdate = true
      mat.needsUpdate = true
    }
  }
}

// 同一高度叠着好几层地面时，把排在后面的往后错一点深度，让"谁在上面"固定下来，不再闪斑点。
// 注意：直接改材质本身（不要 clone），否则这块地面就和调试面板里的材质"脱钩"了——
// 之前 clone 过，导致「深色地面 / 四角浅灰」这些色板改了以后 Floor_Dark、Floor_Corner 没反应。
function offsetFloorMesh(mesh, i) {
  if (!mesh || !mesh.material) return
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  for (const m of mats) {
    if (!m || typeof m !== 'object') continue
    m.polygonOffset = true
    m.polygonOffsetFactor = i
    m.polygonOffsetUnits = i * 2
    m.needsUpdate = true
  }
}

// 厚地台（有厚度的基座）：朝上的大面铺砖纹，侧面/底面用同色纯材质，
// 否则砖纹会被拉成长条。返回是否成功拆成两组。
function splitFloorPlateFaces(mesh) {
  const g = mesh.geometry
  const pos = g && g.attributes && g.attributes.position
  if (!pos) return false
  if (g.groups && g.groups.length > 0) return false
  mesh.updateWorldMatrix(true, false)
  const nm = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
  const idx = g.index ? Array.from(g.index.array) : Array.from({ length: pos.count }, (_, i) => i)
  const vA = new THREE.Vector3()
  const vB = new THREE.Vector3()
  const vC = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  const nrm = new THREE.Vector3()
  const flat = []
  const wall = []
  for (let k = 0; k + 2 < idx.length; k += 3) {
    const a = idx[k]
    const b = idx[k + 1]
    const c = idx[k + 2]
    vA.fromBufferAttribute(pos, a)
    vB.fromBufferAttribute(pos, b)
    vC.fromBufferAttribute(pos, c)
    ab.subVectors(vB, vA)
    ac.subVectors(vC, vA)
    nrm.crossVectors(ab, ac).applyMatrix3(nm)
    const len = nrm.length()
    const ny = len > 1e-9 ? Math.abs(nrm.y / len) : 0
    if (ny > 0.35) flat.push(a, b, c)
    else wall.push(a, b, c)
  }
  if (!flat.length || !wall.length) return false
  g.setIndex(flat.concat(wall))
  g.clearGroups()
  g.addGroup(0, flat.length, 0)
  g.addGroup(flat.length, wall.length, 1)
  return true
}

// 兜底铺砖：模型里还散着一批没按 Floor_* 命名的地面（3ds Max 导出的 Geom3D.*，
// 材质是调色板占位色：绿、橄榄、肉粉、焦糖…）。它们和已铺砖的地面同高、互相压着，
// 深度打架时地面就会一块绿一块灰地闪（就是"规则的斑点"、以及"一块材质和旁边不一样"）。
// 这里把所有"大块、贴地"的板也换成同一套地砖材质，并给同高度的板排出前后顺序。
// 一块网格是不是"整块地面"：算出它真实的三角面面积，和包围盒面积比。
// 斜着的光带、灯圈包围盒跟展厅一样大，但实际面积只有百分之几，靠这个区分。
function meshSurfaceStats(o, box, useCount) {
  const g = o.geometry
  const pos = g && g.attributes && g.attributes.position
  if (!pos) return null
  const idx = g.index ? g.index.array : null
  const n = idx ? idx.length : pos.count
  const vA = new THREE.Vector3()
  const vB = new THREE.Vector3()
  const vC = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  let area = 0
  o.updateWorldMatrix(true, false)
  for (let k = 0; k + 2 < n; k += 3) {
    const a = idx ? idx[k] : k
    const b = idx ? idx[k + 1] : k + 1
    const c = idx ? idx[k + 2] : k + 2
    vA.fromBufferAttribute(pos, a).applyMatrix4(o.matrixWorld)
    vB.fromBufferAttribute(pos, b).applyMatrix4(o.matrixWorld)
    vC.fromBufferAttribute(pos, c).applyMatrix4(o.matrixWorld)
    ab.subVectors(vB, vA)
    ac.subVectors(vC, vA)
    area += ab.cross(ac).length() * 0.5
  }
  const size = box.getSize(new THREE.Vector3())
  const bboxArea = Math.max(size.x * size.z, 1e-6)
  return { area, bboxArea, ratio: area / bboxArea, useCount }
}

function paintLeftoverFloors() {
  // 只有最低档才跳过（这是一次性的加载期处理，不影响每帧开销，但会让地面好看很多）
  if (!modelRoot || QUALITY.tier === 'low') return 0
  modelRoot.updateMatrixWorld(true) // 量尺寸前先刷新世界矩阵，避免拿到没更新的包围盒
  const useCount = geomUseCount()
  // 灯带、灯圈、屏幕、玻璃、展台底座的子孙网格不算地面（哪怕包围盒很大）
  const badAncestor = (o) => {
    let cur = o
    while (cur && cur !== modelRoot) {
      const n = cur.name || ''
      if (/^(Hitbox|Screen|Glass|TopGlass|Light|Title|CamPos|CamTarget|StarGlobe|water|Pedestal)/i.test(n)) return true
      cur = cur.parent
    }
    return false
  }
  const planes = []
  modelRoot.traverse((o) => {
    if (!o.isMesh || !o.geometry || !o.geometry.attributes || !o.geometry.attributes.position) return
    const n = (o.name || '').replace(/^[_ .]+/, '')
    if (/^(Hitbox|Screen|Glass|TopGlass|Light|Title|CamPos|CamTarget|StarGlobe|water)/i.test(n)) return
    if (badAncestor(o)) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    if (mats.some((m) => m && /glass|water|light/i.test(m.name || ''))) return
    const box = new THREE.Box3().setFromObject(o)
    if (box.isEmpty()) return
    const s = box.getSize(new THREE.Vector3())
    if (s.x < 8 || s.z < 8) return // 小块（展台、装饰条）不动
    if (s.y > 0.9) return // 太厚＝立体件，不动
    const topY = box.max.y
    if (topY > hallFloorY + 2.0 || topY < hallFloorY - 0.1) return // 悬空的板（顶棚/夹层）不动
    const stat = meshSurfaceStats(o, box, useCount)
    if (!stat || stat.ratio < 0.08) return // 细条/环：不是地面
    planes.push({ o, s, topY, area: s.x * s.z, named: !!o.userData.isFloor })
  })
  if (!planes.length) return 0
  // 同高度的板分一组。谁在前：
  //   1. 铺了地砖的那层（FLoor.001）——始终盖在最上面，方格才不会被素色板压掉；
  //   2. 其它擦过手的地面（Floor_Side / Floor_Corner / Floor_Dark 等素色底板）；
  //   3. 模型自带、没被接管过的板。
  // 组内按优先级 + 面积从大到小排，排在后面的往后错一点深度，让"谁在前"固定下来。
  const hasTileMap = (p) => {
    const mats = Array.isArray(p.o.material) ? p.o.material : [p.o.material]
    return mats.some((m) => m && m.map && m.userData && m.userData.glow !== undefined)
  }
  const priority = (p) => (hasTileMap(p) ? 0 : p.named ? 1 : 2)
  const groups = new Map()
  for (const p of planes) {
    const key = Math.round(p.topY * 20) // 5 厘米一档
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(p)
  }
  let painted = 0
  let ordered = 0
  const names = []
  for (const list of groups.values()) {
    list.sort((a, b) => priority(a) - priority(b) || b.area - a.area)
    if (list.length > 1) {
      console.log('[floor] 同高度 ' + list[0].topY.toFixed(2) + ' 米的地面顺序：' +
        list.map((p) => priority(p) + '·' + (p.o.name || '?')).join(' > '))
    }
    list.forEach((p, i) => {
      if (p.named) {
        if (i > 0) offsetFloorMesh(p.o, i) // 命名地面也可能和别的板同高，排在后面就往后错
        if (i > 0) ordered++
        return
      }
      // 还没被命名规则接管的大块平板（含展厅中间那块抬高的展示大地面）：一并铺砖
      paintFloorMesh(p.o, mdZone ? mdZone.value : undefined, true, debugFloorMats.dark)
      if (i > 0) offsetFloorMesh(p.o, i)
      painted++
      names.push((p.o.name || '?') + '(' + p.s.x.toFixed(0) + '×' + p.s.z.toFixed(0) + ' y' + p.topY.toFixed(2) + ')')
    })
  }
  if (painted) console.log('[floor] 兜底铺砖 ' + painted + ' 块：' + names.join(', '))
  return painted
}

// 全息折线覆盖层（叠加在主地面上）
function makeHoloLinesOverlayMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: {
      uLines: { value: null },
      uMin: { value: new THREE.Vector2(0, 0) },
      uSize: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 }
    },
    vertexShader: `
      varying vec3 vWorldPos;
      void main() {
        vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uLines;
      uniform vec2 uMin;
      uniform vec2 uSize;
      uniform float uTime;
      varying vec3 vWorldPos;
      void main() {
        vec2 uv = (vWorldPos.xz - uMin) / uSize;
        vec4 t = texture2D(uLines, uv);
        float pulse = 0.6 + 0.4 * sin(uTime * 1.6 + length(vWorldPos.xz) * 0.12);
        vec3 glow = t.rgb * 1.5 * pulse;
        gl_FragColor = vec4(glow, t.a);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide
  })
}

// 全息网格纹理（玻璃地板发光用）
function makeHoloGridTexture() {
  const s = 512
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  ctx.clearRect(0, 0, s, s)
  ctx.strokeStyle = 'rgba(55,213,255,0.9)'
  ctx.lineWidth = 2
  const step = 64
  for (let i = 0; i <= s; i += step) {
    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, s); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(s, i); ctx.stroke()
  }
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// 标签文字折行：按最大宽度切行（英文按词、中文按字，单个超长词硬切），
// 展厅名很短基本用不到，展品名（尤其英文）会用到。
function wrapTitleLines(ctx, text, maxTextPx) {
  const raw = String(text == null ? '' : text)
  if (!(maxTextPx > 0)) return raw.split('\n')
  const out = []
  for (const para of raw.split('\n')) {
    const tokens = para.match(/[A-Za-z0-9][A-Za-z0-9.\-'’&/+]*|\s+|[^\s]/g) || []
    const join = (a, b) => (/[A-Za-z0-9]$/.test(a) && /^[A-Za-z0-9]/.test(b) ? a + ' ' + b : a + b)
    let cur = ''
    for (const tk of tokens) {
      if (/^\s+$/.test(tk)) continue
      const cand = cur ? join(cur, tk) : tk
      if (cur && ctx.measureText(cand).width > maxTextPx) {
        out.push(cur)
        cur = tk
      } else {
        cur = cand
      }
      // 单个词自己就超宽（例如一长串英文型号）：硬切成几段
      while (cur.length > 1 && ctx.measureText(cur).width > maxTextPx) {
        let cut = cur.length - 1
        while (cut > 1 && ctx.measureText(cur.slice(0, cut)).width > maxTextPx) cut--
        out.push(cur.slice(0, cut))
        cur = cur.slice(cut)
      }
    }
    if (cur) out.push(cur)
  }
  return out.length ? out : ['']
}

// Title_* 空物体 → 悬浮标题（Sprite 始终面向相机；按当前语言只绘制一种语言）
// k：相对"展厅名称"的字号比例（展品名标签用 0.5）；
// maxTextPx：文字区最大宽度（像素，0 = 不限制），超过就折行成多行标签。
// presetLines：已算好的行（可选；给了就直接用，不再自己折行）
function paintTitleTexture(text, k = 1, maxTextPx = 0, presetLines = null) {
  const fontSize = 46 * k
  const dotR = 10 * k
  const padX = 40 * k
  const gap = 20 * k
  const lineGap = 62 * k
  const padY = 31 * k
  const font = 'bold ' + fontSize + 'px Inter, "Microsoft YaHei", sans-serif'
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')
  ctx.font = font
  const lines = presetLines && presetLines.length ? presetLines : wrapTitleLines(ctx, text, maxTextPx)
  const tw = Math.max(1, ...lines.map((s) => ctx.measureText(s).width))
  const w = Math.ceil(tw + padX * 2 + dotR * 2 + gap)
  const h = Math.ceil(fontSize + padY * 2 + (lines.length - 1) * lineGap)
  c.width = w
  c.height = h
  // 玻璃标签底
  const r = 22 * k
  const roundRect = (x, y, ww, hh, rr) => {
    ctx.beginPath()
    ctx.moveTo(x + rr, y)
    ctx.lineTo(x + ww - rr, y)
    ctx.quadraticCurveTo(x + ww, y, x + ww, y + rr)
    ctx.lineTo(x + ww, y + hh - rr)
    ctx.quadraticCurveTo(x + ww, y + hh, x + ww - rr, y + hh)
    ctx.lineTo(x + rr, y + hh)
    ctx.quadraticCurveTo(x, y + hh, x, y + hh - rr)
    ctx.lineTo(x, y + rr)
    ctx.quadraticCurveTo(x, y, x + rr, y)
    ctx.closePath()
  }
  const g = ctx.createLinearGradient(0, 0, 0, h)
  g.addColorStop(0, 'rgba(22, 36, 56, 0.74)')
  g.addColorStop(1, 'rgba(9, 17, 28, 0.52)')
  ctx.fillStyle = g
  roundRect(3 * k, 3 * k, w - 6 * k, h - 6 * k, r)
  ctx.fill()
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.24)'
  ctx.lineWidth = 2 * k
  roundRect(3 * k, 3 * k, w - 6 * k, h - 6 * k, r)
  ctx.stroke()
  // 冷青圆点
  ctx.save()
  ctx.shadowColor = 'rgba(143, 220, 236, 0.95)'
  ctx.shadowBlur = 16 * k
  ctx.fillStyle = '#8fdcec'
  ctx.beginPath()
  ctx.arc(padX + dotR, h / 2, dotR, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
  // 文字
  ctx.font = font
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)'
  ctx.shadowBlur = 10 * k
  ctx.fillStyle = '#ffffff'
  const lineX = padX + dotR * 2 + gap
  lines.forEach((line, i) => {
    ctx.fillText(line, lineX, h / 2 + (i - (lines.length - 1) / 2) * lineGap + 2 * k)
  })
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function makeTitleSprite(text, k = 1, maxTextPx = 0) {
  const tex = paintTitleTexture(text, k, maxTextPx)
  const normalMat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false, color: 0xd8ecf8 })
  const highlightMat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false, color: 0xffffff })
  const sprite = new THREE.Sprite(normalMat)
  const baseY = 1.9 * k
  sprite.scale.set(baseY * (tex.image.width / tex.image.height), baseY, 1)
  sprite.userData.normalMat = normalMat
  sprite.userData.highlightMat = highlightMat
  sprite.userData.baseScaleX = sprite.scale.x
  sprite.userData.baseScaleY = sprite.scale.y
  return sprite
}

// 语言切换时重绘悬浮标题（只显示当前语言）
function refreshTitleSprites() {
  for (const t of titleSprites) {
    if (!t.zone) continue
    const zh = t.zh || t.text
    const en = t.en || ZONE_TITLE_EN[t.zone] || zh
    const txt = getLang() === 'en' ? en : zh
    const tex = paintTitleTexture(txt)
    const mats = [t.sprite.userData.normalMat, t.sprite.userData.highlightMat]
    let oldTex = null
    for (const mat of mats) {
      if (!mat) continue
      if (!oldTex) oldTex = mat.map
      mat.map = tex
      mat.needsUpdate = true
    }
    if (oldTex) oldTex.dispose()
    // 贴图换了宽高比也要跟着更新，否则文字会被非等比拉伸（中文被压扁）
    const img = tex.image || {}
    const iw = img.width || 108
    const ih = img.height || 108
    const baseY = 1.9
    const baseX = baseY * (iw / ih)
    t.sprite.userData.baseScaleX = baseX
    t.sprite.userData.baseScaleY = baseY
    t.sprite.scale.set(baseX, baseY, 1)
  }
}

// 当前展区标题高亮
function updateTitleHighlights(zoneId) {
  for (const t of titleSprites) {
    const active = t.zone === zoneId
    t.sprite.material = active ? t.sprite.userData.highlightMat : t.sprite.userData.normalMat
    // 高亮只改亮度，不放大尺寸：保证所有展区标题框高度一致
    t.sprite.scale.set(t.sprite.userData.baseScaleX, t.sprite.userData.baseScaleY, 1)
  }
}

// ---------- 展品名标签：大厅里每个展品"前方"一小块名牌 ----------
// 和展厅名称同一套玻璃标签，只是字号更小（EXHIBIT_LABEL_K = 0.35），
// 挂在每个展品中心的正上方；中/英文按当前界面语言只绘制一种；Sprite 永远正对相机。
const EXHIBIT_LABEL_K = 0.35           // 字号 = 展厅名称 × 0.35（= 最初半号字的 70%）
const EXHIBIT_LABEL_MAX_TEXT_PX = 140  // 文字区最大宽度（像素；世界尺寸里约 2.5 米），超了折行
const EXHIBIT_LABEL_MAX_LINES = 3      // 最多折几行；再长（超长英文名）就把字号整体缩一点
const EXHIBIT_LABEL_MAX_HEIGHT = 1.12  // 名牌最大高度（米）：行数多就整块等比缩一点，免得比展品还高
const EXHIBIT_LABEL_ABOVE = 1          // 名牌挂到"展品中心上方"多少米
const EXHIBIT_LABEL_RAISED_ZONES = [1, 2, 7, 8] // 这几个展厅的展品名牌再额外加高 1 米
const EXHIBIT_LABEL_EXTRA_ABOVE = 1
// 这些展品的名字固定显示成一行（不折行）——名字较长但希望一整行看全的那几台
const EXHIBIT_LABEL_ONE_LINE = new Set(['81', '86', '21', '22', '23', '24', '25', '31', '41', '71', '12'])
// 这几个展品的英文名固定折成两排（英文名长、但一排太宽），和 11 号一样
const EXHIBIT_LABEL_EN_TWO_LINE = new Set(['13'])
const EXHIBIT_LABEL_EN_ONE_LINE_PX = 370 // 英文一行最宽（像素，约 6.5 米）：放得下就一整排
const EXHIBIT_LABEL_EN_SHORT_WORDS = 6   // 6 个词以内的英文名就直接一整排（折行会剩 3 个词的碎行）
const EXHIBIT_LABEL_EN_ONE_LINE_MAX_PX = 625 // 周围很空时允许的一行上限（约 11 米）
const EXHIBIT_LABEL_PX_PER_M = 108 / 1.9     // 贴图像素 → 世界米（1 行 108px 高 = 1.9 米，和展厅名标签同一比例）
// 展品名称总开关（入口界面右上角"展品名称"按钮控制；默认显示，选择记在本机）
const EXHIBIT_NAMES_KEY = 'pf-exhibit-names'
let exhibitNamesOn = true
try { exhibitNamesOn = localStorage.getItem(EXHIBIT_NAMES_KEY) !== '0' } catch {}
const exhibitLabels = new Map()        // 展品编号 -> { sprite, zh, en, lang }
let exhibitLabelGroup = null

function ensureExhibitLabelGroup() {
  if (exhibitLabelGroup) return exhibitLabelGroup
  exhibitLabelGroup = new THREE.Group()
  exhibitLabelGroup.name = 'ExhibitNameLabels'
  scene.add(exhibitLabelGroup)
  return exhibitLabelGroup
}

function hallHitboxById(id) {
  return hitboxes.find((h) => h.id === String(id)) || null
}

// 展品名：优先用弹窗那份数据（exhibitData.json）；名字还没配好（"待补充/待定"）时，
// 退回模型文件名里的产品编号再查一次（例如 45_01_FP.glb → 01 = 风机盘管）。
function exhibitLabelNames(id) {
  const usable = (n) => !!n && n !== '待补充' && !/^待定/.test(n)
  const own = (PRODUCTS[id] && PRODUCTS[id].name) || ''
  let zh = usable(own) ? own : ''
  if (!zh) {
    const cfg = hallExhibitCfg && hallExhibitCfg.exhibits ? hallExhibitCfg.exhibits[id] : null
    const m = String((cfg && cfg.model) || '').match(/^models\/(?:\d+)_(\d+)/)
    const alt = m ? (PRODUCTS[m[1]] && PRODUCTS[m[1]].name) || '' : ''
    if (usable(alt)) zh = alt
  }
  return { zh, en: zh ? (PRODUCT_NAMES_EN[zh] || zh) : '' }
}

/**
 * 英文名牌排版：优先一整排；一行实在太宽才折行，按词数均分（8~10 个词正好每行 4~5 个词）。
 * 英文字号始终不缩，所以只决定"几排、每排几个词"。
 */
function wrapEnglishLines(ctx, text, maxOneLinePx = EXHIBIT_LABEL_EN_ONE_LINE_PX, id = '') {
  const words = String(text == null ? '' : text).trim().split(/\s+/).filter(Boolean)
  if (words.length <= 1) return [words.join(' ')]
  const total = ctx.measureText(words.join(' ')).width
  const forceTwo = EXHIBIT_LABEL_EN_TWO_LINE.has(String(id))
  if (!forceTwo && (words.length <= EXHIBIT_LABEL_EN_SHORT_WORDS || total <= maxOneLinePx)) {
    return [words.join(' ')]
  }
  const n = Math.min(3, Math.max(2, Math.ceil(total / maxOneLinePx)))
  const per = Math.ceil(words.length / n)
  const lines = []
  for (let i = 0; i < words.length; i += per) lines.push(words.slice(i, i + per))
  return lines.map((l) => l.join(' '))
}

// 展品到自己最近邻展品的水平距离（米）：周围越空，英文名牌越可以放宽成一整排
function exhibitNeighborGap(id) {
  const hb = hallHitboxById(id)
  if (!hb) return 0
  let best = Infinity
  for (const h of hitboxes) {
    if (h.id === String(id) || !h.center) continue
    const d = Math.hypot(h.center.x - hb.center.x, h.center.z - hb.center.z)
    if (d < best) best = d
  }
  return Number.isFinite(best) ? best : 0
}

// 这个展品的英文名牌"一整排"最多能有多宽（像素）：默认约 6.5 米，周围空就放宽（最多约 11 米）
function exhibitLabelOneLinePx(id) {
  const byGap = exhibitNeighborGap(id) * 0.85 * EXHIBIT_LABEL_PX_PER_M
  return Math.min(EXHIBIT_LABEL_EN_ONE_LINE_MAX_PX, Math.max(EXHIBIT_LABEL_EN_ONE_LINE_PX, byGap))
}

// 名牌贴图：每行字号固定在"展厅名的一半"，行数随名字长短增加（名牌高度跟着长）。
// 中文名字超长时会把字号整体缩一点；英文不缩（按单词排版，见 wrapEnglishLines）。
function exhibitLabelTexture(text, id) {
  const raw = String(text == null ? '' : text)
  const isEn = !/[\u4e00-\u9fff]/.test(raw) && /[A-Za-z]/.test(raw)
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')
  const fontOf = (k) => 'bold ' + 46 * k + 'px Inter, "Microsoft YaHei", sans-serif'
  let k = EXHIBIT_LABEL_K
  let lines
  if (isEn) {
    ctx.font = fontOf(k)
    lines = wrapEnglishLines(ctx, raw, exhibitLabelOneLinePx(id), id)
  } else {
    // 指定成"一行显示"的展品不折行；其余中文名超过 3 行才把字号整体缩一点
    const maxPx = EXHIBIT_LABEL_ONE_LINE.has(String(id)) ? 0 : EXHIBIT_LABEL_MAX_TEXT_PX
    ctx.font = fontOf(k)
    for (let i = 0; i < 2 && wrapTitleLines(ctx, raw, maxPx).length > EXHIBIT_LABEL_MAX_LINES; i++) {
      k *= 0.85
      ctx.font = fontOf(k)
    }
    lines = wrapTitleLines(ctx, raw, maxPx)
  }
  const tex = paintTitleTexture(raw, k, 0, lines)
  const img = tex.image || { width: 108, height: 108 }
  // 贴图像素 → 世界尺寸：1 行时名牌高 0.95 米（= 展厅名标签 1.9 米的一半），行数多则等比长高
  let height = 1.9 * (img.height / 108)
  let width = height * (img.width / img.height)
  if (height > EXHIBIT_LABEL_MAX_HEIGHT) {
    const s = EXHIBIT_LABEL_MAX_HEIGHT / height
    height *= s
    width *= s
  }
  return { tex, k, height, width }
}

function makeExhibitLabelSprite(id, text) {
  const { tex, height, width } = exhibitLabelTexture(text, id)
  // depthTest: false —— 和展厅名一样始终压在最上层。
  // 名牌挂在展品中心上方 1 米，比展品矮的机器会"顶到"名牌，开深度测试就会被机身吃掉。
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false, color: 0xd8ecf8 })
  const sprite = new THREE.Sprite(mat)
  sprite.scale.set(width, height, 1)
  return sprite
}

// 按当前语言重绘名牌贴图（中英文名长度不同，尺寸也要跟着变）
function applyExhibitLabelTexture(rec) {
  const lang = getLang()
  const text = (lang === 'en' ? rec.en : rec.zh) || rec.zh || rec.en
  if (!text) return
  const { tex, height, width } = exhibitLabelTexture(text, rec.id)
  const old = rec.sprite.material.map
  rec.sprite.material.map = tex
  rec.sprite.material.needsUpdate = true
  if (old && old !== tex) old.dispose()
  rec.sprite.scale.set(width, height, 1)
  rec.lang = lang
}

/**
 * 展品名标签摆哪儿：展品（几何）中心的正上方 1 米。
 * 中心取展品自身包围盒的中心，用和泛光遮挡同一套"稳健包围盒"
 * （个别模型里有零件包围盒跑飞，直接算会把中心带偏）；
 * 展品模型还没加载好时退回它在大厅里的热区中心。
 */
function exhibitLabelPosition(id, cfg) {
  const hb = hallHitboxById(id)
  const rec = hallExhibitObjs.get(String(id))
  let box = null
  if (rec && rec.root) {
    try { box = exhibitOccluderBox(rec.root) } catch { box = null }
  }
  const pos = (cfg && cfg.pos) || [0, 0, 0]
  const cx = box ? (box.min.x + box.max.x) / 2 : (hb ? hb.center.x : Number(pos[0]) || 0)
  const cz = box ? (box.min.z + box.max.z) / 2 : (hb ? hb.center.z : Number(pos[2]) || 0)
  const cy = box ? (box.min.y + box.max.y) / 2 : (hb ? hb.center.y : Number(pos[1]) || 0)
  // 1/2/7/8 展厅还要再高 1 米（编号首位就是展厅号）
  const zone = Number(String(id)[0]) || 0
  const extra = EXHIBIT_LABEL_RAISED_ZONES.indexOf(zone) >= 0 ? EXHIBIT_LABEL_EXTRA_ABOVE : 0
  return new THREE.Vector3(cx, cy + EXHIBIT_LABEL_ABOVE + extra, cz)
}

// 位置 / 显隐跟着展品走（挪展品、整体隐藏展品时一起变）
function syncExhibitLabel(id) {
  const rec = exhibitLabels.get(String(id))
  const cfg = hallExhibitCfg && hallExhibitCfg.exhibits ? hallExhibitCfg.exhibits[id] : null
  if (!rec || !cfg) return
  const on = exhibitNamesOn && hallExhibitAllOn && cfg.visible !== false
  rec.sprite.visible = on
  if (!on) return
  rec.sprite.position.copy(exhibitLabelPosition(id, cfg))
}

// 建名牌 / 改名后重绘（配置或展品名就绪、切换语言时调用）
function refreshExhibitLabels() {
  if (!hallExhibitCfg || !hallExhibitCfg.exhibits) return
  const group = ensureExhibitLabelGroup()
  const lang = getLang()
  for (const id of Object.keys(hallExhibitCfg.exhibits)) {
    const names = exhibitLabelNames(id)
    let rec = exhibitLabels.get(id)
    if (!rec) {
      if (!names.zh) continue
      const sprite = makeExhibitLabelSprite(id, lang === 'en' ? (names.en || names.zh) : names.zh)
      group.add(sprite)
      rec = { id, sprite, zh: names.zh, en: names.en, lang }
      exhibitLabels.set(id, rec)
    } else if ((names.zh && names.zh !== rec.zh) || (names.en && names.en !== rec.en) || rec.lang !== lang) {
      if (names.zh) { rec.zh = names.zh; rec.en = names.en }
      applyExhibitLabelTexture(rec)
    }
    syncExhibitLabel(id)
  }
}

// 入口界面右上角的"展品名称"按钮：一键显示 / 隐藏所有展品名牌
const btnExhibitNames = document.querySelector('#btn-exhibit-names')

function applyExhibitNamesToggle() {
  if (btnExhibitNames) {
    btnExhibitNames.classList.toggle('active', exhibitNamesOn)
    btnExhibitNames.setAttribute('aria-pressed', exhibitNamesOn ? 'true' : 'false')
  }
  for (const id of exhibitLabels.keys()) syncExhibitLabel(id)
}

btnExhibitNames?.addEventListener('click', () => {
  exhibitNamesOn = !exhibitNamesOn
  try { localStorage.setItem(EXHIBIT_NAMES_KEY, exhibitNamesOn ? '1' : '0') } catch {}
  applyExhibitNamesToggle()
  try { showToast(t(exhibitNamesOn ? 'site.exhibitNamesOn' : 'site.exhibitNamesOff')) } catch {}
})
// 标签是异步建起来的，这里先把按钮状态摆正
applyExhibitNamesToggle()

// 全息光环（ring.webp 效果）：内层细环 + 分段亮弧 + 底部光源节点 + 能量流
// ---------- 发光件强度（灯带 / 灯圈 / 发光球）----------
// 只改这几个材质自己的颜色倍率，不动灯光、雾、泛光等全局参数。
// 渲染是在 HalfFloat 的合成器里做的，颜色超过 1 依然有效：更亮、泛光也更明显。
const glowBoostMats = []
function glowColorRef(mat) {
  if (!mat) return null
  if (mat.color && mat.color.isColor) return mat.color
  const u = mat.uniforms && mat.uniforms.uColor && mat.uniforms.uColor.value
  if (u && u.isColor) return u
  return null
}
function registerGlowMat(mat) {
  if (!glowColorRef(mat)) return mat
  if (!glowBoostMats.some((e) => e.mat === mat)) glowBoostMats.push({ mat, base: glowColorRef(mat).clone() })
  return mat
}
function applyGlowBoost() {
  const el = document.querySelector('#fd-glow-i')
  const k = el ? Math.max(0, Number(el.value) || 1) : 1
  const label = document.querySelector('#fd-glow-i-v')
  if (label) label.textContent = k.toFixed(1)
  for (const e of glowBoostMats) {
    const c = glowColorRef(e.mat)
    if (!c) continue
    c.copy(e.base).multiplyScalar(k)
    if (e.mat.needsUpdate !== undefined) e.mat.needsUpdate = true
  }
}
document.querySelector('#fd-glow-i')?.addEventListener('input', applyGlowBoost)
document.querySelector('#fd-glow-i')?.addEventListener('change', applyGlowBoost)

function makeHologramRingMaterial(color, dir) {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(color) },
      uPlane: { value: new THREE.Vector2(1, 0) },
      uDir: { value: dir }
    },
    vertexShader: `
      uniform vec2 uPlane;
      varying vec2 vPos;
      void main() {
        vPos = mix(vec2(position.x, position.y), vec2(position.x, position.z), uPlane.y);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform vec3 uColor;
      uniform float uDir;
      varying vec2 vPos;
      #define PI 3.14159265
      float angDist(float a, float b) {
        return abs(mod(a - b + PI, 2.0 * PI) - PI);
      }
      float trailGlow(float d) {
        return exp(-d * 4.0) + pow(1.0 - clamp(d / 1.1, 0.0, 1.0), 2.5) * 0.5;
      }
      void main() {
        float a = atan(vPos.y, vPos.x);
        // 基环：无光处极暗
        float ring = 0.05;
        // 能量流：两个移动光点 + 拖尾
        float head = mod(uTime * 0.8 * uDir, 2.0 * PI);
        float e1 = trailGlow(angDist(a, head));
        float e2 = trailGlow(angDist(a, mod(head + PI, 2.0 * PI)));
        float bright = ring + (e1 + e2) * 1.1;
        vec3 white = vec3(0.75, 0.97, 1.0);
        float wh = clamp(e1 * 0.45 + e2 * 0.45, 0.0, 1.0);
        vec3 col = mix(uColor, white, wh) * bright;
        float alpha = clamp(bright * 0.95, 0.0, 1.0);
        gl_FragColor = vec4(col, alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide
  })
  lightTrailMats.push(mat)
  mat.userData.targetColor = mat.uniforms.uColor.value.clone()
  return mat
}

// 根据圆环所在平面设置方向（XY 或 XZ）
function setTrailPlane(mesh, mat) {
  const pos = mesh.geometry.attributes.position
  const box = new THREE.Box3().setFromBufferAttribute(pos)
  const size = box.getSize(new THREE.Vector3())
  mat.uniforms.uPlane.value.set(size.y <= size.x && size.y <= size.z ? 0 : 1, size.y <= size.x && size.y <= size.z ? 1 : 0)
}

// 外圈粒子晕：紧贴光环外侧一圈（环内保持干净），场景顶层放置
function makeRingHalo(mesh, mat, color) {
  const posAttr = mesh.geometry.attributes.position
  const box = new THREE.Box3().setFromBufferAttribute(posAttr)
  const size = box.getSize(new THREE.Vector3())
  const r = Math.max(size.x, size.y, size.z) / 2 || 1
  const plane = mat.uniforms.uPlane.value
  const worldPos = mesh.getWorldPosition(new THREE.Vector3())
  const quat = mesh.getWorldQuaternion(new THREE.Quaternion())
  const count = 600
  const positions = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    const ang = Math.random() * Math.PI * 2
    const rr = r * (1.04 + Math.pow(Math.random(), 1.2) * 0.14)
    const jitter = (Math.random() - 0.5) * r * 0.03
    let lx, ly, lz
    if (plane.y > 0.5) {
      lx = Math.cos(ang) * rr
      ly = jitter
      lz = Math.sin(ang) * rr
    } else {
      lx = Math.cos(ang) * rr
      ly = Math.sin(ang) * rr
      lz = jitter
    }
    // 跟随光环网格的缩放
    lx *= mesh.scale.x
    ly *= mesh.scale.y
    lz *= mesh.scale.z
    const v = new THREE.Vector3(lx, ly, lz).applyQuaternion(quat).add(worldPos)
    positions[i * 3] = v.x
    positions[i * 3 + 1] = v.y
    positions[i * 3 + 2] = v.z
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const pm = new THREE.PointsMaterial({
    color,
    size: 0.045,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true
  })
  const pts = new THREE.Points(geo, pm)
  scene.add(pts)
  mat.userData.haloMat = pm
}

function applyRingEffectToNode(node, color, dir) {
  const meshes = []
  node.traverse((m) => {
    if (m.isMesh) meshes.push(m)
  })
  if (!meshes.length) return
  const main = meshes[0]
  // 蓝/紫光环本体：恢复原始大小 + 发光材质，保留不动
  meshes.forEach((m) => {
    m.scale.set(1, 1, 1)
    m.material = registerGlowMat(new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }))
    m.userData.noCast = true
  })
  // 效果环：只保留一个（第一个），半径 50m，中心横向移到光球投影正下方
  fxRingCount++
  if (fxRingCount > 1) return
  const posAttr = main.geometry.attributes.position
  const box = new THREE.Box3().setFromBufferAttribute(posAttr)
  const size = box.getSize(new THREE.Vector3())
  const worldPos = main.getWorldPosition(new THREE.Vector3())
  const quat = main.getWorldQuaternion(new THREE.Quaternion())
  fxRingFloorY = worldPos.y // 记录中心地面高度，供六边形地面叠加层使用
  const mat = registerGlowMat(makeHologramRingMaterial(color, dir))
  const fxRing = new THREE.Mesh(
    new THREE.TorusGeometry(50, 0.15, 16, 256),
    mat
  )
  // 单环：青/冰蓝之间微切换（不再出现紫色）
  mat.userData.targetColor = color === 0x00e5ff ? new THREE.Color(0x00e5ff) : new THREE.Color(0x00e5ff)
  fxRing.position.set(starGlobeWorld.x, worldPos.y, starGlobeWorld.z)
  fxRing.quaternion.copy(quat)
  fxRing.castShadow = false
  fxRing.receiveShadow = false
  scene.add(fxRing)
  if (RING_HALO_ENABLED) makeRingHalo(fxRing, mat, color)
}

// ---------- 中心蜂窝发光地面（真实六边形网格线 + 流动光层） ----------
let hexFlowMat = null
const HEX_FLOOR_ENABLED = false // 六边形地面特效开关
const INFINITE_GROUND_ENABLED = false // 无限地面（草地）开关，先关闭

function addCentralHexFloor() {
  const radius = 24 // 只覆盖中心平台
  const hexSize = 1.6 / Math.sqrt(3) // 蜂窝单元中心间距 = 1.6m
  const centers = []
  const limit = Math.ceil(radius) + 3
  // 每个蜂窝的“亮度值”：低频混合噪声，形成局部亮、局部暗的斑块
  const cellBright = (q, r) => {
    // 低频大块明暗 + 中频相邻差异，让蜂窝一片亮一片暗
    const w = (q + r) * 0.42
    const w2 = (q - r) * 0.23
    let v = 0.5
    v += 0.24 * Math.sin(w + 1.7) * Math.cos(w2)
    v += 0.18 * Math.sin(q * 1.3 + 0.4) * Math.cos(r * 1.7 + 2.2)
    v += 0.26 * Math.sin(q * 3.4 + r * 2.1) * Math.cos(q * 1.1 - r * 2.7 + 4.0)
    return Math.max(0.06, Math.min(1, v))
  }
  // 点顶六边形轴向坐标 -> 世界 XZ
  for (let q = -limit; q <= limit; q++) {
    for (let r = -limit; r <= limit; r++) {
      const cx = hexSize * Math.sqrt(3) * (q + r / 2)
      const cz = hexSize * 1.5 * r
      if (Math.hypot(cx, cz) <= radius + 1) centers.push({ x: cx, z: cz, q, r, b: cellBright(q, r) })
    }
  }
  // 六边形顶点（点顶朝上：30° 起每 60° 一个）
  const verts = []
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 6 + (k * Math.PI) / 3)
    verts.push([Math.cos(a) * hexSize, Math.sin(a) * hexSize])
  }
  // 收集边并去重（每两个相邻蜂窝共享同一条边）
  const edgeMap = new Map()
  for (const cell of centers) {
    const cx = cell.x
    const cz = cell.z
    for (let k = 0; k < 6; k++) {
      const x1 = cx + verts[k][0]
      const z1 = cz + verts[k][1]
      const x2 = cx + verts[(k + 1) % 6][0]
      const z2 = cz + verts[(k + 1) % 6][1]
      const key = [[x1, z1], [x2, z2]].sort((a, b) => a[0] - b[0] || a[1] - b[1])
        .map((v) => v[0].toFixed(3) + ',' + v[1].toFixed(3)).join('|')
      const hit = edgeMap.get(key)
      if (hit) hit.vals.push(cell.b)
      else edgeMap.set(key, { x1, z1, x2, z2, vals: [cell.b] })
    }
  }
  const pts = []
  const cols = []
  edgeMap.forEach((e) => {
    const { x1, z1, x2, z2, vals } = e
    pts.push(x1, 0, z1, x2, 0, z2)
    // 边的亮度 = 相邻两个蜂窝的平均，再压出明暗范围
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length
    const lum = 0.18 + 0.82 * avg
    cols.push(lum, lum, lum, lum, lum, lum)
  })
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3))
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(cols), 3))
  const lineMat = new THREE.LineBasicMaterial({
    color: 0x00ffff,
    vertexColors: true,
    transparent: true,
    opacity: 0.22,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  })
  const floorY = (fxRingFloorY || hallFloorY) + 0.06
  const lines = new THREE.LineSegments(geo, lineMat)
  lines.position.set(starGlobeWorld.x, floorY, starGlobeWorld.z)
  lines.renderOrder = 4
  scene.add(lines)

  // 蜂窝上叠加的呼吸/扫描光层（真正的六边形线由上面的网格保证）
  hexFlowMat = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      varying vec2 vUv;
      void main() {
        vec2 q = vUv - 0.5;
        float dist = length(q) * 2.0;
        float mask = 1.0 - smoothstep(0.9, 1.0, dist);
        float pulse = 0.35 + 0.25 * sin(uTime * 1.2 - dist * 2.8);
        float scanY = fract(uTime * 0.09);
        float scan = 1.0 - smoothstep(0.0, 0.08, abs(vUv.y - scanY));
        vec3 col = vec3(0.0, 0.9, 1.0) * pulse;
        col += vec3(0.4, 0.7, 1.0) * scan * 0.5;
        float alpha = mask * (0.06 + 0.14 * pulse + 0.18 * scan);
        gl_FragColor = vec4(col, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide
  })
  const flowMesh = new THREE.Mesh(new THREE.CircleGeometry(radius, 128), hexFlowMat)
  flowMesh.rotation.x = -Math.PI / 2
  flowMesh.position.set(starGlobeWorld.x, floorY + 0.02, starGlobeWorld.z)
  flowMesh.renderOrder = 4
  scene.add(flowMesh)
  console.log('[hex-floor] honeycomb lines=' + (pts.length / 6) + ' at', starGlobeWorld.x.toFixed(1), floorY.toFixed(2), starGlobeWorld.z.toFixed(1))
}

// 无限地面：Grass008 PBR 草地（颜色/法线/粗糙度）
let infiniteGround = null
let infiniteGroundMat = null
let groundFallbackDone = false
let infiniteGroundOffset = 9.9
let infiniteColorTex = null
let infiniteNormalTex = null
let infiniteRoughTex = null
// 草地材质预设（面板上可一键切换）
const GROUND_SIZE = 2400
const GROUND_REPEAT = 420          // 每 ~5.7 米一张贴图
let grassPreset = 'mixA'
let groundBright = 1
let mixAmount = 0   // 调试期：默认只开一套草地贴图（混拼滑杆调到 1 就恢复混拼）
let vegetation = null
// 地面高度（滑杆）变化时，草/树/灌木整体跟着地面升降，避免“树悬空或陷进地面”
function syncVegetationToGround() {
  if (vegetation && vegetation.group) vegetation.group.position.y = infiniteGroundOffset
}
// 展厅周围随机种树、种草（实例化）
function ensureVegetation() {
  if (vegetation) return vegetation
  const cx = starGlobeWorld.x || 0
  const cz = starGlobeWorld.z || 0
  vegetation = createVegetation({
    center: { x: cx, z: cz },
    // 只写“地面基准高度”，地面起伏量由 group.position.y 统一控制
    groundY: hallFloorY + 0.02,
    // 展厅本体范围内不种（半径 78 米以内）
    isBlocked: (x, z) => Math.hypot(x - cx, z - cz) < 78,
    maxAniso: maxAniso(),
  })
  scene.add(vegetation.group)
  vegetation.group.position.y = infiniteGroundOffset
  vegetation.rebuild()
  // 创建后立刻套用面板里（含已保存）的参数，否则刷新后要手动拖一下滑杆才对
  try { applyVegetationParams() } catch {}
  return vegetation
}
let vegTimer = 0
function scheduleVegRebuild() {
  clearTimeout(vegTimer)
  vegTimer = setTimeout(() => { if (vegetation) vegetation.rebuild(); markShadowsDirty() }, 260)
}
const grassPresets = {
  // 只用「平原森林」素材里的草地（5 套里任选两套混拼）
  mixA: { label: '原野草地A', set: 'grass_08', other: 'field_02', roughness: 0.9, env: 0.2 },
  mixB: { label: '原野草地B', set: 'field_07', other: 'duo_field', roughness: 0.9, env: 0.2 },
  mixC: { label: '干草地', set: 'dry_01', other: 'field_07', roughness: 0.92, env: 0.15 },
  solid: {
    label: '纯色',
    color: '',
    normal: '',
    rough: '',
    height: '',
    roughness: 0.92,
    env: 0.1,
  },
}
const grassTexCache = new Map()
let _whiteTex = null
function whiteTex() {
  if (_whiteTex) return _whiteTex
  _whiteTex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat)
  _whiteTex.colorSpace = THREE.SRGBColorSpace
  _whiteTex.needsUpdate = true
  return _whiteTex
}
// 地面 shader：4 套草地按程序噪声混拼 + 视差模拟随机起伏
const groundUniforms = {
  uRepeat: { value: 420 },
  uRepeat1: { value: 420 }, uRepeat2: { value: 420 },
  uRepeat3: { value: 420 }, uRepeat4: { value: 420 },
  uTile: { value: 3.0 },
  uMixAmount: { value: 0.65 }, uMixScale: { value: 1.0 },
  uSlotCount: { value: 2 },
  uParallax: { value: 0.0 }, uRelief: { value: 1.0 },   // 视差默认关（0 = 完全不做 UV 偏移，贴图不会扭曲）
}
for (let i = 1; i <= 4; i++) {
  groundUniforms['uCol' + i] = { value: null }
  groundUniforms['uNrm' + i] = { value: null }
  groundUniforms['uRgh' + i] = { value: null }
  groundUniforms['uHgt' + i] = { value: null }
}
// 可选的草地材质组（每个槽位可从下拉里选任意一套）
const GRASS_SETS = {
  grass_08: { label: '草地08' },
  field_02: { label: '原野草地02' },
  field_07: { label: '原野草地07' },
  dry_01: { label: '干草地01' },
  duo_field: { label: '混合原野02' },
}
for (const k of Object.keys(GRASS_SETS)) {
  GRASS_SETS[k].color = withVersion(`/textures/grass_sets/${k}_color.jpg`)
  GRASS_SETS[k].normal = withVersion(`/textures/grass_sets/${k}_normal.jpg`)
  GRASS_SETS[k].rough = withVersion(`/textures/grass_sets/${k}_rough.jpg`)
  GRASS_SETS[k].height = withVersion(`/textures/grass_sets/${k}_height.jpg`)
}

// ---- 草地拼接：4 个槽位（下拉自选）+ 随机搭配 ----
const SLOT_IDS = ['md-slot-1', 'md-slot-2', 'md-slot-3', 'md-slot-4']
const SLOT_OFF = 'off'
const DEFAULT_SLOTS = ['grass_08', 'field_02', null, null]
let groundSlots = DEFAULT_SLOTS.slice()
function buildSlotSelects() {
  const opts = [`<option value="${SLOT_OFF}">— 不使用 —</option>`]
    .concat(Object.keys(GRASS_SETS).map((k) => `<option value="${k}">${GRASS_SETS[k].label}</option>`))
    .join('')
  SLOT_IDS.forEach((id, i) => {
    const el = document.querySelector('#' + id)
    if (!el) return
    el.innerHTML = opts
    el.value = groundSlots[i] || SLOT_OFF
  })
}
function readSlotsFromUI() {
  groundSlots = SLOT_IDS.map((id) => {
    const el = document.querySelector('#' + id)
    return el && el.value && el.value !== SLOT_OFF ? el.value : null
  })
}
function writeSlotsToUI() {
  SLOT_IDS.forEach((id, i) => {
    const el = document.querySelector('#' + id)
    if (el) el.value = groundSlots[i] || SLOT_OFF
  })
}
// 把当前 4 个槽位推进着色器
function applyGroundSlots() {
  if (!infiniteGroundMat) return
  const solid = grassPreset === 'solid'
  const white = whiteTex()
  for (let i = 1; i <= 4; i++) {
    const s = (!solid && groundSlots[i - 1]) ? GRASS_SETS[groundSlots[i - 1]] : null
    const has = !!(s && s.color)
    groundUniforms['uCol' + i].value = has ? grassTexture(s.color, true) : white
    groundUniforms['uNrm' + i].value = has ? grassTexture(s.normal, false) : white
    groundUniforms['uRgh' + i].value = has ? grassTexture(s.rough, false) : white
    groundUniforms['uHgt' + i].value = has ? grassTexture(s.height, false) : white
  }
  groundUniforms.uSlotCount.value = solid ? 1 : Math.max(1, groundSlots.filter(Boolean).length)
  applyGroundShader(infiniteGroundMat)   // 套数变了就换一份只带必要采样器的 shader
  infiniteGroundMat.map = null   // 颜色全部由 shader 采样 uCol1~4 混合
  infiniteGroundMat.normalMap = (!solid && groundSlots[0])
    ? grassTexture(GRASS_SETS[groundSlots[0]].normal, false) : white
  infiniteGroundMat.roughness = solid ? 0.92 : 0.9
  infiniteGroundMat.envMapIntensity = solid ? 0.1 : 0.2
  infiniteGroundMat.needsUpdate = true
  applyGroundBright()
  applyGroundShaderParams()
}
// 按面板当前状态（含存档恢复）刷新地面
function applyGroundFromUI() {
  const hidden = document.querySelector('#md-grass-preset')
  if (hidden && grassPresets[hidden.value]) grassPreset = hidden.value
  readSlotsFromUI()
  document.querySelectorAll('#md-grass-presets button').forEach((b) => {
    b.classList.toggle('active', b.dataset.grass === grassPreset)
  })
  applyGroundSlots()
}
buildSlotSelects()
// 关键：只声明"实际用到的那几套"采样器。
// 早前一版把 4 套（16 路贴图）无条件写进 shader，超过部分显卡的纹理单元上限会链接失败，
// 表现就是"整块无限地面不见了"。这里按槽位数量 1~4 动态生成，默认 2 套＝8 路。
const GROUND_CHUNK_CACHE = new Map()
const GROUND_MASK_FREQ = [0, 5.0, 3.1, 7.3]
const GROUND_MASK_OFF = [0, 11.3, 41.7, 77.1]
function groundShaderChunks(n) {
  if (GROUND_CHUNK_CACHE.has(n)) return GROUND_CHUNK_CACHE.get(n)
  const decl = []
  const rep = []
  for (let i = 1; i <= n; i++) {
    decl.push(`uniform sampler2D uCol${i}, uNrm${i}, uRgh${i}, uHgt${i};`)
    rep.push('uRepeat' + i)
  }
  const maskLines = []
  const colLines = []
  const rghLines = []
  const nrmLines = []
  for (let i = 2; i <= n; i++) {
    maskLines.push(`float m${i} = smoothstep(0.30, 0.70, gNoise(gPuv * ${GROUND_MASK_FREQ[i - 1].toFixed(1)} * uMixScale + ${GROUND_MASK_OFF[i - 1].toFixed(1)})) * gAmt;`)
    colLines.push(`gMixCol = mix(gMixCol, texture2D(uCol${i}, gPuv * uRepeat${i}).rgb, m${i});`)
    rghLines.push(`gRgh = mix(gRgh, texture2D(uRgh${i}, gPuv * uRepeat${i}).g, m${i});`)
    nrmLines.push(`gNrm = mix(gNrm, texture2D(uNrm${i}, gPuv * uRepeat${i}).xyz * 2.0 - 1.0, m${i});`)
  }
  let heightExpr = 'texture2D(uHgt1, gUv * uRepeat1).r * 0.6'
  if (n >= 2) heightExpr += '\n             + texture2D(uHgt2, gUv * uRepeat2).r * 0.25'
  if (n >= 3) heightExpr += '\n             + texture2D(uHgt3, gUv * uRepeat3).r * 0.15'
  const chunks = {
    common: `#include <common>
    ${decl.join('\n    ')}
    uniform float ${rep.join(', ')};
    uniform float uMixAmount, uMixScale, uParallax, uRelief;
    float gHash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
    float gNoise(vec2 p){
      vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
      return mix(mix(gHash(i), gHash(i + vec2(1.0, 0.0)), f.x),
                 mix(gHash(i + vec2(0.0, 1.0)), gHash(i + vec2(1.0, 1.0)), f.x), f.y);
    }`,
    map: `#include <map_fragment>
    vec2 gUv = vNormalMapUv;
    // 视差：用视空间里视线方向的横向分量近似（地面是水平面，够用且不依赖 tbn）
    vec2 gViewT = normalize(vViewPosition).xy;
    float gH = ${heightExpr}
             + gNoise(gUv * 24.0 * uMixScale) * 0.55;            // 大尺度随机起伏
    vec2 gPuv = gUv - gViewT * (gH - 0.6) * (0.05 * uParallax * uRelief);
    float gAmt = clamp(uMixAmount, 0.0, 1.0);
    ${maskLines.join('\n    ')}
    vec3 gMixCol = texture2D(uCol1, gPuv * uRepeat1).rgb;
    ${colLines.join('\n    ')}
    diffuseColor.rgb *= gMixCol;`,
    rough: `#include <roughnessmap_fragment>
    float gRgh = texture2D(uRgh1, gPuv * uRepeat1).g;
    ${rghLines.join('\n    ')}
    roughnessFactor = gRgh;`,
    normal: `#include <normal_fragment_maps>
    #ifdef USE_NORMALMAP_TANGENTSPACE
      vec3 gNrm = texture2D(uNrm1, gPuv * uRepeat1).xyz * 2.0 - 1.0;
      ${nrmLines.join('\n      ')}
      normal = normalize(tbn * normalize(gNrm));
    #endif`,
  }
  GROUND_CHUNK_CACHE.set(n, chunks)
  return chunks
}
let groundShaderSlots = 0
function applyGroundShader(mat) {
  if (groundFallbackDone) return       // 已经退回普通材质了，别再塞自定义 shader
  const n = Math.max(1, Math.min(4, Number(groundUniforms.uSlotCount.value) | 0))
  if (groundShaderSlots === n && mat.onBeforeCompile) return   // 套数没变就不用重编
  groundShaderSlots = n
  const chunks = groundShaderChunks(n)
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, groundUniforms)
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', chunks.common)
      .replace('#include <map_fragment>', chunks.map)
      .replace('#include <roughnessmap_fragment>', chunks.rough)
      .replace('#include <normal_fragment_maps>', chunks.normal)
  }
  // 套数变了要换一份程序缓存，否则会沿用旧的（采样器数量对不上）
  mat.customProgramCacheKey = () => 'ground-slots-' + n
  mat.needsUpdate = true
}
function grassTexture(url, srgb) {
  if (!url) return null
  const key = url
  if (grassTexCache.has(key)) return grassTexCache.get(key)
  const t = new THREE.TextureLoader().load(url)
  t.wrapS = THREE.RepeatWrapping
  t.wrapT = THREE.RepeatWrapping
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  t.repeat.set(GROUND_REPEAT, GROUND_REPEAT)
  t.anisotropy = maxAniso()
  grassTexCache.set(key, t)
  return t
}
function addInfiniteGrassGround() {
  if (infiniteGround) return
  const loader = new THREE.TextureLoader()
  const loadRepeat = (src, colorSpace) => {
    const t = loader.load(src)
    t.wrapS = THREE.RepeatWrapping
    t.wrapT = THREE.RepeatWrapping
    t.colorSpace = colorSpace || THREE.SRGBColorSpace
    return t
  }
  const size = GROUND_SIZE
  const geo = new THREE.PlaneGeometry(size, size)
  // 贴图与混拼都交给自定义 shader：这里只留一张法线（提供切线基 tbn）
  infiniteGroundMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: grassTexture(grassPresets.mixA.color, true),
    normalMap: grassTexture(grassPresets.mixA.normal, false),
    roughness: 1.0,
    metalness: 0,
    envMapIntensity: 0.25,
    // 单面：草地是"背景地面"，从下面看的时候就该看不见。
    // （原来是双面，镜头一旦低于草地高度，就会在头顶看到一整块绿色"天花板"）
    side: THREE.FrontSide,
    receiveShadow: true
  })
  // 混拼 + 视差 shader（贴图在 applyGrassPreset 里绑到 uniform 上，这里不要再用 map 叠加）
  applyGroundShader(infiniteGroundMat)
  // 兜底：万一某些显卡上自定义草地 shader 链接失败，自动退回普通草地材质，
  // 宁可少点混拼效果，也不能让地面整块消失
  if (renderer && renderer.debug) {
    renderer.debug.checkShaderErrors = true
    renderer.debug.onShaderError = (gl, program, vsh, fsh) => {
      if (groundFallbackDone) return
      // 只认领"草地地面"这一份 shader，别把别的材质也一起处理了
      let isGround = false
      try {
        const src = [vsh, fsh].map((s) => (s ? String(gl.getShaderSource(s) || '') : '')).join('\n')
        isGround = src.includes('uCol1')
      } catch {}
      if (!isGround) return
      groundFallbackDone = true
      let log = ''
      try { log = gl.getProgramInfoLog(program) || '' } catch {}
      console.warn('[ground] 自定义草地 shader 编译失败，已切回普通草地材质：' + log.slice(0, 240))
      const key = groundSlots[0] || 'grass_08'
      const set = GRASS_SETS[key] || GRASS_SETS.grass_08
      infiniteGroundMat.onBeforeCompile = () => {}
      infiniteGroundMat.customProgramCacheKey = () => 'ground-fallback'
      infiniteGroundMat.map = grassTexture(set.color, true)
      infiniteGroundMat.normalMap = grassTexture(set.normal, false)
      infiniteGroundMat.color.setHex(0xffffff).multiplyScalar(Math.max(0.05, groundBright))
      infiniteGroundMat.needsUpdate = true
      try { showToast('草地着色器不兼容，已自动切回基础草地材质') } catch {}
    }
  }
  infiniteGround = new THREE.Mesh(geo, infiniteGroundMat)
  infiniteGround.name = 'InfiniteGround'
  infiniteGround.receiveShadow = true   // 注意：receiveShadow 要设在"网格"上，设在材质上无效（之前就是这个坑）
  infiniteGround.castShadow = false
  infiniteGround.rotation.x = -Math.PI / 2
  infiniteGround.position.set(starGlobeWorld.x || 0, hallFloorY + infiniteGroundOffset, starGlobeWorld.z || 0)
  infiniteGround.frustumCulled = false
  scene.add(infiniteGround)
  // 按面板当前状态（含刚恢复的存档）装配草地，而不是无条件套默认预设
  applyGroundFromUI()
  ensureVegetation()
  console.log('[infinite-ground] grass added at y=' + (hallFloorY + infiniteGroundOffset).toFixed(2))
}

// 切换草地材质预设（按钮 / 存档恢复都走这里）
function applyGrassPreset(name) {
  const key = grassPresets[name] ? name : 'mixA'
  grassPreset = key
  const p = grassPresets[key]
  const hidden = document.querySelector('#md-grass-preset')
  if (hidden) hidden.value = key
  // 预设＝帮你在 4 个槽位里填好前两个，槽位 3/4 留空
  groundSlots = [p.set || null, p.other || null, null, null]
  writeSlotsToUI()
  document.querySelectorAll('#md-grass-presets button').forEach((b) => {
    b.classList.toggle('active', b.dataset.grass === key)
  })
  if (!infiniteGroundMat) return
  applyGroundSlots()
  console.log('[infinite-ground] 草地材质切到', p.label)
}

// 随机搭配：从几套草地里随机挑 3~4 套填进槽位，并随机一个混拼强度
function randomGroundSlots() {
  const pool = Object.keys(GRASS_SETS)
  const n = 3 + Math.floor(Math.random() * 2)          // 3 或 4 套
  const picked = []
  while (picked.length < n && pool.length) {
    picked.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0])
  }
  while (picked.length < 4) picked.push(null)
  groundSlots = picked
  writeSlotsToUI()
  if (mdMixAmount) {
    const target = (0.45 + Math.random() * 0.4).toFixed(2)   // 0.45 ~ 0.85
    mdMixAmount.value = target
    mdMixAmount.dispatchEvent(new Event('input', { bubbles: true }))
  }
  if (mdMixScale) {
    mdMixScale.value = (0.7 + Math.random() * 1.0).toFixed(1)
    mdMixScale.dispatchEvent(new Event('input', { bubbles: true }))
  }
  applyGroundSlots()
  const names = picked.filter(Boolean).map((k) => GRASS_SETS[k].label).join(' + ')
  console.log('[infinite-ground] 随机搭配:', names)
  try { showToast('草地随机搭配：' + names) } catch {}
  try { saveDebugParams() } catch {}
}

// 地面亮度 / 高光强度
function applyGroundBright() {
  if (!infiniteGroundMat) return
  if (mdGroundBright) groundBright = Number(mdGroundBright.value)
  const base = grassPreset === 'solid'
    ? new THREE.Color(mdGrassColor ? mdGrassColor.value : '#5a7a3a')
    : new THREE.Color(0xffffff)
  infiniteGroundMat.color.copy(base).multiplyScalar(Math.max(0.05, groundBright))
  infiniteGroundMat.needsUpdate = true
}
document.querySelectorAll('#md-grass-presets button').forEach((b) => {
  b.addEventListener('click', () => {
    applyGrassPreset(b.dataset.grass)
    try { saveDebugParams() } catch {}
  })
})
// 槽位下拉：改哪一个就立刻重拼（槽位 2~4 若混拼强度太低自动提起来，免得"看不出变化"）
SLOT_IDS.forEach((id, i) => {
  const el = document.querySelector('#' + id)
  if (!el) return
  el.addEventListener('change', () => {
    readSlotsFromUI()
    if (i > 0 && mdMixAmount && Number(mdMixAmount.value) < 0.3) {
      mdMixAmount.value = '0.6'
      mdMixAmount.dispatchEvent(new Event('input', { bubbles: true }))
    }
    applyGroundSlots()
    try { saveDebugParams() } catch {}
  })
})
document.querySelector('#md-slot-random')?.addEventListener('click', randomGroundSlots)
document.querySelector('#md-slot-default')?.addEventListener('click', () => {
  applyGrassPreset('mixA')
  try { saveDebugParams() } catch {}
  try { showToast('草地已恢复默认搭配') } catch {}
})
const mdGroundBright = document.querySelector('#md-ground-bright')
mdGroundBright?.addEventListener('input', () => {
  groundBright = Number(mdGroundBright.value)
  applyGroundBright()
})
// ---- 混拼 / 视差 滑杆 ----
const mdGroundTile = document.querySelector('#md-ground-tile')
// 上传自己的草地贴图（本地文件，本次会话有效）
function useLocalGrass(input, slot) {
  const f = input && input.files && input.files[0]
  if (!f) return
  const url = URL.createObjectURL(f)
  groundUniforms['uCol' + slot].value = grassTexture(url, true)
  if (slot === 2 && mdMixAmount && Number(mdMixAmount.value) < 0.3) {
    mdMixAmount.value = 0.6
  }
  applyGroundShaderParams()
  console.log('[ground] 草地' + (slot === 1 ? 'A' : 'B') + ' 换成上传图:', f.name)
  try { showToast('草地' + (slot === 1 ? 'A' : 'B') + ' 已换成：' + f.name) } catch {}
}
document.querySelector('#md-grass-file-a')?.addEventListener('change', (e) => useLocalGrass(e.target, 1))
document.querySelector('#md-grass-file-b')?.addEventListener('change', (e) => useLocalGrass(e.target, 2))
const mdMixAmount = document.querySelector('#md-mix-amount')
const mdMixScale = document.querySelector('#md-mix-scale')
const mdParallax = document.querySelector('#md-parallax')
const mdRelief = document.querySelector('#md-relief')
function applyGroundShaderParams() {
  if (mdMixAmount) mixAmount = Number(mdMixAmount.value)
  // 每块草地贴图占多少米：直接决定"看得清不清楚"
  if (mdGroundTile) {
    const tile = Math.max(0.5, Number(mdGroundTile.value))
    const rep = GROUND_SIZE / tile
    for (let i = 1; i <= 4; i++) groundUniforms['uRepeat' + i].value = rep
  }
  if (mdMixScale) groundUniforms.uMixScale.value = Number(mdMixScale.value)
  if (mdParallax) groundUniforms.uParallax.value = Number(mdParallax.value)
  if (mdRelief) groundUniforms.uRelief.value = Number(mdRelief.value)
  groundUniforms.uMixAmount.value = grassPreset === 'solid' ? 0 : mixAmount
}
for (const el of [mdGroundTile, mdMixAmount, mdMixScale, mdParallax, mdRelief]) {
  el?.addEventListener('input', applyGroundShaderParams)
  el?.addEventListener('change', applyGroundShaderParams)
}
// ---- 树 / 草 滑杆 ----
const vegEls = {
  treeOn: document.querySelector('#md-tree-on'),
  treeCount: document.querySelector('#md-tree-count'),
  treeScale: document.querySelector('#md-tree-scale'),
  grassOn: document.querySelector('#md-grass-on'),
  grassCount: document.querySelector('#md-grass-count'),
  grassScale: document.querySelector('#md-grass-scale'),
  radius: document.querySelector('#md-veg-radius'),
  treeSeed: document.querySelector('#md-tree-seed'),
  shrubOn: document.querySelector('#md-shrub-on'),
  shrubCount: document.querySelector('#md-shrub-count'),
  shrubScale: document.querySelector('#md-shrub-scale'),
  shrubRadius: document.querySelector('#md-shrub-radius'),
  treeA: document.querySelector('#md-tree-a'),
  treeB: document.querySelector('#md-tree-b'),
  treeC: document.querySelector('#md-tree-c'),
}
for (let k = 1; k <= 7; k++) {
  vegEls['shrub' + k] = document.querySelector('#md-shrub-v' + k)
}
function applyVegetationParams() {
  if (!vegetation) return
  const s = vegetation.state
  const r = vegEls.radius ? Number(vegEls.radius.value) : 460
  if (vegEls.treeOn) s.trees.on = vegEls.treeOn.checked
  if (vegEls.treeCount) s.trees.count = Number(vegEls.treeCount.value)
  if (vegEls.treeScale) { const v = Number(vegEls.treeScale.value); s.trees.scaleMin = v * 0.75; s.trees.scaleMax = v * 1.3 }
  if (vegEls.grassOn) s.grass.on = vegEls.grassOn.checked
  if (vegEls.grassCount) s.grass.count = Number(vegEls.grassCount.value)
  if (vegEls.grassScale) { const v = Number(vegEls.grassScale.value); s.grass.scaleMin = v * 0.6; s.grass.scaleMax = v * 1.4 }
  s.trees.maxR = r
  s.grass.maxR = r
  // 矮树单独一个分布半径：它个头小，撒太远等于看不见，默认收在近处
  s.shrubs.maxR = vegEls.shrubRadius ? Number(vegEls.shrubRadius.value) : r
  if (s.shrubs.maxR < s.shrubs.minR) s.shrubs.maxR = s.shrubs.minR
  if (vegEls.treeSeed) s.trees.seed = Number(vegEls.treeSeed.value)
  if (vegEls.shrubOn) s.shrubs.on = vegEls.shrubOn.checked
  if (vegEls.shrubCount) s.shrubs.count = Number(vegEls.shrubCount.value)
  if (vegEls.shrubScale) { const v = Number(vegEls.shrubScale.value); s.shrubs.scaleMin = v * 0.6; s.shrubs.scaleMax = v * 1.5 }
  s.shrubs.enabled = Array.from({ length: 7 }, (_, i) => (vegEls['shrub' + (i + 1)] ? vegEls['shrub' + (i + 1)].checked : true))
  // 树变体开关（GLB 里按 a/b/c 顺序）
  s.trees.enabled = [
    vegEls.treeA ? vegEls.treeA.checked : true,
    vegEls.treeB ? vegEls.treeB.checked : true,
    vegEls.treeC ? vegEls.treeC.checked : true,
  ]
  scheduleVegRebuild()
}
for (const el of Object.values(vegEls)) {
  el?.addEventListener('input', applyVegetationParams)
  el?.addEventListener('change', applyVegetationParams)
}
// 调试：查看无限地面当前用的材质
window.__opt2 = Object.assign(window.__opt2 || {}, {
  groundInfo: () => ({
    preset: grassPreset,
    bright: groundBright,
    map: (infiniteGroundMat && infiniteGroundMat.map && (infiniteGroundMat.map.name || infiniteGroundMat.map.image?.src || '')) || '',
    normal: !!(infiniteGroundMat && infiniteGroundMat.normalMap),
    rough: !!(infiniteGroundMat && infiniteGroundMat.roughnessMap),
    env: infiniteGroundMat ? infiniteGroundMat.envMapIntensity : null,
    color: infiniteGroundMat ? '#' + infiniteGroundMat.color.getHexString() : null,
    visible: !!(infiniteGround && infiniteGround.visible),
  }),
})

// 给展区地面外轮廓加发光描边（参考图里的区域霓虹边）
function addZoneGlowEdge(mesh) {
  if (!mesh || !mesh.geometry || !mesh.geometry.attributes.position) return
  let edges
  try {
    edges = new THREE.EdgesGeometry(mesh.geometry, 15)
  } catch {
    return
  }
  if (!edges.attributes.position || edges.attributes.position.count < 2) return
  const mat = new THREE.LineBasicMaterial({
    color: 0x00e5ff,
    transparent: true,
    opacity: 0.65,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  })
  const line = new THREE.LineSegments(edges, mat)
  mesh.updateWorldMatrix(true, false)
  line.applyMatrix4(mesh.matrixWorld)
  line.renderOrder = 6
  scene.add(line)
}

// 灯带辉光层：同色半透明加法叠加，保持原色但光感更强
function addLightStripGlow(mesh, color) {
  if (!mesh || !mesh.geometry) return
  const glowMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.45,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  })
  const glow = mesh.clone()
  glow.material = glowMat
  glow.castShadow = false
  glow.receiveShadow = false
  mesh.updateWorldMatrix(true, false)
  glow.applyMatrix4(mesh.matrixWorld)
  glow.renderOrder = 2
  scene.add(glow)
}

function applyVisualEffects() {
  if (!modelRoot) return
  floorGeoUse = null // 模型可能刚换过，重新统计 geometry 复用情况
  const mainTex = makeMainScreenTexture()
  const hudTex = makeHUDScreenTexture()
  const screenFxMain = makeScreenFxMaterial(mainTex)
  const screenFxHud = makeScreenFxMaterial(hudTex)
  // HUD 大屏贴图：默认走 images/hud-sd/（最长边 2048，约 2.2 MB / 73 MB 显存）；
  // 原图在 images/hud*.jpg（27.96 MB / 415 MB 显存），仍然被灯箱和 HUD4-6 的点击大图用着。
  // 想看原图效果对比：地址后加 ?hud=hd
  const HUD_DIR = new URLSearchParams(location.search).get('hud') === 'hd' ? 'images/' : 'images/hud-sd/'
  // HUD1：使用外部图片（TextureLoader，可靠加载）
  const hud1Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud1.jpg',
    () => console.log('[hud1] loaded'),
    undefined,
    () => console.log('[hud1] load FAILED')
  )
  hud1Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud1 = makeScreenFxMaterial(hud1Tex)
  // HUD2：外部图片
  const hud2Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud2.jpg',
    () => console.log('[hud2] loaded'),
    undefined,
    () => console.log('[hud2] load FAILED')
  )
  hud2Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud2 = makeScreenFxMaterial(hud2Tex)
  // HUD3：外部图片
  const hud3Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud3.jpg',
    () => console.log('[hud3] loaded'),
    undefined,
    () => console.log('[hud3] load FAILED')
  )
  hud3Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud3 = makeScreenFxMaterial(hud3Tex)
  // HUD4：外部图片
  const hud4Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud4.jpg',
    () => console.log('[hud4] loaded'),
    undefined,
    () => console.log('[hud4] load FAILED')
  )
  hud4Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud4 = makeScreenFxMaterial(hud4Tex)
  // HUD5：外部图片
  const hud5Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud5.jpg',
    () => console.log('[hud5] loaded'),
    undefined,
    () => console.log('[hud5] load FAILED')
  )
  hud5Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud5 = makeScreenFxMaterial(hud5Tex)
  // HUD6：外部图片
  const hud6Tex = new THREE.TextureLoader().load(
    HUD_DIR + 'hud6.jpg',
    () => console.log('[hud6] loaded'),
    undefined,
    () => console.log('[hud6] load FAILED')
  )
  hud6Tex.colorSpace = THREE.SRGBColorSpace
  const screenFxHud6 = makeScreenFxMaterial(hud6Tex)
  // 屏幕自身发光（不进灯带泛光组）。修正颜色空间后，uDim≈1 就是"原图亮度"，
  // 1.3 / 1.6 就是比原图再亮 30%~60%。这三个值都跟着"特效调试"面板走。
  const SCREEN_BRIGHT_HUD = fdScrI ? Number(fdScrI.value) : 1.3
  const SCREEN_BRIGHT_MAIN = fdScrI2 ? Number(fdScrI2.value) : 1.6
  const SCREEN_SOLID = fdScrSol ? Number(fdScrSol.value) : 0.85
  screenFxMain.uniforms.uDim.value = SCREEN_BRIGHT_MAIN
  screenFxHud.uniforms.uDim.value = SCREEN_BRIGHT_MAIN
  screenFxHud1.uniforms.uFlipX.value = 1 // 镜像到正面
  screenFxHud1.uniforms.uOpaque.value = 0 // 允许半透明
  screenFxHud1.uniforms.uWhiteAlpha.value = 0.3 // 白色背景半透明（低透明度=更实）
  screenFxHud1.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxHud2.uniforms.uFlipX.value = 1
  screenFxHud2.uniforms.uOpaque.value = 0
  screenFxHud2.uniforms.uWhiteAlpha.value = 0.3
  screenFxHud2.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxHud2.uniforms.uFit.value = 0.8 // 图片长度调成 0.8
  screenFxHud2.uniforms.uOffsetY.value = 0.06 // 图片向下移动一点
  screenFxHud2.uniforms.uScaleX.value = 2.0 // 横向加宽
  screenFxHud2.uniforms.uOffsetX.value = 0.25 // 横向偏移微调
  screenFxHud2.uniforms.uBlackAlpha.value = 0.5 // 黑色背景轻微半透明，内容清晰可见
  screenFxHud3.uniforms.uFlipX.value = 1
  screenFxHud3.uniforms.uOpaque.value = 0
  screenFxHud3.uniforms.uWhiteAlpha.value = 0.3
  screenFxHud3.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxHud4.uniforms.uFlipX.value = 1
  screenFxHud4.uniforms.uOpaque.value = 0
  screenFxHud4.uniforms.uWhiteAlpha.value = 0.3
  screenFxHud4.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxHud4.uniforms.uFit.value = 1.0 // 保持原始比例
  screenFxHud4.uniforms.uScaleX.value = 5.0 // 横向拉伸
  screenFxHud4.uniforms.uOffsetX.value = 0.38 // 横向偏移 0.38
  screenFxHud5.uniforms.uFlipX.value = 1
  screenFxHud5.uniforms.uOpaque.value = 0
  screenFxHud5.uniforms.uWhiteAlpha.value = 0.3
  screenFxHud5.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxHud6.uniforms.uFlipX.value = 1 // 与其余 HUD 一致，避免前后贴反
  screenFxHud6.uniforms.uOpaque.value = 0
  screenFxHud6.uniforms.uWhiteAlpha.value = 0.3
  screenFxHud6.uniforms.uDim.value = SCREEN_BRIGHT_HUD
  screenFxMain.userData.isMain = true
  screenFxMaterials.push(screenFxMain, screenFxHud, screenFxHud1, screenFxHud2, screenFxHud3, screenFxHud4, screenFxHud5, screenFxHud6)
  // 统一压掉"能透出背景"的透明感（主屏本来就是不透的，这里对它是无操作）
  for (const m of screenFxMaterials) {
    if (m.uniforms && m.uniforms.uSolid) {
      const isMain = !!(m.userData && m.userData.isMain)
      m.uniforms.uSolid.value = Math.min(1, SCREEN_SOLID + (isMain ? SCREEN_MAIN_EXTRA_SOLID : 0))
    }
  }

  // 通用处理：按节点名找到后，应用到其所有子网格（兼容直接网格/父级挂子网格两种结构）
  const applyToMeshes = (o, setMat) => {
    o.traverse((child) => {
      if (child.isMesh) setMat(child)
    })
  }
  // 手机端：真折射（transmission）开销大，降级为半透明玻璃
  // 玻璃"真折射"开关。打开时 three 会把整个场景多画一遍（性能开销约占三分之一）。
  // 关掉之后玻璃只剩自己的颜色叠在背景上，所以必须同时把不透明度压到很低，
  // 否则就会变成一层白膜把里面的展品冲淡（＝"模型看不见了"）。
  //   想改回真折射：把 GLASS_REFRACTION 改成 true，并把两个 opacity 改回 0.55
  const GLASS_REFRACTION = false
  // 手机端也用和电脑一样的玻璃参数：折射早就关了，两者其实只剩"不透明度"差别，
  // 而 0.45 的不透明度会把玻璃后面的展品冲淡（手机端看着更糊就是这个原因）。
  const glassMat = false
    ? new THREE.MeshStandardMaterial({
        color: 0x9fd8ff,
        metalness: 0,
        roughness: 0.2,
        transparent: true,
        opacity: 0.45,
        envMapIntensity: 0.8,
        depthWrite: false,
        side: THREE.DoubleSide
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0x9fd8ff,
        metalness: 0,
        roughness: 0.08,
        transmission: GLASS_REFRACTION ? 1.0 : 0,
        thickness: 0.05,
        ior: 1.4,
        transparent: true,
        opacity: GLASS_REFRACTION ? 0.55 : 0.18,
        envMapIntensity: 0.8,
        depthWrite: false,
        side: THREE.DoubleSide
      })
  const lightStripMat = registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00ffff, toneMapped: false }))
  const lightStripGlassMat = registerGlowMat(new THREE.MeshBasicMaterial({ color: 0x00ffff, toneMapped: false }))
  matPedestal = new THREE.MeshStandardMaterial({
    color: 0x10253f,
    metalness: 0.3,
    roughness: 0.6,
    envMapIntensity: 1.0,
    emissive: 0x000000,
    emissiveIntensity: 0
  })
  const techBlueFloorMat = new THREE.MeshPhysicalMaterial({ color: 0x16395e, metalness: 0.08, roughness: 0.58, envMapIntensity: 1.0, clearcoat: 0.3, clearcoatRoughness: 0.4 })
  matGrayFloor = new THREE.MeshPhysicalMaterial({ color: 0x14365f, metalness: 0.5, roughness: 0.4, envMapIntensity: 1.2, clearcoat: 0.25, clearcoatRoughness: 0.4 })
  matLeatherFloor = new THREE.MeshPhysicalMaterial({ color: 0x1b3456, metalness: 0.45, roughness: 0.45, envMapIntensity: 1.1, clearcoat: 0.25, clearcoatRoughness: 0.45, map: makeSilverLeatherTexture() })
  const topGlassMat = new THREE.MeshStandardMaterial({ color: 0x143055, metalness: 0.5, roughness: 0.35, envMapIntensity: 1.2 })
  glassFloorHoloMat = false
    ? new THREE.MeshStandardMaterial({
        color: 0x14375f,
        metalness: 0.5,
        roughness: 0.4,
        transparent: true,
        opacity: 0.45,
        emissive: 0x000000,
        emissiveIntensity: 0,
        envMapIntensity: 1.2,
        depthWrite: false,
        side: THREE.DoubleSide
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0x14375f,
        metalness: 0.5,
        roughness: 0.4,
        transmission: GLASS_REFRACTION ? 0.2 : 0,
        thickness: 0.1,
        ior: 1.4,
        transparent: true,
        opacity: GLASS_REFRACTION ? 0.55 : 0.28,
        emissive: 0x000000,
        emissiveIntensity: 0,
        envMapIntensity: 1.2,
        clearcoat: 0.2,
        clearcoatRoughness: 0.4,
        depthWrite: false,
        side: THREE.DoubleSide
      })
  modelRoot.traverse((o) => {
    // three.js 读 glTF 会把名字里的空格换成下划线、去掉点（"  Floor_Side" → "__Floor_Side"），
    // 先还原再匹配，否则这类地面会被漏掉、保持模型自带的调色板占位色
    const n = (o.name || '').replace(/^[_ .]+/, '')
    if (/^Screen_Main/i.test(n)) return applyToMeshes(o, (m) => {
      ensureScreenUV(m)
      // 等比适配：按屏幕世界宽高比 vs 贴图宽高比(2:1) 缩放 v
      const box = new THREE.Box3().setFromObject(m)
      const ws = box.getSize(new THREE.Vector3())
      const width = Math.max(ws.x, ws.z)
      const height = ws.y || 1
      screenFxMain.uniforms.uFit.value = Math.min(2, Math.max(0.3, (width / height) / 2 * 0.92 * 0.8 * 1.2 * 1.5 * 1.2 * 0.9))
      screenFxMain.uniforms.uFlipX.value = 1 // 默认贴正面（水平镜像）
      setChamferFromMesh(screenFxMain, m)
      m.material = screenFxMain
      m.userData.noCast = true
      screenFxMain.userData.mesh = m
      console.log('[screen-main] applied to', m.name, 'aspect', (width / height).toFixed(2), 'fit', screenFxMain.uniforms.uFit.value.toFixed(2))
    })
    if (/^Screen_HUD_01/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud1; setChamferFromMesh(screenFxHud1, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud1-link.jpg'; hud1Meshes.push(m); console.log('[hud1] applied to', m.name) })
    if (/^Screen_HUD_02/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud2; setChamferFromMesh(screenFxHud2, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud2-link.jpg'; hud2Meshes.push(m); console.log('[hud2] applied to', m.name) })
    if (/^Screen_HUD_03/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud3; setChamferFromMesh(screenFxHud3, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud3-link.jpg'; hud3Meshes.push(m); console.log('[hud3] applied to', m.name) })
    if (/^Screen_HUD_04/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud4; setChamferFromMesh(screenFxHud4, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud4.jpg'; hud4Meshes.push(m); console.log('[hud4] applied to', m.name) })
    if (/^Screen_HUD_05/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud5; setChamferFromMesh(screenFxHud5, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud5.jpg'; hud5Meshes.push(m); console.log('[hud5] applied to', m.name) })
    if (/^Screen_HUD_06/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud6; setChamferFromMesh(screenFxHud6, m); m.userData.noCast = true; m.userData.hud1Link = 'images/hud6.jpg'; hud6Meshes.push(m); console.log('[hud6] applied to', m.name) })
    if (/^Screen_Data/i.test(n)) return applyToMeshes(o, (m) => {
      // 按要求隐藏模型里的 Screen_Data 屏幕（保留 nodes，不做材质/贴图处理）
      m.visible = false
      m.userData.noCast = true
    })
    if (/^Screen_HUD/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = screenFxHud; setChamferFromMesh(screenFxHud, m); m.userData.noCast = true })
    if (/^Screen_Video/i.test(n)) return applyToMeshes(o, (m) => {
      m.userData.noCast = true
      registerPlantScreen(m)
    })
    if (/^GlassWall/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = glassMat; m.userData.noCast = true; m.userData.glassType = 'wall' })
    if (/^TopGlass/i.test(n)) return applyToMeshes(o, (m) => { m.material = topGlassMat; m.userData.noCast = true })
    if (/^GlassFloor/i.test(n)) return applyToMeshes(o, (m) => { ensureScreenUV(m); m.material = glassFloorHoloMat; m.userData.noCast = true; m.userData.glassType = 'floor' })
    if (/^LightStrip_Glass/i.test(n)) return applyToMeshes(o, (m) => { m.material = lightStripGlassMat; m.userData.noCast = true })
    if (/^LightStrip/i.test(n)) return applyToMeshes(o, (m) => { m.material = lightStripMat; m.userData.noCast = true })
    if (/^Light_Circle_Blue/i.test(n)) return applyRingEffectToNode(o, 0x00e5ff, 1)
    if (/^Light_Circle_Purple/i.test(n)) return applyRingEffectToNode(o, 0x00e5ff, -1)
    if (/^Floor_Central/i.test(n)) return applyToMeshes(o, (m) => { m.material = matGrayFloor; m.userData.isFloor = true })
    // 只有 FLoor.001 这层铺地砖；Floor_Dark / Floor_Corner / Floor_Side 都是素色，不带方格
    if (/^Floor_Dark/i.test(n)) return applyToMeshes(o, (m) => { m.material = matLeatherFloor; m.userData.isFloor = true })
    if (/^Floor_Corner/i.test(n)) {
      // 四角展厅的底板：浅灰素色（色板"四角浅灰"可调），不铺方格
      applyToMeshes(o, (m) => {
        const mat = makeFloorPlainMaterial(mdCorner ? mdCorner.value : '#c0c8d6')
        m.material = mat
        debugFloorMats.corner.push(mat)
        m.userData.isFloor = true
      })
      return
    }
    if (/^Floor_Side/i.test(n)) {
      // 展厅侧面底板：素色（色板"四向深地面"可调），不铺方格
      applyToMeshes(o, (m) => {
        const mat = makeFloorPlainMaterial(mdZone ? mdZone.value : '#6b6b6b')
        m.material = mat
        debugFloorMats.dark.push(mat)
        m.userData.isFloor = true
      })
      return
    }
    if (/^Floor_Zone(\d+)/i.test(n)) {
      // 按展区拆分的独立地面：1/3/5/7 角落展区用浅灰，其余保持现有灰
      const zoneNum = Number(n.match(/^Floor_Zone(\d+)/i)?.[1] || 0)
      applyToMeshes(o, (m) => {
        const light = [1, 3, 5, 7].includes(zoneNum)
        const zoneTone = {
          2: '#17305a', 4: '#12284a', 6: '#1d3a68', 8: '#10203c'
        }
        paintFloorMesh(m, light ? (mdCorner ? mdCorner.value : '#8a94a6') : (zoneTone[zoneNum] || '#49566a'), true,
          light ? debugFloorMats.light : debugFloorMats.dark)
      })
      return
    }
    if (/^Floor/i.test(n)) {
      // 其它地面统一铺砖：方格 + 发光砖缝
      applyToMeshes(o, (m) => paintFloorMesh(m, mdZone ? mdZone.value : undefined, true, debugFloorMats.dark))
      return
    }
    if (/^Pedestal/i.test(n)) return applyToMeshes(o, (m) => { m.material = matPedestal })
  })

  // 兜底：把没按 Floor_* 命名的地面（Geom3D.* 等）也铺上同一套砖纹，消除"一块绿一块灰"的打架斑
  paintLeftoverFloors()

  // 阴影关系：地面接收阴影，屏幕/玻璃/灯带不投影，其余网格投影
  modelRoot.traverse((m) => {
    if (!m.isMesh) return
    if (m.userData.isFloor) {
      m.castShadow = false
      m.receiveShadow = true
      return
    }
    m.castShadow = !m.userData.noCast
    m.receiveShadow = false
  })

  // 缓存点击检测目标（避免每次点击都遍历模型）
  clickTargets = []
  modelRoot.traverse((m) => {
    if (m.isMesh) clickTargets.push(m)
  })

  // Title_* 空物体 → 悬浮标题
  modelRoot.traverse((o) => {
    const n = o.name || ''
    if (!/^Title_/i.test(n)) return
    const text = n.replace(/^Title_/i, '')
    if (!text) return
    const pos = o.getWorldPosition(new THREE.Vector3())
    // 就近匹配展区（用于点击跳转与高亮）
    let bestZone = null
    let bestD = Infinity
    for (let z = 1; z <= 8; z++) {
      const t = camPoints['CamTarget1' + z]
      if (!t) continue
      const d = Math.hypot(t.x - pos.x, t.z - pos.z)
      if (d < bestD) {
        bestD = d
        bestZone = z
      }
    }
    const zh = (bestZone && ZONE_TITLE_ZH[bestZone]) || text
    const en = (bestZone && ZONE_TITLE_EN[bestZone]) || TITLE_EN[text] || text
    const sprite = makeTitleSprite(getLang() === 'en' ? en : zh)
    sprite.position.copy(pos)
    sprite.userData.zone = bestZone
    scene.add(sprite)
    titleSprites.push({ sprite, zone: bestZone, text, zh, en })
    // 用真实展厅名更新展区名称（toast / 地图提示等使用）
    if (bestZone && ZONES[bestZone]) ZONES[bestZone].name = zh
  })
  // 全局关闭模型自带的自发光（只剩真实灯光 + 屏幕类 shader 发光）
  modelRoot.traverse((m) => {
    if (!m.isMesh) return
    const mat = m.material
    if (mat && mat.emissive) {
      mat.emissive.setHex(0x000000)
      if ('emissiveIntensity' in mat) mat.emissiveIntensity = 0
    }
  })
  // 中心六边形发光地面叠加层
  if (HEX_FLOOR_ENABLED) addCentralHexFloor()
  if (INFINITE_GROUND_ENABLED) addInfiniteGrassGround()
  // 厂容厂貌图片大屏（5 号展区）
  loadPlantImages()
}

// ---------- 展品点击（Raycaster） ----------
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()
let modalOpen = false
let pointerStart = null
let clickTargets = []

// 手指点小屏幕很容易差几个像素，这里在手指位置周围多试几圈再判定，
// 命中任意一个点就算点中（结束后把射线还原到手指位置，不影响后面的判定）。
function raycastForgiving(meshes, cx, cy) {
  if (!meshes || !meshes.length) return []
  const rect = canvas.getBoundingClientRect()
  const setRay = (px, py) => {
    pointer.x = ((px - rect.left) / rect.width) * 2 - 1
    pointer.y = -((py - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(pointer, camera)
  }
  const offsets = [[0, 0], [14, 0], [-14, 0], [0, 14], [0, -14], [16, 16], [-16, -16], [16, -16], [-16, 16]]
  let hits = []
  for (const [ox, oy] of offsets) {
    setRay(cx + ox, cy + oy)
    hits = raycaster.intersectObjects(meshes, false)
    if (hits.length) break
  }
  setRay(cx, cy)
  return hits
}

// 按下记录起点；松手时若几乎没有移动且没按 Shift，才算点击（避免拖动/Shift旋转误触）
canvas.addEventListener('pointerdown', (e) => {
  if (modalOpen || transformControls.dragging) return
  if (e.button !== 0 || e.shiftKey) return
  // 视频不自动播放（仅点击按钮播放），不再在按下时强制播放
  pointerStart = { x: e.clientX, y: e.clientY, t: performance.now(), touch: e.pointerType === 'touch' }
})

// 悬停检测：可点击对象（标题/展品/入口展区）上显示手指光标
canvas.addEventListener('mousemove', (e) => {
  if (isFlying) return
  const rect = canvas.getBoundingClientRect()
  pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
  pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  raycaster.setFromCamera(pointer, camera)
  const targets = [
    ...(SUPPORT_ENABLED && supportEntry ? [supportEntry] : []), // 客服关掉后，发光球也不再变手型
    ...(lightHelperVisible ? lightHelpers : []),
    ...titleSprites.map((t) => t.sprite),
    ...hitboxes.map((h) => h.mesh),
    ...hud1Meshes,
    ...hud2Meshes,
    ...hud3Meshes,
    ...hud4Meshes,
    ...hud5Meshes,
    ...hud6Meshes,
    ...videoButtons,
    ...plantScreens,
    ...(currentZone === null ? zoneMarkers : [])
  ]
  const hits = raycaster.intersectObjects(targets, false)
  canvas.style.cursor = hits.length ? 'pointer' : ''
})

canvas.addEventListener('pointerup', (e) => {
  if (!pointerStart) return
  if (transformControls.dragging) { pointerStart = null; return }
  const dx = e.clientX - pointerStart.x
  const dy = e.clientY - pointerStart.y
  const dt = performance.now() - pointerStart.t
  // 手指点屏幕多少会抖一下、按久一点，判定标准要比鼠标宽一些，
  // 否则平板上"点了没反应"（以前是 6 像素/0.5 秒，对手指太严了）
  const isTouch = pointerStart.touch || e.pointerType === 'touch'
  const moveTol = isTouch ? 16 : 6
  const timeTol = isTouch ? 900 : 500
  pointerStart = null
  if (Math.hypot(dx, dy) > moveTol || dt > timeTol || e.shiftKey) return

  const rect = canvas.getBoundingClientRect()
  pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
  pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  raycaster.setFromCamera(pointer, camera)

  // 点击视频控制按钮 → 播放/暂停 / 重播
  const btnHits = raycaster.intersectObjects(videoButtons, false)
  if (btnHits.length) {
    const b = btnHits[0].object
    const v = b.userData.video
    const updateIcon = (toggleBtn) => {
      if (!toggleBtn || !toggleBtn.userData.texPlay || !toggleBtn.userData.texPause) return
      toggleBtn.material.map = v.paused ? toggleBtn.userData.texPlay : toggleBtn.userData.texPause
      toggleBtn.material.needsUpdate = true
    }
    if (v) {
      if (b.userData.btnType === 'replay') {
        v.currentTime = 0
        v.play().catch(() => {})
        updateIcon(b.userData.toggleBtn)
      } else if (v.paused) {
        v.play().catch(() => {})
        updateIcon(b)
      } else {
        v.pause()
        updateIcon(b)
      }
    }
    return
  }

  // 点击 5 号展区大屏 → 打开厂容厂貌图集
  if (plantScreens.length) {
    const plantHits = raycaster.intersectObjects(plantScreens, false)
    if (plantHits.length) {
      openPlantGallery()
      return
    }
  }

  // 点击中心发光球 → 打开智能客服
  if (lightHelpers.length) {
    const lhHits = raycaster.intersectObjects(lightHelpers, false)
    if (lhHits.length) {
      selectCustomLight(lhHits[0].object.userData.lightEntry)
      return
    }
  }
  if (SUPPORT_ENABLED && supportEntry) {
    const supportHits = raycaster.intersectObject(supportEntry, false)
    if (supportHits.length) {
      scOpen()
      return
    }
  }

  // 点击悬浮标题 → 跳转对应展区
  const titleHits = raycaster.intersectObjects(titleSprites.map((t) => t.sprite), false)
  if (titleHits.length && titleHits[0].object.userData.zone) {
    flyToZone(titleHits[0].object.userData.zone)
    return
  }

  // 点击 HUD1 屏 → 打开链接图片（灯箱）
  const hud1Hits = raycastForgiving(hud1Meshes, e.clientX, e.clientY)
  if (hud1Hits.length) {
    const link = hud1Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(0)
      return
    }
  }

  // 点击 HUD2 屏 → 打开链接图片（灯箱）
  const hud2Hits = raycastForgiving(hud2Meshes, e.clientX, e.clientY)
  if (hud2Hits.length) {
    const link = hud2Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(1)
      return
    }
  }

  // 点击 HUD3 屏 → 打开链接图片（灯箱）
  const hud3Hits = raycastForgiving(hud3Meshes, e.clientX, e.clientY)
  if (hud3Hits.length) {
    const link = hud3Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(2)
      return
    }
  }

  // 点击 HUD4 屏 → 打开链接图片（灯箱）
  const hud4Hits = raycastForgiving(hud4Meshes, e.clientX, e.clientY)
  if (hud4Hits.length) {
    const link = hud4Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(3)
      return
    }
  }

  // 点击 HUD5 屏 → 打开链接图片（灯箱）
  const hud5Hits = raycastForgiving(hud5Meshes, e.clientX, e.clientY)
  if (hud5Hits.length) {
    const link = hud5Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(4)
      return
    }
  }

  // 点击 HUD6 屏 → 打开链接图片（灯箱）
  const hud6Hits = raycastForgiving(hud6Meshes, e.clientX, e.clientY)
  if (hud6Hits.length) {
    const link = hud6Hits[0].object.userData.hud1Link
    if (link) {
      lbShowHud(5)
      return
    }
  }

  // 入口界面：点击展区平面直接进入分区
  if (currentZone === null) {
    const zoneHits = raycaster.intersectObjects(zoneMarkers, false)
    if (zoneHits.length && zoneHits[0].object.userData.zone) {
      flyToZone(zoneHits[0].object.userData.zone)
    }
    return
  }

  // 展区内：点击展品弹出详情
  if (CLICK_OCCLUSION_ENABLED) {
    // 遮挡判定：最近的命中若不是展品热区则不弹窗
    const hits = raycaster.intersectObjects(clickTargets, false)
    if (hits.length) {
      const hit = hitboxes.find((h) => h.mesh === hits[0].object)
      if (hit) openProduct(hit.id, hit, hits[0].point)
    }
  } else {
    const targets = hitboxes.map((h) => h.mesh)
    const hits = raycaster.intersectObjects(targets, false)
    if (hits.length) {
      const hit = hitboxes.find((h) => h.mesh === hits[0].object)
      if (hit) openProduct(hit.id, hit, hits[0].point)
    }
  }
})

// ---------- 产品弹窗 ----------
let PRODUCTS = {}
fetch('data/exhibitData.json')
  .then((r) => r.json())
  .then((data) => {
    PRODUCTS = data
    // 展品名标签要用这份数据里的名字（中/英文），到了之后补画一遍
    try { refreshExhibitLabels() } catch {}
  })
  .catch(() => {})

const modal = document.querySelector('#product-modal')
const modalName = document.querySelector('#pm-name')
const modalModel = document.querySelector('#pm-model')
const modalImage = document.querySelector('#pm-image')
const pmImageWrap = document.querySelector('.pm-image-wrap')
const modalDesc = document.querySelector('#pm-desc')
const modalMetrics = document.querySelector('#pm-metrics')

// ---------- 全屏产品介绍页 ----------
const productFull = document.querySelector('#product-full')
const pfBg = document.querySelector('#pf-bg')
const pfToast = document.querySelector('#pf-toast')
const pfTitleEl = document.querySelector('#pf-title')
const pfGuideEl = document.querySelector('#pf-guide')
const pfGuideOpsEl = document.querySelector('#pf-guide-ops')
const pfLangZh = document.querySelector('#pf-lang-zh')
const pfLangEn = document.querySelector('#pf-lang-en')
let currentPfId = null
const pfTitleState = { zh: '', en: '' }
let pfModelStartId = null

// ---------- 新版六个按钮（threejs-ui 组件） ----------
let pfBoothUI = null
const PF_BOOTH_LABELS = {
  zh: {
    information: '产品简介',
    manual: '产品手册',
    video: '产品视频',
    exploded: '分解演示',
    photos: '产品图片',
    siteshots: '实拍项目'
  },
  en: {
    information: 'Information',
    manual: 'Manual',
    video: 'Video',
    exploded: 'Explosive',
    photos: 'Photos',
    siteshots: 'Reference'
  }
}

function pfBoothButtons() {
  const labels = getLang() === 'en' ? PF_BOOTH_LABELS.en : PF_BOOTH_LABELS.zh
  return [
    { id: 'information', label: labels.information, icon: 'information' },
    { id: 'manual', label: labels.manual, icon: 'manual' },
    { id: 'video', label: labels.video, icon: 'video' },
    { id: 'exploded', label: labels.exploded, icon: 'exploded' },
    { id: 'photos', label: labels.photos, icon: 'photos' },
    { id: 'siteshots', label: labels.siteshots, icon: 'siteshots' }
  ]
}

function pfBoothSelect(id) {
  // 手机上点展品时，浏览器会在弹窗出现后再补一个合成点击，正好落在刚出现的按钮上
  // （47 号展品就因此"点一下直接跳进产品视频"）。弹窗刚打开的这一小段时间里忽略按钮点击。
  if (IS_TOUCH && performance.now() < pfOpenedAt + 700) return
  if (id === 'information') openPfIntro()
  else if (id === 'manual') openPfPdf()
  else if (id === 'video') openPfVideo()
  else if (id === 'exploded') pfOpenExplodeIfAvailable()
  else if (id === 'photos') openProductMedia('photos')
  else if (id === 'siteshots') openProductMedia('projects')
}

// 只有确实配了爆炸图模型的展品才打开；否则按"暂无"处理
// （爆炸图页在 model 参数为空时会兜底加载 FP 模型，所以这里必须挡住）
function pfOpenExplodeIfAvailable() {
  const p = PRODUCTS[currentPfId] || {}
  if ((p.explode && p.explodeModel) || currentPfId === '45') openExplode()
  else pfShowToast(t('pf.noExplode'))
}

async function openProductMedia(type) {
  if (!currentPfId) return
  const en = getLang() === 'en'
  const label = type === 'projects' ? (en ? 'Reference' : '实拍项目') : (en ? 'Product photos' : '产品图片')
  try {
    const res = await fetch('/api/product-media?id=' + encodeURIComponent(currentPfId) + '&type=' + type)
    const list = await res.json()
    if (!Array.isArray(list) || !list.length) {
      pfShowToast(en ? ('No ' + label.toLowerCase() + ' yet') : ('暂无' + label))
      return
    }
    lbShowGallery(list, 0)
  } catch {
    pfShowToast(en ? 'Failed to load photos' : '图片加载失败')
  }
}

function pfBoothLayoutWidth() {
  // 电脑：按视口宽的 15.5%（168~238px），和原来完全一致。
  // 手机/平板：一律以 1600px 的"电脑基准宽"算出 238px，再乘同一个等比系数，
  // 这样按钮既不会被"最小宽度"撑大，也不会比弹窗里的其它元素缩得慢。
  const base = IS_TOUCH
    ? 238
    : Math.max(168, Math.min(238, Math.round(window.innerWidth * 0.155)))
  return Math.round(base * pfUiScale())
}
// 产品页界面等比系数：电脑上维持 1（跟现在一模一样）；
// 手机/平板上直接用弹窗内容同一个系数（--pop-scale），
// 这样"六个按钮 : 标题/说明/模型"的比例和电脑上一致。
function pfUiScale() {
  // 电脑：沿用原来的算法（1440×900 基准），窗口多大按钮都跟现在一模一样
  if (!IS_TOUCH) return Math.max(0.5, Math.min(1, Math.min(window.innerWidth / 1440, window.innerHeight / 900) * 1.4))
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pop-scale'))
  if (!Number.isFinite(v) || v <= 0) return 1
  return Math.max(0.35, Math.min(1, v))
}

function ensurePfBoothUI() {
  if (pfBoothUI) return pfBoothUI
  pfBoothUI = createBoothUI({
    container: productFull,
    camera: new THREE.PerspectiveCamera(),
    layout: 'screen',
    inset: 0.085,
    centerY: 0.47,
    buttons: pfBoothButtons(),
    theme: 'glass',
    accent: '#8fdcec',
    buttonWidth: pfBoothLayoutWidth(),
    onSelect: pfBoothSelect
  })
  pfBoothUI.root.style.zIndex = '6'
  pfApplyBoothScale()
  return pfBoothUI
}
// 把"六个按钮"的宽高、字号、图标、间距、圆角一起按同一系数缩放，
// 这样小屏上文字和按钮比例与电脑一致，也不会把标签挤到省略号。
function pfApplyBoothScale() {
  if (!pfBoothUI) return
  const k = pfUiScale()
  const s = pfBoothUI.root.style
  s.setProperty('--booth-btn-w', pfBoothLayoutWidth() + 'px')
  s.setProperty('--booth-btn-h', Math.round(72 * k) + 'px')
  s.setProperty('--booth-label-size', (17 * k).toFixed(1) + 'px')
  s.setProperty('--booth-label-size-long', (14.5 * k).toFixed(1) + 'px')
  s.setProperty('--booth-icon', Math.round(26 * k) + 'px')
  s.setProperty('--booth-chev', Math.round(14 * k) + 'px')
  s.setProperty('--booth-gap', Math.round(26 * k) + 'px')
  s.setProperty('--booth-radius', Math.round(16 * k) + 'px')
  pfFitBoothLabels()
}
// 小屏上按钮变窄后，英文长词（Information / Reference…）可能还是差一点放不下。
// 这里逐个量一下，实在放不小就把那一个标签的字号再收一点，保证永远不出现省略号。
function pfFitBoothLabels() {
  if (!pfBoothUI) return
  // 手机/平板可以收得小一点；电脑上只做"轻微"收字，保证文字完整又不会看着小
  const floor = IS_TOUCH ? 7 : 12
  productFull.querySelectorAll('.booth-btn').forEach((btn) => {
    const label = btn.querySelector('.booth-btn__label')
    if (!label) return
    label.style.fontSize = ''
    let size = parseFloat(getComputedStyle(label).fontSize) || 14
    let guard = 0
    // 用 Range 量文字本身的宽度：手机端标签允许换行，scrollWidth 量不出"放不下"，
    // 会变成换行或挤出按钮；这样量才能保证六个标签都收在一行里、且不超出按钮。
    while (!pfBoothLabelFits(label) && size > floor && guard < 30) {
      size -= 0.4
      label.style.fontSize = size.toFixed(1) + 'px'
      guard++
    }
  })
}
function pfBoothLabelFits(label) {
  const range = document.createRange()
  range.selectNodeContents(label)
  const textW = range.getBoundingClientRect().width
  return textW <= label.clientWidth + 0.5
}
window.addEventListener('resize', () => {
  if (!pfBoothUI) return
  pfApplyBoothScale()
})

function pfSetTitleZh(zh) {
  pfTitleState.zh = String(zh || '').trim()
  pfTitleState.en = PRODUCT_NAMES_EN[pfTitleState.zh] || pfTitleState.zh
  pfApplyTitleTexts()
}

function pfApplyTitleTexts() {
  if (!pfTitleEl || !currentPfId) return
  const en = getLang() === 'en'
  pfTitleEl.textContent = en ? (pfTitleState.en || pfTitleState.zh) : pfTitleState.zh
  pfTitleEl.classList.toggle('is-en', en)
  pfGuideEl.classList.toggle('is-en', en)
  if (pfGuideEl) {
    // 底部提示统一成一句话（电脑和触屏都一样）
    pfGuideEl.textContent = t('pf.guide')
  }
  if (pfGuideOpsEl) {
    // 第二行：操作方式，电脑说鼠标、手机/平板说手指
    pfGuideOpsEl.textContent = t(IS_TOUCH ? 'pf.guideOpsTouch' : 'pf.guideOpsMouse')
  }
  pfFitSingleLine(pfTitleEl, en ? '70vw' : '')
  pfFitSingleLine(pfGuideEl, en ? '90vw' : '')
  pfFitSingleLine(pfGuideOpsEl, '92vw')
}

// 英文单行显示：超宽时自动缩小字号直到一行放下
function pfFitSingleLine(el, maxWidth) {
  if (!el) return
  if (!maxWidth) {
    el.style.whiteSpace = ''
    el.style.width = ''
    el.style.maxWidth = ''
    el.style.fontSize = ''
    return
  }
  el.style.whiteSpace = 'nowrap'
  el.style.width = 'max-content'
  el.style.maxWidth = maxWidth
  let px = parseFloat(window.getComputedStyle(el).fontSize) || 12
  el.style.fontSize = px + 'px'
  let guard = 0
  while (el.scrollWidth > el.clientWidth + 1 && px > 9 && guard++ < 60) {
    px -= 0.5
    el.style.fontSize = px + 'px'
  }
}

pfLangZh?.addEventListener('click', () => { setLang('zh'); applyLanguage() })
pfLangEn?.addEventListener('click', () => { setLang('en'); applyLanguage() })

// ---------- 弹窗背景图热区（技术参数 / 产品视频 / 爆炸图） ----------
const pfHotspotInner = document.querySelector('#pf-hotspot-inner')
let pfZoom = 1
let pfPanX = 0
let pfPanY = 0
let pfPanDrag = null
let pfPanMoved = false
let pfJustOpened = false // 弹窗刚打开时忽略紧随的合成 click（手机触摸）
let pfOpenedAt = 0       // 弹窗打开的时刻（配合上面那条：700ms 内忽略按钮点击）
const pfZoomEls = [
  document.querySelector('#pf-bg'),
  document.querySelector('#pf-edit-canvas'),
  document.querySelector('#pf-text-layer'),
  document.querySelector('#pf-hotspot-layer')
]
function pfApplyZoom() {
  pfZoomEls.forEach((el) => {
    el.style.transform = 'translate(' + pfPanX + 'px, ' + pfPanY + 'px) scale(' + pfZoom + ')'
    el.style.transformOrigin = '50% 50%'
  })
}
// 滚轮缩放暂时关闭
// 非编辑模式不再支持拖动平移（背景图固定）
// 双击复位暂时关闭
const hotspotRects = {
  video: { left: 9.5, top: 24.3, width: 7.6, height: 15.5 },
  explode: { left: 9.7, top: 41.1, width: 7.7, height: 14.2 },
  intro: { left: 81.8, top: 24.4, width: 7.4, height: 15.5 },
  pdf: { left: 81.7, top: 41.0, width: 7.5, height: 14.3 }
}

// ---------- 产品简介弹窗 ----------
const pfIntroWindow = document.querySelector('#pf-intro-window')
const pfIntroText = document.querySelector('#pf-intro-text')
const pfIntroBg = document.querySelector('#pf-intro-bg')
const pfIntroTitle = document.querySelector('#pf-intro-title')
const introTextCache = {}

// 与产品弹窗匹配：同一张底图 + 居中磨砂玻璃内容面板
function pfIntroLayout() {
  if (!pfIntroWindow.classList.contains('show')) return
  const rect = pfIntroWindow.getBoundingClientRect()
  const cardW = Math.min(rect.width * 0.74, 1000)
  const cardH = Math.min(rect.height * 0.62, 620)
  const top = Math.max(rect.height * 0.15, 96)
  pfIntroText.style.left = ((rect.width - cardW) / 2) + 'px'
  pfIntroText.style.top = Math.min(top, Math.max(80, rect.height - cardH - 36)) + 'px'
  pfIntroText.style.width = cardW + 'px'
  pfIntroText.style.height = cardH + 'px'
}

function pfApplyIntroTitle() {
  if (!pfIntroTitle) return
  const en = getLang() === 'en'
  const name = en ? (pfTitleState.en || pfTitleState.zh) : pfTitleState.zh
  const suffix = t('pf.introTitleSuffix')
  pfIntroTitle.textContent = name ? name + ' · ' + suffix : suffix
  pfIntroTitle.classList.toggle('is-en', en)
}

function showIntroContent(text) {
  pfIntroText.textContent = text || ''
  pfIntroBg.src = PRODUCT_UI_BG
  pfIntroWindow.classList.add('show')
  opt2PushOverlay(currentPfId) // 返回键先关"产品简介"，再关弹窗
  pfApplyIntroTitle()
  pfIntroLayout()
}

function openPfIntro() {
  if (!currentPfId) return
  const p = PRODUCTS[currentPfId] || {}
  const langKey = getLang() === 'en' ? 'en' : 'zh'
  const cacheKey = langKey + '-' + currentPfId
  if (introTextCache[cacheKey]) {
    showIntroContent(introTextCache[cacheKey])
    return
  }
  fetch('data/intro/' + langKey + '/' + currentPfId + '.txt')
    .then((r) => {
      // 没有这个文件时静态服务器会回退成 index.html 并返回 200，
      // 所以不能只看 r.ok，还要排掉 HTML（否则整页代码会被塞进简介窗）
      const ct = String(r.headers.get('content-type') || '')
      if (!r.ok || ct.includes('text/html')) throw new Error('no intro')
      return r.text()
    })
    .then((txt) => {
      const t = String(txt || '').trim()
      if (!t) throw new Error('empty intro')
      introTextCache[cacheKey] = t
      showIntroContent(t)
    })
    .catch(() => {
      const t = String(p.desc || '').trim()
      if (!t) { pfShowToast(getLang() === 'en' ? 'No product brief yet' : '暂无产品简介'); return }
      showIntroContent(t)
    })
}
function closePfIntro() {
  pfIntroWindow.classList.remove('show')
}
document.querySelector('#pf-intro-close').addEventListener('click', () => opt2CloseUser(closePfIntro))
window.addEventListener('resize', () => {
  if (pfIntroWindow.classList.contains('show')) pfIntroLayout()
})

// ---------- 弹窗中央 3D 模型查看（替换背景图中的产品图，可原地转动） ----------
const pfModelCanvas = document.querySelector('#pf-model-view')
const pfModelScale = document.querySelector('#pf-model-scale')
const pfModelRot = document.querySelector('#pf-model-rot')
const pfModelScaleV = document.querySelector('#pf-model-scale-v')
const pfModelRotV = document.querySelector('#pf-model-rot-v')
const pfModelRotX = document.querySelector('#pf-model-rot-x')
const pfModelRotXV = document.querySelector('#pf-model-rot-x-v')
const pfModelUp = document.querySelector('#pf-model-up')
const pfModelDown = document.querySelector('#pf-model-down')
const pfModelLeft = document.querySelector('#pf-model-left')
const pfModelRight = document.querySelector('#pf-model-right')
const pfModelSaveView = document.querySelector('#pf-model-save-view')
const pfModelResetView = document.querySelector('#pf-model-reset-view')
const pfModelPanel = document.querySelector('#pf-model-panel')
const pfModelToggle = document.querySelector('#pf-model-toggle')
let pfModelAdjusting = false
let pfModelDirty = false
let pfModelCtx = null // { renderer, scene, camera, controls, group, raf }
// 服务器上的"产品默认视角"（所有设备共用一份）；本机调过的话本机优先
let productViews = {}
async function loadProductViews() {
  try {
    const res = await fetch('/api/product-views?t=' + Date.now(), { cache: 'no-store' })
    if (res.ok) productViews = (await res.json()) || {}
  } catch { productViews = {} }
  // 把本机调好的视角补传上去（自动迁移：在电脑上打开一次，平板就跟着一致了）
  const local = {}
  for (const k of Object.keys(localStorage)) {
    if (k.indexOf('pf-model-') !== 0) continue
    try {
      const v = JSON.parse(localStorage.getItem(k))
      if (!v || !v.viewSaved) continue
      const id = k.slice('pf-model-'.length)
      if (!productViews[id]) local[id] = v
    } catch { /* 忽略坏数据 */ }
  }
  const ids = Object.keys(local)
  if (ids.length) {
    try {
      await fetch('/api/product-views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids.map((id) => ({ id, params: local[id] }))),
      })
      Object.assign(productViews, local)
      console.log('[views] 已把本机 ' + ids.length + ' 个产品视角同步到服务器')
    } catch { /* 忽略 */ }
  }
}
loadProductViews()
let pfModelDrag = null
let pfModelBounds = null // { x0, x1, yTop, yBot, margin }
function ensurePfModelCtx() {
  if (pfModelCtx) return pfModelCtx
  try {
    const renderer = new THREE.WebGLRenderer({ canvas: pfModelCanvas, alpha: true, antialias: true })
    renderer.setClearColor(0x000000, 0)
    // 渲染倍率跟随当前画质档位（原来默认 1.0，高档位下会明显比主场景糊）
    renderer.setPixelRatio(effectivePixelRatio())
    renderer.shadowMap.enabled = false
    const scene = new THREE.Scene()
    // 环境反射：与爆炸图一致用 RoomEnvironment（明亮中性），金属反光清晰不偏灰
    const pmremEnv = new THREE.PMREMGenerator(renderer)
    scene.environment = pmremEnv.fromScene(new RoomEnvironment(), 0.04).texture
    scene.environmentIntensity = 0.65
    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 2000)
    camera.position.set(0, 1.5, 6)
    const controls = new OrbitControls(camera, pfModelCanvas)
    controls.enablePan = false
    controls.enableZoom = true
    controls.enableDamping = true
    controls.dampingFactor = 0.08
    controls.minDistance = 0.05
    controls.maxDistance = 20
    // 和主场景一致：左键旋转、中键平移、右键也平移（滚轮缩放）
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN }
    scene.add(new THREE.AmbientLight(0xffffff, 0.7))
    const key = new THREE.DirectionalLight(0xffffff, 1.1)
    key.position.set(5, 8, 6)
    scene.add(key)
    const fill = new THREE.DirectionalLight(0x88ccff, 0.4)
    fill.position.set(-6, 3, -4)
    scene.add(fill)
    const group = new THREE.Group()
    scene.add(group)
    pfModelCtx = { renderer, scene, camera, controls, group, pivot: null, metrics: null, params: { zoom: 1, tx: 0, ty: 0, rotX: 0, rotY: 0 } }
    // 拖动旋转会触发 pointer 事件，阻止冒泡到弹窗平移/关闭
    pfModelCanvas.addEventListener('pointerdown', (e) => e.stopPropagation())
    pfModelCanvas.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false })
    // 挡掉中键的浏览器默认行为（自动滚动小圆盘），否则中键拖拽平移会被它抢走
    pfModelCanvas.addEventListener('mousedown', (e) => { if (e.button === 1) e.preventDefault() })
  } catch (err) {
    console.warn('[pf-model] init failed:', err && err.message)
    pfModelCtx = null
  }
  return pfModelCtx
}
function pfModelLayout() {
  // 进来先对齐一次渲染倍率：档位/超采样可能刚变过（弹窗打开、切档、窗口缩放都会走这里）
  if (pfModelCtx) {
    const r = pfModelCtx.renderer
    const next = effectivePixelRatio()
    if (Math.abs((r.getPixelRatio() || 1) - next) > 0.01) r.setPixelRatio(next)
  }
  const h = pfBgHalf()
  const rect = productFull.getBoundingClientRect()
  const vw = Math.max(1, rect.width || pfEdViewW || 1)
  const vh = Math.max(1, rect.height || pfEdViewH || 1)
  pfEdViewW = vw
  pfEdViewH = vh
  // 展示区：中心 (50%, 50%)，原宽 40%/高 55%，整体放大 1.5 倍
  const cxR = 0.5
  const cyR = 0.5
  const wR = 0.40 * 1.5
  const hR = 0.55 * 1.5
  const leftRatio = cxR - wR / 2
  const rightRatio = cxR + wR / 2
  const topRatio = cyR - hR / 2
  const bottomRatio = cyR + hR / 2
  const x0 = (leftRatio - 0.5) * 2 * h.halfW
  const x1 = (rightRatio - 0.5) * 2 * h.halfW
  const yTop = -(topRatio - 0.5) * 2 * h.halfH
  const yBottom = -(bottomRatio - 0.5) * 2 * h.halfH
  pfModelCanvas.style.left = (x0 + vw / 2) + 'px'
  pfModelCanvas.style.top = (vh / 2 - yTop) + 'px'
  pfModelCanvas.style.width = (x1 - x0) + 'px'
  pfModelCanvas.style.height = (yTop - yBottom) + 'px'
  pfModelBounds = { x0, x1, yTop, yBottom, margin: (yTop - yBottom) * 0.12 }
  const w = Math.max(1, x1 - x0)
  const hh = Math.max(1, yTop - yBottom)
  if (pfModelCtx) {
    pfModelCtx.renderer.setSize(w, hh, false)
    pfModelCtx.camera.aspect = w / hh
    pfModelCtx.camera.updateProjectionMatrix()
  }
}
function pfModelFit() {
  if (!pfModelCtx || !pfModelCtx.group.children.length) return
  pfModelApply()
}
function pfModelApply() {
  if (!pfModelCtx || !pfModelCtx.pivot || !pfModelCtx.pivot.children.length) return
  const p = pfModelCtx.params
  const pivot = pfModelCtx.pivot
  // 模型固定在原点，不做平移/旋转；所有操作都作用于摄像机视口
  pfModelCtx.group.position.set(0, 0, 0)
  pfModelCtx.group.rotation.set(0, 0, 0)
  pivot.rotation.set(0, 0, 0)
  if (!pfModelCtx.metrics) {
    pivot.position.set(0, 0, 0)
    pivot.rotation.set(0, 0, 0)
    const box = new THREE.Box3().setFromObject(pivot)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())
    const r = Math.max(size.x, size.y, size.z) / 2 || 1
    pivot.position.set(-center.x, -center.y, -center.z)
    pfModelCtx.metrics = { r }
  }
  const r = pfModelCtx.metrics.r
  const dist = (r * 3.2) / (p.zoom || 1)
  // 水平=方位角，竖向=俯仰角
  const az = (p.rotY || 0) * Math.PI / 180
  const pol = Math.max(0.05, Math.min(Math.PI - 0.05, Math.PI / 2 - (p.rotX || 0) * Math.PI / 180))
  // 上下左右：只移动摄像机目标点（模型仍在原点），正值=模型相对视口偏上/偏右
  const panX = -(p.tx || 0)
  const panY = -(p.ty || 0)
  const target = new THREE.Vector3(panX, panY, 0)
  pfModelCtx.controls.target.copy(target)
  const offset = new THREE.Vector3().setFromSphericalCoords(dist, pol, az)
  pfModelCtx.camera.position.copy(target).add(offset)
  pfModelCtx.camera.lookAt(target)
  pfModelCtx.controls.update()
}
function pfModelSaveParams(id, captureView) {
  if (!pfModelCtx) return
  if (captureView !== false && pfModelCtx.controls && pfModelCtx.metrics) {
    try {
      pfModelCtx.controls.update()
      const azi = pfModelCtx.controls.getAzimuthalAngle()
      const pol = pfModelCtx.controls.getPolarAngle()
      pfModelCtx.params.rotY = (azi * 180 / Math.PI + 360) % 360
      pfModelCtx.params.rotX = Math.round((Math.PI / 2 - pol) * 180 / Math.PI)
      pfModelCtx.params.tx = -pfModelCtx.controls.target.x
      pfModelCtx.params.ty = -pfModelCtx.controls.target.y
      const dist = pfModelCtx.controls.target.distanceTo(pfModelCtx.camera.position)
      const r = pfModelCtx.metrics.r || 1
      const zoom = (r * 3.2) / dist
      pfModelCtx.params.zoom = Math.max(0.2, Math.min(8, zoom))
      pfModelScale.value = Math.round(pfModelCtx.params.zoom * 100)
      pfModelScaleV.textContent = pfModelCtx.params.zoom.toFixed(1)
      pfModelRot.value = Math.round(pfModelCtx.params.rotY || 0)
      pfModelRotV.textContent = pfModelRot.value + '°'
      pfModelRotX.value = Math.round(pfModelCtx.params.rotX || 0)
      pfModelRotXV.textContent = pfModelRotX.value + '°'
    } catch {}
  }
  if (captureView !== false) pfModelCtx.params.viewSaved = true
  try { localStorage.setItem('pf-model-' + id, JSON.stringify(pfModelCtx.params)) } catch {}
  // 同步到服务器：这样别的设备（平板/手机/别人电脑）打开看到的是同一个默认角度
  try {
    productViews[id] = pfModelCtx.params
    fetch('/api/product-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, params: pfModelCtx.params }),
    }).catch(() => {})
  } catch {}
}
function pfModelLoadParams(id) {
  if (!pfModelCtx) return
  // 优先顺序：服务器上存的默认视角（所有设备共用）→ 本机存的视角（如果本机手动调过）
  pfModelCtx.params = { zoom: 1, tx: 0, ty: 0, rotX: 0, rotY: 0 }
  const remote = productViews && productViews[id]
  if (remote && typeof remote === 'object') pfModelCtx.params = Object.assign(pfModelCtx.params, remote)
  try {
    const s = localStorage.getItem('pf-model-' + id)
    const saved = s ? JSON.parse(s) : null
    if (saved && saved.viewSaved) pfModelCtx.params = Object.assign(pfModelCtx.params, saved)
  } catch {}
  pfModelDirty = false
  pfModelScale.value = Math.round((pfModelCtx.params.zoom || 1) * 100)
  pfModelScaleV.textContent = (pfModelCtx.params.zoom || 1).toFixed(1)
  pfModelRot.value = Math.round(pfModelCtx.params.rotY || 0)
  pfModelRotV.textContent = Math.round(pfModelCtx.params.rotY || 0) + '°'
  pfModelRotX.value = Math.round(pfModelCtx.params.rotX || 0)
  pfModelRotXV.textContent = Math.round(pfModelCtx.params.rotX || 0) + '°'
}
pfModelScale.addEventListener('input', () => {
  if (!pfModelCtx) return
  pfModelCtx.params.zoom = Number(pfModelScale.value) / 100
  pfModelScaleV.textContent = pfModelCtx.params.zoom.toFixed(1)
  pfModelDirty = true
  pfModelApply()
})
pfModelRot.addEventListener('input', () => {
  if (!pfModelCtx) return
  pfModelCtx.params.rotY = Number(pfModelRot.value)
  pfModelRotV.textContent = pfModelRot.value + '°'
  pfModelDirty = true
  pfModelApply()
})
pfModelRotX.addEventListener('input', () => {
  if (!pfModelCtx) return
  pfModelCtx.params.rotX = Number(pfModelRotX.value)
  pfModelRotXV.textContent = pfModelRotX.value + '°'
  pfModelDirty = true
  pfModelApply()
})
function pfModelNudge(dx, dy) {
  if (!pfModelCtx || !pfModelCtx.metrics) return
  const step = pfModelCtx.metrics.r * 0.06
  pfModelCtx.params.tx = (pfModelCtx.params.tx || 0) + dx * step
  pfModelCtx.params.ty = (pfModelCtx.params.ty || 0) + dy * step
  pfModelDirty = true
  pfModelApply()
}
pfModelUp?.addEventListener('click', () => pfModelNudge(0, 1))
pfModelDown?.addEventListener('click', () => pfModelNudge(0, -1))
pfModelLeft?.addEventListener('click', () => pfModelNudge(-1, 0))
pfModelRight?.addEventListener('click', () => pfModelNudge(1, 0))
pfModelSaveView?.addEventListener('click', () => {
  if (!pfModelCtx || !currentPfId) return
  pfModelSaveParams(currentPfId, true)
  pfModelDirty = false
  pfShowToast('视角已保存到产品 ' + currentPfId)
})
pfModelResetView?.addEventListener('click', () => {
  if (!pfModelCtx || !currentPfId) return
  try { localStorage.removeItem('pf-model-' + currentPfId) } catch {}
  pfModelLoadParams(currentPfId)
  pfModelApply()
  pfShowToast('已恢复默认视角，可重新调整后保存')
})
// 编辑模式下：在模型区域拖动 = 平移模型位置
pfModelCanvas.addEventListener('pointerdown', (e) => {
  if (!productFull.classList.contains('editing') || pfModelAdjusting || !pfModelCtx) return
  e.stopPropagation()
  pfModelCanvas.setPointerCapture(e.pointerId)
  pfModelDrag = { x: e.clientX, y: e.clientY }
})
pfModelCanvas.addEventListener('pointermove', (e) => {
  if (!pfModelDrag || pfModelAdjusting || !pfModelCtx) return
  const dx = e.clientX - pfModelDrag.x
  const dy = e.clientY - pfModelDrag.y
  pfModelDrag.x = e.clientX
  pfModelDrag.y = e.clientY
  // 屏幕像素 → 世界位移（按当前相机距离换算）
  const p = pfModelCtx.params
  const rr = (pfModelCtx.metrics && pfModelCtx.metrics.r) || 1
  const dist = (rr * 3.2) / (p.zoom || 1)
  const canvasH = Math.max(1, pfModelCanvas.clientHeight || 1)
  const worldPerPx = (2 * dist * Math.tan((pfModelCtx.camera.fov * Math.PI) / 360)) / canvasH
  pfModelCtx.params.tx = (pfModelCtx.params.tx || 0) + dx * worldPerPx
  pfModelCtx.params.ty = (pfModelCtx.params.ty || 0) - dy * worldPerPx
  // 直接用当前相机可视范围限制，避免不同模型尺寸/旧布局坐标造成的“贴顶拖不动”
  const viewHalfW = dist * Math.tan((pfModelCtx.camera.fov * Math.PI) / 360) * pfModelCtx.camera.aspect
  const viewHalfH = dist * Math.tan((pfModelCtx.camera.fov * Math.PI) / 360)
  pfModelCtx.params.tx = Math.max(-viewHalfW * 0.9, Math.min(viewHalfW * 0.9, pfModelCtx.params.tx))
  pfModelCtx.params.ty = Math.max(-viewHalfH * 0.85, Math.min(viewHalfH * 0.85, pfModelCtx.params.ty))
  pfModelDirty = true
  pfModelApply()
})
pfModelCanvas.addEventListener('pointerup', () => { pfModelDrag = null })
pfModelCanvas.addEventListener('pointercancel', () => { pfModelDrag = null })
// 部分模型材质带贴图但几何没有 UV，按包围盒生成平面 UV，避免着色器报错
function ensureMeshUVs(root) {
  if (!root) return
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry || !o.geometry.attributes.position) return
    const mat = o.material
    if (!mat) return
    const needsUV = mat.map || mat.normalMap || mat.roughnessMap || mat.metalnessMap || mat.aoMap || mat.emissiveMap
    if (!needsUV) return
    if (!o.geometry.attributes.uv) {
      const pos = o.geometry.attributes.position
      const box = new THREE.Box3().setFromBufferAttribute(pos)
      const size = box.getSize(new THREE.Vector3())
      const axes = [['x', size.x], ['y', size.y], ['z', size.z]].sort((a, b) => b[1] - a[1])
      const ua = axes[0][0]
      const va = axes[1][0]
      const get = (axis, i) => (axis === 'x' ? pos.getX(i) : axis === 'y' ? pos.getY(i) : pos.getZ(i))
      const arr = new Float32Array(pos.count * 2)
      for (let i = 0; i < pos.count; i++) {
        arr[i * 2] = (get(ua, i) - box.min[ua]) / (size[ua] || 1)
        arr[i * 2 + 1] = (get(va, i) - box.min[va]) / (size[va] || 1)
      }
      o.geometry.setAttribute('uv', new THREE.BufferAttribute(arr, 2))
    }
    for (const tex of [mat.map, mat.normalMap, mat.roughnessMap, mat.metalnessMap, mat.aoMap, mat.emissiveMap]) {
      if (tex && tex.channel > 0) {
        tex.channel = 0
        tex.needsUpdate = true
      }
    }
    mat.needsUpdate = true
  })
}

function pfModelAttach(rootScene) {
  ensureMeshUVs(rootScene)
  if (!pfModelCtx) return
  pfModelCtx.group.clear()
  pfModelCtx.metrics = null
  pfModelCtx.pivot = null
  if (!rootScene) return
  // 每次载入新产品都重建控制器，清除上一个产品残留的相机角度/阻尼
  if (pfModelCtx.controls) {
    try { pfModelCtx.controls.dispose() } catch {}
  }
  pfModelCtx.controls = new OrbitControls(pfModelCtx.camera, pfModelCanvas)
  pfModelCtx.controls.enablePan = true
  pfModelCtx.controls.enableZoom = true
  pfModelCtx.controls.enableDamping = true
  pfModelCtx.controls.dampingFactor = 0.08
  pfModelCtx.controls.minDistance = 0.05
  pfModelCtx.controls.maxDistance = 20
  // 和主场景一致：左键旋转、中键平移、右键也平移（滚轮缩放）
  pfModelCtx.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN }
  const pivot = new THREE.Group()
  pivot.add(rootScene)
  pfModelCtx.group.add(pivot)
  pfModelCtx.pivot = pivot
}
function pfModelLoad(url) {
  const ctx = ensurePfModelCtx()
  if (!ctx) return
  pfModelAttach(null)
  const pfGltfLoader = new GLTFLoader()
  if (dracoLoader) pfGltfLoader.setDRACOLoader(dracoLoader)
  pfGltfLoader.setMeshoptDecoder(MeshoptDecoder)
  pfGltfLoader.load(url, (gltf) => {
    pfModelAttach(gltf.scene)
    ctx._viewInit = false // 每次载入都重置为默认正面视角
    pfModelFit()
    pfModelCanvas.style.display = 'block'
    if (pfGuideOpsEl) pfGuideOpsEl.style.display = ''
    pfModelLayout()
  }, undefined, () => {
    pfModelCanvas.style.display = 'none'
    // 没有 3D 模型就不提"旋转/缩放"了
    if (pfGuideOpsEl) pfGuideOpsEl.style.display = 'none'
  })
}
function pfModelTryLoad(id) {
  const p = PRODUCTS[id] || {}
  const raw = []
  // 优先使用展品数据里配置的模型（弹窗 3D 与爆炸图共用同一份）
  if (p.explodeModel) raw.push(p.explodeModel)
  else if (id === '45') raw.push('models/45_01_FP.glb')
  else if (id === '47') raw.push('models/47_02柜式风盘.glb')
  raw.push('models/' + id + '_01_FP.glb')
  raw.push('models/explode/' + id + '.glb')
  // 弹窗里的 3D 预览和展厅一样优先用轻量版（LOD）：全精度 22/23 有 57/82 MB，
  // 打开弹窗要等几分钟；LOD 只要 1~7 MB。LOD 不存在时自动回退到原路径。
  const paths = []
  for (const r of raw) {
    if (String(r).startsWith('models/') && !String(r).startsWith('models/lod/')) paths.push(lodPathOf(r))
    paths.push(r)
  }
  let i = 0
  const next = () => {
    if (i >= paths.length) {
      pfModelCanvas.style.display = 'none'
      // 这款产品没有 3D 模型：不提"旋转/缩放"
      if (pfGuideOpsEl) pfGuideOpsEl.style.display = 'none'
      return
    }
    const ctx = ensurePfModelCtx()
    if (!ctx) return
    pfModelAttach(null)
    const pfGltfLoader = new GLTFLoader()
    if (dracoLoader) pfGltfLoader.setDRACOLoader(dracoLoader)
    pfGltfLoader.setMeshoptDecoder(MeshoptDecoder)
    pfGltfLoader.load(withVersion(paths[i++]), (g) => {
      pfModelAttach(g.scene)
      ctx._viewInit = false // 重置相机视角，避免沿用上一款产品的视角
      pfModelLoadParams(id)
      pfModelApply()
      pfModelCanvas.style.display = 'block'
      if (pfGuideOpsEl) pfGuideOpsEl.style.display = ''
      pfModelLayout()
      // 下一帧再应用一次，消除首帧摆位差异
      requestAnimationFrame(() => {
        if (pfModelCtx && pfModelCtx.pivot && pfModelCtx.pivot.children.length) {
          pfModelApply()
        }
      })
    }, undefined, next)
  }
  next()
}
function pfModelRenderLoop() {
  requestAnimationFrame(pfModelRenderLoop)
  if (!pfModelCtx || pfModelCanvas.style.display === 'none') return
  if (!productFull.classList.contains('show')) return
  pfModelCtx.controls.enabled = !productFull.classList.contains('editing') || pfModelAdjusting
  if (pfModelCtx.controls.enabled) pfModelCtx.controls.update()
  pfModelCtx.renderer.render(pfModelCtx.scene, pfModelCtx.camera)
}
pfModelRenderLoop()
window.addEventListener('resize', () => { if (productFull.classList.contains('show')) pfModelLayout() })

// 热区跟随背景图 contain 显示区域对齐（图片比例与全屏可能不同）
function layoutHotspots() {
  const rect = productFull.getBoundingClientRect()
  const iw = pfBg.naturalWidth || 2400
  const ih = pfBg.naturalHeight || 1600
  const scale = Math.min(rect.width / iw, rect.height / ih)
  const w = iw * scale
  const h = ih * scale
  pfHotspotInner.style.left = ((rect.width - w) / 2) + 'px'
  pfHotspotInner.style.top = ((rect.height - h) / 2) + 'px'
  pfHotspotInner.style.width = w + 'px'
  pfHotspotInner.style.height = h + 'px'
}
pfBg.addEventListener('load', layoutHotspots)
window.addEventListener('resize', layoutHotspots)

// 技术参数 PDF 内嵌预览
const pfPdfOverlay = document.querySelector('#pf-pdf-overlay')
const pfPdfFrame = document.querySelector('#pf-pdf-frame')
let pfPdfListCache = null
function fetchPfPdfList() {
  if (pfPdfListCache) return Promise.resolve(pfPdfListCache)
  return fetch('/api/pdfs')
    .then((r) => r.json())
    .then((list) => { pfPdfListCache = list; return list })
    .catch(() => [])
}
function openPfPdf() {
  if (!currentPfId) return
  fetchPfPdfList().then((list) => {
    const pool = list.filter((f) => f.startsWith(currentPfId + '_') || f.startsWith(currentPfId + '-') || f.startsWith(currentPfId + '.'))
    if (!pool.length) {
      pfShowToast(t('pf.noPdf'))
      return
    }
    const isZh = (f) => /[\u4e00-\u9fff]/.test(f)
    // 中文界面 → 中文参数表；英文界面 → 英文参数表，找不到时回退另一种
    const pdf = getLang() === 'en'
      ? (pool.find((f) => !isZh(f)) || pool[0])
      : (pool.find(isZh) || pool[0])
    pfPdfFrame.src = 'pdf/products/' + pdf
    pfPdfOverlay.classList.add('show')
    opt2PushOverlay(currentPfId) // 返回键先关"产品手册"，再关弹窗
  })
}
document.querySelector('#pf-pdf-close').addEventListener('click', () => {
  opt2CloseUser(() => {
    pfPdfOverlay.classList.remove('show')
    pfPdfFrame.src = ''
  })
})

// 产品视频内嵌播放
const pfVideoOverlay = document.querySelector('#pf-video-overlay')
const pfVideoPlayer = document.querySelector('#pf-video-player')
const pfVpPlayBtn = document.querySelector('#pf-vp-play')
const pfVpSeek = document.querySelector('#pf-vp-seek')
const pfVpTime = document.querySelector('#pf-vp-time')
let pfVideoListCache = null
// ---------- 产品视频加载进度条 ----------
const pfVideoLoading = document.querySelector('#pf-video-loading')
const pfVideoLoadingFill = document.querySelector('#pf-video-loading-fill')
const pfVideoLoadingText = document.querySelector('#pf-video-loading-text')
let pfVLoadDone = true
let pfVLoadTimer = null
function pfVideoLoadingSet(on, pct) {
  if (!pfVideoLoading) return
  pfVideoLoading.classList.toggle('show', !!on)
  const known = typeof pct === 'number' && isFinite(pct)
  pfVideoLoading.classList.toggle('is-indeterminate', !known)
  if (known && pfVideoLoadingFill) pfVideoLoadingFill.style.width = Math.max(0, Math.min(100, pct)) + '%'
  if (pfVideoLoadingText) {
    const en = getLang() === 'en'
    pfVideoLoadingText.textContent = known
      ? (en ? 'Loading ' + Math.round(pct) + '%' : '加载中 ' + Math.round(pct) + '%')
      : (en ? 'Loading…' : '加载中…')
  }
}
// 已经缓冲到的比例（拿不到时长就返回 null，界面走"来回跑"样式）
function pfVideoBufferedPct() {
  const d = pfVideoPlayer.duration
  if (!d || !isFinite(d) || d <= 0) return null
  let end = 0
  try {
    const b = pfVideoPlayer.buffered
    for (let i = 0; i < b.length; i++) end = Math.max(end, b.end(i))
  } catch {}
  if (!end) return 0
  return Math.max(0, Math.min(100, (end / d) * 100))
}
function pfVideoLoadingStart() {
  pfVLoadDone = false
  pfVideoLoadingSet(true, pfVideoBufferedPct())
  if (pfVLoadTimer) clearInterval(pfVLoadTimer)
  // 缓冲事件不一定连续触发，这里定时刷一下，进度条才会一直往前走
  pfVLoadTimer = setInterval(() => {
    if (pfVLoadDone) { clearInterval(pfVLoadTimer); pfVLoadTimer = null; return }
    pfVideoLoadingSet(true, pfVideoBufferedPct())
  }, 250)
}
function pfVideoLoadingStop() {
  pfVLoadDone = true
  if (pfVLoadTimer) { clearInterval(pfVLoadTimer); pfVLoadTimer = null }
  pfVideoLoadingSet(false, 100)
}
function pfFmtTime(s) {
  if (!isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return m + ':' + String(sec).padStart(2, '0')
}
pfVpSeek.addEventListener('input', () => {
  if (pfVideoPlayer.duration) pfVideoPlayer.currentTime = (pfVpSeek.value / 1000) * pfVideoPlayer.duration
})
pfVpPlayBtn.addEventListener('click', () => {
  if (pfVideoPlayer.paused) pfVideoPlayer.play().catch(() => {})
  else pfVideoPlayer.pause()
})
pfVideoPlayer.addEventListener('timeupdate', () => {
  if (pfVideoPlayer.duration) {
    pfVpSeek.value = Math.round((pfVideoPlayer.currentTime / pfVideoPlayer.duration) * 1000)
  }
  pfVpTime.textContent = pfFmtTime(pfVideoPlayer.currentTime) + ' / ' + pfFmtTime(pfVideoPlayer.duration)
})
pfVideoPlayer.addEventListener('play', () => { pfVpPlayBtn.textContent = '⏸' })
pfVideoPlayer.addEventListener('pause', () => { pfVpPlayBtn.textContent = '▶' })
pfVideoPlayer.addEventListener('ended', () => { pfVpPlayBtn.textContent = '▶' })
// 缓冲相关：开始加载/卡顿时显示进度条，能播了/播起来就收起来
pfVideoPlayer.addEventListener('loadstart', () => pfVideoLoadingStart())
pfVideoPlayer.addEventListener('waiting', () => pfVideoLoadingStart())
pfVideoPlayer.addEventListener('stalled', () => pfVideoLoadingStart())
pfVideoPlayer.addEventListener('progress', () => { if (!pfVLoadDone) pfVideoLoadingSet(true, pfVideoBufferedPct()) })
pfVideoPlayer.addEventListener('loadedmetadata', () => { if (!pfVLoadDone) pfVideoLoadingSet(true, pfVideoBufferedPct()) })
pfVideoPlayer.addEventListener('canplay', () => pfVideoLoadingStop())
pfVideoPlayer.addEventListener('playing', () => pfVideoLoadingStop())
pfVideoPlayer.addEventListener('error', () => pfVideoLoadingStop())
function fetchPfVideoList() {
  if (pfVideoListCache) return Promise.resolve(pfVideoListCache)
  // 和厂区图片清单一样：换版本号绕过旧 CDN 缓存，新增视频后改这里即可
  return fetch('/api/videos?v=20261022a', { cache: 'no-cache' })
    .then((r) => r.json())
    .then((list) => { pfVideoListCache = list; return list })
    .catch(() => [])
}
function pfPlayVideo(file) {
  pfVideoLoadingStart()
  pfVideoPlayer.src = withVersion(videoUrl('products/' + file))
  pfVideoOverlay.classList.add('show')
  opt2PushOverlay(currentPfId) // 返回键先关"产品视频"，再关弹窗
  pfVideoPlayer.play().catch(() => {})
}
/**
 * 产品视频文件名的语言标记：
 *   <编号>_EN…  → 英文版视频
 *   <编号>_CN…  → 中文版视频
 *   <编号>…     → 只有编号（没有 EN/CN）= 中英双语视频
 */
function pfVideoLangTag(file) {
  const m = /^\d+[\s_-]*(EN|CN)(?![A-Za-z])/i.exec(String(file || ''))
  return m ? m[1].toUpperCase() : ''
}

function openPfVideo() {
  if (!currentPfId) return
  fetchPfVideoList().then((list) => {
    const owned = list
      .filter((f) => f.startsWith(currentPfId + '_') || f.startsWith(currentPfId + '.'))
    const en = getLang() === 'en'
    // 英文界面：只挂"英文版 + 双语版"；中文界面：只挂"中文版 + 双语版"。
    // 两种都没有时（只有另一种语言的版本）才退回全部，别出现"该产品暂无视频"。
    const wantTag = en ? 'EN' : 'CN'
    const mine = owned.filter((f) => {
      const tag = pfVideoLangTag(f)
      return tag === wantTag || tag === ''
    })
    const videos = (mine.length ? mine : owned).sort()
    if (!videos.length) {
      pfShowToast(t('pf.noVideo'))
      return
    }
    if (videos.length === 1) {
      pfPlayVideo(videos[0])
      return
    }
    // 多个视频：弹出选择层
    const picker = document.querySelector('#pf-video-picker')
    const listEl = document.querySelector('#pf-video-picker-list')
    listEl.innerHTML = ''
    videos.forEach((f, i) => {
      const btn = document.createElement('button')
      // 显示名：去掉展品编号与 EN/CN 标记后的名字
      const name = f.replace(/\.(mp4|webm|mov)$/i, '')
        .replace(/^\d+[\s_-]*/, '')
        .replace(/^(EN|CN)[\s_-]*/i, '')
      // 英文模式下：Video 1: <英文名>；视频名来自文件名，需要对照翻译
      btn.textContent = en
        ? 'Video ' + (i + 1) + ': ' + enVideoName(name)
        : '视频' + (i + 1) + '：' + name
      btn.addEventListener('click', () => {
        picker.classList.remove('show')
        pfPlayVideo(f)
      })
      listEl.appendChild(btn)
    })
    picker.classList.add('show')
  })
}
document.querySelector('#pf-video-picker-cancel').addEventListener('click', () => {
  document.querySelector('#pf-video-picker').classList.remove('show')
})
document.querySelector('#pf-video-close').addEventListener('click', () => {
  opt2CloseUser(() => {
    pfVideoOverlay.classList.remove('show')
    pfVideoLoadingStop()
    pfVideoPlayer.pause()
    pfVideoPlayer.removeAttribute('src')
    pfVideoPlayer.load()
  })
})

// 热区定位与点击
document.querySelectorAll('.pf-hotspot').forEach((hs) => {
  const r = hotspotRects[hs.dataset.hotspot]
  if (r) {
    hs.style.left = r.left + '%'
    hs.style.top = r.top + '%'
    hs.style.width = r.width + '%'
    hs.style.height = r.height + '%'
  }
  hs.addEventListener('click', (e) => {
    e.stopPropagation()
    if (hs.dataset.hotspot === 'video') openPfVideo()
    else if (hs.dataset.hotspot === 'explode') pfOpenExplodeIfAvailable()
    else if (hs.dataset.hotspot === 'intro') openPfIntro()
    else if (hs.dataset.hotspot === 'pdf') openPfPdf()
  })
})

function pfShowToast(msg) {
  pfToast.textContent = msg
  pfToast.classList.remove('hidden')
  clearTimeout(pfShowToast._t)
  pfShowToast._t = setTimeout(() => pfToast.classList.add('hidden'), 2200)
}

// 所有产品弹窗统一使用的新 UI 背景模板
// 弹窗/全屏产品页的背景图：原 2.9 MB PNG，换成 WebP 后约 70 KB（带版本号，可被 CDN 长缓存）
const PRODUCT_UI_BG = withVersion('images/products/product-full-bg.webp')

function openProductFull(id) {
  const p = PRODUCTS[id] || {}
  currentPfId = id
  pfJustOpened = true
  // 所有产品统一使用新 UI 背景模板
  pfBgBaseSrc = PRODUCT_UI_BG
  pfBg.onload = () => {
    layoutHotspots()
    pfEdUpdateBgPlane()
    pfModelLayout() // 背景图就绪后再校准模型视口，避免首开位置偏移
    if (pfModelCtx && pfModelCtx.pivot && pfModelCtx.pivot.children.length) pfModelApply()
    // 背景图完全就绪后才开始加载中央模型，保证第一次打开不再用错误视口
    if (pfModelStartId) {
      const id = pfModelStartId
      pfModelStartId = null
      pfModelTryLoad(id)
    }
  }
  pfBg.onerror = () => {
    // 原来的兜底图 45-intro.jpg 已随"清理无效引用"一起删除：再切过去只会
    // 触发第二次 onerror（而且不会成功）。失败就记一条日志，页面用自身底色。
    pfBg.onerror = null
    console.warn('[product-full] 背景图加载失败：' + pfBg.src)
  }
  pfBg.src = PRODUCT_UI_BG
  productFull.classList.add('show')
  pfOpenedAt = performance.now() // 记下打开时刻，手机上的"补点击"要挡掉
  // 新版六按钮：打开弹窗时按当前语言重建，并显示
  const booth = ensurePfBoothUI()
  booth.setButtons(pfBoothButtons())
  booth.setVisible(!productFull.classList.contains('editing'))
  // setButtons 会重建按钮 DOM，重建后再按当前屏幕尺寸量一次（含"标签自适应"）
  pfApplyBoothScale()
  pfSetTitleZh((p.name && p.name !== '待补充') ? p.name : '')
  pfEdResize() // 弹窗显示后再更新编辑视口（此前元素不可见，尺寸为 0）
  pfModelStartId = id // 等背景图 onload 后再加载模型
  // 加载该产品自己保存的页面布局（共享文件优先，兼容旧 localStorage）
  loadPfLayout(id)
}

// ?pf=<编号> 用：直接打开某个展品的弹窗，并补上"爆炸图"需要的那本书
// （正常点展品时这本书由 openProduct 记录，调试入口要自己补一下）
function pfDebugOpen(id) {
  const p = PRODUCTS[id] || {}
  currentExplodeProduct = { id, name: p.name || ('产品' + id), explodeModel: p.explodeModel || '' }
  openProductFull(id)
  // 调试入口打开弹窗时也补一条历史记录，保证返回键行为和正常点开时一致
  opt2PushProduct(id)
}

// 调试：自动化核对用（只读，不参与任何界面逻辑）
window.__opt2 = Object.assign(window.__opt2 || {}, {
  openProductFull,
  pfOpen: pfDebugOpen,
  pfBoothMetrics: () => {
    if (!pfBoothUI) return null
    const r1 = (v) => Math.round(v)
    const btns = [...productFull.querySelectorAll('.booth-btn')].map((b) => {
      const r = b.getBoundingClientRect()
      return { text: (b.textContent || '').trim().slice(0, 14), w: r1(r.width), h: r1(r.height), x: r1(r.x), y: r1(r.y) }
    })
    const card = productFull.querySelector('.pm-card')
    const cr = card ? card.getBoundingClientRect() : null
    const cs = getComputedStyle(pfBoothUI.root)
    return {
      vw: window.innerWidth,
      vh: window.innerHeight,
      uiScale: +pfUiScale().toFixed(3),
      boothW: pfBoothLayoutWidth(),
      labelSize: cs.getPropertyValue('--booth-label-size').trim(),
      card: cr ? { x: r1(cr.x), y: r1(cr.y), w: r1(cr.width), h: r1(cr.height) } : null,
      buttons: btns,
    }
  },
})

function closeProductFull() {
  productFull.classList.remove('show')
  pfJustOpened = false
  pfEditorExit()
  if (pfModelCtx) pfModelCtx.group.clear()
  pfModelCanvas.style.display = 'none'
  pfZoom = 1
  pfPanX = 0
  pfPanY = 0
  pfApplyZoom()
  pfPdfOverlay.classList.remove('show')
  pfPdfFrame.src = ''
  pfVideoOverlay.classList.remove('show')
  pfVideoLoadingStop()
  pfVideoPlayer.pause()
  pfVideoPlayer.removeAttribute('src')
  pfVideoPlayer.load()
  document.querySelector('#pf-video-picker').classList.remove('show')
  closePfIntro()
  // 同步复位弹窗状态，避免 modalOpen 一直为 true 导致后续点击全部被拦截
  modal.classList.remove('show')
  modalOpen = false
}

// ---------- 弹窗图层编辑器（PC：文字/图片 + 四角透视变形） ----------
const pfEditCanvas = document.querySelector('#pf-edit-canvas')
const pfEditToggle = document.querySelector('#pf-edit-toggle')
const pfAddTextBtn = document.querySelector('#pf-add-text')
const pfAddImageBtn = document.querySelector('#pf-add-image')
const pfDelLayerBtn = document.querySelector('#pf-del-layer')
const pfEditDoneBtn = document.querySelector('#pf-edit-done')
const pfFreezeBtn = document.querySelector('#pf-freeze-btn')
const pfStylePanel = document.querySelector('#pf-style-panel')
const pfStyleEditBtn = document.querySelector('#pf-style-edit')
const pfStyleColor = document.querySelector('#pf-style-color')
const pfStyleSize = document.querySelector('#pf-style-size')
const pfStyleSizeVal = document.querySelector('#pf-style-size-val')
const pfStyleFont = document.querySelector('#pf-style-font')
const pfImageInput = document.createElement('input')
pfImageInput.type = 'file'
pfImageInput.accept = 'image/*'
pfImageInput.style.display = 'none'
document.body.appendChild(pfImageInput)

const pfEdScene = new THREE.Scene()
const pfEdCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100)
pfEdCamera.position.z = 10
let pfEdRenderer = null // 懒创建：仅进入编辑模式时才初始化 WebGL 上下文，避免手机端多上下文崩溃
function ensurePfEdRenderer() {
  if (pfEdRenderer) return pfEdRenderer
  try {
    pfEdRenderer = new THREE.WebGLRenderer({ canvas: pfEditCanvas, alpha: true, antialias: true, preserveDrawingBuffer: true })
    pfEdRenderer.setClearColor(0x000000, 0)
  } catch (err) {
    console.warn('[pf-editor] WebGL init failed:', err && err.message)
    pfEdRenderer = null
  }
  return pfEdRenderer
}
const pfEdRay = new THREE.Raycaster()
const pfEdPtr = new THREE.Vector2()
const pfEdLayers = []   // { mesh, type, corners:[Vector3 x4] }
const pfEdHandleEls = []  // 4 个角点手柄（DOM，覆盖在文本框层之上）
let pfEdSelected = null
let pfEdDrag = null
let pfEdViewW = 1
let pfEdViewH = 1
let pfEdRenderFailed = false
let pfEdDiagFrame = 0
let pfBgBaseSrc = '' // 弹窗原始背景图（不含图层），烘焙时作为底层

// 背景图 contain 显示区域（世界像素坐标）
function pfBgHalf() {
  const rect = productFull.getBoundingClientRect()
  const iw = pfBg.naturalWidth || 2400
  const ih = pfBg.naturalHeight || 1600
  const scale = Math.min(rect.width / iw, rect.height / ih)
  return { halfW: Math.max(1, (iw * scale) / 2), halfH: Math.max(1, (ih * scale) / 2) }
}
// 图层坐标：相对背景图的比例（背景图中心为原点，±1 对应背景图半宽/半高）
function pfCornerToWorld(px, py) {
  const h = pfBgHalf()
  return new THREE.Vector3(px * h.halfW, py * h.halfH, 0)
}
function pfWorldToCorner(wx, wy) {
  const h = pfBgHalf()
  return [wx / h.halfW, wy / h.halfH]
}
// 字号按背景图宽度等比缩放：存储相对背景图宽的比例，显示时换算像素
function pfBgWidthPx() {
  return pfBgHalf().halfW * 2
}
function pfFontToPx(ratio) {
  return (ratio || 0.013) * pfBgWidthPx()
}
function pfFontToRatio(px) {
  return (px || 12) / pfBgWidthPx()
}

// 编辑场景底层背景平面（模拟 object-fit: contain 的显示区域）
const pfEdBgPlane = new THREE.Mesh(
  new THREE.PlaneGeometry(1, 1),
  new THREE.MeshBasicMaterial({ map: null, depthTest: false, transparent: true })
)
pfEdBgPlane.position.z = -1
pfEdScene.remove(pfEdBgPlane) // 背景由 DOM 图提供，画布只渲染编辑图层

function pfEdUpdateBgPlane() {
  if (!pfBgBaseSrc) return
  const img = new Image()
  img.onload = () => {
    const tex = new THREE.Texture(img)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.needsUpdate = true
    pfEdBgPlane.material.map = tex
    pfEdBgPlane.material.needsUpdate = true
    // 平面尺寸 = 背景图 contain 显示区域（与 layoutHotspots 一致）
    const rect = productFull.getBoundingClientRect()
    const iw = img.width
    const ih = img.height
    const scale = Math.min(rect.width / iw, rect.height / ih)
    pfEdBgPlane.scale.set(iw * scale, ih * scale, 1)
  }
  img.src = pfBgBaseSrc
}

// 烘焙：把背景 + 图片/冻结文字图层渲染成一张图，作为弹窗背景预览
function pfEdBake() {
  const hasMesh = pfEdLayers.some((l) => l.mesh)
  console.warn('[pf-bake] layers=' + pfEdLayers.length + ' hasMesh=' + hasMesh + ' view=' + pfEdViewW + 'x' + pfEdViewH)
  if (!hasMesh) return
  const img = new Image()
  img.onload = () => {
    const tex = new THREE.Texture(img)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.needsUpdate = true
    pfEdBgPlane.material.map = tex
    pfEdBgPlane.material.needsUpdate = true
    const rect = productFull.getBoundingClientRect()
    const scale = Math.min(rect.width / img.width, rect.height / img.height)
    pfEdBgPlane.scale.set(img.width * scale, img.height * scale, 1)
    try {
      const r = ensurePfEdRenderer()
      if (!r) return
      r.setSize(pfEdViewW, pfEdViewH, false)
      r.render(pfEdScene, pfEdCamera)
      const url = pfEditCanvas.toDataURL('image/png')
      const meshInfo = pfEdLayers.filter((l) => l.mesh).map((l) => ({
        type: l.type,
        frozen: !!l.frozen,
        c: l.corners.map((c) => [Math.round(c.x), Math.round(c.y)])
      }))
      console.warn('[pf-bake] dataURL len=' + (url ? url.length : 0) + ' view=' + pfEdViewW + 'x' + pfEdViewH + ' mesh=' + JSON.stringify(meshInfo))
      if (url && url.length > 100) {
        pfBg.src = url
        pfBg.onload = () => { console.warn('[pf-bake] bg set ok'); layoutHotspots() }
      }
    } catch (err) {
      console.warn('[pf-bake] failed:', err && err.message)
    }
  }
  img.src = pfBgBaseSrc || PRODUCT_UI_BG
}

function pfEdResize() {
  const rect = productFull.getBoundingClientRect()
  pfEdViewW = Math.max(1, rect.width || 1)
  pfEdViewH = Math.max(1, rect.height || 1)
  pfEdCamera.left = -pfEdViewW / 2
  pfEdCamera.right = pfEdViewW / 2
  pfEdCamera.top = pfEdViewH / 2
  pfEdCamera.bottom = -pfEdViewH / 2
  pfEdCamera.updateProjectionMatrix()
  if (pfEdRenderer) pfEdRenderer.setSize(pfEdViewW, pfEdViewH, false)
  pfEdUpdateHandles()
}
window.addEventListener('resize', () => {
  if (!productFull.classList.contains('show')) return
  pfEdResize()
  // 屏幕尺寸变化时，按背景图比例刷新所有图层位置
  pfEdLayers.forEach((l) => {
    if (l.div) pfEdUpdateTextBox(l)
    if (l.mesh) pfEdUpdateGeometry(l)
  })
  pfEdUpdateHandles()
})

// 文字转 canvas 纹理（支持按指定宽度自动换行）
function makePfTextTexture(text, color, fontSize, widthPx, fontFamily) {
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')
  const fs = fontSize || 40
  const family = fontFamily || 'Microsoft YaHei'
  ctx.font = `bold ${fs}px "${family}", sans-serif`
  const maxW = widthPx || 600
  // 按字符切分并逐行测量，宽度超 maxW 换行
  const chars = String(text || '').split('')
  const lines = []
  let cur = ''
  for (const ch of chars) {
    if (ctx.measureText(cur + ch).width > maxW && cur) {
      lines.push(cur)
      cur = ch
    } else {
      cur += ch
    }
  }
  if (cur) lines.push(cur)
  const textW = lines.length ? Math.max(...lines.map((ln) => ctx.measureText(ln).width)) : 20
  const w = Math.max(20, Math.ceil(textW) + 8)
  const h = Math.max(20, Math.ceil(lines.length * fs * 1.6 + fs * 0.4))
  c.width = w
  c.height = h
  ctx.font = `bold ${fs}px "${family}", sans-serif`
  ctx.fillStyle = color || '#ffffff'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  lines.forEach((ln, i) => ctx.fillText(ln, 4, fs * 0.6 + i * fs * 1.6))
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// 平滑网格：4 角目标位置 → 双线性插值更新所有顶点
function pfEdUpdateGeometry(layer) {
  const geo = layer.mesh.geometry
  const pos = geo.attributes.position
  const seg = 8
  const c = layer.corners
  const w0 = pfCornerToWorld(c[0].x, c[0].y)
  const w1 = pfCornerToWorld(c[1].x, c[1].y)
  const w2 = pfCornerToWorld(c[2].x, c[2].y)
  const w3 = pfCornerToWorld(c[3].x, c[3].y)
  for (let iy = 0; iy <= seg; iy++) {
    for (let ix = 0; ix <= seg; ix++) {
      const u = ix / seg
      const v = iy / seg
      const idx = iy * (seg + 1) + ix
      const topX = w0.x + (w1.x - w0.x) * u
      const topY = w0.y + (w1.y - w0.y) * u
      const botX = w2.x + (w3.x - w2.x) * u
      const botY = w2.y + (w3.y - w2.y) * u
      pos.setXYZ(idx, topX + (botX - topX) * v, topY + (botY - topY) * v, 0)
    }
  }
  pos.needsUpdate = true
  geo.computeVertexNormals()
}

function pfEdCreateLayer(type, texture, w, h, meta, cornersOverride) {
  const bh = pfBgHalf()
  const halfW = ((w || 260) / 2) / bh.halfW
  const halfH = ((h || 140) / 2) / bh.halfH
  const corners = cornersOverride || [
    new THREE.Vector3(-halfW, halfH, 0),
    new THREE.Vector3(halfW, halfH, 0),
    new THREE.Vector3(-halfW, -halfH, 0),
    new THREE.Vector3(halfW, -halfH, 0)
  ]
  if (type === 'text') {
    const rawFs = (meta && meta.fontSize) || 12
    // 文字图层：DOM 文本框（排版容器，文字在框内自动换行、可直接编辑）
    const layer = {
      type,
      mesh: null,
      div: null,
      corners,
      texture: null,
      text: (meta && meta.text) || '文字',
      color: (meta && meta.color) || '#ffffff',
      fontSize: rawFs > 1 ? pfFontToRatio(rawFs) : rawFs,
      fontFamily: (meta && meta.fontFamily) || 'Microsoft YaHei',
      frozen: false
    }
    const div = document.createElement('div')
    div.className = 'pf-text-box'
    div.textContent = layer.text
    document.querySelector('#pf-text-layer').appendChild(div)
    layer.div = div
    pfEdLayers.push(layer)
    pfEdUpdateTextBox(layer)
    pfEdBindTextDiv(layer)
    return layer
  }
  // 图片图层：3D 网格（四角自由透视）
  const geo = new THREE.PlaneGeometry(1, 1, 8, 8)
  const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide, depthTest: false })
  const mesh = new THREE.Mesh(geo, mat)
  const layer = {
    mesh,
    type,
    div: null,
    corners,
    texture,
    text: meta && meta.text,
    color: meta && meta.color,
    fontSize: meta && meta.fontSize,
    fontFamily: meta && meta.fontFamily,
    frozen: false
  }
  mesh.renderOrder = 100 + pfEdLayers.length
  pfEdUpdateGeometry(layer)
  pfEdScene.add(mesh)
  pfEdLayers.push(layer)
  return layer
}

// DOM 角点手柄
function pfEdMakeHandles() {
  const layer = document.querySelector('#pf-text-layer')
  for (let i = 0; i < 4; i++) {
    const h = document.createElement('div')
    h.className = 'pf-handle'
    h.dataset.idx = i
    layer.appendChild(h)
    pfEdHandleEls.push(h)
    h.addEventListener('pointerdown', (e) => {
      if (!productFull.classList.contains('editing') || !pfEdSelected) return
      e.preventDefault()
      e.stopPropagation()
      pfEdDrag = {
        type: 'corner',
        layer: pfEdSelected,
        idx: i,
        startWorld: pfEdScreenToWorld(e.clientX, e.clientY),
        startCornerWorld: pfCornerToWorld(pfEdSelected.corners[i].x, pfEdSelected.corners[i].y)
      }
      h.setPointerCapture(e.pointerId)
    })
    h.addEventListener('pointermove', (e) => {
      if (!pfEdDrag || pfEdDrag.type !== 'corner' || pfEdDrag.idx !== i) return
      const w = pfEdScreenToWorld(e.clientX, e.clientY)
      const layer = pfEdDrag.layer
      const nx = pfEdDrag.startCornerWorld.x + (w.x - pfEdDrag.startWorld.x)
      const ny = pfEdDrag.startCornerWorld.y + (w.y - pfEdDrag.startWorld.y)
      if (layer.frozen || layer.type === 'image') {
        // 冻结/图片：自由透视，只动拖的角
        const pc = pfWorldToCorner(nx, ny)
        layer.corners[i].set(pc[0], pc[1], 0)
      } else {
        // 文本框：对角固定，其余三点移动形成新矩形
        const c = layer.corners
        const fi = 3 - i // 正确对角：0↔3（左上↔右下）、1↔2（右上↔左下）
        const fw = pfCornerToWorld(c[fi].x, c[fi].y)
        const x0 = Math.min(nx, fw.x)
        const x1 = Math.max(nx, fw.x)
        const y0 = Math.max(ny, fw.y)
        const y1 = Math.min(ny, fw.y)
        const p0 = pfWorldToCorner(x0, y0)
        const p1 = pfWorldToCorner(x1, y0)
        const p2 = pfWorldToCorner(x0, y1)
        const p3 = pfWorldToCorner(x1, y1)
        c[0].set(p0[0], p0[1], 0)
        c[1].set(p1[0], p1[1], 0)
        c[2].set(p2[0], p2[1], 0)
        c[3].set(p3[0], p3[1], 0)
      }
      if (layer.mesh) pfEdUpdateGeometry(layer)
      if (layer.div) pfEdUpdateTextBox(layer)
      pfEdUpdateHandles()
    })
    h.addEventListener('pointerup', () => { pfEdDrag = null })
    h.addEventListener('pointercancel', () => { pfEdDrag = null })
  }
}
pfEdMakeHandles()

function pfEdSelect(layer) {
  pfEdSelected = layer
  pfDelLayerBtn.classList.remove('hidden')
  if (layer && layer.div) layer.div.classList.toggle('selected', !layer.frozen)
  const isText = layer && layer.type === 'text' && !layer.frozen
  pfStylePanel.classList.toggle('hidden', !isText)
  pfFreezeBtn.classList.toggle('hidden', !(layer && layer.type === 'text' && !layer.frozen))
  if (isText) {
    pfStyleColor.value = layer.color || '#ffffff'
    pfStyleSize.value = Math.round(pfFontToPx(layer.fontSize))
    pfStyleSizeVal.textContent = pfStyleSize.value
    pfStyleFont.value = layer.fontFamily || 'Microsoft YaHei'
  }
  pfEdUpdateHandles()
}

// 根据四角（矩形）更新 DOM 文本框位置/大小
function pfEdUpdateTextBox(layer) {
  if (!layer.div) return
  const c = layer.corners
  const w0 = pfCornerToWorld(c[0].x, c[0].y)
  const w1 = pfCornerToWorld(c[1].x, c[1].y)
  const w2 = pfCornerToWorld(c[2].x, c[2].y)
  const x0 = Math.min(w0.x, w1.x)
  const x1 = Math.max(w0.x, w1.x)
  const y0 = Math.max(w0.y, w2.y)
  const y1 = Math.min(w0.y, w2.y)
  const el = layer.div
  el.style.left = (x0 + pfEdViewW / 2) + 'px'
  el.style.top = (pfEdViewH / 2 - y0) + 'px'
  el.style.width = (x1 - x0) + 'px'
  el.style.height = (y0 - y1) + 'px'
  el.style.fontSize = pfFontToPx(layer.fontSize) + 'px'
  el.style.color = layer.color || '#ffffff'
  el.style.fontFamily = `"${layer.fontFamily || 'Microsoft YaHei'}", sans-serif`
  el.style.lineHeight = '1.6'
}

// 绑定文本框 DOM 事件：点击选中、拖动移动、双击编辑
function pfEdBindTextDiv(layer) {
  const div = layer.div
  div.addEventListener('pointerdown', (e) => {
    if (!productFull.classList.contains('editing')) return
    if (div.classList.contains('editing')) return
    e.preventDefault()
    pfEdSelect(layer)
    div.setPointerCapture(e.pointerId)
    const start = { x: e.clientX, y: e.clientY }
    const onMove = (ev) => {
      const w = pfEdScreenToWorld(ev.clientX, ev.clientY)
      const last = pfEdScreenToWorld(start.x, start.y)
      const dx = w.x - last.x
      const dy = w.y - last.y
      const pc = pfWorldToCorner(dx, dy)
      for (const c of layer.corners) { c.x += pc[0]; c.y += pc[1] }
      start.x = ev.clientX
      start.y = ev.clientY
      pfEdUpdateTextBox(layer)
      pfEdUpdateHandles()
    }
    const onUp = () => {
      div.removeEventListener('pointermove', onMove)
      div.removeEventListener('pointerup', onUp)
    }
    div.addEventListener('pointermove', onMove)
    div.addEventListener('pointerup', onUp)
  })
  div.addEventListener('dblclick', () => {
    if (!productFull.classList.contains('editing')) return
    pfEdSelect(layer)
    div.classList.add('editing')
    div.contentEditable = 'true'
    div.focus()
    // 全选文字方便替换
    const sel = window.getSelection()
    const range = document.createRange()
    range.selectNodeContents(div)
    sel.removeAllRanges()
    sel.addRange(range)
  })
  div.addEventListener('blur', () => {
    div.classList.remove('editing')
    div.contentEditable = 'false'
    layer.text = div.textContent.trim() || '文字'
  })
}

// 文字转图片：用 SVG foreignObject 快照文本框 DOM，100% 复刻字体/字重/颜色/行距
function pfEdFreezeText(layer, preserveCorners) {
  const isReFreeze = !!layer.mesh || preserveCorners
  if (layer.mesh) pfEdScene.remove(layer.mesh)
  const c0w = pfCornerToWorld(layer.corners[0].x, layer.corners[0].y)
  const c1w = pfCornerToWorld(layer.corners[1].x, layer.corners[1].y)
  const boxW = Math.max(20, Math.abs(c1w.x - c0w.x))
  // 离屏快照 div：完全复刻文本框的 CSS 状态
  const snap = document.createElement('div')
  snap.style.cssText = [
    `width:${boxW}px`,
    `font-family:"${layer.fontFamily || 'Microsoft YaHei'}",sans-serif`,
    `font-size:${Math.round(pfFontToPx(layer.fontSize))}px`,
    `color:${layer.color || '#ffffff'}`,
    'line-height:1.6',
    'padding:6px 8px',
    'box-sizing:border-box',
    'word-break:break-all',
    'white-space:normal',
    'font-weight:400',
    'position:fixed',
    'left:-9999px',
    'top:0'
  ].join(';')
  snap.textContent = layer.text || '文字'
  document.body.appendChild(snap)
  const w = Math.max(1, snap.offsetWidth)
  const h = Math.max(1, snap.offsetHeight)
  snap.remove()
  // 生成 SVG 时只保留排版样式（不能带 fixed/left 测量定位，否则渲染到屏幕外变空白）
  const snapStyle = [
    `width:${w}px`,
    `font-family:'${layer.fontFamily || 'Microsoft YaHei'}',sans-serif`,
    `font-size:${Math.round(pfFontToPx(layer.fontSize))}px`,
    `color:${layer.color || '#ffffff'}`,
    'line-height:1.6',
    'padding:6px 8px',
    'box-sizing:border-box',
    'word-break:break-all',
    'white-space:normal',
    'font-weight:400'
  ].join(';')
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  // 高分辨率渲染：SVG 按 3 倍像素输出（viewBox 保持逻辑尺寸，div 排版不变）
  const RES = 3
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w * RES}" height="${h * RES}" viewBox="0 0 ${w} ${h}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="${snapStyle}">${esc(layer.text || '文字')}</div></foreignObject></svg>`
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = w * RES
    canvas.height = h * RES
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    const geo = new THREE.PlaneGeometry(1, 1, 8, 8)
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthTest: false })
    const mesh = new THREE.Mesh(geo, mat)
    layer.mesh = mesh
    layer.texture = tex
    layer.frozen = true
    mesh.renderOrder = 100 + pfEdLayers.indexOf(layer)
    if (!isReFreeze) {
      // 首次冻结：网格尺寸 = 快照尺寸（1:1），以原文本框中心为基准
      const cx = (layer.corners[0].x + layer.corners[1].x) / 2
      const cy = (layer.corners[0].y + layer.corners[2].y) / 2
      const bh = pfBgHalf()
      const hw = (w / 2) / bh.halfW
      const hh = (h / 2) / bh.halfH
      layer.corners[0].set(cx - hw, cy + hh, 0)
      layer.corners[1].set(cx + hw, cy + hh, 0)
      layer.corners[2].set(cx - hw, cy - hh, 0)
      layer.corners[3].set(cx + hw, cy - hh, 0)
    }
    // 再次冻结（编辑文字后）：保留当前角点（含拉伸透视），只更新纹理
    pfEdUpdateGeometry(layer)
    pfEdScene.add(mesh)
    pfEdUpdateHandles()
    if (layer.div) {
      layer.div.remove()
      layer.div = null
    }
  }
  img.onerror = () => {
    pfShowToast('文字转图片失败，请重试')
  }
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

pfStyleEditBtn.addEventListener('click', () => {
  if (!pfEdSelected || pfEdSelected.type !== 'text') return
  const dialog = document.querySelector('#pf-text-dialog')
  const input = document.querySelector('#pf-text-input')
  input.value = pfEdSelected.text || ''
  dialog.classList.add('show')
  setTimeout(() => input.focus(), 50)
})
pfStyleColor.addEventListener('input', () => {
  if (!pfEdSelected || pfEdSelected.type !== 'text') return
  pfEdSelected.color = pfStyleColor.value
  pfEdUpdateTextBox(pfEdSelected)
})
pfStyleSize.addEventListener('input', () => {
  if (!pfEdSelected || pfEdSelected.type !== 'text') return
  pfEdSelected.fontSize = pfFontToRatio(Number(pfStyleSize.value))
  pfStyleSizeVal.textContent = pfStyleSize.value
  pfEdUpdateTextBox(pfEdSelected)
})
pfStyleFont.addEventListener('change', () => {
  if (!pfEdSelected || pfEdSelected.type !== 'text') return
  pfEdSelected.fontFamily = pfStyleFont.value
  pfEdUpdateTextBox(pfEdSelected)
})
pfFreezeBtn.addEventListener('click', () => {
  if (!pfEdSelected || pfEdSelected.type !== 'text') return
  pfEdFreezeText(pfEdSelected)
  pfStylePanel.classList.add('hidden')
  pfFreezeBtn.classList.add('hidden')
  pfEdUpdateHandles()
})

function pfEdUpdateHandles() {
  const show = !!pfEdSelected
  pfEdHandleEls.forEach((el, i) => {
    el.style.display = show ? 'block' : 'none'
    if (show) {
      const c = pfEdSelected.corners[i]
      const w = pfCornerToWorld(c.x, c.y)
      el.style.left = (w.x + pfEdViewW / 2) + 'px'
      el.style.top = (pfEdViewH / 2 - w.y) + 'px'
    }
  })
}

// 屏幕坐标 → 编辑器世界坐标（像素 1:1）
function pfEdScreenToWorld(clientX, clientY) {
  const rect = pfEditCanvas.getBoundingClientRect()
  if (!rect.width || !rect.height) return new THREE.Vector3(0, 0, 0)
  const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1
  const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1
  return new THREE.Vector3(ndcX * pfEdViewW / 2, ndcY * pfEdViewH / 2, 0)
}

pfEditCanvas.addEventListener('pointerdown', (e) => {
  if (!productFull.classList.contains('editing')) return
  e.preventDefault()
  pfEditCanvas.setPointerCapture(e.pointerId)
  pfEdPtr.x = ((e.clientX - pfEditCanvas.getBoundingClientRect().left) / pfEdViewW) * 2 - 1
  pfEdPtr.y = -((e.clientY - pfEditCanvas.getBoundingClientRect().top) / pfEdViewH) * 2 + 1
  pfEdRay.setFromCamera(pfEdPtr, pfEdCamera)
  const meshLayers = pfEdLayers.filter((l) => l.mesh)
  const lHits = pfEdRay.intersectObjects(meshLayers.map((l) => l.mesh), false)
  if (lHits.length) {
    const layer = pfEdLayers.find((l) => l.mesh === lHits[0].object)
    if (layer) {
      pfEdSelect(layer)
      pfEdDrag = { type: 'move', layer, last: pfEdScreenToWorld(e.clientX, e.clientY) }
    }
    return
  }
  pfEdSelected = null
  pfEdUpdateHandles()
  document.querySelectorAll('.pf-text-box.selected').forEach((d) => d.classList.remove('selected'))
  pfStylePanel.classList.add('hidden')
  pfFreezeBtn.classList.add('hidden')
})

pfEditCanvas.addEventListener('pointermove', (e) => {
  if (!pfEdDrag) return
  const w = pfEdScreenToWorld(e.clientX, e.clientY)
  if (pfEdDrag.type === 'corner') {
    // 角点拖动已由 DOM 手柄处理，此处不再处理
    return
  } else if (pfEdDrag.type === 'move') {
    const dx = w.x - pfEdDrag.last.x
    const dy = w.y - pfEdDrag.last.y
    pfEdDrag.last.copy(w)
    const pc = pfWorldToCorner(dx, dy)
    for (const c of pfEdDrag.layer.corners) { c.x += pc[0]; c.y += pc[1] }
    if (pfEdDrag.layer.mesh) pfEdUpdateGeometry(pfEdDrag.layer)
    if (pfEdDrag.layer.div) pfEdUpdateTextBox(pfEdDrag.layer)
    pfEdUpdateHandles()
  }
})

function pfEdDragEnd() {
  pfEdDrag = null
}
pfEditCanvas.addEventListener('pointerup', pfEdDragEnd)
pfEditCanvas.addEventListener('pointercancel', pfEdDragEnd)

function pfEdRenderLoop() {
  requestAnimationFrame(pfEdRenderLoop)
  if (pfEdRenderFailed || !productFull.classList.contains('show')) return
  if (!pfEdRenderer && !ensurePfEdRenderer()) return
  try {
    pfEdRenderer.render(pfEdScene, pfEdCamera)
  } catch (err) {
    console.warn('[pf-editor] render error, stopping:', err && err.message)
    pfEdRenderFailed = true
  }
}
pfEdRenderLoop()

// 编辑图层要密码：初始密码 obair（想换就改这一行，改完重新构建即可）
const PF_EDIT_PASSWORD = 'obair'
let pfEditUnlocked = false
const pfPassDialog = document.querySelector('#pf-pass-dialog')
const pfPassInput = document.querySelector('#pf-pass-input')
const pfPassError = document.querySelector('#pf-pass-error')
const pfPassTitle = document.querySelector('#pf-pass-title')
let pfPassOnOk = null
function pfPassOpen(onOk) {
  pfPassOnOk = onOk
  if (pfPassTitle) pfPassTitle.textContent = getLang() === 'en' ? 'Password required' : '请输入编辑密码'
  if (pfPassError) pfPassError.classList.add('hidden')
  if (pfPassInput) pfPassInput.value = ''
  pfPassDialog?.classList.add('show')
  setTimeout(() => pfPassInput?.focus(), 30)
}
function pfPassClose() {
  pfPassOnOk = null
  pfPassDialog?.classList.remove('show')
}
function pfPassSubmit() {
  const val = (pfPassInput?.value || '').trim()
  if (val !== PF_EDIT_PASSWORD) {
    if (pfPassError) pfPassError.classList.remove('hidden')
    if (pfPassInput) { pfPassInput.value = ''; pfPassInput.focus() }
    return
  }
  const ok = pfPassOnOk
  pfPassClose()
  if (ok) ok()
}
document.querySelector('#pf-pass-ok')?.addEventListener('click', pfPassSubmit)
document.querySelector('#pf-pass-cancel')?.addEventListener('click', pfPassClose)
pfPassInput?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { e.preventDefault(); pfPassSubmit() }
  else if (e.key === 'Escape') { e.preventDefault(); pfPassClose() }
})
pfPassDialog?.addEventListener('click', (e) => { if (e.target === pfPassDialog) pfPassClose() })

function pfEditorEnter() {
  console.warn('[pf-editor] enter edit')
  productFull.classList.add('editing')
  pfBoothUI?.setVisible(false)
  pfModelToggle.classList.remove('hidden')
  pfModelCanvas.style.display = 'none' // 默认隐藏模型，避免挡图层编辑
  if (!ensurePfEdRenderer()) pfShowToast('当前设备不支持编辑（WebGL 不可用）')
  pfEdResize()
  pfEditToggle.classList.add('hidden')
  pfEditDoneBtn.classList.remove('hidden')
  pfAddTextBtn.classList.remove('hidden')
  pfAddImageBtn.classList.remove('hidden')
}
pfEditToggle.addEventListener('click', () => {
  if (pfEditUnlocked) { pfEditorEnter(); return }
  pfPassOpen(() => { pfEditUnlocked = true; pfEditorEnter() })
})

function pfEditorExit() {
  const wasEditing = productFull.classList.contains('editing')
  productFull.classList.remove('editing')
  pfBoothUI?.setVisible(true)
  pfEditToggle.classList.remove('hidden')
  pfEditDoneBtn.classList.add('hidden')
  pfAddTextBtn.classList.add('hidden')
  pfAddImageBtn.classList.add('hidden')
  pfDelLayerBtn.classList.add('hidden')
  pfEdSelected = null
  pfEdUpdateHandles()
  document.querySelectorAll('.pf-text-box.editing').forEach((d) => {
    d.classList.remove('editing')
    d.contentEditable = 'false'
  })
  document.querySelectorAll('.pf-text-box.selected').forEach((d) => d.classList.remove('selected'))
  pfStylePanel.classList.add('hidden')
  pfFreezeBtn.classList.add('hidden')
  pfEdDrag = null
  pfModelPanel.classList.add('hidden')
  pfModelToggle.classList.add('hidden')
  pfModelAdjusting = false
  if (wasEditing && pfModelCtx && pfModelCtx.group.children.length) {
    if (pfModelDirty) {
      pfModelSaveParams(currentPfId, true) // 编辑里动过模型则自动保存视角
      pfModelDirty = false
    }
    pfModelCanvas.style.display = 'block' // 恢复预览显示
    pfModelLayout()
    pfModelApply() // 退出编辑时按保存参数重建一次模型/相机关系，避免移动被重置
  }
}
pfEditDoneBtn.addEventListener('click', pfEditorExit)

pfModelToggle.addEventListener('click', () => {
  if (!pfModelCtx || !pfModelCtx.group.children.length) {
    pfShowToast('当前产品无 3D 模型')
    return
  }
  pfModelAdjusting = !pfModelAdjusting
  pfModelPanel.classList.toggle('hidden', !pfModelAdjusting)
  pfModelCanvas.style.display = pfModelAdjusting ? 'block' : 'none'
  if (pfModelAdjusting) {
    pfModelLayout()
    // 把预览中当前相机状态同步到滑杆/参数，进入编辑即所见即所得
    if (pfModelCtx && pfModelCtx.controls) {
      try {
        pfModelCtx.controls.update()
        const azi = pfModelCtx.controls.getAzimuthalAngle()
        const pol = pfModelCtx.controls.getPolarAngle()
        pfModelCtx.params.rotY = (azi * 180 / Math.PI + 360) % 360
        pfModelCtx.params.rotX = Math.round((Math.PI / 2 - pol) * 180 / Math.PI)
        pfModelCtx.params.tx = -pfModelCtx.controls.target.x
        pfModelCtx.params.ty = -pfModelCtx.controls.target.y
        const dist = pfModelCtx.controls.target.distanceTo(pfModelCtx.camera.position)
        const rr = (pfModelCtx.metrics && pfModelCtx.metrics.r) || 1
        const zoom = (rr * 3.2) / dist
        pfModelCtx.params.zoom = Math.max(0.2, Math.min(8, zoom))
        pfModelScale.value = Math.round(pfModelCtx.params.zoom * 100)
        pfModelScaleV.textContent = pfModelCtx.params.zoom.toFixed(1)
      } catch {}
      pfModelApply()
    }
  }
})

pfAddTextBtn.addEventListener('click', () => {
  pfEdSelected = null
  pfEdUpdateHandles()
  document.querySelectorAll('.pf-text-box.selected').forEach((d) => d.classList.remove('selected'))
  pfStylePanel.classList.add('hidden')
  pfFreezeBtn.classList.add('hidden')
  const dialog = document.querySelector('#pf-text-dialog')
  const input = document.querySelector('#pf-text-input')
  input.value = ''
  dialog.classList.add('show')
  setTimeout(() => input.focus(), 50)
})
document.querySelector('#pf-text-ok').addEventListener('click', () => {
  const dialog = document.querySelector('#pf-text-dialog')
  const input = document.querySelector('#pf-text-input')
  const text = input.value.trim()
  dialog.classList.remove('show')
  if (!text) return
  if (pfEdSelected && pfEdSelected.type === 'text') {
    // 编辑已有文字
    pfEdSelected.text = text
    if (pfEdSelected.div) {
      pfEdSelected.div.textContent = text
      pfEdUpdateTextBox(pfEdSelected)
    } else {
      pfEdFreezeText(pfEdSelected)
    }
  } else {
    const boxW = 420
    const tex = makePfTextTexture(text, '#ffffff', 12, boxW)
    const layer = pfEdCreateLayer('text', tex, boxW, tex.image.height, { text, color: '#ffffff', fontSize: 12 })
    pfEdSelect(layer)
  }
})
document.querySelector('#pf-text-cancel').addEventListener('click', () => {
  document.querySelector('#pf-text-dialog').classList.remove('show')
})
document.querySelector('#pf-text-input').addEventListener('keydown', (e) => {
  if (e.key === 'Enter') document.querySelector('#pf-text-ok').click()
})

pfAddImageBtn.addEventListener('click', () => pfImageInput.click())
pfImageInput.addEventListener('change', () => {
  const file = pfImageInput.files && pfImageInput.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    const img = new Image()
    img.onload = () => {
      const tex = new THREE.Texture(img)
      tex.colorSpace = THREE.SRGBColorSpace
      tex.needsUpdate = true
      const scale = Math.min(600 / img.width, 340 / img.height, 1)
      const layer = pfEdCreateLayer('image', tex, img.width * scale, img.height * scale)
      pfEdSelect(layer)
      pfImageInput.value = ''
    }
    img.src = reader.result
  }
  reader.readAsDataURL(file)
})

pfDelLayerBtn.addEventListener('click', () => {
  if (!pfEdSelected) return
  if (pfEdSelected.mesh) pfEdScene.remove(pfEdSelected.mesh)
  if (pfEdSelected.div) pfEdSelected.div.remove()
  const i = pfEdLayers.indexOf(pfEdSelected)
  if (i >= 0) pfEdLayers.splice(i, 1)
  pfEdSelected = null
  pfEdUpdateHandles()
  document.querySelectorAll('.pf-text-box.selected').forEach((d) => d.classList.remove('selected'))
  pfDelLayerBtn.classList.add('hidden')
})

// ---------- 每个产品独立的页面布局：保存 / 加载 ----------
function pfEdSerialize(layers) {
  return layers.map((l) => {
    const corners = l.corners.map((c) => [c.x, c.y])
    if (l.type === 'image') {
      const src = l.texture && l.texture.image && l.texture.image.src
      return { type: 'image', src: src || '', corners }
    }
    return {
      type: 'text',
      frozen: !!l.frozen,
      text: l.text,
      color: l.color,
      fontSize: l.fontSize,
      fontFamily: l.fontFamily,
      corners
    }
  })
}

function pfEdClearLayers() {
  for (const l of pfEdLayers) {
    if (l.mesh) pfEdScene.remove(l.mesh)
    if (l.div) l.div.remove()
  }
  pfEdLayers.length = 0
  pfEdSelected = null
  pfEdUpdateHandles()
  document.querySelectorAll('.pf-text-box.selected').forEach((d) => d.classList.remove('selected'))
  pfStylePanel.classList.add('hidden')
  pfFreezeBtn.classList.add('hidden')
}

function pfEdLoadLayout(data, onDone) {
  pfEdClearLayers()
  let pending = 0
  const finish = () => { if (--pending <= 0 && onDone) onDone() }
  // 系统标题：各产品顶部标题统一渲染，不再使用各产品手工摆放的标题文字层
  const list = Array.isArray(data) ? data : []
  const texts = list.filter((i) => i.type === 'text')
  const dataName = (PRODUCTS[currentPfId] && PRODUCTS[currentPfId].name) || ''
  let titleZh = dataName && dataName !== '待补充'
    ? dataName
    : (texts[0] ? texts[0].text || '' : '')
  if (titleZh) pfSetTitleZh(titleZh)
  const titleKey = String(titleZh || '').replace(/\s+/g, '')
  const skipTitleLayers = new Set()
  if (titleKey) {
    texts.forEach((txt) => {
      if (String(txt.text || '').replace(/\s+/g, '') === titleKey) skipTitleLayers.add(txt)
    })
  }
  const norm = (pts) => {
    if (!pts) return null
    const isOldPx = pts.some((p) => Math.abs(p[0]) > 2 || Math.abs(p[1]) > 2)
    if (!isOldPx) return pts.map((p) => new THREE.Vector3(p[0], p[1], 0))
    // 旧像素数据：按固定参考尺寸换算（参考编辑画布 1291x912 + 背景图 2400x1600 contain）
    const refScale = Math.min(1291 / 2400, 912 / 1600)
    const refHalfW = (2400 * refScale) / 2
    const refHalfH = (1600 * refScale) / 2
    return pts.map((p) => new THREE.Vector3(p[0] / refHalfW, p[1] / refHalfH, 0))
  }
  for (const item of list) {
    if (skipTitleLayers.has(item)) continue
    if (item.type === 'image') {
      if (!item.src) continue
      pending++
      const img = new Image()
      img.onload = () => {
        const tex = new THREE.Texture(img)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.needsUpdate = true
        pfEdCreateLayer('image', tex, img.width, img.height, null, norm(item.corners))
        finish()
      }
      img.onerror = finish
      img.src = item.src
    } else if (item.type === 'text') {
      const layer = pfEdCreateLayer('text', null, 0, 0, {
        text: item.text,
        color: item.color,
        fontSize: item.fontSize,
        fontFamily: item.fontFamily
      }, norm(item.corners))
      pfEdUpdateTextBox(layer)
      if (item.frozen) pfEdFreezeText(layer, true) // 从布局恢复：保留保存的角点（含透视）
    }
  }
  pfEdSelected = null
  pfEdUpdateHandles()
  if (pending === 0 && onDone) onDone()
}

const pfSaveLayoutBtn = document.querySelector('#pf-save-layout')
pfSaveLayoutBtn.addEventListener('click', async () => {
  if (!currentPfId) return
  try {
    const resp = await fetch('/api/layouts/' + currentPfId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pfEdSerialize(pfEdLayers))
    })
    if (resp.ok) {
      pfShowToast('已保存到产品 ' + currentPfId + ' 的页面（所有设备可见）')
    } else {
      pfShowToast('保存失败：服务返回 ' + resp.status)
    }
  } catch (err) {
    pfShowToast('保存失败：' + (err && err.message))
  }
})

// 加载产品布局：优先读共享文件（data/layouts/{id}.json），不存在则兼容旧 localStorage
function loadPfLayout(id, onDone) {
  fetch('data/layouts/' + id + '.json')
    .then((r) => { if (!r.ok) throw new Error('no layout file'); return r.json() })
    .then((data) => pfEdLoadLayout(data, onDone))
    .catch(() => {
      try {
        const saved = localStorage.getItem('pf-layout-' + id)
        pfEdLoadLayout(saved ? JSON.parse(saved) : null, onDone)
      } catch {
        pfEdLoadLayout(null, onDone)
      }
    })
}

// ---------- 浏览器返回键 = 关界面，而不是离开这个网页 ----------
// 打开产品弹窗、或产品页里"六个按钮"打开的浮层时，往浏览历史里压一条记录，
// 返回键就变成"退一层"：
//   浮层（简介/手册/视频/爆炸图/产品图片/实拍项目）→ 弹窗 → 大厅；
//   退到大厅之后再按返回，才是离开网页（和以前一样）。
// 记录里带一个本页随机令牌，用来分辨"我们自己压的那几条"和别的历史条目；
// 界面上点 × / 返回弹窗按钮关掉时，也会把对应那条记录一起退掉，返回键不会"空按一次"。
const OPT2_VIEW_KEY = 'opt2View'
const OPT2_TOKEN_KEY = 'opt2Token'
const OPT2_DEPTH_KEY = 'opt2Depth'
const opt2Token = Math.random().toString(36).slice(2) + Date.now().toString(36)
let opt2Pushed = 0 // 本页自己压入的历史条数：0 = 大厅 / 1 = 弹窗 / 2 = 弹窗 + 一个浮层

// 产品页里"六个按钮"打开的浮层：简介 / 手册 / 视频 / 爆炸图 / 产品图片 / 实拍项目。
// 返回键先关它们，再关产品弹窗，最后才是回大厅。
const pfLightboxEl = document.querySelector('#lightbox')
const opt2Overlays = [
  { key: 'explode', isOpen: () => explodeOverlay.classList.contains('show'), close: () => closeExplode() },
  {
    key: 'video',
    isOpen: () => pfVideoOverlay.classList.contains('show'),
    close: () => {
      pfVideoOverlay.classList.remove('show')
      pfVideoLoadingStop()
      pfVideoPlayer.pause()
      pfVideoPlayer.removeAttribute('src')
      pfVideoPlayer.load()
    },
  },
  {
    key: 'pdf',
    isOpen: () => pfPdfOverlay.classList.contains('show'),
    close: () => { pfPdfOverlay.classList.remove('show'); pfPdfFrame.src = '' },
  },
  { key: 'intro', isOpen: () => pfIntroWindow.classList.contains('show'), close: () => closePfIntro() },
  { key: 'gallery', isOpen: () => pfLightboxEl.classList.contains('show'), close: () => pfLightboxEl.classList.remove('show') },
]

function opt2OpenOverlay() {
  for (const o of opt2Overlays) if (o.isOpen()) return o
  return null
}

// 当前界面处在哪一层（以界面本身为准，历史记录只负责"触发退出"）
function opt2ViewDepth() {
  const product = productFull && productFull.classList.contains('show') ? 1 : 0
  return product + (opt2OpenOverlay() ? 1 : 0)
}

function opt2PushState(view, depth, id) {
  try {
    history.pushState(
      { [OPT2_VIEW_KEY]: view, [OPT2_TOKEN_KEY]: opt2Token, [OPT2_DEPTH_KEY]: depth, id: id == null ? undefined : id },
      ''
    )
    opt2Pushed = depth
  } catch { /* 环境不支持 History API 时保持原样：返回键还是会离开网页 */ }
}

// 打开产品弹窗：压一条"弹窗层"记录
function opt2PushProduct(id) {
  if (opt2Pushed >= 1) return
  opt2PushState('product', 1, id)
}

// 打开产品页里某个浮层：再压一条"浮层"记录（在大厅里点开图集时就是第 1 层）
function opt2PushOverlay(id) {
  const depth = Math.min(2, opt2Pushed + 1)
  if (opt2Pushed >= depth) return
  opt2PushState('overlay', depth, id)
}

// 界面上主动关闭时，把对应的历史记录一起退掉：退 n 条（n = 关掉的层数），没压过就不动
function opt2PopView(n = 1) {
  const step = Math.min(Math.max(1, n), opt2Pushed)
  if (step <= 0) return
  opt2Pushed -= step
  try { history.go(-step) } catch { opt2Pushed += step }
}

// 界面上的关闭按钮统一走这里：关界面 + 同步历史（返回键不会"空按一次"）
function opt2CloseUser(closeFn) {
  const before = opt2ViewDepth()
  closeFn()
  opt2PopView(before - opt2ViewDepth())
}

// 把界面同步到某一层（返回键触发；幂等，前后层数一样时什么都不做）
function opt2SyncView(depth) {
  let cur = opt2ViewDepth()
  if (depth >= cur) return // 前进（例如按"前进"回到弹窗）：不自动重开弹窗
  // 一层一层往回退：先关产品页里的浮层，再关产品弹窗（和点 × 的行为一致，含机位还原）
  while (cur > depth) {
    const ov = opt2OpenOverlay()
    if (ov) { ov.close(); cur = opt2ViewDepth(); continue }
    if (productFull.classList.contains('show')) { closeModal(); cur = opt2ViewDepth(); continue }
    break // 兜底：没有可关的了就停，别死循环
  }
}

window.addEventListener('popstate', (e) => {
  const st = e.state
  const ours = !!(st && st[OPT2_TOKEN_KEY] === opt2Token)
  const view = ours ? st[OPT2_VIEW_KEY] : null
  const depth = ours && typeof st[OPT2_DEPTH_KEY] === 'number'
    ? st[OPT2_DEPTH_KEY]
    : (view === 'overlay' || view === 'explode' ? 2 : view === 'product' ? 1 : 0)
  opt2Pushed = depth
  opt2SyncView(depth)
})

document.querySelector('#pf-close').addEventListener('click', () => {
  opt2CloseUser(() => {
    closeProductFull()
    // 关掉弹窗后回到打开它时的机位；万一没有记录再放开控制
    if (!restoreCameraBeforeProduct()) controls.enabled = true
  })
})
productFull.addEventListener('click', (e) => {
  if (productFull.classList.contains('editing')) return // 编辑模式点击不关闭弹窗
  // 点击空白不再关闭弹窗，仅通过关闭按钮（×）关闭
})
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return
  const depth = opt2ViewDepth()
  if (depth === 0) return
  // Esc 和返回键一样"一次退一层"：先关产品页里的浮层，再关弹窗
  opt2CloseUser(() => opt2SyncView(depth - 1))
})

function openProduct(id, hit, hitPoint) {
  const p = PRODUCTS[id] || { name: '产品' + id, model: '', desc: '产品信息待补充。', metrics: {} }
  // 记住"点开弹窗那一刻"的机位，关闭时原样还回去（不再跳回展区预设视角）
  camBeforeProduct = { pos: camera.position.clone(), target: controls.target.clone() }
  modalName.textContent = p.name || ('产品' + id)
  modalModel.textContent = '型号：' + (p.model || '待补充')
  // 产品配图：没配图就把图区整块收起来（原来退回那张占位图，该文件已删除）
  const productImgSrc = String(p.image || '').trim()
  if (productImgSrc) {
    modalImage.src = productImgSrc
    pmImageWrap?.classList.remove('hidden')
  } else {
    modalImage.removeAttribute('src')
    pmImageWrap?.classList.add('hidden')
  }
  // 没配介绍就显示"暂无"（不占位、不假装有内容）
  modalDesc.textContent = p.desc || '暂无'
  // 不要再用 FP 模型兜底：没有爆炸图的展品在入口处就会被拦下（见 pfOpenExplodeIfAvailable）
  currentExplodeProduct = { id, name: p.name || ('产品' + id), explodeModel: p.explodeModel || '' }
  modalMetrics.innerHTML = ''
  const metrics = p.metrics || {}
  for (const [k, v] of Object.entries(metrics)) {
    const tr = document.createElement('tr')
    const th = document.createElement('th')
    th.textContent = k
    const td = document.createElement('td')
    td.textContent = v
    tr.append(th, td)
    modalMetrics.appendChild(tr)
  }
  modal.classList.add('show')
  openProductFull(id)
  modalOpen = true
  // 压一条历史记录：浏览器返回键这时先关弹窗，而不是离开网页
  opt2PushProduct(id)
  // 相机贴近放大：沿当前视角直线靠近点击点，不做左右旋转；弹窗打开期间锁定视角
  if (hit && hitPoint) {
    controls.enabled = false
    // 先停掉正在进行的镜头动画，避免两个动画抢同一个相机
    if (typeof gsap.killTweensOf === 'function') {
      gsap.killTweensOf(camera.position)
      gsap.killTweensOf(controls.target)
    }
    const targetDist = (hit.radius || 1) * 1.6 + 0.5
    const dir = camera.position.clone().sub(hitPoint).normalize()
    const newPos = hitPoint.clone().add(dir.multiplyScalar(targetDist))
    gsap.to(camera.position, {
      x: newPos.x, y: newPos.y, z: newPos.z,
      duration: 0.9,
      ease: 'power2.inOut',
      onUpdate: () => camera.lookAt(hitPoint),
      onComplete: () => { controls.enabled = false }
    })
  }
}

function closeModal() {
  modal.classList.remove('show')
  closeProductFull()
  modalOpen = false
  closeExplode()
  // 关闭产品信息后，先还原到打开弹窗时的机位
  if (restoreCameraBeforeProduct()) return
  // 没有记录时的兜底：自动切回当前展区视角
  if (currentZone !== null && ZONES[currentZone]) {
    const z = ZONES[currentZone]
    if (camPoints[z.camPos] && camPoints[z.camTarget]) {
      animateCameraTo(camPoints[z.camPos], camPoints[z.camTarget], 1.2)
    }
  } else {
    controls.enabled = true
  }
}

function opt2CloseProductView() {
  opt2CloseUser(() => closeModal())
}
document.querySelector('.pm-close').addEventListener('click', opt2CloseProductView)
document.querySelector('.pm-backdrop').addEventListener('click', opt2CloseProductView)

// HUD1 链接灯箱开关
document.querySelector('#lightbox-close').addEventListener('click', () => {
  opt2CloseUser(() => document.querySelector('#lightbox').classList.remove('show'))
})
document.querySelector('#lightbox').addEventListener('click', (e) => {
  // 手机上点 HUD 屏是"松手就打开"，紧接着浏览器还会补一个合成 click，
  // 正好落在刚盖满屏幕的灯箱上 —— 于是"打开一秒就被关掉"。
  // 所以灯箱刚打开的这一小段时间里忽略背景点击。
  if (performance.now() - lbOpenedAt < 500) return
  if (e.target === document.querySelector('#lightbox')) {
    opt2CloseUser(() => document.querySelector('#lightbox').classList.remove('show'))
  }
})

// 四块 HUD 屏链接图画廊（1→2→3→4→5→6，左右箭头循环切换）
// HUD1~4 有中英文两套图，跟着右上角的"中 / EN"切换；5、6 暂时只有中文版
// 20261020：12 张大图转 WebP（52.2 MB -> 4.2 MB），单独用 HUD_ASSET_VERSION 控制缓存，
// 以后只换企业介绍图时改这里即可，不用连带把展厅模型版本一起改掉。
const HUD_ASSET_VERSION = '20261020a'
const hudAsset = (p) => p + (p.includes('?') ? '&' : '?') + 'v=' + HUD_ASSET_VERSION
const HUD_GALLERY_ZH = [
  'images/hud1-link.webp',
  'images/hud2-link.webp',
  'images/hud3-link.webp',
  'images/hud4.webp',
  'images/hud5.webp',
  'images/hud6.webp'
].map(hudAsset)
const HUD_GALLERY_EN = [
  'images/hud1-link-en.webp',
  'images/hud2-link-en.webp',
  'images/hud3-link-en.webp',
  'images/hud4-link-en.webp',
  'images/hud5-link-en.webp',
  'images/hud6-link-en.webp'
].map(hudAsset)
let hudGallery = HUD_GALLERY_ZH
// 调试：查看当前（按语言）的 HUD 灯箱图列表
window.__opt2 = Object.assign(window.__opt2 || {}, {
  hudGalleryInfo: () => ({ lang: getLang(), items: hudGallery.slice() })
})

// ---------- 企业宣传视频（右上角按钮；中/英各一个文件） ----------
const PROMO_SOURCES = {
  zh: 'company/promo-zh.mp4',
  en: 'company/promo-en.mp4',
}
const promoOverlay = document.querySelector('#promo-overlay')
const promoPlayer = document.querySelector('#promo-player')
const promoMissing = document.querySelector('#promo-missing')
const promoPlayBtn = document.querySelector('#promo-play')
const promoSeek = document.querySelector('#promo-seek')
const promoTime = document.querySelector('#promo-time')
const promoLangBtn = document.querySelector('#promo-lang')
let promoLang = 'zh'
let promoFallbackTried = false
let promoOpened = false

// 手机 / 平板看企业宣传片：底部播放条改用系统自带控件
// （浏览器保证它一定画在画面之上，手指也好按），顶部条相应变成
// "标题 + 中英切换 + 关闭"。电脑端保持原来的自定义控制条，显示效果不变。
if (IS_TOUCH && promoOverlay) {
  if (promoPlayer) promoPlayer.controls = true
  const promoBar = promoOverlay.querySelector('.pf-overlay-bar')
  if (promoBar && promoLangBtn && promoLangBtn.parentElement !== promoBar) promoBar.appendChild(promoLangBtn)
}

function promoFmtTime(s) {
  if (!isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return m + ':' + String(sec).padStart(2, '0')
}
function promoShowMissing() {
  promoPlayer.classList.add('hidden')
  promoMissing.classList.remove('hidden')
  promoMissing.textContent = t('promo.missing')
  promoPlayBtn.textContent = '▶'
}
// 顶栏标题和"关闭"跟着正在播的那一版走：播英文版就显示 Company Video / Close
function promoApplyChromeLang() {
  if (!promoOverlay) return
  const lang = promoLang === 'en' ? 'en' : 'zh'
  const titleEl = promoOverlay.querySelector('.pf-overlay-bar > span')
  if (titleEl) titleEl.textContent = (TRANSLATIONS['promo.title'] && TRANSLATIONS['promo.title'][lang]) || titleEl.textContent
  const closeEl = promoOverlay.querySelector('#promo-close')
  if (closeEl) closeEl.textContent = (TRANSLATIONS['map.close'] && TRANSLATIONS['map.close'][lang]) || closeEl.textContent
}
function promoSetLang(lang, autoplay = true) {
  promoLang = lang === 'en' ? 'en' : 'zh'
  promoFallbackTried = false
  // 按钮显示的是"点一下会切到的语言"（正在播中文时显示 EN，反之显示 中）
  promoLangBtn.textContent = promoLang === 'en' ? '中' : 'EN'
  promoLangBtn.title = promoLang === 'en' ? '切换中文版' : 'Switch to English'
  promoApplyChromeLang()
  promoPlayer.classList.remove('hidden')
  promoMissing.classList.add('hidden')
  promoPlayer.src = withVersion(videoUrl(PROMO_SOURCES[promoLang]))
  promoPlayer.load()
  if (autoplay) promoPlayer.play().catch(() => {})
}
function openPromoVideo() {
  if (!promoOverlay) return
  promoOpened = true
  promoOverlay.classList.add('show')
  promoOverlay.classList.remove('chrome-hidden')
  promoChromeTimer = IS_TOUCH ? 0 : setTimeout(promoHideChrome, 3000)
  promoSetLang(getLang(), true)
}
function closePromoVideo() {
  if (!promoOverlay) return
  promoOpened = false
  clearTimeout(promoChromeTimer)
  promoOverlay.classList.remove('show')
  try { promoPlayer.pause() } catch {}
}
// 播放时鼠标不动就把顶栏/控制条淡出，让画面尽量占满；动一下就回来
let promoChromeTimer = 0
function promoHideChrome() {
  if (!promoOpened) return
  // 手机/平板上不自动隐藏：触屏没有"鼠标移动"这种唤回动作，一隐藏用户就找不到关闭按钮了
  if (IS_TOUCH) return
  if (promoPlayer && !promoPlayer.paused) promoOverlay.classList.add('chrome-hidden')
}
function promoWakeChrome() {
  if (!promoOpened) return
  promoOverlay.classList.remove('chrome-hidden')
  clearTimeout(promoChromeTimer)
  // 触屏设备不排"自动隐藏"的计时器，控件常驻，关闭按钮随时可点
  promoChromeTimer = IS_TOUCH ? 0 : setTimeout(promoHideChrome, 3000)
}
promoOverlay?.addEventListener('mousemove', promoWakeChrome)
promoOverlay?.addEventListener('touchstart', promoWakeChrome, { passive: true })
promoPlayer?.addEventListener('click', () => {
  // 触屏用的是系统自带控件：点画面只用来唤出控件，不再顺手暂停，免得"点一下画面反而停了"
  if (promoPlayer.controls) return
  // 点画面 = 播放/暂停
  if (promoPlayer.paused) promoPlayer.play().catch(() => {})
  else promoPlayer.pause()
})
promoPlayer?.addEventListener('play', promoWakeChrome)
// 把视频的真实比例写到 CSS 变量上：手机上的播放区就和画面一样大（不会只占上半屏）
function promoSyncAspect() {
  const w = promoPlayer && promoPlayer.videoWidth
  const h = promoPlayer && promoPlayer.videoHeight
  if (promoOverlay && w && h) promoOverlay.style.setProperty('--promo-ar', w + ' / ' + h)
}
promoPlayer?.addEventListener('loadedmetadata', promoSyncAspect)
promoPlayer?.addEventListener('resize', promoSyncAspect)
// 横竖屏切换、从后台切回来时，把顶栏/关闭按钮重新显示一遍，避免控件停在"隐藏"状态
window.addEventListener('orientationchange', () => { if (promoOpened) promoWakeChrome() })
document.addEventListener('visibilitychange', () => { if (promoOpened && !document.hidden) promoWakeChrome() })
document.querySelector('#btn-promo-video')?.addEventListener('click', openPromoVideo)
document.querySelector('#promo-close')?.addEventListener('click', closePromoVideo)
promoPlayBtn?.addEventListener('click', () => {
  if (promoPlayer.paused) promoPlayer.play().catch(() => {})
  else promoPlayer.pause()
})
document.querySelector('#promo-back')?.addEventListener('click', () => {
  promoPlayer.currentTime = Math.max(0, (promoPlayer.currentTime || 0) - 10)
})
document.querySelector('#promo-fwd')?.addEventListener('click', () => {
  const d = promoPlayer.duration
  promoPlayer.currentTime = isFinite(d) ? Math.min(d, (promoPlayer.currentTime || 0) + 10) : (promoPlayer.currentTime || 0) + 10
})
promoLangBtn?.addEventListener('click', () => {
  promoSetLang(promoLang === 'zh' ? 'en' : 'zh', true)
})
promoSeek?.addEventListener('input', () => {
  if (promoPlayer.duration) promoPlayer.currentTime = (promoSeek.value / 1000) * promoPlayer.duration
  promoTime.textContent = promoFmtTime(promoPlayer.currentTime) + ' / ' + promoFmtTime(promoPlayer.duration)
})
// 换源/加载完成时把进度条和时间重置，避免显示上一支视频的进度
for (const ev of ['loadstart', 'loadedmetadata', 'durationchange']) {
  promoPlayer?.addEventListener(ev, () => {
    if (!promoPlayer.duration) {
      promoSeek.value = 0
      promoTime.textContent = '0:00 / 0:00'
      return
    }
    promoSeek.value = Math.round((promoPlayer.currentTime / promoPlayer.duration) * 1000)
    promoTime.textContent = promoFmtTime(promoPlayer.currentTime) + ' / ' + promoFmtTime(promoPlayer.duration)
  })
}
promoPlayer?.addEventListener('timeupdate', () => {
  if (promoPlayer.duration) {
    promoSeek.value = Math.round((promoPlayer.currentTime / promoPlayer.duration) * 1000)
  }
  promoTime.textContent = promoFmtTime(promoPlayer.currentTime) + ' / ' + promoFmtTime(promoPlayer.duration)
})
promoPlayer?.addEventListener('play', () => { promoPlayBtn.textContent = '⏸' })
promoPlayer?.addEventListener('pause', () => { promoPlayBtn.textContent = '▶' })
promoPlayer?.addEventListener('ended', () => { promoPlayBtn.textContent = '▶' })
promoPlayer?.addEventListener('error', () => {
  if (!promoOpened) return
  if (!promoFallbackTried) {
    // 当前语言的文件不在，先试另一个语言；两个都没有就提示放文件的位置
    promoFallbackTried = true
    const other = promoLang === 'zh' ? 'en' : 'zh'
    promoPlayer.src = withVersion(videoUrl(PROMO_SOURCES[other]))
    promoPlayer.load()
    promoPlayer.play().catch(() => promoShowMissing())
    return
  }
  promoShowMissing()
})
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && promoOverlay?.classList.contains('show')) closePromoVideo()
})
let lbItems = hudGallery
let lbIndex = 0
let lbOpenedAt = -1e9 // 灯箱打开的时刻（用来忽略紧随其后的合成点击）
// 灯箱条目：展厅/HUD/产品图片是图片地址字符串；实拍项目是 { src, zh, en }（带项目名，要显示在照片下面）
function lbSrcOf(item) {
  return typeof item === 'string' ? item : (item && item.src) || ''
}
function lbCaptionOf(item) {
  if (!item || typeof item === 'string') return ''
  const en = getLang() === 'en'
  const fromApi = ((en ? (item.en || item.zh) : (item.zh || item.en)) || '').trim()
  if (fromApi) return fromApi
  // 兜底：文字全部来自它所在的项目文件夹名（"中文名_English Name"，只写一种语言就两边都用它）。
  // 需要兜底是因为接口是服务器上跑的，服务器没重启时可能还是旧逻辑、不给单语言文件夹的名字。
  const parts = String(item.src || '').split('/').filter(Boolean)
  const folder = decodeURIComponent(parts[parts.length - 2] || '')
  if (!folder || /^\d+_/.test(folder)) return ''   // "22_一体式蒸发冷冷水机组10pic" 这种是展品文件夹，不是项目名
  const i = folder.indexOf('_')
  const zh = i > 0 ? folder.slice(0, i).trim() : folder.trim()
  const enName = i > 0 ? folder.slice(i + 1).trim() : ''
  return (en ? (enName || zh) : (zh || enName)).trim()
}
// 照片下面的项目名：中文界面只显示中文名、英文界面只显示英文名；没有项目名就整条不显示
function lbApplyCaption() {
  const el = document.querySelector('#lightbox-caption')
  if (!el) return
  const text = lbCaptionOf(lbItems[lbIndex])
  el.textContent = text
  el.classList.toggle('show', !!text)
}
// 灯箱预取：打开一张图时顺手把前后几张下载好。
// HUD/企业介绍只有 6 张，整组预取；产品图片、厂容厂貌可能有几十张，只预取前后各 2 张。
const lbPreloaded = new Map()
function lbPreload(src) {
  if (!src || lbPreloaded.has(src)) return
  const im = new Image()
  im.decoding = 'async'
  im.src = src
  lbPreloaded.set(src, im)
  if (lbPreloaded.size > 24) {
    const oldest = lbPreloaded.keys().next().value
    lbPreloaded.delete(oldest)
  }
}
function lbPreloadNearby(count = 2) {
  const n = lbItems.length
  if (!n) return
  for (let d = 1; d <= count; d++) {
    lbPreload(lbSrcOf(lbItems[(lbIndex + d) % n]))
    lbPreload(lbSrcOf(lbItems[(lbIndex - d + n) % n]))
  }
}
function lbShowIndex(i) {
  if (!lbItems.length) return
  lbIndex = (i + lbItems.length) % lbItems.length
  const item = lbItems[lbIndex]
  const img = document.querySelector('#lightbox-img')
  img.src = lbSrcOf(item)
  img.alt = lbCaptionOf(item) || '图片'
  img.decoding = 'async'
  lbPreloadNearby(lbItems === hudGallery ? hudGallery.length - 1 : 2)
  lbApplyCaption()
  lbReset()
  lbOpenedAt = performance.now()
  document.querySelector('#lightbox').classList.add('show')
  opt2PushOverlay(currentPfId) // 返回键先关图集（产品图片/实拍项目），再关弹窗
}
function lbShowGallery(items, i = 0) {
  if (!items || !items.length) return
  lbItems = items
  lbShowIndex(i)
}
function lbShowHud(i) {
  lbShowGallery(hudGallery, i)
}
document.querySelector('#lightbox-prev').addEventListener('click', (e) => {
  e.stopPropagation()
  lbShowIndex(lbIndex - 1)
})
document.querySelector('#lightbox-next').addEventListener('click', (e) => {
  e.stopPropagation()
  lbShowIndex(lbIndex + 1)
})

// 灯箱图片缩放/拖拽
const lbImg = document.querySelector('#lightbox-img')
let lbScale = 1
let lbTx = 0
let lbTy = 0
let lbDrag = null
let lbPinch = null
function lbApply() {
  lbImg.style.transform = `translate(${lbTx}px, ${lbTy}px) scale(${lbScale})`
}
function lbReset() {
  lbScale = 1
  lbTx = 0
  lbTy = 0
  lbApply()
}
lbImg.addEventListener('wheel', (e) => {
  e.preventDefault()
  lbScale = Math.min(8, Math.max(0.5, lbScale * (e.deltaY < 0 ? 1.12 : 0.88)))
  lbApply()
}, { passive: false })
lbImg.addEventListener('pointerdown', (e) => {
  if (e.button !== 0) return
  e.preventDefault()
  lbDrag = { x: e.clientX, y: e.clientY, tx: lbTx, ty: lbTy }
  lbImg.setPointerCapture(e.pointerId)
})
lbImg.addEventListener('pointermove', (e) => {
  if (!lbDrag) return
  lbTx = lbDrag.tx + e.clientX - lbDrag.x
  lbTy = lbDrag.ty + e.clientY - lbDrag.y
  lbApply()
})
lbImg.addEventListener('pointerup', () => { lbDrag = null })
lbImg.addEventListener('touchstart', (e) => {
  if (e.touches.length === 2) {
    lbPinch = { d: Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY), s: lbScale }
  }
}, { passive: true })
lbImg.addEventListener('touchmove', (e) => {
  if (e.touches.length === 2 && lbPinch) {
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
    lbScale = Math.min(8, Math.max(0.5, lbPinch.s * (d / lbPinch.d)))
    lbApply()
  }
}, { passive: true })
lbImg.addEventListener('touchend', () => { lbPinch = null })
lbImg.addEventListener('dblclick', lbReset)
document.querySelectorAll('#lightbox-controls button').forEach((b) => {
  b.addEventListener('click', () => {
    const z = b.dataset.zoom
    if (z === 'in') lbScale = Math.min(8, lbScale * 1.3)
    else if (z === 'out') lbScale = Math.max(0.5, lbScale / 1.3)
    else lbReset()
    lbApply()
  })
})

// ---------- 弹窗内爆炸图（覆盖层，关闭后返回弹窗） ----------
const explodeOverlay = document.querySelector('#explode-overlay')
const explodeFrame = document.querySelector('#explode-frame')
const explodeClose = document.querySelector('#explode-close')
const explodeTitle = document.querySelector('#explode-title')
let currentExplodeProduct = null

// 打开产品弹窗前的主场景机位（关闭弹窗时还原）
let camBeforeProduct = null

/**
 * 关闭产品弹窗后把镜头还原成"点开弹窗时"的样子。
 * 没有记录（例如不是从热点打开的）时返回 false，由调用方决定兜底行为。
 */
function restoreCameraBeforeProduct(duration = 0.8) {
  if (!camBeforeProduct) return false
  window.__opt2 && (window.__opt2.restoreCalls = (window.__opt2.restoreCalls || 0) + 1)
  // 记录保留：先关整页、再关产品信息卡时会再还原一次（此时已经在原位，等于不动）
  animateCameraTo(camBeforeProduct.pos, camBeforeProduct.target, duration)
  return true
}

function openExplode() {
  if (!currentExplodeProduct) return
  // 兜底：没有模型就别打开 —— 爆炸图页在 model 参数为空时会兜底加载 FP 模型
  const pid0 = String(currentExplodeProduct.id || '')
  const model = (PRODUCTS[pid0] && PRODUCTS[pid0].explodeModel) || currentExplodeProduct.explodeModel || ''
  if (!model) { pfShowToast(t('pf.noExplode')); return }
  currentExplodeProduct.explodeModel = model
  // 爆炸图内可能刚切过语言，外层收到 iframe 的 message 后会 setLang，这里直接取当前值
  const lang = getLang()
  const p = PRODUCTS[currentExplodeProduct.id] || {}
  const modelKey = currentExplodeProduct.explodeModel || ''
  const zh =
    (p.name && p.name !== '待补充')
      ? p.name
      : (modelKey.endsWith('FP.glb') || modelKey.includes('_FP.glb') ? 'FP-119WA风机盘管' : (p.name || ('产品' + currentExplodeProduct.id)))
  const en = PRODUCT_NAMES_EN[zh] || zh
  explodeTitle.textContent = lang === 'en' ? en : zh
  explodeBarZh = zh
  explodeBarEn = en
  const qs = new URLSearchParams({
    id: currentExplodeProduct.id,
    model: currentExplodeProduct.explodeModel || 'models/FP.glb',
    title: zh,
    titleEn: en,
    // 爆炸图模型的版本号：模型重新压缩/替换后改这里，浏览器和 CDN 就会重新拉取
    v: EXPLODE_VERSION
  })
  explodeFrameNavigate((import.meta.env.BASE_URL || '/') + 'explode.html?' + qs.toString())
  explodeOverlay.classList.add('show')
  // 再压一条历史记录：爆炸图里按返回键 → 回到弹窗（不离开网页）
  opt2PushOverlay(currentExplodeProduct.id)
}

// 切换中英文时，爆炸图顶部状态栏的文字跟着切换（标题 + 返回按钮）
let explodeBarZh = ''
let explodeBarEn = ''
// 爆炸图内部切换了中英文 → 外层（返回栏、整个界面）跟着切
window.addEventListener('message', (e) => {
  const d = e && e.data
  if (!d || d.type !== 'opt2-lang-child') return
  try {
    setLang(d.lang === 'en' ? 'en' : 'zh')
    applyLanguage()
  } catch {}
})
function refreshExplodeBarLang() {
  if (!explodeTitle || !explodeOverlay || !explodeOverlay.classList.contains('show')) return
  const lang = getLang()
  if (explodeBarZh) explodeTitle.textContent = lang === 'en' ? (explodeBarEn || explodeBarZh) : explodeBarZh
  // 通知爆炸图页面一起切换
  try { explodeFrame && explodeFrame.contentWindow && explodeFrame.contentWindow.postMessage({ type: 'opt2-lang', lang }, location.origin) } catch {}
}

// 爆炸图是 iframe：直接改 src 会给浏览历史再塞一条"框架导航"记录，
// 结果在爆炸图里按返回键会先被框架自己的记录吃掉（页面闪一下空白），要按第二次才退出去。
// 用 location.replace 换文档就不产生新记录，返回键一次就退到弹窗。
function explodeFrameNavigate(url) {
  try {
    const w = explodeFrame.contentWindow
    if (w && w.location && typeof w.location.replace === 'function') {
      w.location.replace(url)
      return
    }
  } catch { /* 跨域等异常：退回 src 赋值 */ }
  explodeFrame.src = url
}

function closeExplode() {
  explodeOverlay.classList.remove('show')
  explodeFrameNavigate('about:blank')
}

explodeClose.addEventListener('click', () => opt2CloseUser(() => closeExplode()))

// ---------- 平面图开关与提示 ----------
const mapCard = document.querySelector('#map-card')
const mapToggle = document.querySelector('#map-toggle')
document.querySelector('#map-close').addEventListener('click', () => {
  mapCard.classList.add('hidden')
  mapToggle.style.display = 'block'
})
mapToggle.addEventListener('click', () => {
  mapCard.classList.remove('hidden')
  mapToggle.style.display = 'none'
})
// 分区导航：手机/平板也默认打开（星标：里面字号已按屏幕放大，不会挤在一起）

let toastTimer = null
function showToast(msg) {
  const el = document.querySelector('#toast')
  el.textContent = msg
  el.classList.add('show')
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => el.classList.remove('show'), 1800)
}

// ---------- 智能客服（中心发光球入口 → 本地 RAG 知识库） ----------
let supportEntry = null // 中心球上的隐形点击球
const supportChat = document.querySelector('#support-chat')
const supportBubble = document.querySelector('#support-bubble')
const supportMsgs = document.querySelector('#sc-messages')
const supportInput = document.querySelector('#sc-input')
const supportSend = document.querySelector('#sc-send')
const supportClose = document.querySelector('#sc-close')
const supportHead = supportChat ? supportChat.querySelector('.sc-head') : null
const supportSuggest = document.querySelector('#sc-suggest')
const supportMinBtn = document.querySelector('#sc-min')
const supportMaxBtn = document.querySelector('#sc-max')
const supportResizeHandles = Array.from(document.querySelectorAll('#support-chat .sc-rz'))
let supportResizeDrag = null
if (!SUPPORT_ENABLED) {
  // 关掉时连入口一起隐藏（气泡按钮 + 对话窗 + 中心球上的隐形热区）
  if (supportBubble) supportBubble.style.display = 'none'
  if (supportChat) supportChat.style.display = 'none'
} else if (supportBubble) {
  // index.html 里默认给气泡加了 display:none，避免脚本执行前闪一下；开启时再显示
  supportBubble.style.display = ''
}
let supportHistory = []
let supportBusy = false
let supportGreeted = false
let supportDrag = null
let bubbleDrag = null
let bubbleMoved = false

function scAdd(text, cls) {
  const div = document.createElement('div')
  div.className = 'sc-msg ' + cls
  div.textContent = text
  supportMsgs.appendChild(div)
  supportMsgs.scrollTop = supportMsgs.scrollHeight
  return div
}

function scWelcome() {
  if (supportGreeted) return
  supportGreeted = true
  const msg = scAdd(t('kb.welcome'), 'bot')
  msg.setAttribute('data-welcome', '1')
}

function fillSupportSuggestions() {
  if (!SUPPORT_ENABLED) return
  supportSuggest.innerHTML = ''
  const chips = getLang() === 'en'
    ? ['What models of fan coil units do you offer?', 'Please send me your product manual.', 'Which chiller suits a hotel project?']
    : ['风机盘管的型号都有哪些？', '发给我你们的产品手册', '酒店项目适合什么机组？']
  for (const text of chips) {
    const chip = document.createElement('div')
    chip.className = 'sc-chip'
    chip.textContent = text
    chip.addEventListener('click', () => {
      supportInput.value = text
      scSend()
    })
    supportSuggest.appendChild(chip)
  }
}

function scOpen() {
  if (!SUPPORT_ENABLED) return // 知识库训练期间：整个客服功能关闭
  supportChat.classList.remove('hidden')
  // 弹窗打开时右下角的气泡先收起来（点气泡和点发光球打开，效果一样）；关掉弹窗它再出现
  if (supportBubble) supportBubble.style.display = 'none'
  // 不管上次拖到哪，先保证标题栏和关闭按钮一定在屏幕内；高度超过屏幕时同步压到可视高度。
  const rc = supportChat.getBoundingClientRect()
  const maxW = Math.max(SC_MIN_W, window.innerWidth - 16)
  const maxH = Math.max(SC_MIN_H, window.innerHeight - 16)
  const w = Math.min(rc.width, maxW)
  const h = Math.min(rc.height, maxH)
  const left = Math.min(Math.max(8, rc.left), Math.max(8, window.innerWidth - w - 8))
  const top = Math.min(Math.max(8, rc.top), Math.max(8, window.innerHeight - h - 8))
  if (rc.left !== left || rc.top !== top || rc.width !== w || rc.height !== h) {
    supportChat.style.right = 'auto'
    supportChat.style.bottom = 'auto'
    supportChat.style.left = left + 'px'
    supportChat.style.top = top + 'px'
    supportChat.style.width = w + 'px'
    supportChat.style.height = h + 'px'
  }
  scWelcome()
  fillSupportSuggestions()
  // 手机上不要自动聚焦，否则软键盘一弹出会把窗口顶到屏幕外
  if (!IS_TOUCH) supportInput.focus({ preventScroll: true })
}

function scClosePanel() {
  supportChat.classList.add('hidden')
  scShowBubble()
}
// 显示右下角气泡（弹窗关掉时回来）。顺手兜个底：拖出去或位置信息丢了就复位到右下角，
// 否则会出现"点了关闭，图标不见了"的情况。
function scShowBubble() {
  if (!SUPPORT_ENABLED) return
  if (!supportBubble) return
  supportBubble.style.display = ''
  const rc = supportBubble.getBoundingClientRect()   // display 恢复后再量，隐藏时量出来是 0
  const outside = rc.width === 0 || rc.height === 0 ||
    rc.left < 0 || rc.top < 0 ||
    rc.right > window.innerWidth + 1 || rc.bottom > window.innerHeight + 1
  if (outside) {
    supportBubble.style.left = 'auto'
    supportBubble.style.top = 'auto'
    supportBubble.style.right = '16px'
    supportBubble.style.bottom = '70px'
  }
}

async function scSend() {
  if (!SUPPORT_ENABLED) return
  const text = (supportInput.value || '').trim()
  if (!text || supportBusy) return
  supportInput.value = ''
  scAdd(text, 'user')
  supportHistory.push({ role: 'user', content: text })
  const thinking = scAdd(t('kb.thinking'), 'bot thinking')
  supportBusy = true
  supportSend.disabled = true
  try {
    const res = await fetch('/api/kb/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // lang 只决定"用什么语言回答"：英文界面下即使用中文提问也回英文（见 docs/api_contract.md）
      body: JSON.stringify({ query: text, history: supportHistory.slice(-10), lang: getLang() })
    })
    const data = await res.json()
    if (!res.ok || data.error) throw new Error(data.error || ('HTTP ' + res.status))
    thinking.classList.remove('thinking')
    thinking.textContent = data.answer || ''
    // 不显示"参考来源"：客户只要答案。出处仍然由知识库记录在 qa.log 里，排查时能看到。
    // 手册下发：知识库只在"索取文件"的问题上返回 files（见 docs/api_contract.md）。
    // 知识库给的是 /files/<id>，这里改走展厅的同源代理 /api/kb/files/<id>。
    if (data.files && data.files.length) {
      const fbox = document.createElement('div')
      fbox.className = 'sc-files'
      fbox.textContent = t('kb.files') + ':'
      const ful = document.createElement('ul')
      for (const f of data.files) {
        const furl = String(f.url || '').replace(/^\/files\//, '/api/kb/files/')
        if (!furl) continue
        const li = document.createElement('li')
        const a = document.createElement('a')
        a.href = furl
        a.setAttribute('download', '')
        a.textContent = String(f.name || f.id || '') +
          (f.size_mb ? '（' + f.size_mb + ' MB）' : '')
        li.appendChild(a)
        ful.appendChild(li)
      }
      if (ful.children.length) {
        fbox.appendChild(ful)
        thinking.appendChild(fbox)
      }
    }
    supportHistory.push({ role: 'assistant', content: data.answer || '' })
    supportMsgs.scrollTop = supportMsgs.scrollHeight
  } catch (err) {
    thinking.classList.remove('thinking')
    thinking.textContent = t('kb.fail') + (err && err.message ? '（' + err.message + '）' : '')
  }
  supportBusy = false
  supportSend.disabled = false
  supportInput.focus({ preventScroll: true })
}

supportBubble?.addEventListener('click', (e) => {
  e.stopPropagation()
  if (!SUPPORT_ENABLED) return
  if (bubbleMoved) {
    bubbleMoved = false
    return
  }
  supportChat.classList.contains('hidden') ? scOpen() : scClosePanel()
})
// 气泡可拖动：按住圆球拖动换位置，拖动不算点击
supportBubble?.addEventListener('pointerdown', (e) => {
  // 关键：先把当前屏幕位置写成 left/top，再清掉 right/bottom。
  // 否则元素会掉回"静态位置"（跑到左上角被顶栏盖住），看着就像图标消失了。
  const rc = supportBubble.getBoundingClientRect()
  supportBubble.style.right = 'auto'
  supportBubble.style.bottom = 'auto'
  supportBubble.style.left = rc.left + 'px'
  supportBubble.style.top = rc.top + 'px'
  bubbleDrag = {
    id: e.pointerId,
    dx: e.clientX - rc.left,
    dy: e.clientY - rc.top,
    sx: e.clientX,
    sy: e.clientY
  }
  bubbleMoved = false
  try { supportBubble.setPointerCapture(e.pointerId) } catch {}
  e.preventDefault()
})
supportBubble?.addEventListener('pointermove', (e) => {
  if (!bubbleDrag || e.pointerId !== bubbleDrag.id) return
  const nx = e.clientX - bubbleDrag.dx
  const ny = e.clientY - bubbleDrag.dy
  if (Math.abs(e.clientX - bubbleDrag.sx) > 4 || Math.abs(e.clientY - bubbleDrag.sy) > 4) bubbleMoved = true
  supportBubble.style.left = Math.min(Math.max(8, nx), Math.max(8, window.innerWidth - supportBubble.offsetWidth - 8)) + 'px'
  supportBubble.style.top = Math.min(Math.max(8, ny), Math.max(8, window.innerHeight - supportBubble.offsetHeight - 8)) + 'px'
})
const endBubbleDrag = (e) => {
  if (bubbleDrag && e.pointerId === bubbleDrag.id) bubbleDrag = null
}
supportBubble?.addEventListener('pointerup', endBubbleDrag)
supportBubble?.addEventListener('pointercancel', endBubbleDrag)
supportClose?.addEventListener('click', scClosePanel)
supportSend?.addEventListener('click', scSend)
supportInput?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') scSend()
})
// 聊天窗拖拽：按住标题栏移动（点关闭按钮不触发）
supportHead?.addEventListener('pointerdown', (e) => {
  if (e.target.closest('button')) return
  if (!supportChat) return
  const rc = supportChat.getBoundingClientRect()
  supportChat.style.right = 'auto'
  supportChat.style.bottom = 'auto'
  supportChat.style.left = rc.left + 'px'
  supportChat.style.top = rc.top + 'px'
  supportDrag = {
    id: e.pointerId,
    startX: e.clientX,
    startY: e.clientY,
    left: rc.left,
    top: rc.top,
    w: rc.width,
    h: rc.height
  }
  supportChat.classList.add('dragging')
  try { supportHead.setPointerCapture(e.pointerId) } catch {}
  e.preventDefault()
})
supportHead?.addEventListener('pointermove', (e) => {
  if (!supportDrag || !supportChat || e.pointerId !== supportDrag.id) return
  const maxX = Math.max(8, window.innerWidth - supportDrag.w - 8)
  const maxY = Math.max(8, window.innerHeight - supportDrag.h - 8)
  const nx = Math.min(Math.max(8, supportDrag.left + (e.clientX - supportDrag.startX)), maxX)
  const ny = Math.min(Math.max(8, supportDrag.top + (e.clientY - supportDrag.startY)), maxY)
  supportDrag.nx = nx
  supportDrag.ny = ny
  supportChat.style.transform = `translate3d(${nx - supportDrag.left}px, ${ny - supportDrag.top}px, 0)`
})
const endSupportDrag = (e) => {
  if (!supportDrag || e.pointerId !== supportDrag.id) return
  if (supportDrag.nx !== undefined) {
    supportChat.style.left = supportDrag.nx + 'px'
    supportChat.style.top = supportDrag.ny + 'px'
  }
  supportChat.style.transform = ''
  supportChat.classList.remove('dragging')
  supportDrag = null
}
supportHead?.addEventListener('pointerup', endSupportDrag)
supportHead?.addEventListener('pointercancel', endSupportDrag)
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && supportChat && !supportChat.classList.contains('hidden')) scClosePanel()
})

// ---------- 客服窗：最小化 / 最大化 / 拖右下角改大小 ----------
// 拖动和缩放都先把定位从 right/bottom 换算成 left/top 的像素值，
// 这样才是"左上角不动、往右下长大"的窗口行为。
function scFreezeRect() {
  const rc = supportChat.getBoundingClientRect()
  supportChat.style.right = 'auto'
  supportChat.style.bottom = 'auto'
  supportChat.style.left = rc.left + 'px'
  supportChat.style.top = rc.top + 'px'
  supportChat.style.width = rc.width + 'px'
  supportChat.style.height = rc.height + 'px'
  return rc
}
function scIsMaximized() {
  return !!supportChat && supportChat.classList.contains('maximized')
}
function scSyncWinTitles() {
  if (!supportChat) return
  const collapsed = supportChat.classList.contains('collapsed')
  const maximized = scIsMaximized()
  if (supportMinBtn) {
    supportMinBtn.setAttribute('data-i18n-title', collapsed ? 'kb.restore' : 'kb.minimize')
    supportMinBtn.title = t(collapsed ? 'kb.restore' : 'kb.minimize')
  }
  if (supportMaxBtn) {
    supportMaxBtn.setAttribute('data-i18n-title', maximized ? 'kb.restore' : 'kb.maximize')
    supportMaxBtn.title = t(maximized ? 'kb.restore' : 'kb.maximize')
  }
}
function scToggleMaximize(force) {
  if (!supportChat) return
  const want = force === undefined ? !scIsMaximized() : !!force
  if (want) {
    scFreezeRect()                       // 记住当前尺寸/位置，还原就是这套像素值
    supportChat.classList.remove('collapsed')
    supportChat.classList.add('maximized')
  } else {
    supportChat.classList.remove('maximized')
    // 还原后如果窗口比以前小，别让面板跑到屏幕外
    const rc = supportChat.getBoundingClientRect()
    supportChat.style.left = Math.min(Math.max(8, rc.left), Math.max(8, window.innerWidth - rc.width - 8)) + 'px'
    supportChat.style.top = Math.min(Math.max(8, rc.top), Math.max(8, window.innerHeight - rc.height - 8)) + 'px'
  }
  scSyncWinTitles()
}
function scToggleCollapse(force) {
  if (!supportChat) return
  const want = force === undefined ? !supportChat.classList.contains('collapsed') : !!force
  if (want && scIsMaximized()) scToggleMaximize(false)
  supportChat.classList.toggle('collapsed', want)
  scSyncWinTitles()
}
supportMinBtn?.addEventListener('click', (e) => {
  e.stopPropagation()
  scToggleCollapse()
})
supportMaxBtn?.addEventListener('click', (e) => {
  e.stopPropagation()
  scToggleMaximize()
})
// 双击标题栏 = 最大化/还原
supportHead?.addEventListener('dblclick', (e) => {
  if (e.target.closest('button') || !supportChat) return
  scToggleMaximize()
})
// 四边四角都能拉：和普通窗口一致。拖哪条边就只改那一维，拖角同时改宽高；
// 拖左边/上边时对边固定（左上角以外的地方不动），并且不允许小于最小尺寸。
const SC_MIN_W = 250
const SC_MIN_H = 190
function onSupportResizeMove(e) {
  if (!supportResizeDrag || !supportChat || e.pointerId !== supportResizeDrag.id) return
  const d = supportResizeDrag
  const dx = e.clientX - d.x
  const dy = e.clientY - d.y
  let left = d.left
  let top = d.top
  let w = d.w
  let h = d.h
  if (d.dir.includes('e')) {
    w = Math.min(Math.max(SC_MIN_W, d.w + dx), Math.max(SC_MIN_W, window.innerWidth - d.left - 8))
  }
  if (d.dir.includes('s')) {
    h = Math.min(Math.max(SC_MIN_H, d.h + dy), Math.max(SC_MIN_H, window.innerHeight - d.top - 8))
  }
  if (d.dir.includes('w')) {
    const nl = Math.min(Math.max(8, d.left + dx), d.left + d.w - SC_MIN_W)
    left = nl
    w = d.left + d.w - nl
  }
  if (d.dir.includes('n')) {
    const nt = Math.min(Math.max(8, d.top + dy), d.top + d.h - SC_MIN_H)
    top = nt
    h = d.top + d.h - nt
  }
  supportChat.style.left = left + 'px'
  supportChat.style.top = top + 'px'
  supportChat.style.width = w + 'px'
  supportChat.style.height = h + 'px'
}
const endSupportResize = (e) => {
  if (!supportResizeDrag || e.pointerId !== supportResizeDrag.id) return
  if (supportChat) supportChat.classList.remove('resizing')
  supportResizeDrag = null
}
for (const handle of supportResizeHandles) {
  handle.addEventListener('pointerdown', (e) => {
    if (!supportChat || scIsMaximized()) return
    const rc = scFreezeRect()
    supportResizeDrag = {
      id: e.pointerId,
      dir: handle.dataset.dir || 'se',
      x: e.clientX,
      y: e.clientY,
      left: rc.left,
      top: rc.top,
      w: rc.width,
      h: rc.height
    }
    supportChat.classList.add('resizing')
    try { handle.setPointerCapture(e.pointerId) } catch {}
    e.preventDefault()
    e.stopPropagation()
  })
  handle.addEventListener('pointermove', onSupportResizeMove)
  handle.addEventListener('pointerup', endSupportResize)
  handle.addEventListener('pointercancel', endSupportResize)
}

// ---------- 顶部按钮：资料下载 / 联系我们 ----------
const baseUrl = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '') + '/'
const btnDownload = document.querySelector('#btn-download')
const btnContact = document.querySelector('#btn-contact')
// 浏览器全屏：手机/平板/电脑都可用（iPhone 的 Safari 不支持 element 全屏，会给出提示）
const btnFullscreen = document.querySelector('#btn-fullscreen')
function toggleFullscreen() {
  const el = document.documentElement
  const isFs = document.fullscreenElement || document.webkitFullscreenElement
  // 已经在全屏 → 退出
  if (isFs) { exitNativeFullscreen(); return }
  // 进入：只要"浏览器原生全屏"——地址栏、工具栏一起藏掉。
  // 我们自己的顶部栏、分区导航保持原样，不参与隐藏。
  const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
  if (!req) {
    try {
      showToast(getLang() === 'en'
        ? "This browser can't go fullscreen. On iPhone, add the page to Home Screen and open it from the icon."
        : '这个浏览器不支持网页全屏；iPhone 可以"添加到主屏幕"后从桌面图标打开')
    } catch {}
    return
  }
  try {
    const r = req.call(el)
    if (r && r.catch) r.catch(() => {
      try { showToast(getLang() === 'en' ? 'Fullscreen was blocked by the browser' : '浏览器拒绝了全屏请求') } catch {}
    })
  } catch {
    try { showToast(getLang() === 'en' ? 'Fullscreen is not available here' : '这个环境无法进入全屏') } catch {}
  }
  syncFullscreenLabel()
}
function exitNativeFullscreen() {
  try {
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen
    const r = exit && exit.call(document)
    if (r && r.catch) r.catch(() => {})
  } catch {}
}
btnFullscreen?.addEventListener('click', (e) => { e.stopPropagation(); toggleFullscreen() })
// 顶部栏保留电脑上的完整文字，移动端整条按可用宽度等比缩放。
// 不用短标签、不隐藏品牌名，缩放比例由内容和屏幕宽度实时计算。
function fitBrandBar() {
  const brand = document.querySelector('#brand')
  if (!brand) return
  brand.style.transform = 'none'
  const naturalWidth = Math.max(1, brand.offsetWidth)
  const left = parseFloat(getComputedStyle(brand).left) || 0
  const available = Math.max(220, window.innerWidth - left - 8)
  const baseScale = IS_PHONE ? 0.72 : IS_TABLET ? 0.88 : 1
  const scale = Math.min(baseScale, available / naturalWidth)
  brand.style.transformOrigin = 'top left'
  brand.style.transform = `scale(${scale.toFixed(4)})`
}
window.addEventListener('resize', fitBrandBar)
window.addEventListener('orientationchange', () => setTimeout(fitBrandBar, 50))
try { document.fonts?.ready.then(() => fitBrandBar()) } catch {}
function syncFullscreenLabel() {
  const isFs = document.fullscreenElement || document.webkitFullscreenElement
  const en = getLang() === 'en'
  if (btnFullscreen) btnFullscreen.textContent = isFs
    ? (en ? 'Exit' : '退出全屏')
    : (en ? 'Fullscreen' : '全屏')
  fitBrandBar()
}
// 浏览器原生全屏进/出时同步按钮文字
function onFullscreenChange() { syncFullscreenLabel() }
document.addEventListener('fullscreenchange', onFullscreenChange)
document.addEventListener('webkitfullscreenchange', onFullscreenChange)
const contactMenu = document.querySelector('#contact-menu')
const contactInfo = document.querySelector('#contact-info')
const ciTitle = document.querySelector('#ci-title')
const ciQrImg = document.querySelector('#ci-qr-img')
const ciText = document.querySelector('#ci-text')
const ciMissing = document.querySelector('#ci-missing')
const ciCopyBtn = document.querySelector('#ci-copy')
const ciTip = document.querySelector('#ci-tip')
const ciCloseBtn = document.querySelector('#ci-close')
const downloadModal = document.querySelector('#download-modal')
const dmScroll = document.querySelector('#dm-scroll')
const dmEmpty = document.querySelector('#dm-empty')
const dmCloseBtn = document.querySelector('#dm-close')
const dmPrevBtn = document.querySelector('.dm-prev')
const dmNextBtn = document.querySelector('.dm-next')

// 联系我们：value 填上真实联系方式；wechat 显示二维码图片
const CONTACT_CONFIG = {
  // ==== 中文界面 ====
  hotline: { action: 'tel', value: '400-915-8448' },
  switchboard: { action: 'tel', value: '0797-7775625' },
  fax: { action: 'copy', value: '0797-7775627' },
  'email-zh': { action: 'mailto', value: 'obair@oubokt.com' },
  'address-zh': { action: 'address', value: '江西省赣州市欧博大道欧博路欧博工业园' },
  'website-zh': { action: 'link', value: 'https://www.oubokt.com/' },
  // ==== 英文界面 ====
  'address-en': { action: 'address', value: 'Oubo Industrial Park, Oubo Road, Oubo Avenue, Ganzhou City, Jiangxi Province' },
  'tel-en': { action: 'tel', value: '+86-135-3408-5300' },
  'email-en': { action: 'mailto', value: 'global@oubokt.com' },
  'website-en': { action: 'link', value: 'https://www.oubokt.net/' },
  facebook: { action: 'link', value: 'https://www.facebook.com/people/Oubo-Technology-Co-Ltd/61564873842181/' },
  // ==== 旧条目（保留，避免别处引用出错） ====
  email: { action: 'mailto', value: 'lvjinhan@oubokt.com' },
  phone: { action: 'tel', value: '+86 135 3408 5300' },
  whatsapp: { action: 'link', value: 'https://wa.me/8613534085300' },
  wechat: { action: 'qr', value: '' },
  tiktok: { action: 'link', value: 'https://www.tiktok.com/@.obair' },
  instagram: { action: 'link', value: 'https://www.instagram.com/haojinoubo' },
  youtube: { action: 'link', value: 'https://www.youtube.com/@OuBoAIR' },
  // 领英用公开的公司主页地址（后台管理页链接别人打不开）
  linkedin: { action: 'link', value: 'https://www.linkedin.com/company/104875676/' },
  x: { action: 'link', value: 'https://x.com/OuBoAIR' },
  address: { action: 'address', value: 'Oubo Industrial Park, Oubo Road, Ganzhou City, Jiangxi Province, China · 江西 · 赣州' },
  website: { action: 'link', value: 'www.oubokt.com' }
}

function contactMenuPosition() {
  if (!btnContact || !contactMenu) return
  const rect = btnContact.getBoundingClientRect()
  const menuWidth = contactMenu.offsetWidth || 168
  let left = rect.right - menuWidth
  let top = rect.bottom + 6
  if (left < 8) left = 8
  if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8
  if (top + contactMenu.offsetHeight > window.innerHeight - 8) top = rect.top - contactMenu.offsetHeight - 6
  contactMenu.style.left = left + 'px'
  contactMenu.style.top = top + 'px'
}

function hideContactMenu() {
  contactMenu?.classList.add('hidden')
}

function toggleContactMenu() {
  if (contactMenu.classList.contains('hidden')) {
    contactMenuPosition()
    contactMenu.classList.remove('hidden')
  } else {
    hideContactMenu()
  }
}

function copyText(text, okMsg) {
  const done = () => showToast(okMsg || text)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => showToast(text))
  } else {
    done()
  }
}

function openContactInfo(kind, val) {
  hideContactMenu()
  const isQr = kind === 'qr'
  ciTitle.textContent = t(isQr ? 'contact.wechat' : 'contact.address')
  ciText.classList.toggle('hidden', isQr)
  ciQrImg.classList.add('hidden')
  ciMissing.classList.add('hidden')
  ciCopyBtn.classList.toggle('hidden', isQr)
  ciTip.classList.toggle('hidden', !isQr)
  if (isQr) {
    ciQrImg.onload = () => {
      ciQrImg.classList.remove('hidden')
      ciMissing.classList.add('hidden')
    }
    ciQrImg.onerror = () => {
      ciQrImg.classList.add('hidden')
      ciMissing.classList.remove('hidden')
    }
    // 加时间戳避免更换二维码后仍显示旧缓存
    ciQrImg.src = baseUrl + 'images/contact/wechat-qr.png?t=' + Date.now()
  } else {
    ciText.textContent = val || ''
    ciCopyBtn.dataset.copy = val || ''
  }
  contactInfo.classList.remove('hidden')
  ciCloseBtn.focus({ preventScroll: true })
}

function closeContactInfo() {
  contactInfo.classList.add('hidden')
  ciQrImg.removeAttribute('src')
  btnContact?.focus({ preventScroll: true })
}

function handleContactItem(key) {
  const cfg = CONTACT_CONFIG[key]
  if (!cfg) return
  hideContactMenu()
  if (cfg.action === 'qr') {
    openContactInfo('qr')
    return
  }
  if (cfg.action === 'address') {
    openContactInfo('address', cfg.value)
    return
  }
  const val = (cfg.value || '').trim()
  if (!val) {
    showToast(t('contact.unset'))
    return
  }
  if (cfg.action === 'mailto') {
    window.location.href = 'mailto:' + val
  } else if (cfg.action === 'tel') {
    // 拨号链接里不能带空格，只留数字和开头的 +
    window.location.href = 'tel:' + val.replace(/[^\d+]/g, '')
  } else if (cfg.action === 'copy') {
    copyText(val, t('contact.copied'))
  } else if (cfg.action === 'link') {
    const url = /^https?:\/\//i.test(val) ? val : 'https://' + val
    window.open(url, '_blank', 'noopener')
  }
}

btnContact?.addEventListener('click', (e) => {
  e.stopPropagation()
  toggleContactMenu()
})
document.querySelectorAll('#contact-menu button[data-contact]').forEach((btn) => {
  btn.addEventListener('click', (e) => {
    e.stopPropagation()
    handleContactItem(btn.dataset.contact)
  })
})

// 调试：查看"联系我们"各项最终会跳到哪
window.__opt2 = Object.assign(window.__opt2 || {}, {
  contactConfig: () => JSON.parse(JSON.stringify(CONTACT_CONFIG)),
})
ciCloseBtn?.addEventListener('click', closeContactInfo)
contactInfo?.querySelector('.ci-backdrop')?.addEventListener('click', closeContactInfo)
ciCopyBtn?.addEventListener('click', () => {
  copyText(ciCopyBtn.dataset.copy || '', t('contact.addressCopied'))
})
document.addEventListener('pointerdown', (e) => {
  if (contactMenu?.classList.contains('hidden')) return
  if (e.target.closest('#contact-menu') || e.target.closest('#btn-contact')) return
  hideContactMenu()
})
window.addEventListener('resize', () => {
  if (contactMenu && !contactMenu.classList.contains('hidden')) contactMenuPosition()
})

// 资料下载：PS5 式横向卡片滑动窗
let dmListCache = null
let dmItemsShown = 0

function dmEsc(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}

function dmNavUpdate() {
  const maxLeft = dmScroll.scrollWidth - dmScroll.clientWidth - 1
  dmPrevBtn.disabled = dmScroll.scrollLeft <= 0
  dmNextBtn.disabled = dmScroll.scrollLeft >= maxLeft
}

function makeDmItem(item) {
  const stem = String(item.name || '').replace(/\.pdf$/i, '')
  // 文件名开头的序号（10-、21-…）只是为了在文件夹里排序，界面上不显示；
  // 下载保存时用的还是真文件名（item.name），所以磁盘上依然是有序号的。
  const label = stem.replace(/^\d+\s*[-_—–]\s*/, '').trim() || stem
  const url = baseUrl + 'downloads/pdf/' + encodeURIComponent(item.name)
  const cover = item.cover ? baseUrl + String(item.cover).replace(/^\/+/, '') : ''
  const el = document.createElement('div')
  el.className = 'dm-item'
  el.title = label
  el.innerHTML =
    '<div class="dm-cover">' +
      '<span class="dm-tag">PDF</span>' +
      (cover
        ? '<img src="' + dmEsc(cover) + '" alt="' + dmEsc(label) + '" loading="lazy" draggable="false" />'
        : '<span class="dm-fallback">' + dmEsc(label.slice(0, 6)) + '</span>') +
      '<button type="button" class="dm-dl" title="' + dmEsc(t('site.downloadPdf')) + '">⤓</button>' +
    '</div>' +
    '<div class="dm-name">' + dmEsc(label) + '</div>'
  const dlBtn = el.querySelector('.dm-dl')
  dlBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    const a = document.createElement('a')
    a.href = url
    a.download = item.name
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  })
  el.addEventListener('click', () => {
    const w = window.open(url, '_blank', 'noopener')
    if (!w) {
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
  })
  return el
}

function renderDownloads(list) {
  dmScroll.querySelectorAll('.dm-item').forEach((n) => n.remove())
  dmEmpty.classList.toggle('hidden', list.length > 0)
  list.forEach((item) => dmScroll.appendChild(makeDmItem(item)))
  dmItemsShown = list.length
  dmNavUpdate()
}

function dmCloseModal() {
  downloadModal.classList.add('hidden')
  dmScroll.scrollLeft = 0
  btnDownload?.focus({ preventScroll: true })
}

async function dmOpen() {
  hideContactMenu()
  if (dmListCache) renderDownloads(dmListCache)
  try {
    const res = await fetch(baseUrl + 'api/downloads')
    if (!res.ok) throw new Error('http ' + res.status)
    dmListCache = await res.json()
    renderDownloads(dmListCache)
  } catch {
    showToast(t('site.downloadFail'))
    if (!dmItemsShown) dmEmpty.classList.remove('hidden')
  }
  downloadModal.classList.remove('hidden')
  dmCloseBtn.focus({ preventScroll: true })
  dmNavUpdate()
}

btnDownload?.addEventListener('click', (e) => {
  e.stopPropagation()
  dmOpen()
})
dmCloseBtn?.addEventListener('click', (e) => {
  e.stopPropagation()
  dmCloseModal()
})
downloadModal?.querySelector('.dm-backdrop')?.addEventListener('click', dmCloseModal)
dmPrevBtn?.addEventListener('click', () => {
  dmScroll.scrollBy({ left: -Math.round(dmScroll.clientWidth * 0.85), behavior: 'smooth' })
})
dmNextBtn?.addEventListener('click', () => {
  dmScroll.scrollBy({ left: Math.round(dmScroll.clientWidth * 0.85), behavior: 'smooth' })
})
dmScroll?.addEventListener('scroll', dmNavUpdate)
dmScroll?.addEventListener('wheel', (e) => {
  if (downloadModal.classList.contains('hidden')) return
  e.preventDefault()
  dmScroll.scrollLeft += (e.deltaY || e.deltaX || 0)
}, { passive: false })
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && downloadModal && !downloadModal.classList.contains('hidden')) {
    dmCloseModal()
  }
  if (e.key === 'Escape' && contactInfo && !contactInfo.classList.contains('hidden')) {
    closeContactInfo()
  }
})

// ---------- 多语言切换 ----------
function applyLanguage() {
  const lang = getLang()
  document.documentElement.lang = lang === 'en' ? 'en' : 'zh-CN'
  // 联系我们的下拉菜单：中/英各一套条目（中文：热线/总机/传真/微信/邮箱/地址；
  // 英文：Add/Tel/E-mail/Facebook/Linkedin/YouTube/X/TikTok）
  document.querySelectorAll('#contact-menu button[data-lang]').forEach((btn) => {
    btn.style.display = btn.getAttribute('data-lang') === (lang === 'en' ? 'en' : 'zh') ? '' : 'none'
  })
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n')
    if (key) el.textContent = t(key)
  })
  // 企业宣传片顶栏跟着"正在播的那一版"（上面这轮会按界面语言刷一遍，这里再纠正回来）
  try { if (promoOpened) promoApplyChromeLang() } catch {}
  // 触屏设备：底部操作提示换成手指说法（原来是"滚轮"）
  if (IS_TOUCH) {
    const hintEl = document.querySelector('#hint')
    // 手机/平板：展厅和弹窗提示统一成同一套文案
    if (hintEl) hintEl.textContent = t('hint.rotateTouch')
  }
  try { refreshExplodeBarLang() } catch {}
  try { syncFullscreenLabel() } catch {}
  document.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder')
    if (key) el.setAttribute('placeholder', t(key))
  })
  document.querySelectorAll('[data-i18n-title]').forEach((el) => {
    const key = el.getAttribute('data-i18n-title')
    if (key) el.setAttribute('title', t(key))
  })
  const zhBtn = document.querySelector('#lang-zh')
  const enBtn = document.querySelector('#lang-en')
  if (zhBtn && enBtn) {
    zhBtn.classList.toggle('active', lang !== 'en')
    enBtn.classList.toggle('active', lang === 'en')
  }
  const pfZhBtn = document.querySelector('#pf-lang-zh')
  const pfEnBtn = document.querySelector('#pf-lang-en')
  if (pfZhBtn && pfEnBtn) {
    pfZhBtn.classList.toggle('active', lang !== 'en')
    pfEnBtn.classList.toggle('active', lang === 'en')
  }
  // 地图展区名称（SVG text）按语言切换：英文用简短名称（保留中文样式，仅切文字）
  const zoneNames = {
    1: lang === 'en' ? 'Centrifugal Units' : '离心机展厅',
    2: lang === 'en' ? 'Integrated Units' : '一体机展厅',
    3: lang === 'en' ? 'DX Units' : '直膨机及水力模块展厅',
    4: lang === 'en' ? 'Air-side' : '末端产品展厅',
    5: lang === 'en' ? 'Plant Tour' : '厂容厂貌',
    6: lang === 'en' ? 'Company Introduction' : '企业介绍',
    7: lang === 'en' ? 'Scroll Units' : '涡旋机展厅',
    8: lang === 'en' ? 'Screw Units' : '螺杆机展厅'
  }
  document.querySelectorAll('.map-body polygon[data-zone]').forEach((poly) => {
    const z = Number(poly.dataset.zone)
    const name = zoneNames[z] ? zoneNames[z].replace('\n', '') : ''
    poly.setAttribute('title', (lang === 'en' ? 'Go to ' : '前往') + name)
  })
  // SVG 展区名称文字
  document.querySelectorAll('[data-zone-name]').forEach((txt) => {
    const z = Number(txt.dataset.zoneName)
    const en = ZONE_NAMES_EN[z]
    if (!txt.dataset.zhHtml) txt.dataset.zhHtml = txt.innerHTML
    if (lang === 'en' && en) {
      // 英文名里带换行就分两行显示（如 Company / Introduction）
      txt.innerHTML = String(en).split('\n').join('<br />')
    } else if (lang !== 'en') {
      txt.innerHTML = txt.dataset.zhHtml
    }
    txt.classList.toggle('is-en', lang === 'en')
    // HTML 文字不受 SVG 拉伸影响；按展区宽度自适应字号
    const mapW = document.querySelector('.map-body')?.clientWidth || 300
    // 每个展区在地图上留给文字的相对宽度（英文名长短不一，用来算自适应字号）
    const zonePct = { 1: 24, 2: 24, 3: 21, 4: 26, 5: 21, 6: 35, 7: 17, 8: 26 }[z] || 20
    if (lang === 'en') {
      // 多行时按最长那一行算字号
      const len = Math.max(5, ...String(en || '').split('\n').map((s) => s.length))
      const px = (mapW * zonePct) / 100
      const size = Math.min(11, Math.max(8.5, (px * 0.92) / (0.58 * len)))
      txt.style.fontSize = size.toFixed(1) + 'px'
      txt.style.letterSpacing = '0'
    } else {
      txt.style.fontSize = '11px'
      txt.style.letterSpacing = '0'
    }
  })
  // 3D 悬浮展厅标题：只显示当前语言
  refreshTitleSprites()
  // 3D 展品名标签：同样只显示当前语言（中英文长度不同，贴图一起重画）
  try { refreshExhibitLabels() } catch {}
  // 客服欢迎语与快捷问题随语言刷新
  const kbWelcome = supportMsgs ? supportMsgs.querySelector('.sc-msg[data-welcome]') : null
  if (kbWelcome) kbWelcome.textContent = t('kb.welcome')
  if (pfBoothUI) {
    pfBoothUI.setButtons(pfBoothButtons())
    // 中英文标签长度不同：整组尺寸重算一遍，并重新做"标签自适应"
    pfApplyBoothScale()
    if (productFull.classList.contains('editing')) pfBoothUI.setVisible(false)
  }
  if (supportChat && !supportChat.classList.contains('hidden')) fillSupportSuggestions()
  // 弹窗顶部标题与底部操作指导：跟随当前语言
  if (productFull.classList.contains('show')) {
    pfApplyTitleTexts()
  }
  if (pfIntroWindow.classList.contains('show')) pfApplyIntroTitle()

  // HUD 灯箱（点墙上 HUD 屏弹出的图）：中/英各一套，切换语言时同步换掉
  const gal = lang === 'en' ? HUD_GALLERY_EN : HUD_GALLERY_ZH
  if (gal !== hudGallery) {
    hudGallery = gal
    lbItems = hudGallery
    const lb = document.querySelector('#lightbox')
    if (lb && lb.classList.contains('show')) {
      lbShowIndex(Math.min(lbIndex, hudGallery.length - 1))
    }
  }
  // 灯箱里"实拍项目"的项目名跟着语言换（中文界面中文名 / 英文界面英文名）
  try {
    const lbEl = document.querySelector('#lightbox')
    if (lbEl && lbEl.classList.contains('show')) lbApplyCaption()
  } catch {}
  fitBrandBar()
}
document.addEventListener('DOMContentLoaded', applyLanguage)
document.querySelector('#lang-zh')?.addEventListener('click', () => { setLang('zh'); applyLanguage() })
document.querySelector('#lang-en')?.addEventListener('click', () => { setLang('en'); applyLanguage() })
applyLanguage()

// ---------- 帧率 ----------
let frames = 0
let lastT = performance.now()
// 诊断用：记录"上一帧"的绘制批次 / 三角面（排查性能用，不影响画面）
const perfAcc = { total: 0, tris: 0, _mark: 0 }
setInterval(markShadowsDirty, 30000) // 阴影兜底重算（原来是 8 秒一次，平板上太频繁，改成 30 秒）
if (CENSUS_ON) {
  setTimeout(() => {
    const byGroup = new Map()
    scene.traverse((o) => {
      if (!o.isMesh && !o.isPoints) return
      const g = o.geometry
      if (!g) return
      let root = o
      while (root.parent && root.parent !== scene) root = root.parent
      const k = (root.name || '(无名)') + '>' + (o.name || '')
      const tri = g.index ? g.index.count / 3 : (g.attributes.position ? g.attributes.position.count / 3 : 0)
      const mult = o.isInstancedMesh ? o.count : 1
      const rec = byGroup.get(k) || { meshes: 0, tris: 0, vis: 0 }
      rec.meshes += 1
      if (o.visible !== false) rec.vis += 1
      if (o.visible !== false) rec.tris += tri * mult
      byGroup.set(k, rec)
    })
    const rows = [...byGroup.entries()].sort((a, b) => b[1].tris - a[1].tris)
    let total = 0
    for (const [, v] of rows) total += v.tris
    console.log('[census] 可见三角面合计(单遍)=' + (total / 1e6).toFixed(1) + 'M  分组=' + rows.length)
    console.log('[census] TOP: ' + rows.slice(0, 12)
      .map(([k, v]) => k + '=' + (v.tris / 1e6).toFixed(1) + 'M/' + v.meshes).join(' | '))
    const veg = rows.filter(([k]) => /Vegetation|TreeMesh|Shrub|Grass|Proxy/i.test(k))
    const vegSum = veg.reduce((n, [, v]) => n + v.tris, 0)
    console.log('[census] 植物合计=' + (vegSum / 1e6).toFixed(1) + 'M  明细: ' +
      veg.slice(0, 10).map(([k, v]) => k + '=' + (v.tris / 1e6).toFixed(2) + 'M/' + v.meshes).join(' | '))
    // 还有没有"真折射"材质？只要有一个，整场景就会被多画一遍
    const trans = new Set()
    scene.traverse((o) => {
      if (!o.material) return
      const mats = Array.isArray(o.material) ? o.material : [o.material]
      for (const m of mats) if (m && m.transmission > 0) trans.add((m.name || '(无名材质)') + '=' + m.transmission)
    })
    console.log('[census] 真折射材质: ' + (trans.size ? [...trans].join(' | ') : '无（折射那遍已经不渲染了）'))
  }, 80000)
}
// 切回前台时重新计时，避免"后台被限速"被误判成机器卡
document.addEventListener('visibilitychange', () => { qGood = 0; qBad = 0; qWarmup = Math.max(qWarmup, 3) })
// 任何面板改动都可能影响阴影（开关灯、移展品、换树…），统一标记一下
document.addEventListener('input', markShadowsDirty, true)
document.addEventListener('change', markShadowsDirty, true)
setInterval(() => {
  const now = performance.now()
  const fpsNow = Math.round((frames * 1000) / (now - lastT))
  const paused = sceneCovered()
  document.querySelector('#fps-v').textContent = paused ? '–' : fpsNow
  if (!paused) autoQualityTick(fpsNow)
  // 兜底：万一有别的路径把泛光关掉了（旧存档/旧参数档），自动打开回来并记一笔
  // 注意 QUALITY.bloom：当前档位本来就不要泛光时（low 档），不该在这里又把它打开
  if (!bloomAutoOff && bloomComposer && QUALITY.bloom) {
    let fixed = false
    if (selectiveBloom && bloomPassOnly && !bloomPassOnly.enabled) { bloomPassOnly.enabled = true; fixed = true }
    if (selectiveBloom && bloomMixPass && !bloomMixPass.enabled) { bloomMixPass.enabled = true; fixed = true }
    if (fixed) logQuality('检测到泛光被外部关闭 → 已自动恢复')
  }
  const drawEl = document.querySelector('#fps-draw')
  const tierEl = document.querySelector('#fps-tier')
  if (tierEl) {
    // 读"实际生效"的状态（例如 ?fxaa=0 时这里不该再显示 FXAA）
    const feats = [QUALITY.shadows && '阴影', QUALITY.bloom && !bloomAutoOff && '泛光', FXAA_ENABLED && 'FXAA'].filter(Boolean)
    // 手动锁定有两来源：地址参数 ?q= 或点了画质按钮
    tierEl.textContent = '  [' + QUALITY.tier + ' 档' + (qualityLocked ? (qualityUserChoice ? '·手动' + ({ high: '高', mid: '中', low: '低' }[qualityUserChoice] || '') : '·手动') : '·自动') + '：' +
      (feats.length ? feats.join('+') : '无特效') + ' · ' + GPU_SHORT + ']'
  }
  if (drawEl) {
    drawEl.textContent = ' 每帧批' + perfAcc.total + ' 面' + (perfAcc.tris / 1e6).toFixed(1) + 'M' +
      // 泛光这里要读"实际状态"：低档位是整条泛光链都跳过（以前这栏读的是面板开关，会误导）
      '  泛光' + ((QUALITY.bloom && !bloomAutoOff) ? '开' : '关') + ' 倍率' + ssCurrent.toFixed(2) +
      (buildVersionSeen ? '  v' + buildVersionSeen : '')
  }
  // 调试读数：机位距离 + 距地面高度（看镜头手感时很有用，正式页面默认不显示）
  const camEl = document.querySelector('#fps-cam')
  if (camEl) {
    try {
      const dist = camera.position.distanceTo(controls.target)
      const groundY = hallFloorY
      const dir = new THREE.Vector3().subVectors(camera.position, controls.target)
      const polar = Math.acos(Math.max(-1, Math.min(1, dir.y / (dir.length() || 1)))) * 180 / Math.PI
      camEl.textContent = '  机位' + dist.toFixed(1) + 'm 离地' + (camera.position.y - groundY).toFixed(1) +
        'm 俯仰' + polar.toFixed(1) + '°' +
        (performance.now() - camCollideAt < 1500 ? ' 防穿模' : '')
    } catch { camEl.textContent = '' }
  }
  const qEl = document.querySelector('#fps-quality')
  if (qEl) qEl.textContent = qualityEvents.length ? ('  ' + qualityEvents[qualityEvents.length - 1]) : '  暂无画质调整'
  // 只在带 ?perf= 时打印，正式页面不刷日志
  if (PERF_TEST) {
    console.log('[perf] "' + PERF_TEST + '" fps=' + Math.round((frames * 1000) / (now - lastT)) +
      ' 批=' + perfAcc.total + ' 面=' + (perfAcc.tris / 1e6).toFixed(1) + 'M' +
      ' 倍率=' + renderer.getPixelRatio().toFixed(2) +
      ' 泛光层倍率=' + BLOOM_RES_SCALE)
  }
  frames = 0
  lastT = now
}, 1000)

// ---------- 自适应 ----------
function resize() {
  const w = window.innerWidth
  const h = window.innerHeight
  camera.aspect = w / h
  applyCameraFov()
  renderer.setSize(w, h, false)
  // 后处理合成器要跟渲染器的像素比保持一致（超采样时它也得跟着放大）
  // 档位把泛光/抗锯齿都关掉时，直接渲染到屏幕，连后处理那一次全屏拷贝都省掉
  const useComposer = !!(composer && (QUALITY.bloom || QUALITY.fxaa || (ssaoPass && ssaoPass.enabled)))
  if (useComposer) {
    composer.setPixelRatio(renderer.getPixelRatio())
    composer.setSize(w, h)
  }
  // 泛光层：按 BLOOM_RES_SCALE 决定分辨率（1 = 跟主画面同分辨率，细灯带才不会被采样漏掉）
  if (bloomComposer) {
    const bs = BLOOM_RES_SCALE
    bloomComposer.setPixelRatio(renderer.getPixelRatio())
    bloomComposer.setSize(Math.max(1, Math.round(w * bs)), Math.max(1, Math.round(h * bs)))
  }
  const dbSize = new THREE.Vector2()
  renderer.getDrawingBufferSize(dbSize)
  const resEl = document.querySelector('#fps-res')
  if (resEl) resEl.textContent = ' ' + Math.round(dbSize.x) + '×' + Math.round(dbSize.y) + ' @' + renderer.getPixelRatio().toFixed(2)
  if (productFull.classList.contains('show')) pfApplyTitleTexts()
}
window.addEventListener('resize', resize)
resize()

// ---------- 展品摆进 hall（按配置文件落位） ----------
// 配置在 public/data/hall-exhibits.json：每个展品一条 model / pos / rotY / scale
const HALL_EXHIBITS_URL = 'data/hall-exhibits.json'
// 轻量版展品模型（public/models/lod/，减面 60%，实测渲染逐像素几乎无差别）：
// 高档电脑用全精度版，手机/平板/低档机器用轻量版。想强制指定：?lod=1 / ?lod=0
const USE_LOD_MODELS = (() => {
  const q = new URLSearchParams(location.search).get('lod')
  if (q === '1') return true
  if (q === '0') return false
  // 所有设备都用轻量版：电脑端慢的也是"下载 + 每帧三角面"（全精度 346MB/1.53 亿面，
  // 轻量版 34MB/3150 万面），而外观差别测下来比动画自身的抖动还小。
  // 想看全精度版对比：地址后加 ?lod=0
  return true
})()
const lodPathOf = (p) => String(p).replace(/^models\//, 'models/lod/')
let hallExhibitCfg = null                 // 配置全文
const hallExhibitObjs = new Map()         // 展品编号 -> { root, cfg }
let hallExhibitLoader = null
let hallExhibitAllOn = true

function makeHallExhibitLoader() {
  const l = new GLTFLoader()
  if (typeof dracoLoader !== 'undefined' && dracoLoader) l.setDRACOLoader(dracoLoader)
  l.setMeshoptDecoder(MeshoptDecoder)
  return l
}

// ---------------------------------------------------------------------------
// 展品"零件合并"：从 Blender 导出的展品，每个模型里有成百上千个小零件
// （螺丝、翅片、盘管…），每个零件都是一次单独的绘制。整个展厅下来有两万多个，
// 显卡每帧要跑两万多次，这就是卡顿的主因。
// 这里在加载完成后，把"同一个展品里、用同一个材质"的零件合并成一整块几何体：
//   · 材质、贴图、造型都不变 → 画面完全一样
//   · 不同展品之间不合并 → 以后单独调位置/旋转不受影响
//   · 有骨骼/实例化的零件跳过不合并 → 安全
// ---------------------------------------------------------------------------
// 想对比/排查时可加 ?merge=0 临时关掉合并
const MERGE_EXHIBIT_MESHES = new URLSearchParams(location.search).get('merge') !== '0'
const __mergeMatIds = new WeakMap()
let __mergeMatSeq = 0

function mergeExhibitMeshes(root) {
  if (!MERGE_EXHIBIT_MESHES) return 0
  root.updateMatrixWorld(true)
  const rootInv = new THREE.Matrix4().copy(root.matrixWorld).invert()
  const groups = new Map()
  const victims = []

  root.traverse((o) => {
    if (!o.isMesh || !o.geometry || !o.geometry.attributes.position) return
    if (o.isSkinnedMesh || o.isInstancedMesh) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    if (mats.length !== 1 || !mats[0]) return // 多材质组的零件先不动
    const m = mats[0]
    let mid = __mergeMatIds.get(m)
    if (mid === undefined) {
      mid = ++__mergeMatSeq
      __mergeMatIds.set(m, mid)
    }
    const g0 = o.geometry
    const sig = Object.keys(g0.attributes).sort()
      .map((k) => k + ':' + g0.attributes[k].itemSize + ':' + g0.attributes[k].normalized)
      .join(',') + (g0.index ? '|idx' : '|noidx') +
      ((g0.morphAttributes && Object.keys(g0.morphAttributes).length) ? '|morph' : '')
    const key = mid + '#' + sig
    let rec = groups.get(key)
    if (!rec) {
      rec = { material: m, geos: [] }
      groups.set(key, rec)
    }
    const g = g0.clone()
    const rel = new THREE.Matrix4().multiplyMatrices(rootInv, o.matrixWorld)
    g.applyMatrix4(rel)
    // 镜像（负缩放）的零件：合并后三角形绕向会反过来，看起来就是"面翻进去了"。
    // 这里把绕向和法线翻回来，保证和合并前完全一致。
    if (rel.determinant() < 0) {
      const idx = g.getIndex()
      if (idx) {
        for (let i = 0; i + 2 < idx.count; i += 3) {
          const a = idx.getX(i)
          idx.setX(i, idx.getX(i + 2))
          idx.setX(i + 2, a)
        }
        idx.needsUpdate = true
      }
      const nrm = g.getAttribute('normal')
      if (nrm) {
        for (let i = 0; i < nrm.count; i++) nrm.setXYZ(i, -nrm.getX(i), -nrm.getY(i), -nrm.getZ(i))
        nrm.needsUpdate = true
      }
    }
    rec.geos.push(g)
    victims.push(o)
  })

  if (!victims.length) return 0
  let merged = 0
  const added = []
  for (const rec of groups.values()) {
    let geo
    if (rec.geos.length === 1) {
      geo = rec.geos[0]
    } else {
      try {
        geo = mergeGeometries(rec.geos, false)
      } catch (e) {
        console.warn('[merge] 合并失败，保留原零件', e && e.message)
        geo = null
      }
    }
    if (!geo) continue
    const mesh = new THREE.Mesh(geo, rec.material)
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.name = 'merged'
    added.push(mesh)
    merged += rec.geos.length
  }
  if (!added.length) return 0
  for (const o of victims) o.parent && o.parent.remove(o)
  for (const mesh of added) root.add(mesh)
  return victims.length - added.length
}

/**
 * 模型"自身落地中心"相对原点的偏移，按当前朝向/缩放转到世界方向。
 * 很多展品模型的原点并不在自己中心（比如 71 号和 45 号），直接改 rotY 会绕原点转、
 * 整个模型甩出去；这里用它自己的底面中心做补偿，让展品绕"自己站的地方"转。
 */
function hallExhibitFootOffset(cfg, rotY, scale) {
  const foot = (cfg._info && cfg._info.ownFoot) || [0, 0, 0]
  const r = THREE.MathUtils.degToRad(Number(rotY) || 0)
  const s = Number(scale) || 1
  const fx = (Number(foot[0]) || 0) * s
  const fz = (Number(foot[2]) || 0) * s
  const cs = Math.cos(r)
  const sn = Math.sin(r)
  // 绕竖轴旋转：x' = x·cos + z·sin，z' = -x·sin + z·cos
  return [fx * cs + fz * sn, -fx * sn + fz * cs]
}

function applyHallExhibitTransform(id) {
  const rec = hallExhibitObjs.get(id)
  if (!rec) return
  const c = rec.cfg
  const s = Number(c.scale) || 1
  const rot = Number(c.rotY) || 0
  // 配置里的 pos 记的是"落地中心"（展品站在地面上的位置），摆放时再按朝向把模型原点挪回去
  const [ox, oz] = hallExhibitFootOffset(c, rot, s)
  rec.root.position.set(c.pos[0] - ox, c.pos[1], c.pos[2] - oz)
  rec.root.rotation.y = THREE.MathUtils.degToRad(rot)
  rec.root.scale.setScalar(s)
  rec.root.visible = hallExhibitAllOn && c.visible !== false
  // 展品名标签跟着一起挪 / 一起隐藏
  syncExhibitLabel(id)
}

function applyAllHallExhibits() {
  for (const id of hallExhibitObjs.keys()) applyHallExhibitTransform(id)
}

/**
 * 按 hall 里展位热区的"当前位置"校正展品落位。
 * 配置里的坐标是上次生成时按热区算的（_info.anchor 记着当时的热区中心），
 * 如果之后在 Blender 里挪动了热区，这里就把差值补上 —— 展品跟着热区走。
 * 高度不动。
 */
function syncExhibitsToHitboxes() {
  if (!modelRoot || !hallExhibitCfg) return 0
  const hits = {}
  modelRoot.traverse((o) => {
    const m = /^Hitbox_Product_(\d+)$/i.exec(o.name || '')
    if (m) hits[m[1]] = o
  })
  const box = new THREE.Box3()
  const c = new THREE.Vector3()
  let moved = 0
  for (const [id, cfg] of Object.entries(hallExhibitCfg.exhibits || {})) {
    const h = hits[id]
    if (!h || !cfg || !cfg.pos) continue
    const anchor = cfg._info && cfg._info.anchor
    if (!anchor) continue
    box.setFromObject(h)
    box.getCenter(c)
    const dx = c.x - anchor[0]
    const dz = c.z - anchor[2]
    if (Math.abs(dx) < 0.001 && Math.abs(dz) < 0.001) continue
    cfg.pos[0] = +(cfg.pos[0] + dx).toFixed(3)
    cfg.pos[2] = +(cfg.pos[2] + dz).toFixed(3)
    cfg._info.anchor = [+c.x.toFixed(3), anchor[1], +c.z.toFixed(3)]
    moved++
    console.log('[hall-exhibits] ' + id + ' 跟着热区移动了 ' + dx.toFixed(2) + ', ' + dz.toFixed(2) + ' 米')
  }
  return moved
}

// 调试：看展品摆放这条链路走到哪一步
window.__opt2 = Object.assign(window.__opt2 || {}, {
  hallExhibitInfo: () => ({
    cfgLoaded: !!hallExhibitCfg,
    cfgIds: hallExhibitCfg ? Object.keys(hallExhibitCfg.exhibits || {}).length : 0,
    modelsReady: hallExhibitObjs.size,
    allOn: hallExhibitAllOn,
  }),
})

async function loadHallExhibits() {
  if (hallExhibitCfg) return
  try {
    const res = await fetch(HALL_EXHIBITS_URL + '?t=' + Date.now())
    hallExhibitCfg = await res.json()
  } catch (e) {
    console.warn('[hall-exhibits] 读配置失败', e)
    return
  }
  const list = hallExhibitCfg.exhibits || {}
  const ids = Object.keys(list)
  if (!ids.length) return
  // 配置读进来之后再按热区校正一次：在 Blender 里挪过展位热区的展品要跟着一起挪。
  // （原来这个校正放在模型加载回调里，那时候配置还没读回来，等于一直没执行）
  try {
    const moved = syncExhibitsToHitboxes()
    if (moved) console.log('[hall-exhibits] ' + moved + ' 个展品按热区位置校正')
  } catch (e) {
    console.warn('[hall-exhibits] 热区校正失败', e)
  }
  // 展品位置定下来之后建展品名标签（大字报式名牌，位置跟着热区走）
  try {
    refreshExhibitLabels()
    console.log('[hall-exhibits] 展品名标签 ' + exhibitLabels.size + ' 个')
  } catch (e) {
    console.warn('[hall-exhibits] 展品名标签生成失败', e)
  }
  if (!hallExhibitLoader) hallExhibitLoader = makeHallExhibitLoader()
  console.log('[hall-exhibits] 开始加载 ' + ids.length + ' 个展品模型')
  // 加载遮罩要等展品全部就位才揭开，所以这里统计进度（失败也算完成，避免卡在遮罩上）
  const totalN = ids.filter((id) => list[id] && list[id].model).length
  let doneN = 0
  let mergeMs = 0   // 诊断：统计"零件合并"花的总时间（手机上这一项可能是瓶颈）
  const oneExhibitDone = () => {
    doneN++
    setLoadingProgress(40 + (doneN / Math.max(1, totalN)) * 60, '正在加载展品 ' + doneN + ' / ' + totalN)
    if (doneN >= totalN) {
      console.log('[perf-load] 展品全部就位，其中零件合并共耗时 ' + Math.round(mergeMs) + ' ms')
      loadingState.exhibits = true
      finishLoadingIfReady()
    }
  }
  // 兜底：万一有模型一直不回调，两分钟后也揭开，别把人锁在加载页
  setTimeout(() => {
    if (!loadingState.exhibits) {
      loadingState.exhibits = true
      finishLoadingIfReady()
    }
  }, 120000)
  ids.forEach((id) => {
    const cfg = list[id]
    if (!cfg || !cfg.model) return
    // 手机/平板/低档机器走轻量版；万一轻量版缺失，自动回退到全精度版，不影响使用
    const fullUrl = withVersion(cfg.model)
    const useLod = USE_LOD_MODELS
    const firstUrl = useLod ? withVersion(lodPathOf(cfg.model)) : fullUrl
    const loadInto = (url, allowFallback) => hallExhibitLoader.load(url, (gltf) => {
      const root = gltf.scene
      root.name = 'HallExhibit_' + id
      root.traverse((o) => {
        if (!o.isMesh) return
        o.castShadow = true
        o.receiveShadow = true
      })
      const tMerge = performance.now()
      const savedParts = mergeExhibitMeshes(root)
      mergeMs += performance.now() - tMerge
      scene.add(root)
      hallExhibitObjs.set(id, { root, cfg })
      applyHallExhibitTransform(id)
      markShadowsDirty()
      console.log('[hall-exhibits] ' + id + ' 就位' + (useLod ? '（轻量版）' : '') + (savedParts ? '（零件 ' + savedParts + ' 个合并掉）' : ''))
      oneExhibitDone()
    }, undefined, (err) => {
      if (allowFallback && useLod) {
        console.warn('[hall-exhibits] ' + id + ' 轻量版缺失，回退全精度版')
        loadInto(fullUrl, false)
        return
      }
      console.warn('[hall-exhibits] ' + id + ' 加载失败', err)
      oneExhibitDone()
    })
    loadInto(firstUrl, true)
  })
  if (USE_LOD_MODELS) console.log('[hall-exhibits] 本次使用轻量版模型（减面 80%；想看全精度版用 ?lod=0）')
}

// ---------- 展品摆放面板 ----------
const placePanel = document.querySelector('#place-debug')
const pdPick = document.querySelector('#pd-pick')
const pdVisible = document.querySelector('#pd-visible')
const pdAll = document.querySelector('#pd-all')
const pdFine = document.querySelector('#pd-fine')
const PD_BASE_STEP = { x: 0.05, y: 0.02, z: 0.05, rot: 1, scale: 0.01 }
const PD_SLIDERS = {
  x: document.querySelector('#pd-x'),
  y: document.querySelector('#pd-y'),
  z: document.querySelector('#pd-z'),
  rot: document.querySelector('#pd-rot'),
  scale: document.querySelector('#pd-scale'),
}
// 每个滑杆旁边可以直接输入数字的框
const PD_NUMS = {
  x: document.querySelector('#pd-x-num'),
  y: document.querySelector('#pd-y-num'),
  z: document.querySelector('#pd-z-num'),
  rot: document.querySelector('#pd-rot-num'),
  scale: document.querySelector('#pd-scale-num'),
}
let pdCurrent = null

function pdSyncLabels() {
  for (const [k, el] of Object.entries(PD_SLIDERS)) {
    const b = document.querySelector('#pd-' + k + '-v')
    if (b && el) b.textContent = k === 'rot' ? String(Math.round(Number(el.value))) : Number(el.value).toFixed(2)
    const num = PD_NUMS[k]
    if (num && el && document.activeElement !== num) {
      num.value = k === 'rot' ? String(Number(el.value).toFixed(1)) : Number(el.value).toFixed(2)
    }
  }
}

// 数字框 -> 滑杆（数字为准；超出滑杆范围就把范围撑开，保证输入的数值不被夹掉）
function pdApplyFromNumber(key) {
  const num = PD_NUMS[key]
  const sl = PD_SLIDERS[key]
  if (!num || !sl) return
  const raw = Number(num.value)
  if (!Number.isFinite(raw)) return
  let v = key === 'scale' ? Math.max(0.01, raw) : raw
  const min = Number(sl.min)
  const max = Number(sl.max)
  if (v < min) sl.min = String(Math.floor(v))
  if (v > max) sl.max = String(Math.ceil(v))
  sl.value = String(v)
  pdApply()
}

function pdLoadCurrent() {
  const cfg = hallExhibitCfg && hallExhibitCfg.exhibits[pdCurrent]
  if (!cfg) return
  if (PD_SLIDERS.x) PD_SLIDERS.x.value = cfg.pos[0]
  if (PD_SLIDERS.y) PD_SLIDERS.y.value = cfg.pos[1]
  if (PD_SLIDERS.z) PD_SLIDERS.z.value = cfg.pos[2]
  if (PD_SLIDERS.rot) PD_SLIDERS.rot.value = cfg.rotY || 0
  if (PD_SLIDERS.scale) PD_SLIDERS.scale.value = cfg.scale || 1
  if (pdVisible) pdVisible.checked = cfg.visible !== false
  pdSyncLabels()
}

function pdFillPick() {
  if (!pdPick || !hallExhibitCfg) return
  const ids = Object.keys(hallExhibitCfg.exhibits || {})
  pdPick.innerHTML = ''
  for (const id of ids) {
    const opt = document.createElement('option')
    opt.value = id
    const nm = (PRODUCTS[id] && PRODUCTS[id].name) || ''
    opt.textContent = id + (nm ? '  ' + nm : '')
    pdPick.appendChild(opt)
  }
  if (ids.length) {
    pdCurrent = ids[0]
    pdPick.value = ids[0]
    pdLoadCurrent()
  }
}

function pdApply() {
  const cfg = hallExhibitCfg && hallExhibitCfg.exhibits[pdCurrent]
  if (!cfg) return
  cfg.pos = [Number(PD_SLIDERS.x.value), Number(PD_SLIDERS.y.value), Number(PD_SLIDERS.z.value)]
  cfg.rotY = Number(PD_SLIDERS.rot.value)
  cfg.scale = Number(PD_SLIDERS.scale.value)
  cfg.visible = pdVisible ? pdVisible.checked : true
  pdSyncLabels()
  applyHallExhibitTransform(pdCurrent)
  pdScheduleSave()
}

// 自动保存：停手 1.2 秒后写回配置文件，避免"改完忘了点保存、刷新就没了"
let pdSaveTimer = 0
function pdScheduleSave() {
  if (!hallExhibitCfg) return
  clearTimeout(pdSaveTimer)
  pdSaveTimer = setTimeout(async () => {
    try {
      const resp = await fetch('/api/exhibits-layout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hallExhibitCfg),
      })
      const el = document.querySelector('#pd-hall-info')
      if (el && resp.ok) {
        const base = el.textContent.replace(/ · 已自动保存.*$/, '')
        el.textContent = base + ' · 已自动保存'
      }
    } catch (e) { /* 忽略 */ }
  }, 1200)
}

// 微调模式：同样的拖动距离，数值走得更细
function pdUpdateStep(el) {
  const key = el.id.replace('pd-', '')
  const base = PD_BASE_STEP[key]
  if (!base) return
  el.step = String(pdFine && pdFine.checked ? base / 5 : base)
}

for (const el of Object.values(PD_SLIDERS)) {
  el?.addEventListener('input', () => { pdUpdateStep(el); pdApply() })
  el?.addEventListener('change', () => { pdUpdateStep(el); pdApply() })
}
for (const [key, el] of Object.entries(PD_NUMS)) {
  el?.addEventListener('input', () => pdApplyFromNumber(key))
  el?.addEventListener('change', () => pdApplyFromNumber(key))
}
pdPick?.addEventListener('change', () => { pdCurrent = pdPick.value; pdLoadCurrent() })
pdVisible?.addEventListener('change', () => { pdApply(); pdScheduleSave() })
pdAll?.addEventListener('change', () => {
  hallExhibitAllOn = pdAll.checked
  applyAllHallExhibits()
})
document.querySelector('#pd-reset')?.addEventListener('click', () => {
  const cfg = hallExhibitCfg && hallExhibitCfg.exhibits[pdCurrent]
  if (!cfg || !cfg.auto) return
  cfg.pos = cfg.auto.pos.slice()
  cfg.rotY = cfg.auto.rotY
  cfg.scale = cfg.auto.scale
  pdLoadCurrent()
  applyHallExhibitTransform(pdCurrent)
  try { showToast('已回到自动落位（记得点保存）') } catch {}
})
document.querySelector('#pd-save')?.addEventListener('click', async () => {
  if (!hallExhibitCfg) return
  try {
    const resp = await fetch('/api/exhibits-layout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hallExhibitCfg),
    })
    try { showToast(resp.ok ? '已保存，其他电脑打开也是这个位置' : ('保存失败：HTTP ' + resp.status)) } catch {}
  } catch (e) {
    try { showToast('保存失败：' + (e && e.message)) } catch {}
  }
})
document.querySelector('#pd-close')?.addEventListener('click', () => placePanel?.classList.add('hidden'))

// 配置读进来后填充面板
const pdReadyTimer = setInterval(() => {
  if (!hallExhibitCfg) return
  clearInterval(pdReadyTimer)
  pdFillPick()
}, 500)

// 展厅是否被全屏弹层完全盖住了（盖住就不用再渲染 3D 了）
function sceneCovered() {
  const shown = (sel) => {
    const el = document.querySelector(sel)
    return !!(el && el.classList.contains('show'))
  }
  return shown('#lightbox') ||            // HUD 图集 / 厂容厂貌图集
    shown('#pf-video-overlay') ||         // 产品视频
    shown('#promo-overlay') ||            // 企业宣传视频
    shown('#pf-pdf-overlay') ||           // 产品手册 PDF
    shown('#pf-intro-window') ||          // 产品简介
    shown('#explode-overlay') ||          // 爆炸图
    !!(productFull && productFull.classList.contains('show')) // 产品弹窗（自带底图和自己的 3D 视口）
}

function animate() {
  requestAnimationFrame(animate)
  // 全屏弹层（图集灯箱 / 视频 / PDF / 爆炸图 / 产品弹窗）盖住展厅时，不渲染 3D。
  // 展厅这一帧要提交 2000 万个三角面，如果继续在后台跑，弹层里的缩放、翻页、
  // 播放都会被拖出卡顿感 —— 之前"看图集缩放也卡"就是这个原因。
  if (sceneCovered()) { lastAnimT = performance.now() / 1000; return }
  const t = performance.now() / 1000
  renderer.info.reset() // 诊断用：每帧从这里开始累加
  perfAcc._mark = 0
  // 阴影贴图：只在"脏"的这几帧重算（见 markShadowsDirty）
  if (shadowDirtyFrames > 0) {
    renderer.shadowMap.needsUpdate = true
    shadowDirtyFrames--
  }
  const dt = Math.min(0.1, Math.max(0.001, t - lastAnimT))
  lastAnimT = t
  // 展品挡板盒：泛光层要用它当遮挡板，相机防穿模也要用它当碰撞盒，
  // 所以不管泛光开关如何，都要保证它跟着展品加载及时更新。
  if (BLOOM_OCC_MODE === 'box') {
    const exN = hallExhibitObjs.size
    if (exN !== bloomProxyExhibitCount || performance.now() - bloomProxyBuiltAt > 30000) bloomBuildProxy()
  }
  if (controls.enabled) {
    // 滚轮缩放的"安全范围"：入口和展区一样，最近都能到 3 米
    // （原来入口最近只到 25 米，贴近展厅外立面看不了；3 米这个下限是为了不钻进模型里）
    const inZone = currentZone !== null
    // 入口和展区用同一个最近距离（2 米）：从入口贴近展厅、再飞进各展区是"无缝"的，
    // 不会出现"入口能贴近、进了展区反而被推远"的断层。
    // 2 米这个半径让相机待在通道里，不会钻进机器底下/展台缝里。
    const lo = 2
    // 最远都能拉到 100 米：入口拉 100 米是初始构图的上限，展区内往回拉也能拉到同样远的距离
    const hi = 200   // 视角拉远的上限（超过就拉不动了）
    // 不允许转到地面以下 —— 这里只认"展厅地面"。
    // 不能再按"无限地面（草地）"的高度算：草地是一块背景平面（比展厅地面高十几米），
    // 拿它当限制就会出现"入口越拉近、能转的角度越窄"，转到一半就被卡住。
    // 草地已经改成单面，镜头低于它时看不到它，所以不会露出"绿色天花板"。
    // 镜头的地面基准：就是展厅地面本身（按你的要求恢复原高度，不留额外余量）
    const groundY = hallFloorY
    // 关键：这里用"极角上限"来保证相机永远在地面之上，而不是每帧把相机往上搬。
    // 直接改 camera.position.y 会打乱 OrbitControls 的球面状态（它每帧按相机位置反推
    // 距离和角度），表现就是"按住左键转动时镜头自己往外滑"，而且转到角度上限后
    // 继续拖会变成"平移"。用极角限制则是纯旋转约束，转动时距离和中心都不变。
    const dy = groundY - controls.target.y   // 相机相对目标允许的最低高度
    const loSafe = Math.max(lo, dy + 0.5)    // 最近距离永远大于地面余量，约束才有解
    // 注意：这里必须同时判断 min 和 max。原来只判断 maxDistance，而它一直是 100，
    // 所以 minDistance 从头到尾停在 25 米 —— 5 号（13.7 米）和 7 号（19.7 米）展区的
    // 机位比 25 米近，飞进去之后立刻被推回 25 米，看着就是"飞进去又退回来"。
    if (controls.minDistance !== loSafe || controls.maxDistance !== hi) {
      controls.minDistance = loSafe
      controls.maxDistance = hi
    }
    const dist = Math.max(1e-3, camera.position.distanceTo(controls.target))
    const polarLimit = Math.acos(Math.max(-1, Math.min(1, dy / dist)))
    // 只用"地面"这一个限制：想转到多低就转多低，直到相机贴到地面为止
    // （原来是 min(93.6°, 地面角度)，那个 93.6° 的硬上限就是"角度不够"的原因）
    controls.maxPolarAngle = polarLimit
    // 兜底：只有在真的钻到地面以下很多时（例如平移把目标带下去了）才轻抬一下，
    // 平时转动/缩放不会碰到这里，所以不会干扰手感。
    if (camera.position.y < groundY - 2) camera.position.y = groundY - 2
    controls.update()
    // 说明：这里曾经做过"射线防穿模"（撞到展品盒就把相机推到盒子外），
    // 但实测近距离转动时会突然被推一下（看着像"卡住"或"飞"），
    // 现在改成更简单可靠的做法：展区内给一个 3 米的最近距离（见上面的 lo），
    // 相机待在通道里就不会钻进机器底部和展台之间。
  }

  // 云层缓慢漂移
  if (cloudDome && cloudEnabled) cloudDome.rotation.y += dt * 0.01 * cloudDrift

  // 屏幕扫描线动效
  for (const m of screenFxMaterials) m.uniforms.uTime.value = t
  // 中心六边形地面流动
  if (hexFlowMat) hexFlowMat.uniforms.uTime.value = t
  // 光轨（外圈蓝/紫光环）：随机互换颜色 + 平滑过渡
  trailSwapTimer -= dt
  if (trailSwapTimer <= 0) {
    trailSwapTimer = 3 + Math.random() * 5
    const a = lightTrailMats[0]
    const b = lightTrailMats[1]
    if (a && b) {
      const tmp = a.userData.targetColor
      a.userData.targetColor = b.userData.targetColor
      b.userData.targetColor = tmp
    } else if (a) {
      // 单环：青/冰蓝微交替
      a.userData.targetColor = a.userData.targetColor && a.userData.targetColor.getHex() === 0x00e5ff
        ? new THREE.Color(0x00e5ff)
        : new THREE.Color(0x00e5ff)
    }
  }
  for (const m of lightTrailMats) {
    m.uniforms.uTime.value = t
    if (m.userData.targetColor) m.uniforms.uColor.value.lerp(m.userData.targetColor, Math.min(1, dt * 2.5))
    // 粒子晕颜色跟随主环颜色
    if (m.userData.haloMat) m.userData.haloMat.color.copy(m.uniforms.uColor.value)
  }

  // 星星球旋转 + 浮动
  if (starGlobeGroup) {
    starGlobeOuter.rotation.y += 0.0028
    starGlobeInner.rotation.y -= 0.0042
    starGlobeOuter.rotation.x = Math.sin(t * 0.12) * 0.06
    starGlobeInner.rotation.x = -Math.sin(t * 0.16) * 0.08
    starGlobeGroup.position.y = starGlobeBaseY + Math.sin(t * 0.5) * 0.15
    // 顶部 Logo：轻微浮动 + 扫光推进 + 始终面向相机
    if (starLogoHolder) {
      starLogoHolder.position.y = starLogoBaseY + Math.sin(t * 0.7) * 0.1
      if (starLogo && starLogo.material && starLogo.material.uniforms) {
        starLogo.material.uniforms.uTime.value = t
        starLogo.quaternion.copy(camera.quaternion)
      }
    }
  }

  // 档位把泛光和抗锯齿都关掉时，直接渲染到屏幕（省掉后处理那一次全屏拷贝，
  // 低档位机器的表现和以前"根本不创建 composer"时一致）
  const useComposer = !!(composer && (QUALITY.bloom || QUALITY.fxaa || (ssaoPass && ssaoPass.enabled)))
  if (useComposer) {
    // 选择性泛光：先用"只含发光件"的一层渲染出泛光，再叠到正常画面上
    // 交付模式下泛光固定开（面板隐藏，不允许旧存档把它关掉）
    const bloomOnByPanel = DEBUG_UI_ENABLED ? !!(fdBlo && fdBlo.checked) : true
    if (selectiveBloom && QUALITY.bloom && bloomComposer && bloomOnByPanel && !bloomAutoOff && PERF_TEST !== 'nobloom') {
      // 挡板盒的重算放在 animate 开头统一做（相机防穿模也要用它）
      const prevBg = scene.background
      const prevFog = scene.fog
      const prevClear = renderer.getClearColor(new THREE.Color())
      const prevClearAlpha = renderer.getClearAlpha()
      scene.background = null
      scene.fog = null // 雾会把黑色挡板染成雾色，泛光层会被整片误点亮
      // 泛光层这一遍必须用"黑底"清屏：否则会把页面底色（0x050816）也当成发光叠到画面上，
      // 整屏被抬亮一层 —— 就是"糊了一层雾 / 像半透明"的来源。
      renderer.setClearColor(0x000000, 0)
      bloomDarkenNonGlow() // 非发光件涂黑当挡板，泛光层才有遮挡关系
      bloomComposer.render()
      bloomRestoreMaterials()
      renderer.setClearColor(prevClear, prevClearAlpha)
      scene.fog = prevFog
      scene.background = prevBg
    }
    composer.render()
  } else {
    renderer.render(scene, camera)
  }
  perfAcc.total = renderer.info.render.calls
  perfAcc.tris = renderer.info.render.triangles
  frames++
  if (frames === 120 && !window.__gpuInfo) { // 临时：打印渲染器/像素比信息
    window.__gpuInfo = true
    try {
      const gl = renderer.getContext()
      const dbg = gl.getExtension('WEBGL_debug_renderer_info')
      const size = renderer.getDrawingBufferSize(new THREE.Vector2())
      console.log('[gpu] 渲染器=' + (dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)) +
        ' | 厂商=' + (dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : '?') +
        ' | devicePixelRatio=' + window.devicePixelRatio + ' | renderer.pixelRatio=' + renderer.getPixelRatio() +
        ' | 绘制像素=' + Math.round(size.x) + '×' + Math.round(size.y) +
        ' | 面板像素比=' + (document.querySelector('#rd-dpr') ? document.querySelector('#rd-dpr').value : '?'))
    } catch (e) { console.log('[gpu] 读取失败', e && e.message) }
  }

}
animate()








